"use strict";

import Bitstring from "/app/deps/hologram/assets/js/bitstring.mjs";
import ERTS from "/app/deps/hologram/assets/js/erts.mjs";
import Hologram from "/app/deps/hologram/assets/js/hologram.mjs";
import HologramBoxedError from "/app/deps/hologram/assets/js/errors/boxed_error.mjs";
import HologramInterpreterError from "/app/deps/hologram/assets/js/errors/interpreter_error.mjs";
import Interpreter from "/app/deps/hologram/assets/js/interpreter.mjs";
import MemoryStorage from "/app/deps/hologram/assets/js/memory_storage.mjs";
import PerformanceTimer from "/app/deps/hologram/assets/js/performance_timer.mjs";
import Type from "/app/deps/hologram/assets/js/type.mjs";
import Utils from "/app/deps/hologram/assets/js/utils.mjs";

const startTime = PerformanceTimer.start();

globalThis.Hologram.config = {errorOverlay: false, liveReload: false, stacktraces: false};

ERTS.appVersions = {};

Interpreter.defineErlangFunction("binary", "_aho_corasick_search", 3, (subject, rootNode, startIndex) => {
    Bitstring.maybeSetBytesFromText(subject);

    let candidateNode = rootNode;
    let bestMatch = null;

    for (let index = startIndex; index < subject.bytes.length; index++) {
      const byte = subject.bytes[index];

      // Follow failure links until we find a matching transition
      while (candidateNode !== null && !candidateNode.children.has(byte)) {
        candidateNode = candidateNode.failure;
      }

      // Transition to next state or reset to root
      candidateNode = candidateNode
        ? candidateNode.children.get(byte) || rootNode
        : rootNode;

      // Check if current state has any pattern matches
      if (candidateNode.output.length > 0) {
        // Check all matches at this position and keep the longest one
        for (const matchedPattern of candidateNode.output) {
          const matchedPatternLength = matchedPattern.length;
          const matchIndex = index - matchedPatternLength + 1;

          // Update best match if this is first match or a longer match at same/earlier position
          if (
            bestMatch === null ||
            matchIndex < bestMatch.index ||
            (matchIndex === bestMatch.index &&
              matchedPatternLength > bestMatch.length)
          ) {
            bestMatch = {index: matchIndex, length: matchedPatternLength};
          }
        }
      }

      // Check if we should return the current best match
      // Return when we have a match AND we're past the match end AND we're not matching any pattern
      // (candidateNode == rootNode means we're not in the middle of matching)
      if (
        bestMatch !== null &&
        index >= bestMatch.index + bestMatch.length &&
        candidateNode === rootNode
      ) {
        return bestMatch;
      }
    }

    return bestMatch;
  });

Interpreter.defineErlangFunction("binary", "_boyer_moore_search", 4, (subject, patternBytes, badShift, startIndex) => {
    Bitstring.maybeSetBytesFromText(subject);

    const patternLength = patternBytes.length;
    const patternMaxIndex = patternLength - 1;
    const searchLimit = subject.bytes.length - patternLength;

    for (let index = startIndex; index <= searchLimit; index++) {
      // Compare pattern from right to left
      let patternIndex = patternMaxIndex;

      while (
        patternIndex >= 0 &&
        patternBytes[patternIndex] === subject.bytes[index + patternIndex]
      ) {
        // Full match found
        if (patternIndex === 0) {
          return {index, length: patternLength};
        }

        patternIndex--;
      }

      // No match - use bad character shift to skip ahead
      const currentByte = subject.bytes[index + patternMaxIndex];
      const shiftValue = badShift[currentByte];
      const shift = shiftValue >= 0 ? shiftValue : patternLength;

      if (shift > 0) {
        index += shift - 1; // -1 because loop will increment
      }
    }

    return null;
  });

Interpreter.defineErlangFunction("binary", "_is_valid_pattern", 1, (pattern) => {
    if (Type.isCompiledPattern(pattern)) {
      const patternType = pattern.data[0].value;
      const compiledData = ERTS.binaryPatternRegistry.get(pattern.data[1]);

      if (!compiledData || compiledData.type !== patternType) {
        return false;
      }

      return patternType !== "bm" || Boolean(compiledData.patternBytes);
    }

    const isNonEmptyBinary = (term) => {
      if (!Type.isBinary(term)) {
        return false;
      }

      Bitstring.maybeSetBytesFromText(term);

      return term.bytes.length > 0;
    };

    if (Type.isBinary(pattern)) {
      return isNonEmptyBinary(pattern);
    }

    return (
      Type.isProperList(pattern) &&
      pattern.data.length > 0 &&
      pattern.data.every(isNonEmptyBinary)
    );
  });

Interpreter.defineErlangFunction("binary", "_parse_search_opts", 1, (opts) => {
    if (!Type.isList(opts) || Type.isImproperList(opts)) {
      return null;
    }

    let global = false;
    let trim = false;
    let trimAll = false;
    let scopeStart = 0;
    let scopeLength = null; // null means "until end"

    for (const option of opts.data) {
      if (Type.isAtom(option)) {
        if (option.value === "global") {
          global = true;
          continue;
        }

        if (option.value === "trim") {
          trim = true;
          continue;
        }

        if (option.value === "trim_all") {
          trimAll = true;
          continue;
        }

        return null;
      }

      const isScopeTuple =
        Type.isTuple(option) &&
        option.data.length === 2 &&
        Type.isAtom(option.data[0]) &&
        option.data[0].value === "scope";

      if (!isScopeTuple) {
        return null;
      }

      const scopeData = option.data[1];

      const isValidScope =
        Type.isTuple(scopeData) &&
        scopeData.data.length === 2 &&
        Type.isInteger(scopeData.data[0]) &&
        Type.isInteger(scopeData.data[1]);

      if (!isValidScope) {
        return null;
      }

      const startValue = scopeData.data[0].value;
      const lengthValue = scopeData.data[1].value;

      scopeStart = Number(startValue);
      scopeLength = Number(lengthValue);
    }

    return {global, trim, trimAll, scopeStart, scopeLength};
  });

Interpreter.defineErlangFunction("binary", "compile_pattern", 1, (pattern) => {
    const raiseInvalidPattern = () => {
      Interpreter.raiseBifError("badarg", "binary", "compile_pattern", [
        pattern,
      ]);
    };

    const compileBoyerMoorePattern = (singlePattern) => {
      Bitstring.maybeSetBytesFromText(singlePattern);

      if (singlePattern.bytes.length == 0) {
        raiseInvalidPattern();
      }

      const badShift = {};
      const length = singlePattern.bytes.length - 1;
      const patternBytes = singlePattern.bytes;

      // Seed the badShift object with an initial value of -1 for each byte
      for (let i = 0; i < 256; i++) {
        badShift[i] = -1;
      }

      // Overwrite with the actual value for each byte in the pattern
      singlePattern.bytes.forEach((byte, index) => {
        badShift[byte] = length - index;
      });

      const ref = Erlang["make_ref/0"]();
      const compiledPatternData = {type: "bm", badShift, patternBytes};
      ERTS.binaryPatternRegistry.put(ref, compiledPatternData);

      return Type.tuple([Type.atom("bm"), ref]);
    };

    const compileAhoCorasickPattern = (patterns) => {
      const rootNode = {
        children: new Map(),
        output: [],
        failure: null,
      };

      // Build tries for each pattern
      patterns.data.forEach((p) => {
        Bitstring.maybeSetBytesFromText(p);

        if (p.bytes.length === 0) {
          raiseInvalidPattern();
        }

        let node = rootNode;

        p.bytes.forEach((byte) => {
          if (!node.children.has(byte)) {
            node.children.set(byte, {
              children: new Map(),
              output: [],
              failure: null,
            });
          }

          node = node.children.get(byte);
        });

        node.output.push(p.bytes);
      });

      // Build failure links (where to fall back when a match fails)
      const queue = [];

      for (const [_byte, childNode] of rootNode.children) {
        childNode.failure = rootNode;
        queue.push(childNode);
      }

      while (queue.length > 0) {
        const node = queue.shift();

        for (const [byte, childNode] of node.children) {
          queue.push(childNode);

          let failureNode = node.failure;

          while (failureNode !== null && !failureNode.children.has(byte)) {
            failureNode = failureNode.failure;
          }

          childNode.failure =
            failureNode === null ? rootNode : failureNode.children.get(byte);

          childNode.output = childNode.output.concat(childNode.failure.output);
        }
      }

      const ref = Erlang["make_ref/0"]();
      const compiledPatternData = {type: "ac", rootNode};
      ERTS.binaryPatternRegistry.put(ref, compiledPatternData);

      return Type.tuple([Type.atom("ac"), ref]);
    };

    if (Type.isBinary(pattern)) {
      return compileBoyerMoorePattern(pattern);
    } else if (
      Type.isProperList(pattern) &&
      pattern.data.length > 0 &&
      pattern.data.every((i) => Type.isBinary(i))
    ) {
      return pattern.data.length == 1
        ? compileBoyerMoorePattern(pattern.data[0])
        : compileAhoCorasickPattern(pattern);
    }

    raiseInvalidPattern();
  });

Interpreter.defineErlangFunction("binary", "match", 2, (subject, pattern) => {
    try {
      return Erlang_Binary["match/3"](subject, pattern, Type.list());
    } catch (error) {
      // Re-raise with this arity's own identity - the BEAM reports the
      // called function's frame, not the delegate's.
      if (error.struct) {
        Interpreter.raiseBifError("badarg", "binary", "match", [
          subject,
          pattern,
        ]);
      }

      throw error;
    }
  });

Interpreter.defineErlangFunction("binary", "match", 3, (subject, pattern, options) => {
    const raiseBadarg = () => {
      Interpreter.raiseBifError("badarg", "binary", "match", [
        subject,
        pattern,
        options,
      ]);
    };

    if (!Type.isBinary(subject)) {
      raiseBadarg();
    }

    // Ensure subject bytes are available after validation
    Bitstring.maybeSetBytesFromText(subject);

    const parsedOpts = Erlang_Binary["_parse_search_opts/1"](options);

    if (parsedOpts === null) {
      raiseBadarg();
    }

    const {global, trim, trimAll, scopeStart, scopeLength} = parsedOpts;

    // match/3 only supports :scope; reject :global, :trim, and :trim_all
    if (global || trim || trimAll) {
      raiseBadarg();
    }

    // Validate scope start is within subject bounds
    if (scopeStart < 0 || scopeStart > subject.bytes.length) {
      raiseBadarg();
    }

    // Validate that if scopeLength is specified, scopeStart + scopeLength >= 0
    if (scopeLength !== null && scopeStart + scopeLength < 0) {
      raiseBadarg();
    }

    const effectiveLength =
      scopeLength === null ? subject.bytes.length - scopeStart : scopeLength;

    // Validate scope doesn't extend beyond subject
    if (scopeStart + effectiveLength > subject.bytes.length) {
      raiseBadarg();
    }

    const scopeEnd = scopeStart + effectiveLength;

    // For negative scopeLength, ensure slice bounds are in correct order
    const actualStart = Math.min(scopeStart, scopeEnd);
    const actualEnd = Math.max(scopeStart, scopeEnd);

    // Validate pattern before checking scope length - pattern errors take priority
    const isCompiledPattern = Type.isCompiledPattern(pattern);

    let compiledPattern;

    try {
      compiledPattern = isCompiledPattern
        ? pattern
        : Erlang_Binary["compile_pattern/1"](pattern);
    } catch (error) {
      // Re-raise pattern compilation errors with this function's identity
      if (error.struct) {
        raiseBadarg();
      }

      throw error;
    }

    const patternType = compiledPattern.data[0].value;
    const patternRef = compiledPattern.data[1];
    const compiledData = ERTS.binaryPatternRegistry.get(patternRef);

    if (!compiledData || compiledData.type !== patternType) {
      raiseBadarg();
    }

    // After pattern validation passes, check if search range is available
    if (scopeLength === 0) {
      return Type.atom("nomatch");
    }

    const scopedBytes = subject.bytes.slice(actualStart, actualEnd);
    const scopedSubject = Bitstring.fromBytes(scopedBytes);

    // Find first pattern match based on algorithm type
    let match = null;

    if (patternType === "bm") {
      const patternBytes = compiledData.patternBytes;

      if (!patternBytes) {
        raiseBadarg();
      }

      match = Erlang_Binary["_boyer_moore_search/4"](
        scopedSubject,
        patternBytes,
        compiledData.badShift,
        0,
      );
    } else {
      match = Erlang_Binary["_aho_corasick_search/3"](
        scopedSubject,
        compiledData.rootNode,
        0,
      );
    }

    if (match === null) {
      return Type.atom("nomatch");
    }

    // Convert match position from scoped to absolute
    const absolutePos = actualStart + match.index;

    return Type.tuple([Type.integer(absolutePos), Type.integer(match.length)]);
  });

Interpreter.defineErlangFunction("binary", "split", 2, (subject, pattern) => {
    try {
      return Erlang_Binary["split/3"](subject, pattern, Type.list());
    } catch (error) {
      // Re-raise with this arity's own identity - the BEAM reports the
      // called function's frame, not the delegate's.
      if (error.struct) {
        Interpreter.raiseBifError("badarg", "binary", "split", [
          subject,
          pattern,
        ]);
      }

      throw error;
    }
  });

Interpreter.defineErlangFunction("binary", "split", 3, (subject, pattern, options) => {
    // Helper: Apply trimming options to the split parts
    const applyTrim = (parts) => {
      if (parts.length === 0) {
        return parts;
      }

      if (trimAll) {
        return parts.filter((part) => !Bitstring.isEmpty(part));
      }

      if (!trim) {
        return parts;
      }

      let end = parts.length;
      while (end > 0 && Bitstring.isEmpty(parts[end - 1])) {
        end--;
      }

      return parts.slice(0, end);
    };

    // Helper: Convert byte slice to bitstring (text-based if valid UTF-8)
    const bytesToBitstring = (bytes) => {
      try {
        const text = ERTS.utf8Decoder.decode(bytes);
        return Bitstring.fromText(text);
      } catch {
        return Bitstring.fromBytes(bytes);
      }
    };

    const raiseBadarg = () => {
      Interpreter.raiseBifError("badarg", "binary", "split", [
        subject,
        pattern,
        options,
      ]);
    };

    // Validate subject is a binary
    if (!Type.isBinary(subject)) {
      raiseBadarg();
    }

    // Ensure subject bytes are available after validation
    Bitstring.maybeSetBytesFromText(subject);

    const parsedOpts = Erlang_Binary["_parse_search_opts/1"](options);

    if (parsedOpts === null) {
      raiseBadarg();
    }

    const {global, trim, trimAll, scopeStart, scopeLength} = parsedOpts;

    // Validate scope start is within subject bounds
    if (scopeStart < 0 || scopeStart > subject.bytes.length) {
      raiseBadarg();
    }

    // Validate that if scopeLength is specified, scopeStart + scopeLength >= 0
    if (scopeLength !== null && scopeStart + scopeLength < 0) {
      raiseBadarg();
    }

    if (!Erlang_Binary["_is_valid_pattern/1"](pattern)) {
      raiseBadarg();
    }

    // Pre-compile pattern once before loop to avoid recompilation on each match
    const compiledPattern = Type.isCompiledPattern(pattern)
      ? pattern
      : Erlang_Binary["compile_pattern/1"](pattern);

    const effectiveLength =
      scopeLength === null ? subject.bytes.length - scopeStart : scopeLength;

    // Validate scope doesn't extend beyond subject
    if (scopeStart + effectiveLength > subject.bytes.length) {
      raiseBadarg();
    }

    const scopeEnd = scopeStart + effectiveLength;

    // For negative scopeLength, ensure slice bounds are in correct order
    const actualStart = Math.min(scopeStart, scopeEnd);
    const actualEnd = Math.max(scopeStart, scopeEnd);

    // No search range available - return unsplit subject
    if (actualStart >= subject.bytes.length) {
      const parts = [bytesToBitstring(subject.bytes)];
      return Type.list(applyTrim(parts));
    }

    // Main split logic using match/3

    const results = [];
    let cursor = 0; // position in full subject where next segment starts
    let searchPos = actualStart; // current search position
    let foundMatch = true; // track if loop exited naturally vs via break

    while (searchPos < actualEnd) {
      const remainingLength = actualEnd - searchPos;

      const matchOptions = Type.list([
        Type.tuple([
          Type.atom("scope"),
          Type.tuple([Type.integer(searchPos), Type.integer(remainingLength)]),
        ]),
      ]);

      // Use match/3 to find next occurrence with pre-compiled pattern
      const matchResult = Erlang_Binary["match/3"](
        subject,
        compiledPattern,
        matchOptions,
      );

      // No more matches found
      if (Type.isAtom(matchResult) && matchResult.value === "nomatch") {
        const remaining =
          cursor < subject.bytes.length
            ? bytesToBitstring(subject.bytes.slice(cursor))
            : Bitstring.fromText("");

        results.push(remaining);
        foundMatch = false;

        break;
      }

      // Extract match position and length
      const matchPos = matchResult.data[0].value;
      const matchLen = matchResult.data[1].value;
      const matchStart = Number(matchPos);
      const matchLength = Number(matchLen);

      // Add part before match (if any)
      if (matchStart > cursor) {
        const beforeMatch = bytesToBitstring(
          subject.bytes.slice(cursor, matchStart),
        );

        results.push(beforeMatch);
      } else if (matchStart === cursor) {
        // Empty part before match
        results.push(Bitstring.fromText(""));
      }

      // Update cursor to position after match
      cursor = matchStart + matchLength;
      searchPos = cursor;

      if (!global) {
        // For non-global split, append remaining and stop
        const remaining =
          cursor < subject.bytes.length
            ? bytesToBitstring(subject.bytes.slice(cursor))
            : Bitstring.fromText("");

        results.push(remaining);
        foundMatch = false;

        break;
      }
    }

    // Collect any remaining bytes after the last match (for global splits that exited naturally)
    if (global && foundMatch && cursor < subject.bytes.length) {
      const remaining = bytesToBitstring(subject.bytes.slice(cursor));
      results.push(remaining);
    }

    // Add trailing empty when global split ends at subject boundary
    if (global && cursor === subject.bytes.length && results.length > 0) {
      results.push(Bitstring.fromText(""));
    }

    if (results.length === 0) {
      results.push(bytesToBitstring(subject.bytes));
    }

    const trimmedResults = applyTrim(results);

    return Type.list(trimmedResults);
  });

Interpreter.defineErlangFunction("code", "ensure_loaded", 1, (module) => {
    if (!Type.isAtom(module)) {
      Interpreter.raiseFunctionClauseError("code", "ensure_loaded", 1, [
        module,
      ]);
    }

    return typeof Interpreter.moduleProxy(module) === "undefined"
      ? Type.tuple([Type.atom("error"), Type.atom("nofile")])
      : Type.tuple([Type.atom("module"), module]);
  });

Interpreter.defineErlangFunction("elixir_config", "identifier_tokenizer", 0, () => Type.alias("String.Tokenizer"));

Interpreter.defineErlangFunction("erl_erts_errors", "_expand_error", 1, (fragment) => {
    if (typeof fragment === "object") {
      return Type.list([
        Type.bitstring("not a textual representation of "),
        Type.bitstring(fragment.notEncodable),
      ]);
    }

    const texts = {
      bad_base: "not an integer in the range 2 through 36",
      bad_time_unit: "invalid time unit",
      bad_encode_option: "not one of the atoms: latin1, utf8, or unicode",
      bad_ext_term: "invalid external representation of a term",
      bad_unicode: "invalid UTF8 encoding",
      non_existing_atom: "not an already existing atom",
      not_atom: "not an atom",
      not_binary: "not a binary",
      not_bitstring: "not a bitstring",
      not_cons: "not a nonempty list",
      not_float: "not a float",
      not_fun: "not a fun",
      not_integer: "not an integer",
      not_iodata: "not an iodata term",
      not_iolist: "not an iolist term",
      not_list: "not a list",
      not_map: "not a map",
      not_number: "not a number",
      not_pid: "not a pid",
      not_proper_list: "not a proper list",
      not_ref: "not a reference",
      not_string: "not a list of characters",
      not_tuple: "not a tuple",
      range: "out of range",
    };

    return Type.bitstring(texts[fragment] ?? fragment);
  });

Interpreter.defineErlangFunction("erl_erts_errors", "_format_erlang_error", 3, (fun, argsOrArity, _cause) => {
    const args = Type.isList(argsOrArity) ? argsOrArity.data : null;

    const mustBeBinary = (term, error = "") =>
      Type.isBinary(term) ? error : "not_binary";

    const mustBeList = (term, error = "") => {
      if (!Type.isList(term)) {
        return "not_list";
      }

      return Type.isProperList(term) ? error : "not_proper_list";
    };

    const mustBeBase = (term) =>
      Type.isInteger(term) && term.value >= 2n && term.value <= 36n
        ? ""
        : "bad_base";

    // Mirrors OTP's list_to_something/2, which probes the term with
    // length/1.
    const listToSomething = (term, error) =>
      Type.isProperList(term) ? [error] : ["not_list"];

    // Mirrors OTP's is_flat_char_list/1: a proper list of encodable
    // codepoints.
    const isFlatCharList = (term) =>
      Type.isProperList(term) &&
      term.data.every(
        (elem) =>
          Type.isInteger(elem) &&
          elem.value >= 0n &&
          elem.value <= 0x10ffffn &&
          !(elem.value >= 0xd800n && elem.value <= 0xdfffn),
      );

    const mustBeAtom = (term) => (Type.isAtom(term) ? "" : "not_atom");

    const mustBeInt = (term) => (Type.isInteger(term) ? "" : "not_integer");

    const mustBeNonNegInt = (term) => {
      if (!Type.isInteger(term)) {
        return "not_integer";
      }

      return term.value >= 0n ? "" : "range";
    };

    // Mirrors OTP's must_be_time_unit/1, which probes the term with a
    // convert_time_unit call - the client uses the non-raising validity
    // check instead.
    const mustBeTimeUnit = (term) =>
      Erlang["_is_valid_time_unit/1"](term) ? "" : "bad_time_unit";

    // Mirrors OTP's element clause, shared by the delete_element,
    // insert_element, and setelement delegations.
    const elementFragments = (index, tuple) => {
      let indexError = "";

      if (!Type.isInteger(index)) {
        indexError = "not_integer";
      } else if (
        index.value <= 0n ||
        (Type.isTuple(tuple) && index.value > BigInt(tuple.data.length))
      ) {
        indexError = "range";
      }

      return [indexError, Type.isTuple(tuple) ? "" : "not_tuple"];
    };

    // Mirrors OTP's do_binary_to_atom/3. The unicode validity probe checks
    // whether the binary decodes as UTF-8.
    const doBinaryToAtom = (bin, encoding, defaultError) => {
      const encodingName = Type.isAtom(encoding) ? encoding.value : "invalid";

      if (encodingName === "latin1") {
        return [mustBeBinary(bin, defaultError)];
      }

      if (encodingName === "unicode" || encodingName === "utf8") {
        if (!Type.isBinary(bin)) {
          return ["not_binary"];
        }

        return Bitstring.toText(bin) !== false
          ? [defaultError]
          : ["bad_unicode"];
      }

      return [mustBeBinary(bin), "bad_encode_option"];
    };

    switch (fun.value) {
      case "abs":
      case "float":
      case "round":
      case "trunc":
        return ["not_number"];

      case "append_element":
        return ["not_tuple"];

      case "apply": {
        if (args?.length !== 3) {
          return [];
        }

        return [mustBeAtom(args[0]), mustBeAtom(args[1]), mustBeList(args[2])];
      }

      case "atom_to_binary": {
        if (args?.length === 1) {
          return ["not_atom"];
        }

        if (args?.length !== 2) {
          return [];
        }

        const [atom, encoding] = args;

        let atomError = "";

        if (!Type.isAtom(atom)) {
          atomError = "not_atom";
        } else if (Type.isAtom(encoding) && encoding.value === "latin1") {
          atomError = "contains a character not expressible in latin1";
        }

        const isKnownEncoding =
          Type.isAtom(encoding) &&
          ["latin1", "unicode", "utf8"].includes(encoding.value);

        return [
          atomError,
          isKnownEncoding ? "" : "is an invalid encoding option",
        ];
      }

      case "atom_to_list":
        return ["not_atom"];

      case "binary_to_atom": {
        if (args?.length === 1) {
          return doBinaryToAtom(args[0], Type.atom("utf8"), "");
        }

        if (args?.length !== 2) {
          return [];
        }

        return doBinaryToAtom(args[0], args[1], "");
      }

      case "binary_to_existing_atom": {
        if (args?.length === 1) {
          return doBinaryToAtom(
            args[0],
            Type.atom("utf8"),
            "non_existing_atom",
          );
        }

        if (args?.length !== 2) {
          return [];
        }

        return doBinaryToAtom(args[0], args[1], "non_existing_atom");
      }

      case "binary_to_float": {
        if (args?.length !== 1) {
          return [];
        }

        return [mustBeBinary(args[0], {notEncodable: "a float"})];
      }

      case "binary_to_integer": {
        if (args?.length === 1) {
          return [mustBeBinary(args[0], {notEncodable: "an integer"})];
        }

        if (args?.length !== 2) {
          return [];
        }

        const badBase = mustBeBase(args[1]);

        if (badBase === "") {
          return [mustBeBinary(args[0], {notEncodable: "an integer"})];
        }

        return [mustBeBinary(args[0]), badBase];
      }

      case "binary_part": {
        if (args?.length !== 3) {
          return [];
        }

        const [bin, pos, len] = args;

        const errors = [
          mustBeBinary(bin),
          mustBeNonNegInt(pos),
          mustBeInt(len),
        ];

        if (errors.some((error) => error !== "")) {
          return errors;
        }

        Bitstring.maybeSetBytesFromText(bin);

        if (pos.value > BigInt(bin.bytes.length)) {
          return ["", "range"];
        }

        return ["", "", "range"];
      }

      case "binary_to_list":
        return ["not_binary"];

      case "binary_to_term": {
        if (args?.length !== 1) {
          return [];
        }

        return [mustBeBinary(args[0], "bad_ext_term")];
      }

      case "bit_size":
      case "byte_size":
        return ["not_bitstring"];

      case "ceil":
      case "floor":
        return ["not_number"];

      case "convert_time_unit": {
        if (args?.length !== 3) {
          return [];
        }

        return [
          mustBeInt(args[0]),
          mustBeTimeUnit(args[1]),
          mustBeTimeUnit(args[2]),
        ];
      }

      case "delete_element":
      case "element": {
        if (args?.length !== 2) {
          return [];
        }

        return elementFragments(args[0], args[1]);
      }

      case "fun_info": {
        if (args?.length === 1) {
          return ["not_fun"];
        }

        if (args?.length !== 2) {
          return [];
        }

        return Type.isAnonymousFunction(args[0])
          ? ["", "invalid item"]
          : ["not_fun"];
      }

      case "function_exported":
      case "make_fun": {
        if (args?.length !== 3) {
          return [];
        }

        return [
          mustBeAtom(args[0]),
          mustBeAtom(args[1]),
          mustBeNonNegInt(args[2]),
        ];
      }

      case "hd":
      case "tl":
        return ["not_cons"];

      case "insert_element":
      case "setelement": {
        if (args?.length !== 3) {
          return [];
        }

        return elementFragments(args[0], args[1]);
      }

      case "iolist_to_binary":
        return ["not_iodata"];

      case "float_to_binary":
      case "float_to_list": {
        if (args?.length === 1) {
          return ["not_float"];
        }

        if (args?.length !== 2) {
          return [];
        }

        const floatError = Type.isFloat(args[0]) ? "" : "not_float";
        const optionsError = mustBeList(args[1]);

        // Mirrors OTP's maybe_option_list_error/2: a well-formed options
        // list is blamed only when the float argument is fine.
        return [
          floatError,
          floatError === "" && optionsError === ""
            ? "invalid option in list"
            : optionsError,
        ];
      }

      case "integer_to_binary":
      case "integer_to_list": {
        if (args?.length === 1) {
          return ["not_integer"];
        }

        if (args?.length !== 2) {
          return [];
        }

        return [
          Type.isInteger(args[0]) ? "" : "not_integer",
          mustBeBase(args[1]),
        ];
      }

      case "is_map_key":
        return ["", "not_map"];

      case "list_to_atom": {
        if (args?.length !== 1) {
          return [];
        }

        return [mustBeList(args[0], "not_string")];
      }

      case "list_to_binary":
        return ["not_iolist"];

      case "list_to_existing_atom": {
        if (args?.length !== 1) {
          return [];
        }

        return isFlatCharList(args[0])
          ? ["non_existing_atom"]
          : [mustBeList(args[0], "not_string")];
      }

      case "list_to_float": {
        if (args?.length !== 1) {
          return [];
        }

        return listToSomething(args[0], {notEncodable: "a float"});
      }

      case "list_to_integer": {
        if (args?.length === 1) {
          return listToSomething(args[0], {notEncodable: "an integer"});
        }

        if (args?.length !== 2) {
          return [];
        }

        const badBase = mustBeBase(args[1]);

        if (badBase === "") {
          return [mustBeList(args[0], {notEncodable: "an integer"})];
        }

        return [mustBeList(args[0]), badBase];
      }

      case "list_to_pid": {
        if (args?.length !== 1) {
          return [];
        }

        return listToSomething(args[0], {notEncodable: "a pid"});
      }

      case "list_to_ref": {
        if (args?.length !== 1) {
          return [];
        }

        return listToSomething(args[0], {notEncodable: "a reference"});
      }

      case "list_to_tuple":
        return ["not_list"];

      case "make_tuple": {
        if (args?.length !== 2) {
          return [];
        }

        return ["range"];
      }

      case "monotonic_time":
      case "system_time":
      case "time_offset":
        return ["bad_time_unit"];

      case "pid_to_list":
        return ["not_pid"];

      case "ref_to_list":
        return ["not_ref"];

      case "split_binary": {
        if (args?.length !== 2) {
          return [];
        }

        const [bin, pos] = args;
        const errors = [mustBeBinary(bin), mustBeNonNegInt(pos)];

        if (errors.some((error) => error !== "")) {
          return errors;
        }

        Bitstring.maybeSetBytesFromText(bin);

        return pos.value > BigInt(bin.bytes.length) ? ["", "range"] : [];
      }

      case "system_info":
        return ["invalid system info item"];

      case "unique_integer": {
        if (args?.length !== 1) {
          return [];
        }

        return [mustBeList(args[0], "invalid modifier")];
      }

      case "length":
        return ["not_list"];

      case "map_get": {
        if (args?.length !== 2) {
          return [];
        }

        return Type.isMap(args[1]) ? ["not present in map"] : ["", "not_map"];
      }

      case "tuple_size":
      case "tuple_to_list":
        return ["not_tuple"];

      default:
        return [];
    }
  });

Interpreter.defineErlangFunction("erl_erts_errors", "_format_error_map", 3, (fragments, argumentNumber, map) =>
    ERTS.formatErrorMap(
      fragments,
      argumentNumber,
      map,
      Erlang_Erl_Erts_Errors["_expand_error/1"],
    ));

Interpreter.defineErlangFunction("erl_erts_errors", "format_bs_fail", 2, (reason, stacktrace) => {
    const frame = ERTS.callStack.unboxTopFrame(stacktrace);

    if (frame === null) {
      Interpreter.raiseFunctionClauseError(
        "erl_erts_errors",
        "format_bs_fail",
        2,
        [reason, stacktrace],
      );
    }

    const errorInfoMap = frame.errorInfo;

    const cause =
      errorInfoMap?.data[Type.encodeMapKey(Type.atom("cause"))]?.[1];

    if (
      cause === undefined ||
      !Type.isTuple(cause) ||
      cause.data.length !== 4
    ) {
      return Type.map();
    }

    const [segment, type, errorTag, value] = cause.data;

    const overrideEntry =
      errorInfoMap.data[
        Type.encodeMapKey(Type.atom("override_segment_position"))
      ];

    const segmentPosition = overrideEntry ? overrideEntry[1] : segment;

    const prettyPrinterEntry =
      errorInfoMap.data[Type.encodeMapKey(Type.atom("pretty_printer"))];

    const prettyPrint = (term) => {
      if (prettyPrinterEntry === undefined) {
        return Interpreter.inspect(term);
      }

      return Bitstring.toText(
        Interpreter.callAnonymousFunction(prettyPrinterEntry[1], [term]),
      );
    };

    const formatDetail = () => {
      const typeName = type.value;
      const tag = errorTag.value;

      if (Type.isAtom(reason) && reason.value === "system_limit") {
        if (
          typeName === "binary" &&
          tag === "binary" &&
          Type.isAtom(value) &&
          value.value === "size"
        ) {
          return "the size of the binary/bitstring is too large (exceeding 2147483647 bits)";
        }

        return `the size ${Interpreter.inspect(value)} is too large`;
      }

      if (typeName === "float" && tag === "invalid") {
        return `expected one of the supported sizes 16, 32, or 64 but got: ${Interpreter.inspect(value)}`;
      }

      if (typeName === "float" && tag === "no_float") {
        return `the value ${prettyPrint(value)} is outside the range expressible as a float`;
      }

      if (typeName === "binary" && tag === "unit") {
        return `the size of the value ${prettyPrint(value)} is not a multiple of the unit for the segment`;
      }

      if (tag === "short") {
        return `the value ${prettyPrint(value)} is shorter than the size of the segment`;
      }

      if (tag === "size") {
        return `expected a non-negative integer as size but got: ${prettyPrint(value)}`;
      }

      const expected =
        {
          binary: "a binary",
          float: "a float or an integer",
          integer: "an integer",
        }[typeName] ?? `a non-negative integer encodable as ${typeName}`;

      return `expected ${expected} but got: ${prettyPrint(value)}`;
    };

    const general = `segment ${segmentPosition.value} of type '${type.value}': ${formatDetail()}`;

    return Type.map([
      [Type.atom("general"), Type.bitstring(general)],
      [Type.atom("reason"), Type.bitstring("construction of binary failed")],
    ]);
  });

Interpreter.defineErlangFunction("erl_erts_errors", "format_error", 2, (reason, stacktrace) => {
    const frame = ERTS.callStack.unboxTopFrame(stacktrace);

    if (frame === null) {
      Interpreter.raiseFunctionClauseError(
        "erl_erts_errors",
        "format_error",
        2,
        [reason, stacktrace],
      );
    }

    // Mirrors OTP's cause lookup: the frame's error_info map may carry a
    // cause entry that refines the formatter's diagnosis, defaulting to
    // :none.
    const causeEntry =
      frame.errorInfo?.data[Type.encodeMapKey(Type.atom("cause"))];

    const cause = causeEntry ? causeEntry[1] : Type.atom("none");

    let fragments = [];

    // Mirrors OTP's system_limit clause: the reason's explanation is clear
    // enough, so the arguments get no detailed fragments.
    const isSystemLimit =
      Type.isAtom(reason) && reason.value === "system_limit";

    if (!isSystemLimit && frame.module.value === "erlang") {
      fragments = Erlang_Erl_Erts_Errors["_format_erlang_error/3"](
        frame.function,
        frame.arityOrArgs,
        cause,
      );
    }

    return Erlang_Erl_Erts_Errors["_format_error_map/3"](
      fragments,
      1,
      Type.map(),
    );
  });

Interpreter.defineErlangFunction("erl_kernel_errors", "_expand_error", 1, (fragment) => {
    const texts = {
      invalid_time_unit: "invalid time unit",
    };

    return Type.bitstring(texts[fragment] ?? fragment);
  });

Interpreter.defineErlangFunction("erl_kernel_errors", "_format_error_map", 3, (fragments, argumentNumber, map) =>
    ERTS.formatErrorMap(
      fragments,
      argumentNumber,
      map,
      Erlang_Erl_Kernel_Errors["_expand_error/1"],
    ));

Interpreter.defineErlangFunction("erl_kernel_errors", "_format_os_error", 3, (fun, argsOrArity, _cause) => {
    const args = Type.isList(argsOrArity) ? argsOrArity.data : null;

    if (fun.value === "system_time" && args?.length === 1) {
      return ["invalid_time_unit"];
    }

    return [];
  });

Interpreter.defineErlangFunction("erl_kernel_errors", "format_error", 2, (reason, stacktrace) => {
    const frame = ERTS.callStack.unboxTopFrame(stacktrace);

    if (frame === null) {
      Interpreter.raiseFunctionClauseError(
        "erl_kernel_errors",
        "format_error",
        2,
        [reason, stacktrace],
      );
    }

    // Mirrors OTP's cause lookup: the frame's error_info map may carry a
    // cause entry that refines the formatter's diagnosis, defaulting to
    // :none.
    const causeEntry =
      frame.errorInfo?.data[Type.encodeMapKey(Type.atom("cause"))];

    const cause = causeEntry ? causeEntry[1] : Type.atom("none");

    let fragments = [];

    if (frame.module.value === "os") {
      fragments = Erlang_Erl_Kernel_Errors["_format_os_error/3"](
        frame.function,
        frame.arityOrArgs,
        cause,
      );
    }

    return Erlang_Erl_Kernel_Errors["_format_error_map/3"](
      fragments,
      1,
      Type.map(),
    );
  });

Interpreter.defineErlangFunction("erl_stdlib_errors", "_expand_error", 1, (fragment) => {
    // Mirrors OTP's expand_error({bad_regexp, {Reason, Column}}) clause.
    if (typeof fragment === "object" && "badRegexp" in fragment) {
      const {message, position} = fragment.badRegexp;

      return Type.bitstring(
        `could not parse regular expression\n${message} on character ${position}`,
      );
    }

    const texts = {
      bad_binary_pattern: "not a valid pattern",
      bad_char_data: "not valid character data (an iodata term)",
      bad_encoding: "not a valid encoding",
      bad_iterator: "not a valid iterator",
      bad_options: "invalid options",
      bad_replacement: "not a valid replacement",
      bitstring: "is a bitstring (expected a binary)",
      domain_error: "is outside the domain for this function",
      empty_binary: "a zero-sized binary is not allowed",
      not_binary: "not a binary",
      not_compiled_regexp: "not a compiled regular expression",
      not_fun_1: "not a fun that takes one argument",
      not_fun_2: "not a fun that takes two arguments",
      not_fun_3: "not a fun that takes three arguments",
      not_integer: "not an integer",
      not_iodata: "not an iodata term",
      not_list: "not a list",
      not_map: "not a map",
      not_map_or_iterator: "not a map or an iterator",
      not_number: "not a number",
      not_proper_list: "not a proper list",
      not_regexp: "neither an iodata term nor a compiled regular expression",
      range: "out of range",
    };

    return Type.bitstring(texts[fragment] ?? fragment);
  });

Interpreter.defineErlangFunction("erl_stdlib_errors", "_format_binary_error", 3, (fun, argsOrArity, cause) => {
    const mustBeBinary = Erlang_Erl_Stdlib_Errors["_must_be_binary/1"];
    const mustBePattern = Erlang_Erl_Stdlib_Errors["_must_be_pattern/1"];
    const mustBePosition = Erlang_Erl_Stdlib_Errors["_must_be_position/1"];

    const mustBeNonNegInteger =
      Erlang_Erl_Stdlib_Errors["_must_be_non_neg_integer/1"];

    const mustBeReplacement =
      Erlang_Erl_Stdlib_Errors["_must_be_binary_replacement/1"];

    const args = Type.isList(argsOrArity) ? argsOrArity.data : null;

    if (fun.value === "matches") {
      return Erlang_Erl_Stdlib_Errors["_format_binary_error/3"](
        Type.atom("match"),
        argsOrArity,
        cause,
      );
    }

    // Mirrors OTP's clause head [{scope, {Start, Len}}] with integer
    // Start and Len, which turns a syntactically valid scope option into
    // the part-not-inside-binary diagnosis.
    const isSingleScopeOption = (options) => {
      if (!Type.isList(options) || options.data.length !== 1) {
        return false;
      }

      const option = options.data[0];

      if (
        !Type.isTuple(option) ||
        option.data.length !== 2 ||
        !Type.isAtom(option.data[0]) ||
        option.data[0].value !== "scope"
      ) {
        return false;
      }

      const scope = option.data[1];

      return (
        Type.isTuple(scope) &&
        scope.data.length === 2 &&
        Type.isInteger(scope.data[0]) &&
        Type.isInteger(scope.data[1])
      );
    };

    const isEmptyBinary = (term) => {
      if (!Type.isBinary(term)) {
        return false;
      }

      Bitstring.maybeSetBytesFromText(term);

      return term.bytes.length === 0;
    };

    switch (fun.value) {
      case "at":
        if (args?.length === 2) {
          return [mustBeBinary(args[0]), mustBePosition(args[1])];
        }
        break;

      case "compile_pattern":
        if (args?.length === 1) {
          return ["not a valid pattern"];
        }
        break;

      case "copy":
        if (args?.length === 1) {
          return [mustBeBinary(args[0])];
        }

        if (args?.length === 2) {
          return [mustBeBinary(args[0]), mustBeNonNegInteger(args[1])];
        }
        break;

      case "first":
      case "last":
        if (args?.length === 1) {
          return [
            isEmptyBinary(args[0]) ? "empty_binary" : mustBeBinary(args[0]),
          ];
        }
        break;

      case "match":
        if (args?.length === 2) {
          return [mustBeBinary(args[0]), mustBePattern(args[1])];
        }

        if (args?.length === 3) {
          const errors = [mustBeBinary(args[0]), mustBePattern(args[1])];

          if (errors[0] !== "" || errors[1] !== "") {
            return errors;
          }

          return [
            "",
            "",
            isSingleScopeOption(args[2])
              ? "specified part is not wholly inside binary"
              : "bad_options",
          ];
        }
        break;

      case "replace":
        if (args?.length === 3) {
          return [
            mustBeBinary(args[0]),
            mustBePattern(args[1]),
            mustBeReplacement(args[2]),
          ];
        }

        if (args?.length === 4) {
          const errors = [
            mustBeBinary(args[0]),
            mustBePattern(args[1]),
            mustBeReplacement(args[2]),
          ];

          if (cause.value === "badopt") {
            return [...errors, "bad_options"];
          }

          // Options are syntactically correct, but not semantically
          // (e.g. referencing outside the subject).
          if (errors.every((error) => error === "")) {
            return ["", "", "", "bad_options"];
          }

          return errors;
        }
        break;

      case "split":
        if (args?.length === 2) {
          return [mustBeBinary(args[0]), mustBePattern(args[1])];
        }

        if (args?.length === 3) {
          const errors = [mustBeBinary(args[0]), mustBePattern(args[1])];

          if (errors[0] !== "" || errors[1] !== "") {
            return errors;
          }

          return ["", "", "bad_options"];
        }
        break;
    }

    Interpreter.raiseFunctionClauseError(
      "erl_stdlib_errors",
      "format_binary_error",
      3,
      [fun, argsOrArity, cause],
    );
  });

Interpreter.defineErlangFunction("erl_stdlib_errors", "_format_error_map", 3, (fragments, argumentNumber, map) =>
    ERTS.formatErrorMap(
      fragments,
      argumentNumber,
      map,
      Erlang_Erl_Stdlib_Errors["_expand_error/1"],
    ));

Interpreter.defineErlangFunction("erl_stdlib_errors", "_format_lists_error", 2, (fun, argsOrArity) => {
    const raiseFunctionClause = () => {
      Interpreter.raiseFunctionClauseError(
        "erl_stdlib_errors",
        "format_lists_error",
        2,
        [fun, argsOrArity],
      );
    };

    const mustBeInteger = (term) => (Type.isInteger(term) ? "" : "not_integer");
    const mustBeList = Erlang_Erl_Stdlib_Errors["_must_be_list/1"];

    const args = Type.isList(argsOrArity) ? argsOrArity.data : null;

    switch (fun.value) {
      case "keyfind":
      case "keymember":
      case "keysearch": {
        if (args?.length !== 3) {
          raiseFunctionClause();
        }

        const pos = args[1];

        let posError = "";

        if (Type.isInteger(pos)) {
          if (pos.value < 1n) {
            posError = "range";
          }
        } else {
          posError = "not_integer";
        }

        return ["", posError, mustBeList(args[2])];
      }

      case "member": {
        if (args?.length !== 2) {
          raiseFunctionClause();
        }

        return ["", mustBeList(args[1])];
      }

      case "reverse": {
        if (args?.length !== 2) {
          raiseFunctionClause();
        }

        return [mustBeList(args[0])];
      }

      case "seq": {
        if (args?.length !== 3) {
          raiseFunctionClause();
        }

        const [first, last, inc] = args;

        const intErrors = [
          mustBeInteger(first),
          mustBeInteger(last),
          mustBeInteger(inc),
        ];

        if (intErrors.some((error) => error !== "")) {
          return intErrors;
        }

        const incError =
          inc.value <= 0n && first.value - inc.value <= last.value
            ? "not a positive increment"
            : "not a negative increment";

        return ["", "", incError];
      }

      default:
        raiseFunctionClause();
    }
  });

Interpreter.defineErlangFunction("erl_stdlib_errors", "_format_maps_error", 2, (fun, argsOrArity) => {
    const raiseFunctionClause = () => {
      Interpreter.raiseFunctionClauseError(
        "erl_stdlib_errors",
        "format_maps_error",
        2,
        [fun, argsOrArity],
      );
    };

    const mustBeFun = (arity) => (term) =>
      Erlang_Erl_Stdlib_Errors["_must_be_fun/2"](term, arity);

    const mustBeList = Erlang_Erl_Stdlib_Errors["_must_be_list/1"];
    const mustBeMap = Erlang_Erl_Stdlib_Errors["_must_be_map/1"];
    const mustBeMapOrIter = Erlang_Erl_Stdlib_Errors["_must_be_map_or_iter/1"];

    const specs = {
      find: ["", "not_map"],
      fold: [mustBeFun(3), "", mustBeMapOrIter],
      from_keys: [mustBeList, ""],
      from_list: [mustBeList],
      get: (args) => {
        if (args?.length === 2) {
          return Type.isMap(args[1]) ? ["not present in map"] : ["", "not_map"];
        }

        if (args?.length === 3) {
          return ["", "not_map"];
        }

        raiseFunctionClause();
      },
      intersect: [mustBeMap, mustBeMap],
      intersect_with: [mustBeFun(3), mustBeMap, mustBeMap],
      is_key: ["", "not_map"],
      iterator: [mustBeMap],
      keys: ["not_map"],
      map: [mustBeFun(2), mustBeMapOrIter],
      merge: [mustBeMap, mustBeMap],
      merge_with: [mustBeFun(3), mustBeMap, mustBeMap],
      next: ["bad_iterator"],
      put: ["", "", "not_map"],
      remove: ["", "not_map"],
      take: ["", "not_map"],
      to_list: ["not_map_or_iterator"],
      update: (args) =>
        args?.length === 3 && Type.isMap(args[2])
          ? ["not present in map", "", ""]
          : ["", "", "not_map"],
      values: ["not_map"],
    };

    const args = Type.isList(argsOrArity) ? argsOrArity.data : null;
    const spec = specs[fun.value];

    if (spec === undefined) {
      raiseFunctionClause();
    }

    if (typeof spec === "function") {
      return spec(args);
    }

    // A spec with validators destructures the args like the OTP clause head
    // does - an arity or a wrong-length args list matches no clause.
    const hasValidators = spec.some((entry) => typeof entry === "function");

    if (hasValidators && args?.length !== spec.length) {
      raiseFunctionClause();
    }

    return spec.map((entry, index) =>
      typeof entry === "function" ? entry(args[index]) : entry,
    );
  });

Interpreter.defineErlangFunction("erl_stdlib_errors", "_format_math_error", 2, (fun, argsOrArity) => {
    const mustBeNumber = Erlang_Erl_Stdlib_Errors["_must_be_number/1"];

    const args = Type.isList(argsOrArity) ? argsOrArity.data : null;

    const domainErrorFuns = [
      "acos",
      "acosh",
      "asin",
      "atanh",
      "log",
      "log2",
      "log10",
      "sqrt",
    ];

    if (domainErrorFuns.includes(fun.value)) {
      if (args?.length !== 1) {
        Interpreter.raiseFunctionClauseError(
          "erl_stdlib_errors",
          "maybe_domain_error",
          1,
          [argsOrArity],
        );
      }

      const fragment = mustBeNumber(args[0]);

      return [fragment === "" ? "domain_error" : fragment];
    }

    if (args?.length === 1) {
      return [mustBeNumber(args[0])];
    }

    if (args?.length === 2) {
      return [mustBeNumber(args[0]), mustBeNumber(args[1])];
    }

    Interpreter.raiseFunctionClauseError(
      "erl_stdlib_errors",
      "format_math_error",
      2,
      [fun, argsOrArity],
    );
  });

Interpreter.defineErlangFunction("erl_stdlib_errors", "_format_re_error", 3, (fun, argsOrArity, cause) => {
    const mustBeIodata = Erlang_Erl_Stdlib_Errors["_must_be_iodata/1"];
    const mustBeRegexp = Erlang_Erl_Stdlib_Errors["_must_be_regexp/1"];
    const reCompileError = Erlang_Erl_Stdlib_Errors["_re_compile_error/1"];

    const args = Type.isList(argsOrArity) ? argsOrArity.data : null;

    switch (fun.value) {
      case "compile":
        if (args?.length === 1) {
          return ["not_iodata"];
        }

        if (args?.length === 2) {
          const reError = reCompileError(args[0]);

          return cause.value === "badopt"
            ? [reError, "bad_options"]
            : [reError];
        }
        break;

      case "import":
        if (args?.length === 1) {
          return ["not an exported regular expression"];
        }
        break;

      case "inspect": {
        if (args?.length !== 2) {
          break;
        }

        // Mirrors OTP's re:inspect/2 probing, which treats a raise as a
        // non-compiled regular expression. The registry lookup is used
        // instead of the raising port - this runs inside message
        // derivation, so a raise here would derive its own message and
        // recurse.
        const reError =
          ERTS.regexPatternRegistry.lookupByTerm(args[0]) === null
            ? "not_compiled_regexp"
            : "";

        if (reError === "" || !Type.isAtom(args[1])) {
          return [reError, "not a valid item"];
        }

        return [reError];
      }

      case "run":
        if (args?.length === 2) {
          return [mustBeIodata(args[0]), mustBeRegexp(args[1])];
        }

        if (args?.length === 3) {
          const errors = [mustBeIodata(args[0]), mustBeRegexp(args[1])];

          return cause.value === "badopt" ? [...errors, "bad_options"] : errors;
        }
        break;
    }

    Interpreter.raiseFunctionClauseError(
      "erl_stdlib_errors",
      "format_re_error",
      3,
      [fun, argsOrArity, cause],
    );
  });

Interpreter.defineErlangFunction("erl_stdlib_errors", "_format_unicode_error", 2, (fun, argsOrArity) => {
    const unicodeCharData = Erlang_Erl_Stdlib_Errors["_unicode_char_data/1"];
    const unicodeEncoding = Erlang_Erl_Stdlib_Errors["_unicode_encoding/1"];

    const badCharDataFuns = [
      "characters_to_nfc_binary",
      "characters_to_nfc_list",
      "characters_to_nfd_binary",
      "characters_to_nfd_list",
      "characters_to_nfkc_binary",
      "characters_to_nfkc_list",
      "characters_to_nfkd_binary",
      "characters_to_nfkd_list",
    ];

    const args = Type.isList(argsOrArity) ? argsOrArity.data : null;

    if (fun.value === "characters_to_list") {
      return Erlang_Erl_Stdlib_Errors["_format_unicode_error/2"](
        Type.atom("characters_to_binary"),
        argsOrArity,
      );
    }

    if (fun.value === "characters_to_binary") {
      if (args?.length === 1) {
        return ["bad_char_data"];
      }

      if (args?.length === 2) {
        return [unicodeCharData(args[0]), unicodeEncoding(args[1])];
      }

      if (args?.length === 3) {
        return [
          unicodeCharData(args[0]),
          unicodeEncoding(args[1]),
          unicodeEncoding(args[2]),
        ];
      }
    }

    if (badCharDataFuns.includes(fun.value) && args?.length === 1) {
      return ["bad_char_data"];
    }

    Interpreter.raiseFunctionClauseError(
      "erl_stdlib_errors",
      "format_unicode_error",
      2,
      [fun, argsOrArity],
    );
  });

Interpreter.defineErlangFunction("erl_stdlib_errors", "_is_iodata", 1, (term) => {
    if (Type.isBinary(term)) {
      return true;
    }

    if (!Type.isList(term)) {
      return false;
    }

    const isValidIolist = (list) => {
      const data = list.data;
      const elemCount = list.isProper ? data.length : data.length - 1;

      for (let index = 0; index < elemCount; index++) {
        const item = data[index];

        if (Type.isInteger(item)) {
          if (item.value < 0n || item.value > 255n) {
            return false;
          }
        } else if (Type.isList(item)) {
          if (!isValidIolist(item)) {
            return false;
          }
        } else if (!Type.isBinary(item)) {
          return false;
        }
      }

      if (!list.isProper) {
        const tail = data[data.length - 1];

        if (Type.isList(tail)) {
          return isValidIolist(tail);
        }

        return Type.isBinary(tail);
      }

      return true;
    };

    return isValidIolist(term);
  });

Interpreter.defineErlangFunction("erl_stdlib_errors", "_must_be_binary", 1, (term) => {
    if (Type.isBinary(term)) {
      return "";
    }

    return Type.isBitstring(term) ? "bitstring" : "not_binary";
  });

Interpreter.defineErlangFunction("erl_stdlib_errors", "_must_be_binary_replacement", 1, (term) => {
    if (Type.isBinary(term)) {
      return "";
    }

    return Type.isAnonymousFunction(term) && term.arity === 1
      ? ""
      : "bad_replacement";
  });

Interpreter.defineErlangFunction("erl_stdlib_errors", "_must_be_fun", 2, (term, arity) =>
    Type.isAnonymousFunction(term) && term.arity === arity
      ? ""
      : `not_fun_${arity}`);

Interpreter.defineErlangFunction("erl_stdlib_errors", "_must_be_iodata", 1, (term) =>
    Erlang_Erl_Stdlib_Errors["_is_iodata/1"](term) ? "" : "not_iodata");

Interpreter.defineErlangFunction("erl_stdlib_errors", "_must_be_list", 1, (term) => {
    if (!Type.isList(term)) {
      return "not_list";
    }

    return Type.isProperList(term) ? "" : "not_proper_list";
  });

Interpreter.defineErlangFunction("erl_stdlib_errors", "_must_be_map", 1, (term) => (Type.isMap(term) ? "" : "not_map"));

Interpreter.defineErlangFunction("erl_stdlib_errors", "_must_be_map_or_iter", 1, (term) =>
    Type.isMap(term) || Type.isTrue(Erlang_Maps["is_iterator_valid/1"](term))
      ? ""
      : "not_map_or_iterator");

Interpreter.defineErlangFunction("erl_stdlib_errors", "_must_be_non_neg_integer", 1, (term) => {
    if (!Type.isInteger(term)) {
      return "not_integer";
    }

    return term.value >= 0n ? "" : "range";
  });

Interpreter.defineErlangFunction("erl_stdlib_errors", "_must_be_number", 1, (term) => (Type.isNumber(term) ? "" : "not_number"));

Interpreter.defineErlangFunction("erl_stdlib_errors", "_must_be_pattern", 1, (term) =>
    Erlang_Binary["_is_valid_pattern/1"](term) ? "" : "bad_binary_pattern");

Interpreter.defineErlangFunction("erl_stdlib_errors", "_must_be_position", 1, (term) => {
    if (!Type.isInteger(term)) {
      return "not_integer";
    }

    return term.value >= 0n ? "" : "range";
  });

Interpreter.defineErlangFunction("erl_stdlib_errors", "_must_be_regexp", 1, (term) => {
    if (ERTS.regexPatternRegistry.lookupByTerm(term) !== null) {
      return "";
    }

    const reError = Erlang_Erl_Stdlib_Errors["_re_compile_error/1"](term);

    return reError === "not_iodata" ? "not_regexp" : reError;
  });

Interpreter.defineErlangFunction("erl_stdlib_errors", "_re_compile_error", 1, (pattern) => {
    if (!Erlang_Erl_Stdlib_Errors["_is_iodata/1"](pattern)) {
      return "not_iodata";
    }

    const binary = Erlang["iolist_to_binary/1"](pattern);

    Bitstring.maybeSetBytesFromText(binary);

    const result = ERTS.regex.compileBytes(binary.bytes, {});

    return result.error ? {badRegexp: result.error} : "";
  });

Interpreter.defineErlangFunction("erl_stdlib_errors", "_unicode_char_data", 1, (chars) => {
    const result = Erlang_Unicode["_chardata_to_utf8_binary/1"](chars);

    return result === null || Type.isTuple(result) ? "bad_char_data" : "";
  });

Interpreter.defineErlangFunction("erl_stdlib_errors", "_unicode_encoding", 1, (encoding) => {
    if (
      Type.isAtom(encoding) &&
      ["latin1", "unicode", "utf8", "utf16", "utf32"].includes(encoding.value)
    ) {
      return "";
    }

    const isEndiannessTuple =
      Type.isTuple(encoding) &&
      encoding.data.length === 2 &&
      Type.isAtom(encoding.data[0]) &&
      ["utf16", "utf32"].includes(encoding.data[0].value) &&
      Type.isAtom(encoding.data[1]) &&
      ["big", "little"].includes(encoding.data[1].value);

    return isEndiannessTuple ? "" : "bad_encoding";
  });

Interpreter.defineErlangFunction("erl_stdlib_errors", "format_error", 2, (reason, stacktrace) => {
    const frame = ERTS.callStack.unboxTopFrame(stacktrace);

    if (frame === null) {
      Interpreter.raiseFunctionClauseError(
        "erl_stdlib_errors",
        "format_error",
        2,
        [reason, stacktrace],
      );
    }

    // Mirrors OTP's cause lookup: the frame's error_info map may carry a
    // cause entry that refines the formatter's diagnosis, defaulting to
    // :none.
    const causeEntry =
      frame.errorInfo?.data[Type.encodeMapKey(Type.atom("cause"))];

    const cause = causeEntry ? causeEntry[1] : Type.atom("none");

    let fragments;

    switch (frame.module.value) {
      case "binary":
        fragments = Erlang_Erl_Stdlib_Errors["_format_binary_error/3"](
          frame.function,
          frame.arityOrArgs,
          cause,
        );
        break;

      case "lists":
        fragments = Erlang_Erl_Stdlib_Errors["_format_lists_error/2"](
          frame.function,
          frame.arityOrArgs,
        );
        break;

      case "maps":
        fragments = Erlang_Erl_Stdlib_Errors["_format_maps_error/2"](
          frame.function,
          frame.arityOrArgs,
        );
        break;

      case "math":
        fragments = Erlang_Erl_Stdlib_Errors["_format_math_error/2"](
          frame.function,
          frame.arityOrArgs,
        );
        break;

      case "re":
        fragments = Erlang_Erl_Stdlib_Errors["_format_re_error/3"](
          frame.function,
          frame.arityOrArgs,
          cause,
        );
        break;

      case "unicode":
        fragments = Erlang_Erl_Stdlib_Errors["_format_unicode_error/2"](
          frame.function,
          frame.arityOrArgs,
        );
        break;

      default:
        fragments = [];
    }

    return Erlang_Erl_Stdlib_Errors["_format_error_map/3"](
      fragments,
      1,
      Type.map(),
    );
  });

Interpreter.defineErlangFunction("erlang", "+", 2, (left, right) => {
    if (!Type.isNumber(left) || !Type.isNumber(right)) {
      Interpreter.raiseBifError("badarith", "erlang", "+", [left, right]);
    }

    const [type, leftValue, rightValue] = Type.maybeNormalizeNumberTerms(
      left,
      right,
    );

    const result = leftValue.value + rightValue.value;

    return type === "float" ? Type.float(result) : Type.integer(result);
  });

Interpreter.defineErlangFunction("erlang", "++", 2, (left, right) => {
    if (!Type.isProperList(left)) {
      Interpreter.raiseBifError("badarg", "erlang", "++", [left, right]);
    }

    // Identity fast paths (#878): appending onto/into an empty list is a
    // no-op that can return the other operand unchanged instead of copying.
    // Gated on Type.isList(right) rather than being unconditional: when
    // right isn't a list at all, the pre-existing (unrelated to #878) path
    // below already raises on a 1-item improper list, and this fast path
    // intentionally preserves that behavior rather than silently changing
    // it.
    if (left.data.length === 0 && Type.isList(right)) {
      return right;
    }

    if (Type.isProperList(right) && right.data.length === 0) {
      return left;
    }

    const data = left.data.concat(Type.isList(right) ? right.data : [right]);

    return Type.isProperList(right) ? Type.list(data) : Type.improperList(data);
  });

Interpreter.defineErlangFunction("erlang", "-", 2, (left, right) => {
    if (!Type.isNumber(left) || !Type.isNumber(right)) {
      Interpreter.raiseBifError("badarith", "erlang", "-", [left, right]);
    }

    const [type, leftValue, rightValue] = Type.maybeNormalizeNumberTerms(
      left,
      right,
    );

    const result = leftValue.value - rightValue.value;

    return type === "float" ? Type.float(result) : Type.integer(result);
  });

Interpreter.defineErlangFunction("erlang", "/=", 2, (left, right) => {
    return Type.boolean(!Interpreter.isEqual(left, right));
  });

Interpreter.defineErlangFunction("erlang", "<", 2, (left, right) => {
    return Type.boolean(Interpreter.compareTerms(left, right) === -1);
  });

Interpreter.defineErlangFunction("erlang", "=:=", 2, (left, right) => {
    return Type.boolean(Interpreter.isStrictlyEqual(left, right));
  });

Interpreter.defineErlangFunction("erlang", "=<", 2, (left, right) => {
    Interpreter.assertStructuralComparisonSupportedType(left);
    Interpreter.assertStructuralComparisonSupportedType(right);

    const result =
      Type.isTrue(Erlang["==/2"](left, right)) ||
      Type.isTrue(Erlang["</2"](left, right));

    return Type.boolean(result);
  });

Interpreter.defineErlangFunction("erlang", "==", 2, (left, right) => {
    return Type.boolean(Interpreter.isEqual(left, right));
  });

Interpreter.defineErlangFunction("erlang", ">", 2, (left, right) => {
    return Type.boolean(Interpreter.compareTerms(left, right) === 1);
  });

Interpreter.defineErlangFunction("erlang", ">=", 2, (left, right) => {
    Interpreter.assertStructuralComparisonSupportedType(left);
    Interpreter.assertStructuralComparisonSupportedType(right);

    const result =
      Type.isTrue(Erlang["==/2"](left, right)) ||
      Type.isTrue(Erlang[">/2"](left, right));

    return Type.boolean(result);
  });

Interpreter.defineErlangFunction("erlang", "_is_valid_time_unit", 1, (unit) => {
    const validAtomUnits = [
      "nanosecond",
      "nano_seconds",
      "microsecond",
      "micro_seconds",
      "millisecond",
      "milli_seconds",
      "second",
      "seconds",
      "native",
      "perf_counter",
    ];

    return (
      (Type.isAtom(unit) && validAtomUnits.includes(unit.value)) ||
      (Type.isInteger(unit) && unit.value > 0n)
    );
  });

Interpreter.defineErlangFunction("erlang", "atom_to_binary", 1, (atom) => {
    try {
      return Erlang["atom_to_binary/2"](atom, Type.atom("utf8"));
    } catch (error) {
      if (error.struct) {
        // Re-raise with this function's own identity - the BEAM reports the
        // called function's frame, not the delegate's.
        Interpreter.raiseBifError("badarg", "erlang", "atom_to_binary", [atom]);
      }

      throw error;
    }
  });

Interpreter.defineErlangFunction("erlang", "atom_to_binary", 2, (atom, encoding) => {
    if (!Type.isAtom(atom)) {
      Interpreter.raiseBifError("badarg", "erlang", "atom_to_binary", [
        atom,
        encoding,
      ]);
    }

    // TODO: implement encoding argument validation

    // TODO: implement other encodings for encoding param
    if (!Interpreter.isStrictlyEqual(encoding, Type.atom("utf8"))) {
      throw new HologramInterpreterError(
        "encodings other than utf8 are not yet implemented in Hologram",
      );
    }

    return Type.bitstring(atom.value);
  });

Interpreter.defineErlangFunction("erlang", "atom_to_list", 1, (atom) => {
    if (!Type.isAtom(atom)) {
      Interpreter.raiseBifError("badarg", "erlang", "atom_to_list", [atom]);
    }

    return Bitstring.toCodepoints(Type.bitstring(atom.value));
  });

Interpreter.defineErlangFunction("erlang", "binary_part", 3, (subject, start, length) => {
    const raiseBadarg = () => {
      Interpreter.raiseBifError("badarg", "erlang", "binary_part", [
        subject,
        start,
        length,
      ]);
    };

    if (!Type.isBinary(subject)) {
      raiseBadarg();
    }

    if (!Type.isInteger(start)) {
      raiseBadarg();
    }

    if (!Type.isInteger(length)) {
      raiseBadarg();
    }

    const totalBytes = Bitstring.calculateBitCount(subject) / 8;

    if (start.value < 0n || start.value > totalBytes) {
      raiseBadarg();
    }

    const isReverse = length.value < 0n;

    const outOfRangeForward =
      !isReverse && start.value + length.value > totalBytes;

    const outOfRangeReverse = isReverse && start.value + length.value < 0n;

    if (outOfRangeForward || outOfRangeReverse) {
      raiseBadarg();
    }

    const actualStart = isReverse ? start.value + length.value : start.value;
    const actualLength = isReverse ? -length.value : length.value;

    return Bitstring.takeChunk(
      subject,
      Number(actualStart) * 8,
      Number(actualLength) * 8,
    );
  });

Interpreter.defineErlangFunction("erlang", "binary_to_atom", 1, (binary) => {
    try {
      return Erlang["binary_to_atom/2"](binary, Type.atom("utf8"));
    } catch (error) {
      if (error.struct) {
        // Re-raise with this function's own identity - the BEAM reports the
        // called function's frame, not the delegate's.
        Interpreter.raiseBifError("badarg", "erlang", "binary_to_atom", [
          binary,
        ]);
      }

      throw error;
    }
  });

Interpreter.defineErlangFunction("erlang", "binary_to_atom", 2, (binary, encoding) => {
    if (!Type.isBinary(binary)) {
      Interpreter.raiseBifError("badarg", "erlang", "binary_to_atom", [
        binary,
        encoding,
      ]);
    }

    // TODO: implement encoding argument validation

    // TODO: implement other encodings for encoding param
    if (!Interpreter.isStrictlyEqual(encoding, Type.atom("utf8"))) {
      throw new HologramInterpreterError(
        "encodings other than utf8 are not yet implemented in Hologram",
      );
    }

    return Type.atom(Bitstring.toText(binary));
  });

Interpreter.defineErlangFunction("erlang", "byte_size", 1, (bitstring) => {
    if (!Type.isBitstring(bitstring)) {
      Interpreter.raiseBifError("badarg", "erlang", "byte_size", [bitstring]);
    }

    Bitstring.maybeSetBytesFromText(bitstring);

    return Type.integer(bitstring.bytes.length);
  });

Interpreter.defineErlangFunction("erlang", "element", 2, (index, tuple) => {
    const raiseBadarg = () => {
      Interpreter.raiseBifError("badarg", "erlang", "element", [index, tuple]);
    };

    if (!Type.isInteger(index)) {
      raiseBadarg();
    }

    if (!Type.isTuple(tuple)) {
      raiseBadarg();
    }

    if (index.value > tuple.data.length || index.value < 1) {
      raiseBadarg();
    }

    return tuple.data[Number(index.value) - 1];
  });

Interpreter.defineErlangFunction("erlang", "error", 1, (reason) => {
    Erlang["error/3"](reason, Type.atom("none"), Type.list());
  });

Interpreter.defineErlangFunction("erlang", "error", 2, (reason, args) => {
    Erlang["error/3"](reason, args, Type.list());
  });

Interpreter.defineErlangFunction("erlang", "error", 3, (reason, args, options) => {
    // Options that are not a list and options other than {:error_info, map}
    // are ignored, mirroring the BEAM.
    let errorInfo = null;

    if (Type.isList(options)) {
      for (const option of options.data) {
        if (
          Type.isTuple(option) &&
          option.data.length === 2 &&
          Type.isAtom(option.data[0]) &&
          option.data[0].value === "error_info"
        ) {
          errorInfo = option.data[1];
        }
      }
    }

    const error = new HologramBoxedError(reason);

    // The constructor snapshot still contains the dispatch frames of
    // :erlang.error itself - the BEAM never shows erlang:error in a trace.
    const frames = error.stacktrace;

    let callerIndex = 0;

    while (
      callerIndex < frames.length &&
      frames[callerIndex].module === "erlang" &&
      frames[callerIndex].function === "error"
    ) {
      ++callerIndex;
    }

    const callerFrame = frames[callerIndex] ?? null;

    // The raising frame is attached unconditionally - message derivation
    // reads args and error_info from it in every environment. It is a copy,
    // because the caller's live frame must not be mutated: the caller may
    // rescue this error and keep executing.
    const raisingFrame = {
      module: callerFrame?.module ?? null,
      function: callerFrame?.function ?? null,
      // A list (proper or improper) replaces the caller's arity, any other
      // term (including :none) keeps it, mirroring the BEAM.
      arityOrArgs: Type.isList(args)
        ? args
        : (callerFrame?.arityOrArgs ?? null),
      file: callerFrame?.file ?? null,
      line: callerFrame?.line ?? null,
      errorInfo,
    };

    error.stacktrace = [raisingFrame, ...frames.slice(callerIndex + 1)];

    // Message derivation may read args and error_info from the raising
    // frame, so the struct and message re-derive against the fixed trace.
    error.rederive(Type.list(error.stacktrace.map(ERTS.callStack.boxFrame)));

    throw error;
  });

Interpreter.defineErlangFunction("erlang", "float_to_binary", 2, (float, opts) => {
    const raiseBadarg = () => {
      Interpreter.raiseBifError("badarg", "erlang", "float_to_binary", [
        float,
        opts,
      ]);
    };

    if (!Type.isFloat(float)) {
      raiseBadarg();
    }

    if (!Type.isList(opts)) {
      raiseBadarg();
    }

    if (!Type.isProperList(opts)) {
      raiseBadarg();
    }

    const SHORT_EXPONENTIAL_THRESHOLD = 9_007_199_254_740_992.0; // 2^53 - Erlang always uses exponential notation at this boundary
    const JS_PRECISION_LIMIT = 100; // Max precision for toFixed() and toExponential()
    const ERLANG_BUFFER_LIMIT = 256;
    const ERLANG_DEFAULT_SCIENTIFIC = 20;
    const FIXED_PRECISION_FOR_NEGATIVE = 6;

    let decimals = null;
    let scientific = ERLANG_DEFAULT_SCIENTIFIC;
    let isCompact = false;
    let isShort = false;
    let lastOpt = null;

    // Parse options
    for (const opt of opts.data) {
      if (Interpreter.isStrictlyEqual(opt, Type.atom("short"))) {
        isShort = true;
        lastOpt = "short";
        continue;
      }

      if (Interpreter.isStrictlyEqual(opt, Type.atom("compact"))) {
        isCompact = true;
        continue;
      }

      if (!Type.isTuple(opt) || opt.data.length !== 2) {
        raiseBadarg();
      }

      const [key, value] = opt.data;

      if (
        Interpreter.isStrictlyEqual(key, Type.atom("decimals")) &&
        Type.isInteger(value) &&
        value.value >= 0n &&
        value.value <= 253n
      ) {
        decimals = Number(value.value);
        lastOpt = "decimals";
      } else if (
        Interpreter.isStrictlyEqual(key, Type.atom("scientific")) &&
        Type.isInteger(value) &&
        value.value <= 249n
      ) {
        scientific = Number(value.value);
        lastOpt = "scientific";
      } else {
        raiseBadarg();
      }
    }

    // Only keep the last formatting option, reset others to defaults
    if (lastOpt === "short") {
      decimals = null;
      scientific = ERLANG_DEFAULT_SCIENTIFIC;
    } else if (lastOpt === "decimals") {
      isShort = false;
      scientific = ERLANG_DEFAULT_SCIENTIFIC;
    } else if (lastOpt === "scientific") {
      isShort = false;
      decimals = null;
    }

    // Check if we have negative zero (JavaScript preserves signed zero)
    const isNegativeZero = Object.is(float.value, -0);

    let result;

    if (isShort) {
      const absVal = Math.abs(float.value);

      if (absVal >= SHORT_EXPONENTIAL_THRESHOLD) {
        // For values >= 2^53, always use exponential notation per Erlang spec
        result = float.value.toExponential();
      } else {
        // For values < 2^53, compare character counts of decimal vs exponential
        // and choose the shorter representation (decimal wins ties per Erlang spec)

        let decimalResult = float.value.toString();

        // Ensure decimal point exists for proper float format
        if (decimalResult === "0") {
          decimalResult = "0.0";
        } else if (
          absVal >= 1 &&
          !decimalResult.includes(".") &&
          !decimalResult.includes("e")
        ) {
          decimalResult += ".0";
        }

        let expResult = float.value.toExponential();

        // Ensure mantissa has at least one decimal digit (e.g., "9e-4" → "9.0e-4")
        if (!expResult.includes(".")) {
          expResult = expResult.replace(/e/, ".0e");
        }

        // Choose the representation with fewer characters (decimal wins ties)
        result =
          expResult.length < decimalResult.length ? expResult : decimalResult;
      }

      // Format exponent: remove + sign (e.g., e+15 → e15), keep - sign as-is
      if (result.includes("e")) {
        result = result.replace(/e\+/, "e");
      }
    } else if (decimals !== null) {
      // JavaScript's toFixed() has a limit of 100, but Erlang allows up to 253.
      // For values > 100, we use toFixed(100) and manually pad with zeros.
      if (decimals > JS_PRECISION_LIMIT) {
        result = float.value.toFixed(JS_PRECISION_LIMIT);
        const additionalZeros = decimals - JS_PRECISION_LIMIT;
        result = result + "0".repeat(additionalZeros);
      } else {
        result = float.value.toFixed(decimals);
      }

      if (isCompact && decimals > 0) {
        result = result.replace(/0+$/, "").replace(/\.$/, ".0");
      }
    } else {
      // For negative scientific, Erlang uses fixed precision of 6 decimal places
      const precision =
        scientific < 0 ? FIXED_PRECISION_FOR_NEGATIVE : scientific;

      // JavaScript's toExponential() has a limit of 100, but Erlang allows up to 249.
      // For values > 100, we use toExponential(100) and manually pad with zeros.
      if (precision > JS_PRECISION_LIMIT) {
        result = float.value.toExponential(JS_PRECISION_LIMIT);
        const [mantissa, exponent] = result.split("e");
        const currentDigits = mantissa.split(".")[1].length;
        const additionalZeros = precision - currentDigits;
        result = mantissa + "0".repeat(additionalZeros) + "e" + exponent;
      } else {
        result = float.value.toExponential(precision);
      }

      // Erlang format uses zero-padded exponents (e.g., e+00, e-04)
      result = result.replace(/e([+-])(\d)$/, "e$10$2");
    }

    // Preserve negative zero sign if needed
    // JavaScript's toString/toFixed/toExponential lose the sign of -0, so we restore it
    if (isNegativeZero && !result.startsWith("-")) {
      result = "-" + result;
    }

    // Erlang enforces a 256-byte buffer limit for the result
    if (result.length >= ERLANG_BUFFER_LIMIT) {
      raiseBadarg();
    }

    return Type.bitstring(result);
  });

Interpreter.defineErlangFunction("erlang", "fun_info", 1, (fun) => {
    if (!Type.isAnonymousFunction(fun)) {
      Interpreter.raiseBifError("badarg", "erlang", "fun_info", [fun]);
    }

    const arity = Type.integer(fun.arity);

    const isExternal = fun.capturedModule !== null;

    if (isExternal) {
      // Erlang modules are prefixed with ":" (e.g. ":erlang"), Elixir modules are not
      const isErlangModule = fun.capturedModule.startsWith(":");

      const module = isErlangModule
        ? Type.atom(fun.capturedModule.slice(1))
        : Type.alias(fun.capturedModule);

      const env = Type.list();
      const name = Type.atom(fun.capturedFunction);
      const type = Type.atom("external");

      return Type.list([
        Type.tuple([Type.atom("module"), module]),
        Type.tuple([Type.atom("name"), name]),
        Type.tuple([Type.atom("arity"), arity]),
        Type.tuple([Type.atom("env"), env]),
        Type.tuple([Type.atom("type"), type]),
      ]);
    }

    // Local fun

    const env = Type.list(Object.values(fun.context.vars));
    const module = fun.context.module;
    const type = Type.atom("local");

    // fun.uniq is a unique integer for this fun (from ERTS.funSequence).
    // We derive index, new_index, uniq, and new_uniq from it.
    // In Erlang, index/new_index are per-module indices into the fun table,
    // and uniq/new_uniq are calculated from compiled code.
    // TODO: When hot code reloading is implemented, index/new_index should be
    // per-module indices, and uniq/new_uniq should be based on compiled code hash.
    const index = Type.integer(fun.uniq);
    const newIndex = Type.integer(fun.uniq);
    const uniq = Type.integer(fun.uniq);

    // Generate new_uniq as a 16-byte (128-bit) binary derived from fun.uniq
    const newUniq = Type.bitstring([
      Type.bitstringSegment(Type.integer(fun.uniq), {
        type: "integer",
        size: Type.integer(128),
      }),
    ]);

    // A fun defined outside of any function definition carries no name.
    const name = Type.atom(fun.name ?? "");
    const pid = ERTS.INIT_PID;

    return Type.list([
      Type.tuple([Type.atom("pid"), pid]),
      Type.tuple([Type.atom("module"), module]),
      Type.tuple([Type.atom("new_index"), newIndex]),
      Type.tuple([Type.atom("new_uniq"), newUniq]),
      Type.tuple([Type.atom("index"), index]),
      Type.tuple([Type.atom("uniq"), uniq]),
      Type.tuple([Type.atom("name"), name]),
      Type.tuple([Type.atom("arity"), arity]),
      Type.tuple([Type.atom("env"), env]),
      Type.tuple([Type.atom("type"), type]),
    ]);
  });

Interpreter.defineErlangFunction("erlang", "fun_info", 2, (fun, item) => {
    const validItems = new Set([
      "arity",
      "env",
      "index",
      "module",
      "name",
      "new_index",
      "new_uniq",
      "pid",
      "type",
      "uniq",
    ]);

    // Both raises name the same pair of arguments, and the message blames the
    // first one whenever it isn't a fun - so reading the item first still
    // reports a bad fun the way the BEAM does, without building the info list.
    if (!Type.isAtom(item) || !validItems.has(item.value)) {
      Interpreter.raiseBifError("badarg", "erlang", "fun_info", [fun, item]);
    }

    let info;

    try {
      info = Erlang["fun_info/1"](fun);
    } catch (error) {
      if (error.struct) {
        // Re-raise with this function's own identity - the BEAM reports the
        // called function's frame, not the delegate's.
        Interpreter.raiseBifError("badarg", "erlang", "fun_info", [fun, item]);
      }

      throw error;
    }

    const result = info.data.find(
      (tuple) => tuple.data[0].value === item.value,
    );

    if (result === undefined) {
      return Type.tuple([item, Type.atom("undefined")]);
    }

    return result;
  });

Interpreter.defineErlangFunction("erlang", "function_exported", 3, (module, functionName, arity) => {
    const raiseBadarg = () => {
      Interpreter.raiseBifError("badarg", "erlang", "function_exported", [
        module,
        functionName,
        arity,
      ]);
    };

    if (!Type.isAtom(module)) {
      raiseBadarg();
    }

    if (!Type.isAtom(functionName)) {
      raiseBadarg();
    }

    if (!Type.isInteger(arity)) {
      raiseBadarg();
    }

    const moduleProxy = Interpreter.moduleProxy(module);

    if (typeof moduleProxy === "undefined") {
      return Type.boolean(false);
    }

    const functionArityStr = `${functionName.value}/${arity.value}`;

    return Type.boolean(moduleProxy.__exports__.has(functionArityStr));
  });

Interpreter.defineErlangFunction("erlang", "hd", 1, (list) => {
    if (!Type.isList(list) || Type.listIsEmpty(list)) {
      Interpreter.raiseBifError("badarg", "erlang", "hd", [list]);
    }

    // #878: reads the cons cell's own head field, no materialization.
    return Type.isConsCell(list) ? list.head : list.data[0];
  });

Interpreter.defineErlangFunction("erlang", "integer_to_binary", 1, (integer) => {
    try {
      return Erlang["integer_to_binary/2"](integer, Type.integer(10));
    } catch (error) {
      if (error.struct) {
        // Re-raise with this function's own identity - the BEAM reports the
        // called function's frame, not the delegate's.
        Interpreter.raiseBifError("badarg", "erlang", "integer_to_binary", [
          integer,
        ]);
      }

      throw error;
    }
  });

Interpreter.defineErlangFunction("erlang", "integer_to_binary", 2, (integer, base) => {
    if (
      !Type.isInteger(integer) ||
      !Type.isInteger(base) ||
      base.value < 2n ||
      base.value > 36n
    ) {
      Interpreter.raiseBifError("badarg", "erlang", "integer_to_binary", [
        integer,
        base,
      ]);
    }

    const str = integer.value.toString(Number(base.value)).toUpperCase();

    return Type.bitstring(str);
  });

Interpreter.defineErlangFunction("erlang", "iolist_to_binary", 1, (ioListOrBinary) => {
    const raiseBadarg = () => {
      Interpreter.raiseBifError("badarg", "erlang", "iolist_to_binary", [
        ioListOrBinary,
      ]);
    };

    if (Type.isBinary(ioListOrBinary)) {
      return ioListOrBinary;
    }

    if (!Type.isList(ioListOrBinary)) {
      raiseBadarg();
    }

    // :erlang.list_to_binary/1 raises ArgumentError "not an iolist term" on invalid input.
    // Remap to "not an iodata term" to match OTP's iolist_to_binary/1 behavior.
    try {
      return Erlang["list_to_binary/1"](ioListOrBinary);
    } catch {
      raiseBadarg();
    }
  });

Interpreter.defineErlangFunction("erlang", "is_atom", 1, (term) => {
    return Type.boolean(Type.isAtom(term));
  });

Interpreter.defineErlangFunction("erlang", "is_binary", 1, (term) => {
    return Type.boolean(Type.isBinary(term));
  });

Interpreter.defineErlangFunction("erlang", "is_bitstring", 1, (term) => {
    return Type.boolean(Type.isBitstring(term));
  });

Interpreter.defineErlangFunction("erlang", "is_boolean", 1, (term) => {
    return Type.boolean(Type.isBoolean(term));
  });

Interpreter.defineErlangFunction("erlang", "is_float", 1, (term) => {
    return Type.boolean(Type.isFloat(term));
  });

Interpreter.defineErlangFunction("erlang", "is_function", 1, (term) => {
    return Type.boolean(Type.isAnonymousFunction(term));
  });

Interpreter.defineErlangFunction("erlang", "is_function", 2, (term, arity) => {
    return Type.boolean(
      Type.isAnonymousFunction(term) && term.arity === Number(arity.value),
    );
  });

Interpreter.defineErlangFunction("erlang", "is_integer", 1, (term) => {
    return Type.boolean(Type.isInteger(term));
  });

Interpreter.defineErlangFunction("erlang", "is_list", 1, (term) => {
    return Type.boolean(Type.isList(term));
  });

Interpreter.defineErlangFunction("erlang", "is_map", 1, (term) => {
    return Type.boolean(Type.isMap(term));
  });

Interpreter.defineErlangFunction("erlang", "is_map_key", 2, (key, map) => {
    return Erlang_Maps["is_key/2"](key, map);
  });

Interpreter.defineErlangFunction("erlang", "is_pid", 1, (term) => {
    return Type.boolean(Type.isPid(term));
  });

Interpreter.defineErlangFunction("erlang", "is_port", 1, (term) => {
    return Type.boolean(Type.isPort(term));
  });

Interpreter.defineErlangFunction("erlang", "is_reference", 1, (term) => {
    return Type.boolean(Type.isReference(term));
  });

Interpreter.defineErlangFunction("erlang", "is_tuple", 1, (term) => {
    return Type.boolean(Type.isTuple(term));
  });

Interpreter.defineErlangFunction("erlang", "length", 1, (list) => {
    if (!Type.isProperList(list)) {
      Interpreter.raiseBifError("badarg", "erlang", "length", [list]);
    }

    return Type.integer(list.data.length);
  });

Interpreter.defineErlangFunction("erlang", "list_to_binary", 1, (ioList) => {
    const raiseBadarg = () => {
      Interpreter.raiseBifError("badarg", "erlang", "list_to_binary", [ioList]);
    };

    if (!Type.isList(ioList)) {
      raiseBadarg();
    }

    const chunks = [];

    const collect = (list) => {
      const data = list.data;
      const len = data.length;
      const elemCount = list.isProper ? len : len - 1;

      for (let i = 0; i < elemCount; i++) {
        const item = data[i];

        if (Type.isInteger(item)) {
          if (item.value < 0n || item.value > 255n) {
            raiseBadarg();
          }

          const segment = Type.bitstringSegment(item, {
            type: "integer",
            size: Type.integer(8),
            unit: 1n,
            endianness: "big",
          });

          chunks.push(Bitstring.fromSegmentWithIntegerValue(segment));
        } else if (Type.isBinary(item)) {
          chunks.push(item);
        } else if (Type.isList(item)) {
          collect(item);
        } else {
          raiseBadarg();
        }
      }

      if (!list.isProper) {
        const tail = data[len - 1];

        if (Type.isBinary(tail)) {
          chunks.push(tail);
        } else if (Type.isList(tail)) {
          collect(tail);
        } else {
          raiseBadarg();
        }
      }
    };

    collect(ioList);

    return Bitstring.concat(chunks);
  });

Interpreter.defineErlangFunction("erlang", "make_ref", 0, () => {
    return ERTS.uniqueReference();
  });

Interpreter.defineErlangFunction("erlang", "map_size", 1, (map) => {
    if (!Type.isMap(map)) {
      Interpreter.raiseBadMapError(map);
    }

    // #878: Type.mapSize is O(1); Object.keys(map.data).length would force
    // a full trie materialization just to count.
    return Type.integer(Type.mapSize(map));
  });

Interpreter.defineErlangFunction("erlang", "not", 1, (term) => {
    if (!Type.isBoolean(term)) {
      Interpreter.raiseBifError("badarg", "erlang", "not", [term]);
    }

    return Type.boolean(term.value == "true" ? false : true);
  });

Interpreter.defineErlangFunction("erlang", "raise", 3, (kind, reason, stacktrace) => {
    if (
      !Type.isAtom(kind) ||
      !["error", "exit", "throw"].includes(kind.value)
    ) {
      return Type.atom("badarg");
    }

    // A stacktrace entry is either {module, function, arity_or_args, location}
    // with atom module and function, or {fun, arity_or_args, location}. Only
    // these shapes are validated - the arity_or_args slot and the location's
    // content are accepted as given, mirroring the BEAM.
    const isValidEntry = (entry) => {
      if (!Type.isTuple(entry)) {
        return false;
      }

      const elems = entry.data;

      if (elems.length === 4) {
        return (
          Type.isAtom(elems[0]) &&
          Type.isAtom(elems[1]) &&
          Type.isList(elems[3])
        );
      }

      if (elems.length === 3) {
        return Type.isAnonymousFunction(elems[0]) && Type.isList(elems[2]);
      }

      return false;
    };

    if (
      !Type.isProperList(stacktrace) ||
      !stacktrace.data.every(isValidEntry)
    ) {
      return Type.atom("badarg");
    }

    const error = new HologramBoxedError(reason, kind);

    // The given boxed trace replaces the construction-time snapshot verbatim,
    // matching the BEAM - this is what makes reraise preserve traces.
    error.stacktrace = stacktrace;

    throw error;
  });

Interpreter.defineErlangFunction("erlang", "rem", 2, (integer1, integer2) => {
    if (
      !Type.isInteger(integer1) ||
      !Type.isInteger(integer2) ||
      integer2.value === 0n
    ) {
      Interpreter.raiseBifError("badarith", "erlang", "rem", [
        integer1,
        integer2,
      ]);
    }

    // JavaScript's % operator on BigInt has the same sign behavior as Erlang's rem
    // The result has the same sign as the dividend (integer1)
    return Type.integer(integer1.value % integer2.value);
  });

Interpreter.defineErlangFunction("erlang", "system_info", 1, (item) => {
    if (Type.isAtom(item)) {
      switch (item.value) {
        // Byte order of JS typed arrays on all supported platforms
        case "endian":
          return Type.atom("little");

        // The newest Erlang/OTP release the client runtime emulates
        case "otp_release":
          return Type.charlist("29");
      }

      throw new HologramInterpreterError(
        `the ${Interpreter.inspect(item)} system info item is not yet implemented in Hologram`,
      );
    }

    Interpreter.raiseBifError("badarg", "erlang", "system_info", [item]);
  });

Interpreter.defineErlangFunction("erlang", "tl", 1, (list) => {
    if (!Type.isList(list) || Type.listIsEmpty(list)) {
      Interpreter.raiseBifError("badarg", "erlang", "tl", [list]);
    }

    // #878: the cons cell's own tail is already the correct result value
    // (another cons cell, or the packed list it bottoms out in) - no
    // materialization, no allocation, and it's shared with whatever else
    // holds a reference to this tail.
    if (Type.isConsCell(list)) {
      return list.tail;
    }

    const length = list.data.length;

    if (length === 1) {
      return Type.list();
    }

    const isProper = Type.isProperList(list);

    if (length === 2 && !isProper) {
      return list.data[1];
    }

    const data = list.data.slice(1);

    return isProper ? Type.list(data) : Type.improperList(data);
  });

Interpreter.defineErlangFunction("lists", "_do_flatten", 2, (term, tail) => {
    if (!Type.isList(term)) {
      Interpreter.raiseFunctionClauseError("lists", "do_flatten", 2, [
        term,
        tail,
      ]);
    }

    if (!term.isProper) {
      Interpreter.raiseFunctionClauseError("lists", "do_flatten", 2, [
        term.data.at(-1),
        tail,
      ]);
    }

    let result = tail;

    for (let i = term.data.length - 1; i >= 0; i--) {
      const elem = term.data[i];

      if (Type.isList(elem)) {
        result = Erlang_Lists["_do_flatten/2"](elem, result);
      } else {
        result = result.isProper
          ? Type.list([elem, ...result.data])
          : Type.improperList([elem, ...result.data]);
      }
    }

    return result;
  });

Interpreter.defineErlangFunction("lists", "filter", 2, (fun, list) => {
    if (!Type.isAnonymousFunction(fun) || fun.arity !== 1) {
      Interpreter.raiseFunctionClauseError("lists", "filter", 2, [fun, list]);
    }

    if (!Type.isList(list)) {
      Erlang["error/1"](Type.tuple([Type.atom("bad_generator"), list]));
    }

    if (!Type.isProperList(list)) {
      Erlang["error/1"](
        Type.tuple([Type.atom("bad_generator"), list.data.at(-1)]),
      );
    }

    return Type.list(
      list.data.filter((elem) => {
        const result = Interpreter.callAnonymousFunction(fun, [elem]);

        if (!Type.isBoolean(result)) {
          Erlang["error/1"](Type.tuple([Type.atom("bad_filter"), result]));
        }

        return Type.isTrue(result);
      }),
    );
  });

Interpreter.defineErlangFunction("lists", "flatten", 1, (list) => {
    if (!Type.isList(list)) {
      Interpreter.raiseFunctionClauseError("lists", "flatten", 1, [list]);
    }

    return Erlang_Lists["_do_flatten/2"](list, Type.list());
  });

Interpreter.defineErlangFunction("lists", "foldl", 3, (fun, initialAcc, list) => {
    if (!Type.isAnonymousFunction(fun) || fun.arity !== 2) {
      Interpreter.raiseFunctionClauseError("lists", "foldl", 3, [
        fun,
        initialAcc,
        list,
      ]);
    }

    if (!Type.isList(list)) {
      Interpreter.raiseCaseClauseError(list);
    }

    const properLength = list.isProper
      ? list.data.length
      : list.data.length - 1;

    let acc = initialAcc;

    for (let i = 0; i < properLength; i++) {
      acc = Interpreter.callAnonymousFunction(fun, [list.data[i], acc]);
    }

    // The server folds the proper prefix before failing on the tail, so the
    // frame carries the accumulator built so far.
    if (!list.isProper) {
      Interpreter.raiseFunctionClauseError("lists", "foldl_1", 3, [
        fun,
        acc,
        list.data.at(-1),
      ]);
    }

    return acc;
  });

Interpreter.defineErlangFunction("lists", "keyfind", 3, (value, index, tuples) => {
    if (
      !Type.isInteger(index) ||
      index.value < 1n ||
      !Type.isProperList(tuples)
    ) {
      Interpreter.raiseBifError("badarg", "lists", "keyfind", [
        value,
        index,
        tuples,
      ]);
    }

    for (const tuple of tuples.data) {
      if (Type.isTuple(tuple)) {
        if (
          tuple.data.length >= index.value &&
          Interpreter.isEqual(tuple.data[Number(index.value) - 1], value)
        ) {
          return tuple;
        }
      }
    }

    return Type.boolean(false);
  });

Interpreter.defineErlangFunction("lists", "keymember", 3, (value, index, tuples) => {
    let result;

    try {
      result = Erlang_Lists["keyfind/3"](value, index, tuples);
    } catch (error) {
      if (error.struct) {
        // Re-raise with this function's own identity - the BEAM reports the
        // called function's frame, not the delegate's.
        Interpreter.raiseBifError("badarg", "lists", "keymember", [
          value,
          index,
          tuples,
        ]);
      }

      throw error;
    }

    return Type.boolean(Type.isTuple(result));
  });

Interpreter.defineErlangFunction("lists", "map", 2, (fun, list) => {
    if (!Type.isAnonymousFunction(fun) || fun.arity !== 1) {
      Interpreter.raiseFunctionClauseError("lists", "map", 2, [fun, list]);
    }

    if (!Type.isList(list)) {
      Interpreter.raiseCaseClauseError(list);
    }

    const properLength = list.isProper
      ? list.data.length
      : list.data.length - 1;

    const mapped = [];

    for (let i = 0; i < properLength; i++) {
      mapped.push(Interpreter.callAnonymousFunction(fun, [list.data[i]]));
    }

    // The server maps the proper prefix before failing on the tail.
    if (!list.isProper) {
      Interpreter.raiseFunctionClauseError("lists", "map_1", 2, [
        fun,
        list.data.at(-1),
      ]);
    }

    return Type.list(mapped);
  });

Interpreter.defineErlangFunction("lists", "member", 2, (elem, list) => {
    const raiseBadarg = () => {
      Interpreter.raiseBifError("badarg", "lists", "member", [elem, list]);
    };

    if (!Type.isList(list)) {
      raiseBadarg();
    }

    const isProperList = Type.isProperList(list);

    for (let i = 0; i < list.data.length; ++i) {
      if (Interpreter.isStrictlyEqual(list.data[i], elem)) {
        if (i < list.data.length - 1 || isProperList) {
          return Type.boolean(true);
        } else {
          raiseBadarg();
        }
      }
    }

    if (!isProperList) {
      raiseBadarg();
    }

    return Type.boolean(false);
  });

Interpreter.defineErlangFunction("lists", "reverse", 1, (list) => {
    // The BEAM matches the first two elements in reverse/1's own clauses, so
    // a non-list or an improper list with fewer than two proper elements
    // fails there, while a longer improper list fails in the reverse/2 BIF
    // with the reversal state accumulated so far.
    if (
      !Type.isList(list) ||
      (!Type.isProperList(list) && list.data.length === 2)
    ) {
      Interpreter.raiseFunctionClauseError("lists", "reverse", 1, [list]);
    }

    if (!Type.isProperList(list)) {
      const rest =
        list.data.length === 3
          ? list.data.at(-1)
          : Type.improperList(list.data.slice(2));

      Interpreter.raiseBifError("badarg", "lists", "reverse", [
        rest,
        Type.list([list.data[1], list.data[0]]),
      ]);
    }

    return Type.list(list.data.toReversed());
  });

Interpreter.defineErlangFunction("lists", "reverse", 2, (list, tail) => {
    if (!Type.isProperList(list)) {
      Interpreter.raiseBifError("badarg", "lists", "reverse", [list, tail]);
    }

    if (list.data.length === 0 && !Type.isList(tail)) {
      return tail;
    }

    const data = list.data
      .toReversed()
      .concat(Type.isList(tail) ? tail.data : [tail]);

    return Type.isProperList(tail) ? Type.list(data) : Type.improperList(data);
  });

Interpreter.defineErlangFunction("lists", "seq", 3, (fromTerm, toTerm, incrTerm) => {
    const raiseBadarg = () => {
      Interpreter.raiseBifError("badarg", "lists", "seq", [
        fromTerm,
        toTerm,
        incrTerm,
      ]);
    };

    if (
      !Type.isInteger(fromTerm) ||
      !Type.isInteger(toTerm) ||
      !Type.isInteger(incrTerm)
    ) {
      raiseBadarg();
    }

    const from = fromTerm.value;
    const to = toTerm.value;
    const incr = incrTerm.value;

    // Special case: seq(same, same, 0) when is_integer(same) -> [same]
    if (from === to && incr === 0n) {
      return Type.list([Type.integer(from)]);
    }

    // Erlang guard: (incr > 0 andalso from - incr =< to)
    // orelse (incr < 0 andalso from - incr >= to)
    if (!(
      (incr > 0n && from - incr <= to) ||
      (incr < 0n && from - incr >= to)
    )) {
      raiseBadarg();
    }

    const result = [];

    if (incr > 0n) {
      for (let i = from; i <= to; i += incr) {
        result.push(Type.integer(i));
      }
    } else {
      for (let i = from; i >= to; i += incr) {
        result.push(Type.integer(i));
      }
    }

    return Type.list(result);
  });

Interpreter.defineErlangFunction("lists", "sort", 1, (list) => {
    if (!Type.isList(list)) {
      Interpreter.raiseFunctionClauseError("lists", "sort", 1, [list]);
    }

    if (!Type.isProperList(list)) {
      // Match server behavior for improper lists:
      // - 1 proper element: the sort/1 clause head fails
      // - 2 proper elements: the first comparison picks the split function
      //   that then fails on the tail
      // - longer: the failure happens deep in the split state, which the
      //   client doesn't mirror, so the frame carries the bare arity
      if (list.data.length === 2) {
        Interpreter.raiseFunctionClauseError("lists", "sort", 1, [list]);
      }

      if (list.data.length === 3) {
        const x = list.data[0];
        const y = list.data[1];

        const splitFun =
          Interpreter.compareTerms(x, y) <= 0 ? "split_1" : "split_2";

        Interpreter.raiseFunctionClauseError("lists", splitFun, 5, [
          x,
          y,
          list.data.at(-1),
          Type.list(),
          Type.list(),
        ]);
      }

      Interpreter.raiseFunctionClauseError("lists", "split_1", 5);
    }

    return Type.list(list.data.slice().sort(Interpreter.compareTerms));
  });

Interpreter.defineErlangFunction("maps", "fold", 3, (fun, initialAcc, map) => {
    if (!Type.isAnonymousFunction(fun) || fun.arity !== 3) {
      Interpreter.raiseBifError("badarg", "maps", "fold", [
        fun,
        initialAcc,
        map,
      ]);
    }

    if (!Type.isMap(map)) {
      Interpreter.raiseBifError(["badmap", map], "maps", "fold", [
        fun,
        initialAcc,
        map,
      ]);
    }

    return Object.values(map.data).reduce(
      (acc, [key, value]) =>
        Interpreter.callAnonymousFunction(fun, [key, value, acc]),
      initialAcc,
    );
  });

Interpreter.defineErlangFunction("maps", "from_list", 1, (list) => {
    const isPair = (elem) => Type.isTuple(elem) && elem.data.length === 2;

    // An element that isn't a pair derives the bare "argument error", since the
    // fragment the message is built from names only what is wrong with the
    // argument as a whole - which is a list, whatever it holds.
    if (!Type.isProperList(list) || !list.data.every(isPair)) {
      Interpreter.raiseBifError("badarg", "maps", "from_list", [list]);
    }

    return Type.map(list.data.map((tuple) => tuple.data));
  });

Interpreter.defineErlangFunction("maps", "get", 2, (key, map) => {
    if (!Type.isMap(map)) {
      Interpreter.raiseBifError(["badmap", map], "erlang", "map_get", [
        key,
        map,
      ]);
    }

    const pair = Type.mapGet(map, Type.encodeMapKey(key));

    if (pair !== undefined) {
      return pair[1];
    }

    Interpreter.raiseBifError(["badkey", key], "erlang", "map_get", [key, map]);
  });

Interpreter.defineErlangFunction("maps", "get", 3, (key, map, defaultValue) => {
    if (!Type.isMap(map)) {
      Interpreter.raiseFramelessError(["badmap", map]);
    }

    const pair = Type.mapGet(map, Type.encodeMapKey(key));

    return pair === undefined ? defaultValue : pair[1];
  });

Interpreter.defineErlangFunction("maps", "is_iterator_valid", 1, (iterator) => {
    let current = iterator;

    while (Type.isTuple(current) && current.data.length === 3) {
      current = current.data[2];
    }

    if (Type.isAtom(current) && current.value === "none") {
      return Type.boolean(true);
    }

    return Type.boolean(
      Type.isImproperList(current) &&
        current.data.length === 2 &&
        Type.isInteger(current.data[0]) &&
        Type.isMap(current.data[1]),
    );
  });

Interpreter.defineErlangFunction("maps", "is_key", 2, (key, map) => {
    if (!Type.isMap(map)) {
      Interpreter.raiseBifError(["badmap", map], "erlang", "is_map_key", [
        key,
        map,
      ]);
    }

    return Type.boolean(Type.mapHas(map, Type.encodeMapKey(key)));
  });

Interpreter.defineErlangFunction("maps", "merge", 2, (map1, map2) => {
    if (!Type.isMap(map1)) {
      Interpreter.raiseBifError(["badmap", map1], "maps", "merge", [
        map1,
        map2,
      ]);
    }

    if (!Type.isMap(map2)) {
      Interpreter.raiseBifError(["badmap", map2], "maps", "merge", [
        map1,
        map2,
      ]);
    }

    // Identity fast paths (#878). This is the hottest structural copy in the
    // framework: transformer.ex compiles `%{m | k: v}` to Map.merge/2, so
    // this runs once per put_state action plus twice more per stateful
    // component per render (props+state, context+emitted_context). Uses
    // Type.mapSize/mapGet, not .data - a size or point-access check that
    // materialized either map's full contents just to answer "is this
    // empty" or "is this key already there" would turn an O(log32 n) check
    // into the O(n log n) full-map copy it exists to avoid.
    if (Type.mapSize(map2) === 0) {
      return map1;
    }

    if (Type.mapSize(map1) === 0) {
      return map2;
    }

    // If every key map2 would write already holds a reference-identical
    // value in map1, the merge changes nothing - return map1 unchanged
    // rather than paying the copy. Reference identity only, same reasoning
    // as Type.mapPut. Short-circuits on the first real change, so the
    // common single-key `%{state | k: v}` case costs one Type.mapGet, not a
    // full pass over map2.
    let isNoOp = true;

    for (const [encodedKey, pair] of Type.mapEntries(map2)) {
      const existing = Type.mapGet(map1, encodedKey);

      if (existing === undefined || existing[1] !== pair[1]) {
        isNoOp = false;
        break;
      }
    }

    if (isNoOp) {
      return map1;
    }

    return Type.mapMerge(map1, map2);
  });

Interpreter.defineErlangFunction("maps", "put", 3, (key, value, map) => {
    if (!Type.isMap(map)) {
      Interpreter.raiseBifError(["badmap", map], "maps", "put", [
        key,
        value,
        map,
      ]);
    }

    // Identity fast path (#878, see Type.mapPut): a struct write that
    // doesn't actually change anything returns the same map, so it stays
    // cheap to detect downstream (ComponentRegistry.putComponentStruct/
    // RenderCache).
    return Type.mapPut(map, Type.encodeMapKey(key), [key, value]);
  });

Interpreter.defineErlangFunction("maps", "to_list", 1, (mapOrIterator) => {
    if (!Type.isMap(mapOrIterator)) {
      Interpreter.raiseBifError(["badmap", mapOrIterator], "maps", "to_list", [
        mapOrIterator,
      ]);
    }

    return Type.list(
      Object.values(mapOrIterator.data).map((item) => Type.tuple(item)),
    );
  });

Interpreter.defineErlangFunction("maps", "update", 3, (key, value, map) => {
    if (!Type.isMap(map)) {
      Interpreter.raiseBifError(["badmap", map], "maps", "update", [
        key,
        value,
        map,
      ]);
    }

    if (Type.isFalse(Erlang_Maps["is_key/2"](key, map))) {
      Interpreter.raiseBifError(["badkey", key], "maps", "update", [
        key,
        value,
        map,
      ]);
    }

    return Erlang_Maps["put/3"](key, value, map);
  });

Interpreter.defineErlangFunction("re", "compile", 2, (pattern, options) => {
    const buildErrorTuple = (message, position) =>
      Type.tuple([
        Type.atom("error"),
        Type.tuple([Type.charlist(message), Type.integer(position)]),
      ]);

    // Returns null when the options are not a proper list of valid
    // compile options.
    const parseOptions = () => {
      if (!Type.isProperList(options)) return null;

      const acc = {
        anchored: false,
        engineOpts: {},
        firstline: false,
        unicodeOption: false,
      };

      for (const option of options.data) {
        if (ERTS.regex.parseCompileOption(option, acc) === "invalid") {
          return null;
        }
      }

      return acc;
    };

    const parsedOptions = parseOptions();

    if (parsedOptions === null) {
      Interpreter.raiseBifError(
        "badarg",
        "re",
        "compile",
        [pattern, options],
        "erl_stdlib_errors",
        "badopt",
      );
    }

    const engineOpts = parsedOptions.engineOpts;
    let result;

    if (parsedOptions.unicodeOption) {
      engineOpts.unicode = true;

      let patternBytes = null;
      let patternText = null;

      if (Type.isBitstring(pattern)) {
        if (!Type.isBinary(pattern)) {
          Interpreter.raiseBifError("badarg", "re", "compile", [
            pattern,
            options,
          ]);
        }

        Bitstring.maybeSetBytesFromText(pattern);
        patternBytes = pattern.bytes;
      } else {
        patternText = ERTS.regex.charDataToText(pattern);

        // The server raise carries no error_info here - the frame comes
        // from OTP's ucompile wrapper, not from a BIF.
        if (patternText === null) {
          Interpreter.raiseBifError(
            "badarg",
            "re",
            "compile",
            [pattern, options],
            null,
          );
        }
      }

      // The option clash beats UTF-8 validation of a binary pattern
      if (engineOpts.never_utf) {
        return buildErrorTuple("using UTF is disabled by the application", 0);
      }

      if (patternBytes !== null) {
        result = ERTS.regex.compileBytes(patternBytes, engineOpts);
      } else {
        result = ERTS.regex.compileText(patternText, engineOpts);
      }
    } else {
      let binary;

      try {
        binary = Erlang["iolist_to_binary/1"](pattern);
      } catch (error) {
        if (error.struct) {
          // Re-raise with this function's own identity - the BEAM reports
          // the BIF's frame, not the conversion's.
          Interpreter.raiseBifError("badarg", "re", "compile", [
            pattern,
            options,
          ]);
        }

        throw error;
      }

      Bitstring.maybeSetBytesFromText(binary);
      result = ERTS.regex.compileBytes(binary.bytes, engineOpts);
    }

    if (result.error) {
      return buildErrorTuple(result.error.message, result.error.position);
    }

    const captureCount = result.groupMap.count;
    const useCrlf = ERTS.regex.usesCrlf(result.newlineType) ? 1 : 0;
    const ref = Erlang["make_ref/0"]();

    ERTS.regexPatternRegistry.put(ref, {
      anchored: parsedOptions.anchored,
      captureCount: captureCount,
      compiled: result,
      firstline: parsedOptions.firstline,
      unicode: result.opts.unicode === true,
    });

    return Type.tuple([
      Type.atom("ok"),
      Type.tuple([
        Type.atom("re_pattern"),
        Type.integer(captureCount),
        Type.integer(parsedOptions.unicodeOption ? 1 : 0),
        Type.integer(useCrlf),
        ref,
      ]),
    ]);
  });

Interpreter.defineErlangFunction("re", "import", 1, (exportedPattern) => {
    const raiseNotExported = () => {
      Interpreter.raiseBifError("badarg", "re", "import", [exportedPattern]);
    };

    const hasMagicPrefix = (binary, magic) => {
      if (!Type.isBinary(binary)) return false;

      Bitstring.maybeSetBytesFromText(binary);

      if (binary.bytes.length < magic.length) return false;

      return [...magic].every(
        (char, index) => binary.bytes[index] === char.charCodeAt(0),
      );
    };

    if (!Type.isRecordTuple(exportedPattern, "re_exported_pattern", 5)) {
      raiseNotExported();
    }

    const [_tag, header, source, options, code] = exportedPattern.data;

    // The header and code blobs carry PCRE2-native serialization the client
    // can't execute - the pattern is recompiled from source and options
    // instead, so blob validation is limited to the serialization magic
    // bytes ("re-PCRE2" and "S2RP") and the header size.
    if (
      !hasMagicPrefix(header, "re-PCRE2") ||
      header.bytes.length < 14 ||
      !hasMagicPrefix(code, "S2RP")
    ) {
      raiseNotExported();
    }

    if (!Type.isProperList(options)) raiseNotExported();

    const compileOptions = Type.list(
      options.data.filter(
        (option) => !(Type.isAtom(option) && option.value === "export"),
      ),
    );

    let result;

    try {
      result = Erlang_Re["compile/2"](source, compileOptions);
    } catch {
      raiseNotExported();
    }

    // A compile error tuple can only come from a tampered source, since the
    // exported source has already compiled successfully
    if (!Interpreter.isStrictlyEqual(result.data[0], Type.atom("ok"))) {
      raiseNotExported();
    }

    return result.data[1];
  });

Interpreter.defineErlangFunction("re", "run", 3, (subject, pattern, options) => {
    const CAPTURE_KIND_ATOMS = new Set([
      "all",
      "all_but_first",
      "all_names",
      "first",
      "none",
    ]);

    const CAPTURE_TYPE_ATOMS = new Set(["binary", "index", "list"]);

    // Limit tuple option tags mapped to engine run option names.
    const LIMIT_TUPLE_KEYS = {
      match_limit: "matchLimit",
      match_limit_recursion: "matchLimitRecursion",
    };

    // Group indices, offsets and match limits are limited to the positive
    // range of a 32-bit integer.
    const MAX_INT32 = 2_147_483_647n;

    // Scan flag option atoms mapped to engine run option names.
    const RUN_FLAG_KEYS = {
      notbol: "notbol",
      noteol: "noteol",
      notempty: "notempty",
      notempty_atstart: "notemptyAtStart",
    };

    // Run-only option atoms not yet supported.
    // TODO: implement the report_errors run option.
    const RUN_ONLY_ATOMS = new Set(["report_errors"]);

    const buildPatternEntry = (compiled) => ({
      anchored: acc.anchored,
      compiled: compiled,
      firstline: acc.firstline,
      unicode: compiled.opts.unicode === true,
    });

    const isBoundedInteger = (term) =>
      Type.isInteger(term) && term.value >= 0n && term.value <= MAX_INT32;

    // Validates the capture option and returns the capture kind, targets
    // and type.
    const parseCaptureSpec = () => {
      let captureKind = "all";
      let captureTargets = null;
      let captureType = "index";

      if (captureOption !== null) {
        if (captureOption.data.length === 3) {
          const typeTerm = captureOption.data[2];

          if (
            !Type.isAtom(typeTerm) ||
            !CAPTURE_TYPE_ATOMS.has(typeTerm.value)
          ) {
            raiseArgumentError();
          }

          captureType = typeTerm.value;
        }

        const valueSpec = captureOption.data[1];

        if (Type.isAtom(valueSpec) && CAPTURE_KIND_ATOMS.has(valueSpec.value)) {
          captureKind = valueSpec.value;
        } else if (Type.isProperList(valueSpec)) {
          captureKind = "explicit";

          captureTargets = valueSpec.data.map((element) => {
            if (Type.isInteger(element)) {
              if (element.value < 0n || element.value > MAX_INT32) {
                raiseArgumentError();
              }

              return {number: Number(element.value)};
            }

            if (Type.isAtom(element)) return {name: element.value};

            if (Type.isBitstring(element)) {
              if (!Type.isBinary(element)) raiseArgumentError();

              Bitstring.maybeSetBytesFromText(element);

              // An invalid UTF-8 name can't match any group name, which makes
              // it an unset capture rather than an error
              const decoded = ERTS.regex.decodeUtf8(element.bytes);

              return {name: decoded.error ? null : decoded.text};
            }

            if (Type.isList(element)) {
              const name = ERTS.regex.charDataToText(element);

              if (name === null) raiseArgumentError();

              return {name};
            }

            raiseArgumentError();
          });
        } else {
          raiseArgumentError();
        }
      }

      return {captureKind, captureTargets, captureType};
    };

    // Parses the :re.run options list. Later occurrences win for the capture,
    // offset and limit options. optionsValid turns false on the first invalid
    // option.
    const parseRunOptions = () => {
      const acc = {
        anchored: false,
        engineOpts: {},
        firstline: false,
        unicodeOption: false,
      };

      const runFlags = {
        notbol: false,
        noteol: false,
        notempty: false,
        notemptyAtStart: false,
      };

      let captureOption = null;
      let compileOnlyOptionUsed = false;
      let isGlobal = false;
      let notImplementedOption = null;

      const limitOptions = {
        matchLimit: undefined,
        matchLimitRecursion: undefined,
      };

      let dualOptionUsed = false;
      let offsetOption = null;
      let optionsValid = Type.isProperList(options);

      if (optionsValid) {
        for (const option of options.data) {
          if (Type.isAtom(option) && option.value === "global") {
            isGlobal = true;
            continue;
          }

          if (
            Type.isAtom(option) &&
            Object.hasOwn(RUN_FLAG_KEYS, option.value)
          ) {
            runFlags[RUN_FLAG_KEYS[option.value]] = true;
            continue;
          }

          const isCapture =
            Type.isTuple(option) &&
            (option.data.length === 2 || option.data.length === 3) &&
            Interpreter.isStrictlyEqual(option.data[0], Type.atom("capture"));

          // The last capture option wins
          if (isCapture) {
            captureOption = option;
            continue;
          }

          const isOffset =
            Type.isTuple(option) &&
            option.data.length === 2 &&
            Interpreter.isStrictlyEqual(option.data[0], Type.atom("offset"));

          if (isOffset) {
            if (!isBoundedInteger(option.data[1])) {
              optionsValid = false;
              break;
            }

            // The last offset option wins
            offsetOption = Number(option.data[1].value);
            continue;
          }

          const isLimit =
            Type.isTuple(option) &&
            option.data.length === 2 &&
            Type.isAtom(option.data[0]) &&
            Object.hasOwn(LIMIT_TUPLE_KEYS, option.data[0].value);

          if (isLimit) {
            if (!isBoundedInteger(option.data[1])) {
              optionsValid = false;
              break;
            }

            // The last limit option of each kind wins
            limitOptions[LIMIT_TUPLE_KEYS[option.data[0].value]] = Number(
              option.data[1].value,
            );

            continue;
          }

          const isRunOnly =
            Type.isAtom(option) && RUN_ONLY_ATOMS.has(option.value);

          if (isRunOnly) {
            notImplementedOption ??= option;
            continue;
          }

          switch (ERTS.regex.parseCompileOption(option, acc)) {
            case "compile":
              compileOnlyOptionUsed = true;
              break;

            case "dual":
              dualOptionUsed = true;
              break;

            case "invalid":
              optionsValid = false;
              break;
          }

          if (!optionsValid) break;
        }
      }

      return {
        acc,
        captureOption,
        compileOnlyOptionUsed,
        dualOptionUsed,
        isGlobal,
        limitOptions,
        notImplementedOption,
        offsetOption,
        optionsValid,
        runFlags,
      };
    };

    // Used where validation has already passed for the subject and pattern
    // and no badopt cause applies, so the formatter derives no bullets and
    // the message stays the plain "argument error".
    const raiseArgumentError = () => {
      Interpreter.raiseBifError("badarg", "re", "run", [
        subject,
        pattern,
        options,
      ]);
    };

    // Char data conversion failures mirror the server's split: a binary
    // term fails inside the BIF, which raises with stdlib error_info,
    // while any other term fails inside OTP's urun wrapper, whose frame
    // carries no error_info.
    const raiseCharDataError = (term) => {
      Interpreter.raiseBifError(
        "badarg",
        "re",
        "run",
        [subject, pattern, options],
        Type.isBitstring(term) ? "erl_stdlib_errors" : null,
      );
    };

    const raiseNotImplemented = (option) => {
      throw new HologramInterpreterError(
        `the ${Interpreter.inspect(option)} option is not yet implemented in Hologram`,
      );
    };

    // Resolves the offset option to a JS string start position. The offset
    // is a byte offset into the subject. An offset inside a character
    // raises, and an offset beyond the subject raises in a non-global run
    // while a global run returns null, which reports no match found.
    const resolveStartPosition = () => {
      let offsetBeyondSubject = false;
      let startPosition = 0;

      if (offsetOption !== null) {
        if (entry.unicode) {
          startPosition = ERTS.regex.byteOffsetToUtf16Index(
            subjectText,
            offsetOption,
          );

          if (
            ERTS.regex.utf16IndexToByteOffset(subjectText, startPosition) !==
            offsetOption
          ) {
            const subjectByteLength = ERTS.regex.utf16IndexToByteOffset(
              subjectText,
              subjectText.length,
            );

            if (offsetOption > subjectByteLength) {
              offsetBeyondSubject = true;
            } else {
              raiseArgumentError();
            }
          }
        } else {
          // In byte mode JS string indices are byte offsets already
          offsetBeyondSubject = offsetOption > subjectText.length;
          startPosition = offsetOption;
        }

        if (offsetBeyondSubject) {
          if (isGlobal) return null;

          raiseArgumentError();
        }
      }

      return startPosition;
    };

    // Shapes match results into the :re.run return term, applying the
    // capture spec.
    const shapeMatchResults = (matchResults) => {
      const groupMap = entry.compiled.groupMap;

      // The none spec, an empty capture list and all_names without named
      // groups yield a bare :match
      if (
        captureKind === "none" ||
        (captureKind === "explicit" && captureTargets.length === 0) ||
        (captureKind === "all_names" && groupMap.names.size === 0)
      ) {
        return Type.atom("match");
      }

      // PCRE2 stores the name table sorted by the byte order of the names
      const sortedNames =
        captureKind === "all_names"
          ? [...groupMap.names.keys()].sort(ERTS.regex.compareByUtf8Bytes)
          : null;

      // In byte mode JS string indices are byte offsets already
      const toByteOffset = entry.unicode
        ? (index) => ERTS.regex.utf16IndexToByteOffset(subjectText, index)
        : (index) => index;

      const buildValue = (capture) => {
        switch (captureType) {
          case "binary": {
            if (capture === null) return Type.bitstring("");

            const slice = subjectText.slice(capture.start, capture.end);

            return entry.unicode
              ? Type.bitstring(slice)
              : Bitstring.fromBytes(
                  [...slice].map((char) => char.charCodeAt(0)),
                );
          }

          case "index": {
            if (capture === null) {
              return Type.tuple([Type.integer(-1), Type.integer(0)]);
            }

            const start = toByteOffset(capture.start);
            const length = toByteOffset(capture.end) - start;

            return Type.tuple([Type.integer(start), Type.integer(length)]);
          }

          case "list": {
            if (capture === null) return Type.list();

            const slice = subjectText.slice(capture.start, capture.end);

            return Type.list(
              [...slice].map((char) => Type.integer(char.codePointAt(0))),
            );
          }
        }
      };

      // Returns the boxed capture list of a single match result
      const shapeMatch = (matchResult) => {
        // Returns {start, end} in JS string indices, or null when the group
        // is unset or doesn't exist
        const captureForNumber = (number) => {
          if (number === 0) {
            return {start: matchResult.start, end: matchResult.end};
          }

          if (number > groupMap.count) return null;

          return matchResult.captures[number];
        };

        // With dupnames a name maps to multiple group numbers, of which the
        // first set one wins
        const captureForName = (name) => {
          const numbers = groupMap.names.get(name);

          if (numbers === undefined) return null;

          for (const number of numbers) {
            const capture = captureForNumber(number);
            if (capture !== null) return capture;
          }

          return null;
        };

        const captureForTarget = (target) =>
          "number" in target
            ? captureForNumber(target.number)
            : captureForName(target.name);

        let captures;

        switch (captureKind) {
          case "all":
          case "all_but_first":
            captures = [];

            for (
              let number = captureKind === "all" ? 0 : 1;
              number <= groupMap.count;
              number++
            ) {
              captures.push(captureForNumber(number));
            }

            // Trailing unset groups are not reported
            while (
              captures.length > 0 &&
              captures[captures.length - 1] === null
            ) {
              captures.pop();
            }
            break;

          case "all_names":
            captures = sortedNames.map(captureForName);
            break;

          case "explicit":
            captures = captureTargets.map(captureForTarget);
            break;

          case "first":
            captures = [captureForNumber(0)];
            break;
        }

        return Type.list(captures.map(buildValue));
      };

      const captured = isGlobal
        ? Type.list(matchResults.map(shapeMatch))
        : shapeMatch(matchResults[0]);

      return Type.tuple([Type.atom("match"), captured]);
    };

    // --- Options ---

    const {
      acc,
      captureOption,
      compileOnlyOptionUsed,
      dualOptionUsed,
      isGlobal,
      limitOptions,
      notImplementedOption,
      offsetOption,
      optionsValid,
      runFlags,
    } = parseRunOptions();

    // --- Pattern (validation phase) ---

    const registryEntry = ERTS.regexPatternRegistry.lookupByTerm(pattern);

    let entry = null;
    let patternInvalid = false;
    let patternRaisesArgumentError = false;

    if (registryEntry !== null) {
      entry = registryEntry;
    } else if (acc.unicodeOption) {
      // In unicode mode only the pattern term shape is validated here.
      // The pattern resolves later, as char data conversion failures raise
      // from the resolution phase instead of contributing a bullet.
      if (!Type.isBinary(pattern) && !Type.isList(pattern)) {
        patternInvalid = true;
      }
    } else {
      // In byte mode the pattern resolves during validation
      let patternBinary = null;

      try {
        patternBinary = Erlang["iolist_to_binary/1"](pattern);
      } catch {
        patternInvalid = true;
      }

      if (patternBinary !== null) {
        Bitstring.maybeSetBytesFromText(patternBinary);

        const result = ERTS.regex.compileBytes(
          patternBinary.bytes,
          acc.engineOpts,
        );

        if (result.error) {
          if (
            result.error.message === "using UTF is disabled by the application"
          ) {
            patternRaisesArgumentError = true;
          } else {
            patternInvalid = true;
          }
        } else {
          entry = buildPatternEntry(result);
        }
      }
    }

    // --- Subject (validation phase) ---

    // The subject is validated as iodata unless it is consumed as char data,
    // which happens with the unicode option or a unicode compiled pattern
    const byteModeSubject =
      !acc.unicodeOption && !(registryEntry !== null && registryEntry.unicode);

    let subjectBinary = null;
    let subjectInvalid = false;

    if (byteModeSubject) {
      try {
        subjectBinary = Erlang["iolist_to_binary/1"](subject);
      } catch {
        subjectInvalid = true;
      }
    }

    // --- Combined validation errors ---

    // The formatter recomputes the per-argument bullets from the frame
    // args, like the server's must_be_iodata and must_be_regexp probes do.
    if (subjectInvalid || patternInvalid || !optionsValid) {
      Interpreter.raiseBifError(
        "badarg",
        "re",
        "run",
        [subject, pattern, options],
        "erl_stdlib_errors",
        optionsValid ? null : "badopt",
      );
    }

    if (patternRaisesArgumentError) raiseArgumentError();

    // Compile options, including the dual newline and bsr options, don't
    // apply to an already compiled pattern
    if (
      registryEntry !== null &&
      (compileOnlyOptionUsed || dualOptionUsed || acc.unicodeOption)
    ) {
      raiseArgumentError();
    }

    // --- Pattern (unicode resolution phase) ---

    let subjectText = null;

    if (entry === null) {
      acc.engineOpts.unicode = true;

      // The subject char data converts before the pattern compiles
      subjectText = ERTS.regex.charDataToText(subject);

      if (subjectText === null) raiseCharDataError(subject);

      const patternText = ERTS.regex.charDataToText(pattern);

      if (patternText === null) raiseCharDataError(pattern);

      // The option clash raises instead of returning a compile error tuple
      if (acc.engineOpts.never_utf) raiseArgumentError();

      const result = ERTS.regex.compileText(patternText, acc.engineOpts);

      // The formatter recomputes the parse-error bullet by compiling the
      // pattern with default options, like the server's must_be_regexp
      // probe does.
      if (result.error) {
        Interpreter.raiseBifError("badarg", "re", "run", [
          subject,
          pattern,
          options,
        ]);
      }

      entry = buildPatternEntry(result);
    }

    // --- Capture spec (validation phase) ---

    // The spec content is validated before matching, so an invalid spec
    // raises even when the subject wouldn't match
    const {captureKind, captureTargets, captureType} = parseCaptureSpec();

    // --- Options not yet supported ---

    if (notImplementedOption !== null) {
      raiseNotImplemented(notImplementedOption);
    }

    // --- Subject resolution ---

    if (subjectText === null) {
      if (entry.unicode) {
        subjectText = ERTS.regex.charDataToText(subject);

        if (subjectText === null) raiseCharDataError(subject);
      } else {
        Bitstring.maybeSetBytesFromText(subjectBinary);
        subjectText = ERTS.regex.textFromLatin1Bytes(subjectBinary.bytes);
      }
    }

    // --- Start position ---

    const startPosition = resolveStartPosition();

    if (startPosition === null) return Type.atom("nomatch");

    // --- Match ---

    const engineRunOpts = {
      anchored: entry.anchored || acc.anchored,
      firstline: entry.firstline,
      matchLimit: limitOptions.matchLimit,
      matchLimitRecursion: limitOptions.matchLimitRecursion,
      notbol: runFlags.notbol,
      noteol: runFlags.noteol,
      notempty: runFlags.notempty,
      notemptyAtStart: runFlags.notemptyAtStart,
      startPosition: startPosition,
    };

    let matchResults;

    if (isGlobal) {
      matchResults = ERTS.regex.matchGlobal(
        entry.compiled,
        subjectText,
        engineRunOpts,
      );
    } else {
      const matchResult = ERTS.regex.match(
        entry.compiled,
        subjectText,
        engineRunOpts,
      );

      matchResults = matchResult === null ? [] : [matchResult];
    }

    if (matchResults.length === 0) return Type.atom("nomatch");

    // --- Captures ---

    return shapeMatchResults(matchResults);
  });

Interpreter.defineErlangFunction("unicode", "_chardata_to_utf8_binary", 1, (input) => {
    if (Type.isBinary(input)) {
      return input;
    }

    if (!Type.isList(input)) {
      return null;
    }

    const flatInput = Erlang_Unicode["_flatten_chardata/1"](input);

    if (flatInput === null) {
      return null;
    }

    const chunks = [];

    for (let i = 0; i < flatInput.data.length; ++i) {
      const elem = flatInput.data[i];

      if (Type.isBinary(elem)) {
        chunks.push(elem);
      } else if (Type.isInteger(elem)) {
        if (Bitstring.isUnicodeScalarValue(elem.value)) {
          const segment = Type.bitstringSegment(elem, {type: "utf8"});
          chunks.push(Bitstring.fromSegments([segment]));
        } else {
          const remainingElems = flatInput.data.slice(i);

          return Type.tuple([
            Type.atom("error"),
            Bitstring.concat(chunks),
            Type.list(remainingElems),
          ]);
        }
      } else {
        return null;
      }
    }

    return Bitstring.concat(chunks);
  });

Interpreter.defineErlangFunction("unicode", "_flatten_chardata", 1, (chardata) => {
    const elems = [];

    const collect = (list) => {
      for (const elem of list.data) {
        if (Type.isList(elem)) {
          if (!collect(elem)) {
            return false;
          }
        } else {
          elems.push(elem);
        }
      }

      // The tail of an improper list is its last element, so it has already
      // been collected by the time its type is what decides the answer.
      return list.isProper || Type.isBinary(list.data.at(-1));
    };

    return collect(chardata) ? Type.list(elems) : null;
  });

Interpreter.defineErlangFunction("unicode", "characters_to_binary", 1, (input) => {
    const result = Erlang_Unicode["_chardata_to_utf8_binary/1"](input);

    if (result === null) {
      Interpreter.raiseBifError("badarg", "unicode", "characters_to_binary", [
        input,
      ]);
    }

    // Bytes that don't decode aren't characters. The server answers a tuple
    // naming what it read before the break and what was left over, rather than
    // handing back the bytes it couldn't read - which is what this used to do.
    if (Bitstring.toText(result) !== false) {
      return result;
    }

    // characters_to_list/1 walks the same chardata and breaks in the same
    // place, so what it read and what it left are what this answers too - as a
    // binary rather than as code points.
    const [tag, codePoints, rest] =
      Erlang_Unicode["characters_to_list/1"](input).data;

    const text = codePoints.data
      .map(({value}) => String.fromCodePoint(Number(value)))
      .join("");

    // A rest holding nothing but the bytes that broke is answered as those
    // bytes, where a longer one keeps its list form.
    const unwrapsRest =
      Type.isList(rest) &&
      rest.data.length === 1 &&
      Type.isBinary(rest.data[0]);

    return Type.tuple([
      tag,
      Bitstring.fromText(text),
      unwrapsRest ? rest.data[0] : rest,
    ]);
  });

Interpreter.defineErlangFunction("unicode", "characters_to_list", 1, (data) => {
    // Helpers

    // Scans forward once to find the longest valid UTF-8 prefix.
    // Validates UTF-8 by checking byte structure, decoding code points,
    // and rejecting overlong encodings, surrogates, and out-of-range values.
    // Time complexity: O(n) where n is the number of bytes.
    const findValidUtf8Length = (bytes) => {
      // Checks if a byte is a valid UTF-8 continuation byte (10xxxxxx).
      const isValidContinuation = (byte) => (byte & 0xc0) === 0x80;

      // Validates that a code point is within UTF-8 rules:
      // - Not an overlong encoding (using more bytes than necessary)
      // - Not a UTF-16 surrogate (U+D800–U+DFFF)
      // - Not above maximum Unicode (U+10FFFF)
      const isValidCodePoint = (codePoint, encodingLength) => {
        // Check for overlong encodings (security issue)
        const minValueForLength = [0, 0, 0x80, 0x800, 0x10000];
        if (codePoint < minValueForLength[encodingLength]) return false;

        // Reject UTF-16 surrogates (U+D800–U+DFFF)
        if (codePoint >= 0xd800 && codePoint <= 0xdfff) return false;

        // Reject code points beyond Unicode range (> U+10FFFF)
        if (codePoint > 0x10ffff) return false;

        return true;
      };

      // Validates a complete UTF-8 sequence at the given position.
      // Checks: sufficient bytes, valid continuations, and valid code point.
      const isValidSequence = (start, length) => {
        // Check if we have enough bytes
        if (start + length > bytes.length) return false;

        // Verify all continuation bytes have correct pattern (10xxxxxx)
        for (let i = 1; i < length; i++) {
          if (!isValidContinuation(bytes[start + i])) return false;
        }

        // Decode and validate the code point value
        const codePoint = Bitstring.decodeUtf8CodePoint(bytes, start, length);

        return isValidCodePoint(codePoint, length);
      };

      // Checks if there's a truncated (incomplete) sequence at position.
      // Returns true if bytes could be a valid prefix of a UTF-8 sequence.
      const isTruncatedSequence = (start) => {
        const leaderByte = bytes[start];
        const expectedLength = Bitstring.getUtf8SequenceLength(leaderByte);

        if (expectedLength === false) return false;

        const availableBytes = bytes.length - start;
        if (availableBytes >= expectedLength) return false;

        // Check all available continuation bytes
        for (let i = 1; i < availableBytes; i++) {
          if (!isValidContinuation(bytes[start + i])) return false;
        }

        return true;
      };

      // Main loop: scan forward, validating each sequence
      let pos = 0;

      while (pos < bytes.length) {
        const seqLength = Bitstring.getUtf8SequenceLength(bytes[pos]);
        if (seqLength === false || !isValidSequence(pos, seqLength)) break;
        pos += seqLength;
      }

      return {validLength: pos, isTruncated: isTruncatedSequence(pos)};
    };

    // Converts a binary to a list of codepoints.
    const convertBinaryToCodepoints = (binary, preDecodedText = null) => {
      const text =
        preDecodedText !== null ? preDecodedText : Bitstring.toText(binary);

      return Array.from(text).map((char) => Type.integer(char.codePointAt(0)));
    };

    // Converts a single codepoint integer to a UTF-8 encoded binary.
    const convertCodepointToBinary = (codepoint) => {
      const segment = Type.bitstringSegment(codepoint, {type: "utf8"});
      return Bitstring.fromSegments([segment]);
    };

    // Creates an error tuple: {:error, converted_so_far, rest}
    const createErrorTuple = (codepoints, rest) => {
      return Type.tuple([Type.atom("error"), Type.list(codepoints), rest]);
    };

    // Creates an incomplete tuple: {:incomplete, converted_so_far, rest}
    const createIncompleteTuple = (codepoints, rest) => {
      return Type.tuple([Type.atom("incomplete"), Type.list(codepoints), rest]);
    };

    // Handles invalid UTF-8 errors from binary input (not wrapped in list).
    // Returns error or incomplete tuple with binary rest.
    const handleInvalidUtf8FromBinary = (invalidBinary) => {
      Bitstring.maybeSetBytesFromText(invalidBinary);
      const bytes = invalidBinary.bytes ?? new Uint8Array(0);
      const {validLength, isTruncated} = findValidUtf8Length(bytes);

      const validPrefix = Bitstring.fromBytes(bytes.slice(0, validLength));
      const invalidRest = Bitstring.fromBytes(bytes.slice(validLength));

      const codepoints =
        validLength > 0 ? convertBinaryToCodepoints(validPrefix) : [];

      if (isTruncated) {
        return createIncompleteTuple(codepoints, invalidRest);
      }

      return createErrorTuple(codepoints, invalidRest);
    };

    // Handles invalid UTF-8 errors from list input. Returns error or incomplete tuple.
    // For error tuples, the rest is wrapped in a list. For incomplete tuples, it's the binary directly.
    const handleInvalidUtf8FromList = (
      chunks,
      invalidBinary,
      remainingElems,
    ) => {
      Bitstring.maybeSetBytesFromText(invalidBinary);
      const bytes = invalidBinary.bytes ?? new Uint8Array(0);
      const {validLength, isTruncated} = findValidUtf8Length(bytes);

      // The element breaks partway through, so the bytes before the break were
      // read and belong with the elements before it rather than with the rest.
      const readChunks =
        validLength > 0
          ? [...chunks, Bitstring.fromBytes(bytes.slice(0, validLength))]
          : chunks;

      const codepoints =
        readChunks.length > 0
          ? convertBinaryToCodepoints(Bitstring.concat(readChunks))
          : [];

      // Nothing was read from the element, so the whole of it is what was left.
      const invalidRest =
        validLength === 0
          ? invalidBinary
          : Bitstring.fromBytes(bytes.slice(validLength));

      if (isTruncated) {
        // Incomplete: rest is the binary directly (not wrapped in list)
        return createIncompleteTuple(codepoints, invalidRest);
      }

      // Error: the rest is what was left to read - the bytes that broke and
      // everything after them, the way an invalid code point's rest is built.
      const restList = Type.list([invalidRest, ...remainingElems]);

      return createErrorTuple(codepoints, restList);
    };

    // Handles invalid code points from list input. Returns error tuple.
    // The invalid code point and any remaining data is wrapped in a list.
    const handleInvalidCodepoint = (
      chunks,
      invalidCodepoint,
      remainingElems,
    ) => {
      const codepoints =
        chunks.length > 0
          ? convertBinaryToCodepoints(Bitstring.concat(chunks))
          : [];

      // Build the rest list with invalid code point and remaining elements
      const restElems = [invalidCodepoint, ...remainingElems];
      const restList = Type.list(restElems);

      return createErrorTuple(codepoints, restList);
    };

    // Processes a single list element, validating and accumulating it.
    // Returns { type, data } object: type is 'valid', 'utf8error', 'codepointerror', or 'invalid'.
    const processElement = (elem, chunks, remainingElems) => {
      // Guard: reject invalid types
      if (!Type.isBinary(elem) && !Type.isInteger(elem)) {
        return {type: "invalid"};
      }

      // Process binary elements
      if (Type.isBinary(elem)) {
        const text = Bitstring.toText(elem);

        return text === false
          ? {
              type: "utf8error",
              data: handleInvalidUtf8FromList(chunks, elem, remainingElems),
            }
          : {type: "valid", data: elem};
      }

      // Process integer elements (guaranteed integer at this point)
      const isValidCodepoint = Bitstring.isUnicodeScalarValue(elem.value);
      if (!isValidCodepoint) {
        return {
          type: "codepointerror",
          data: handleInvalidCodepoint(chunks, elem, remainingElems),
        };
      }

      return {type: "valid", data: convertCodepointToBinary(elem)};
    };

    const raiseInvalidChardataError = () => {
      Interpreter.raiseBifError("badarg", "unicode", "characters_to_list", [
        data,
      ]);
    };

    // Main logic

    // Guard: reject non-list, non-binary input early
    const isBinary = Type.isBinary(data);
    const isList = Type.isList(data);

    if (!isBinary && !isList) {
      raiseInvalidChardataError();
    }

    // Fast path for binary input
    if (isBinary) {
      const text = Bitstring.toText(data);

      if (text === false) {
        return handleInvalidUtf8FromBinary(data);
      }

      const codepoints = convertBinaryToCodepoints(data, text);

      return Type.list(codepoints);
    }

    // List path (guaranteed to be list at this point)
    const flatInput = Erlang_Unicode["_flatten_chardata/1"](data);

    if (flatInput === null) {
      raiseInvalidChardataError();
    }

    const flatData = flatInput.data;
    const chunks = [];

    // Process elements: concatenate all valid data first, then convert to codepoints.
    for (let i = 0; i < flatData.length; ++i) {
      const remainingElems = flatData.slice(i + 1);
      const result = processElement(flatData[i], chunks, remainingElems);

      if (result.type === "utf8error" || result.type === "codepointerror") {
        return result.data;
      }

      if (result.type === "invalid") {
        raiseInvalidChardataError();
      }

      // result.type === "valid" - accumulate
      chunks.push(result.data);
    }

    // All elements valid - concatenate and convert to codepoints
    if (chunks.length === 0) {
      return Type.list([]);
    }

    const binary = Bitstring.concat(chunks);
    const codepoints = convertBinaryToCodepoints(binary);

    return Type.list(codepoints);
  });

Interpreter.defineErlangFunction("unicode_util", "_cpl", 2, (list, restList) => {
    const isCP = Erlang_Unicode_Util["_is_cp/1"];

    // [C] when is_integer(C)
    if (isCP(list)) {
      return Type.improperList([
        list,
        Erlang_Unicode_Util["_cpl_1_cont/1"](restList),
      ]);
    }

    if (Type.isList(list) && list.data.length > 0) {
      // Inline optimized index-based logic to avoid O(n²) slicing behavior
      const result = [];
      let idx = 0;

      // Fast path: collect consecutive codepoints iteratively
      while (idx < list.data.length && isCP(list.data[idx])) {
        result.push(list.data[idx]);
        idx++;
      }

      // If we collected all remaining elements as codepoints
      if (idx >= list.data.length) {
        if (result.length === 0) {
          // When we've exhausted the nested list, check if restList starts with a binary
          if (restList.data.length > 0 && Type.isBinary(restList.data[0])) {
            return restList;
          }

          return Erlang_Unicode_Util["cp/1"](restList);
        }

        // Merge collected codepoints with restList
        if (restList.data.length > 0 && Type.isBinary(restList.data[0])) {
          if (restList.data.length === 1) {
            return Type.improperList([...result, restList.data[0]]);
          }

          return Type.list([...result, ...restList.data]);
        }

        const restListResult = Erlang_Unicode_Util["cp/1"](restList);

        if (Type.isList(restListResult) && restListResult.isProper) {
          return Type.list([...result, ...restListResult.data]);
        }

        return Type.improperList([...result, restListResult]);
      }

      // If we collected some codepoints but hit a non-codepoint element
      const firstElement = list.data[idx];

      if (idx === list.data.length - 1) {
        const contResult = Erlang_Unicode_Util["_cpl_cont/2"](
          firstElement,
          restList,
        );

        if (result.length === 0) {
          return contResult;
        }

        if (Type.isList(contResult) && contResult.isProper) {
          return Type.list([...result, ...contResult.data]);
        }

        return Type.improperList([...result, contResult]);
      }

      const newRestList =
        restList.data.length === 0
          ? Type.list([Type.list(list.data.slice(idx + 1))])
          : Type.list([Type.list(list.data.slice(idx + 1)), ...restList.data]);

      const contResult = Erlang_Unicode_Util["_cpl_cont/2"](
        firstElement,
        newRestList,
      );

      if (result.length === 0) {
        return contResult;
      }

      if (Type.isList(contResult) && contResult.isProper) {
        return Type.list([...result, ...contResult.data]);
      }

      return Type.improperList([...result, contResult]);
    }

    // []
    if (Type.isList(list) && list.data.length === 0) {
      // When we've exhausted the nested list, check if there's more to process
      // If restList starts with a binary, don't extract from it - just return restList
      // Otherwise, continue processing (e.g., for nested empty lists)
      if (restList.data.length > 0 && Type.isBinary(restList.data[0])) {
        // If next element in restList is binary, return it as-is (don't extract from it)
        return restList;
      }

      // Continue processing restList for non-binary elements or nested structures
      return Erlang_Unicode_Util["cp/1"](restList);
    }

    // Binary handling: <<C/utf8, T/binary>>
    if (Type.isBinary(list)) {
      const text = Bitstring.toText(list);

      // Invalid UTF-8
      if (text === false) {
        const errorPayload = Erlang_Unicode_Util["_merge_lcr/2"](
          list,
          restList,
        );
        return Type.tuple([Type.atom("error"), errorPayload]);
      }

      if (text.length === 0) {
        return Erlang_Unicode_Util["cp/1"](restList);
      }

      const codepoint = text.codePointAt(0);
      const charLength = codepoint > 0xffff ? 2 : 1;
      const restText = text.slice(charLength);
      const restBinary = Bitstring.fromText(restText);

      // If restList is improper with single element (the tail), create improper result
      if (!restList.isProper && restList.data.length === 1) {
        return Type.improperList([
          Type.integer(codepoint),
          restBinary,
          restList.data[0],
        ]);
      }

      return Type.list([Type.integer(codepoint), restBinary, ...restList.data]);
    }

    // Non-byte-aligned bitstring
    if (Type.isBitstring(list)) {
      Interpreter.raiseFunctionClauseError("unicode_util", "cpl", 2, [
        list,
        restList,
      ]);
    }

    // Should not reach here
    Interpreter.raiseFunctionClauseError("unicode_util", "cpl", 2, [
      list,
      restList,
    ]);
  });

Interpreter.defineErlangFunction("unicode_util", "_cpl_1_cont", 1, (restList) => {
    const isCP = Erlang_Unicode_Util["_is_cp/1"];

    if (!Type.isList(restList)) {
      return restList;
    }

    if (restList.data.length === 0) {
      return restList;
    }

    const firstElement = restList.data[0];
    const tail = Type.list(restList.data.slice(1));

    // [C|T] when is_integer(C)
    if (isCP(firstElement)) {
      return Type.improperList([
        firstElement,
        Erlang_Unicode_Util["_cpl_1_cont2/1"](tail),
      ]);
    }

    // [L|T]
    return Erlang_Unicode_Util["_cpl_cont/2"](firstElement, tail);
  });

Interpreter.defineErlangFunction("unicode_util", "_cpl_1_cont2", 1, (restList) => {
    const isCP = Erlang_Unicode_Util["_is_cp/1"];

    if (!Type.isList(restList)) {
      return restList;
    }

    if (restList.data.length === 0) {
      return restList;
    }

    const firstElement = restList.data[0];
    const tail = Type.list(restList.data.slice(1));

    // [C|T] when is_integer(C)
    if (isCP(firstElement)) {
      return Type.improperList([
        firstElement,
        Erlang_Unicode_Util["_cpl_1_cont3/1"](tail),
      ]);
    }

    // [L]
    if (restList.data.length === 1) {
      return Erlang_Unicode_Util["_cpl_1_cont2/1"](firstElement);
    }

    // [L|T]
    return Erlang_Unicode_Util["_cpl_cont2/2"](firstElement, tail);
  });

Interpreter.defineErlangFunction("unicode_util", "_cpl_1_cont3", 1, (restList) => {
    const isCP = Erlang_Unicode_Util["_is_cp/1"];

    if (!Type.isList(restList)) {
      return restList;
    }

    if (restList.data.length === 0) {
      return restList;
    }

    const firstElement = restList.data[0];

    // [C|_]=T when is_integer(C)
    if (isCP(firstElement)) {
      return restList;
    }

    // [L]
    if (restList.data.length === 1) {
      return Erlang_Unicode_Util["_cpl_1_cont3/1"](firstElement);
    }

    // [L|T]
    const tail = Type.list(restList.data.slice(1));

    return Erlang_Unicode_Util["_cpl_cont3/2"](firstElement, tail);
  });

Interpreter.defineErlangFunction("unicode_util", "_cpl_cont", 2, (list, restList) => {
    if (Type.isList(list) && list.data.length > 0) {
      // Inline optimized index-based logic to avoid O(n²) slicing behavior
      const isCP = Erlang_Unicode_Util["_is_cp/1"];
      const result = [];
      let idx = 0;

      // Fast path: collect consecutive codepoints iteratively
      while (idx < list.data.length && isCP(list.data[idx])) {
        result.push(list.data[idx]);
        idx++;
      }

      // If we collected all remaining elements as codepoints
      if (idx >= list.data.length) {
        if (result.length === 0) {
          if (restList.data.length > 0 && Type.isBinary(restList.data[0])) {
            return restList;
          }

          return Erlang_Unicode_Util["cp/1"](restList);
        }

        if (restList.data.length > 0 && Type.isBinary(restList.data[0])) {
          if (restList.data.length === 1) {
            return Type.improperList([...result, restList.data[0]]);
          }

          return Type.list([...result, ...restList.data]);
        }

        const restListResult = Erlang_Unicode_Util["cp/1"](restList);

        if (Type.isList(restListResult) && restListResult.isProper) {
          return Type.list([...result, ...restListResult.data]);
        }

        return Type.improperList([...result, restListResult]);
      }

      const firstElement = list.data[idx];

      if (idx === list.data.length - 1) {
        const cplResult = Erlang_Unicode_Util["_cpl/2"](firstElement, restList);

        if (result.length === 0) {
          return cplResult;
        }

        if (Type.isList(cplResult) && cplResult.isProper) {
          return Type.list([...result, ...cplResult.data]);
        }

        return Type.improperList([...result, cplResult]);
      }

      const newRestList =
        restList.data.length === 0
          ? Type.list([Type.list(list.data.slice(idx + 1))])
          : Type.list([Type.list(list.data.slice(idx + 1)), ...restList.data]);

      const cplResult = Erlang_Unicode_Util["_cpl/2"](
        firstElement,
        newRestList,
      );

      if (result.length === 0) {
        return cplResult;
      }

      if (Type.isList(cplResult) && cplResult.isProper) {
        return Type.list([...result, ...cplResult.data]);
      }

      return Type.improperList([...result, cplResult]);
    }

    // []
    if (Type.isList(list) && list.data.length === 0) {
      return Erlang_Unicode_Util["cp/1"](restList);
    }

    // Binary handling: <<C/utf8, T/binary>>
    if (Type.isBinary(list)) {
      const text = Bitstring.toText(list);

      // Invalid UTF-8: return error tuple
      if (text === false) {
        const errorPayload = Erlang_Unicode_Util["_merge_lcr/2"](
          list,
          restList,
        );
        return Type.tuple([Type.atom("error"), errorPayload]);
      }

      if (text.length === 0) {
        return Erlang_Unicode_Util["cp/1"](restList);
      }

      // Calculate character length in UTF-16 code units (surrogate pairs are 2 units)
      const codepoint = text.codePointAt(0);
      const charLength = codepoint > 0xffff ? 2 : 1;

      const restText = text.slice(charLength);
      const restBinary = Bitstring.fromText(restText);

      // If restList is improper (has a non-list tail), propagate improper structure
      if (!restList.isProper && restList.data.length === 1) {
        // Append extracted codepoint and remaining binary, then the original tail
        return Type.improperList([
          Type.integer(codepoint),
          restBinary,
          restList.data[0],
        ]);
      }

      // Proper list: prepend extracted codepoint and remaining binary to restList elements
      return Type.list([Type.integer(codepoint), restBinary, ...restList.data]);
    }

    // Non-byte-aligned bitstring
    if (Type.isBitstring(list)) {
      Interpreter.raiseFunctionClauseError("unicode_util", "cpl_cont", 2, [
        list,
        restList,
      ]);
    }

    // Should not reach here
    Interpreter.raiseFunctionClauseError("unicode_util", "cpl_cont", 2, [
      list,
      restList,
    ]);
  });

Interpreter.defineErlangFunction("unicode_util", "_cpl_cont2", 2, (list, restList) => {
    if (Type.isList(list) && list.data.length > 0) {
      // Inline optimized index-based logic to avoid O(n²) slicing behavior
      const isCP = Erlang_Unicode_Util["_is_cp/1"];
      const result = [];
      let idx = 0;

      // Fast path: collect consecutive codepoints iteratively
      while (idx < list.data.length && isCP(list.data[idx])) {
        result.push(list.data[idx]);
        idx++;
      }

      if (idx >= list.data.length) {
        if (result.length === 0) {
          return restList;
        }

        // For improper list continuation, chain codepoints
        let current = restList;
        for (let i = result.length - 1; i >= 0; i--) {
          current = Type.improperList([result[i], current]);
        }

        return current;
      }

      const firstElement = list.data[idx];

      if (idx === list.data.length - 1) {
        const cont2Result = Erlang_Unicode_Util["_cpl_1_cont2/1"](
          Type.list([firstElement, ...restList.data]),
        );

        if (result.length === 0) {
          return cont2Result;
        }

        let current = cont2Result;

        for (let i = result.length - 1; i >= 0; i--) {
          current = Type.improperList([result[i], current]);
        }

        return current;
      }

      const newRestList =
        restList.data.length === 0
          ? Type.list([Type.list(list.data.slice(idx + 1))])
          : Type.list([Type.list(list.data.slice(idx + 1)), ...restList.data]);

      const cont2Result = Erlang_Unicode_Util["_cpl_cont2/2"](
        firstElement,
        newRestList,
      );

      if (result.length === 0) {
        return cont2Result;
      }

      let current = cont2Result;

      for (let i = result.length - 1; i >= 0; i--) {
        current = Type.improperList([result[i], current]);
      }

      return current;
    }

    return restList;
  });

Interpreter.defineErlangFunction("unicode_util", "_cpl_cont3", 2, (list, restList) => {
    if (Type.isList(list) && list.data.length > 0) {
      // Inline optimized index-based logic to avoid O(n²) slicing behavior
      const isCP = Erlang_Unicode_Util["_is_cp/1"];
      const result = [];
      let idx = 0;

      // Fast path: collect consecutive codepoints iteratively
      while (idx < list.data.length && isCP(list.data[idx])) {
        result.push(list.data[idx]);
        idx++;
      }

      if (idx >= list.data.length) {
        if (result.length === 0) {
          return restList;
        }

        // For improper list continuation, chain codepoints
        let current = restList;
        for (let i = result.length - 1; i >= 0; i--) {
          current = Type.improperList([result[i], current]);
        }

        return current;
      }

      const firstElement = list.data[idx];

      if (idx === list.data.length - 1) {
        const cont3Result = Erlang_Unicode_Util["_cpl_1_cont3/1"](
          Type.list([firstElement, ...restList.data]),
        );

        if (result.length === 0) {
          return cont3Result;
        }

        let current = cont3Result;

        for (let i = result.length - 1; i >= 0; i--) {
          current = Type.improperList([result[i], current]);
        }

        return current;
      }

      const newRestList =
        restList.data.length === 0
          ? Type.list([Type.list(list.data.slice(idx + 1))])
          : Type.list([Type.list(list.data.slice(idx + 1)), ...restList.data]);

      const cont3Result = Erlang_Unicode_Util["_cpl_cont3/2"](
        firstElement,
        newRestList,
      );

      if (result.length === 0) {
        return cont3Result;
      }

      let current = cont3Result;

      for (let i = result.length - 1; i >= 0; i--) {
        current = Type.improperList([result[i], current]);
      }

      return current;
    }

    return restList;
  });

Interpreter.defineErlangFunction("unicode_util", "_is_cp", 1, (value) => {
    if (!Type.isInteger(value)) {
      return false;
    }

    const codepoint = Number(value.value);

    return codepoint >= 0 && codepoint <= 0x10ffff;
  });

Interpreter.defineErlangFunction("unicode_util", "_merge_lcr", 2, (binary, restList) => {
    // Combine an invalid binary with a rest list for error reporting
    // If rest list is empty or not a list, return just the binary
    if (!Type.isList(restList) || restList.data.length === 0) {
      return binary;
    }

    // Prepend binary to rest list elements
    return Type.list([binary, ...restList.data]);
  });

Interpreter.defineErlangFunction("unicode_util", "cp", 1, (string) => {
    const isCP = Erlang_Unicode_Util["_is_cp/1"];

    // [C|_] when is_integer(C)
    if (Type.isList(string) && string.data.length > 0 && isCP(string.data[0])) {
      return string;
    }

    // [List]
    if (Type.isList(string) && string.data.length === 1) {
      return Erlang_Unicode_Util["cp/1"](string.data[0]);
    }

    // [List|R] - proper list with multiple elements
    if (Type.isList(string) && string.isProper && string.data.length > 1) {
      const restList = Type.list(string.data.slice(1));
      return Erlang_Unicode_Util["_cpl/2"](string.data[0], restList);
    }

    // [List|Tail] - improper list (tail is not a list)
    if (Type.isList(string) && !string.isProper && string.data.length === 2) {
      const head = string.data[0];
      const tail = string.data[1];

      // If head is empty list, continue with tail
      if (Type.isList(head) && head.data.length === 0) {
        return Erlang_Unicode_Util["cp/1"](tail);
      }

      // If head is empty binary, continue with tail
      if (Type.isBinary(head) && Bitstring.isEmpty(head)) {
        return Erlang_Unicode_Util["cp/1"](tail);
      }

      // If tail is empty list, treat as proper list [head]
      if (Type.isList(tail) && tail.data.length === 0) {
        return Erlang_Unicode_Util["cp/1"](head);
      }

      // For other cases, process head element and combine result with tail
      // Case: head is a binary - extract first codepoint from it
      if (Type.isBinary(head)) {
        const text = Bitstring.toText(head);

        if (text === false) {
          return Type.tuple([Type.atom("error"), head]);
        }

        if (text.length === 0) {
          return Erlang_Unicode_Util["cp/1"](tail);
        }

        const codepoint = text.codePointAt(0);
        const charLength = codepoint > 0xffff ? 2 : 1;
        const restText = text.slice(charLength);
        const restBinary = Bitstring.fromText(restText);

        // Create improper list: [codepoint | [restBinary | tail]]
        return Type.improperList([Type.integer(codepoint), restBinary, tail]);
      }

      // Case: head is a list - recursively process it, then append tail
      if (Type.isList(head)) {
        const headResult = Erlang_Unicode_Util["cp/1"](head);

        // If head processing yielded empty list, continue with tail
        if (Type.isList(headResult) && headResult.data.length === 0) {
          return Erlang_Unicode_Util["cp/1"](tail);
        }

        // If head processing yielded a list (proper or improper), append tail
        if (Type.isList(headResult)) {
          return Type.improperList([...headResult.data, tail]);
        }
      }
    }

    // []
    if (Type.isList(string) && string.data.length === 0) {
      return Type.list();
    }

    // <<C/utf8, R/binary>>
    if (Type.isBinary(string)) {
      const text = Bitstring.toText(string);

      // Invalid UTF-8
      if (text === false) {
        return Type.tuple([Type.atom("error"), string]);
      }

      if (text.length === 0) {
        return Type.list();
      }

      // Calculate character length in UTF-16 code units (surrogate pairs are 2 units)
      const codepoint = text.codePointAt(0);
      const charLength = codepoint > 0xffff ? 2 : 1;

      const restText = text.slice(charLength);
      const restBinary = Bitstring.fromText(restText);

      // Return improper list: [codepoint | restBinary] - codepoint followed by remaining binary
      return Type.improperList([Type.integer(codepoint), restBinary]);
    }

    // <<>>
    if (Type.isBitstring(string) && Bitstring.isEmpty(string)) {
      return Type.list();
    }

    // All other cases: invalid input type (including non-byte-aligned bitstrings)
    Interpreter.raiseFunctionClauseError("unicode_util", "cp", 1, [string]);
  });

Interpreter.defineErlangFunction("unicode_util", "gc", 1, (arg) => {
    const segmenter = ERTS.graphemeSegmenter;

    const firstSegment = (text) => {
      const iterator = segmenter.segment(text)[Symbol.iterator]();
      const {done, value} = iterator.next();

      return done ? "" : value.segment;
    };

    const buildCluster = (codepoints) => {
      if (codepoints.length === 1) return Type.integer(codepoints[0]);

      return Type.list(codepoints.map((cp) => Type.integer(cp)));
    };

    const prependCodepointToRest = (codepoint, rest) => {
      if (Type.isBitstring(rest)) {
        const restText = Bitstring.toText(rest);

        // Handle invalid UTF-8: when restText is false, treat as boundary
        if (restText === false) {
          // Return a list [codepointBinary, invalidBinary] to preserve the boundary
          const codepointBinary = Type.bitstring(
            String.fromCodePoint(codepoint),
          );

          return Type.list([codepointBinary, rest]);
        }
        return Type.bitstring(String.fromCodePoint(codepoint) + restText);
      }

      if (!Type.isList(rest)) return rest;

      const data = [Type.integer(codepoint), ...rest.data];

      return Type.isImproperList(rest)
        ? Type.improperList(data)
        : Type.list(data);
    };

    const extractHead = (input) => {
      const cpResult = Erlang_Unicode_Util["cp/1"](input);

      if (Type.isTuple(cpResult)) return {error: cpResult};

      if (cpResult.data.length === 0) return {empty: true};

      const headCodepoint = Number(cpResult.data[0].value);

      const tail = Type.isImproperList(cpResult)
        ? cpResult.data.length === 2
          ? cpResult.data[1]
          : Type.improperList(cpResult.data.slice(1))
        : Type.list(cpResult.data.slice(1));

      return {codepoint: headCodepoint, tail};
    };

    if (Type.isBinary(arg)) {
      const text = Bitstring.toText(arg);

      if (text === false) return Type.tuple([Type.atom("error"), arg]);
      if (text.length === 0) return Type.list();

      const segment = firstSegment(text);

      const clusterCodepoints = Array.from(segment).map((char) =>
        char.codePointAt(0),
      );

      const cluster = buildCluster(clusterCodepoints);
      const restText = text.slice(segment.length);

      return Type.improperList([cluster, Type.bitstring(restText)]);
    }

    if (Type.isList(arg)) {
      if (arg.data.length === 0) return Type.list();

      // Recursively collect codepoints until we can determine the grapheme boundary.
      // We may need to look ahead one codepoint to know if the current cluster is complete
      // (e.g., to check if the next character is a combining mark).
      const collectCluster = (
        remaining,
        collectedText,
        collectedCodepoints,
      ) => {
        const {codepoint, tail, error, empty} = extractHead(remaining);

        if (error) return error;

        if (empty) {
          if (collectedCodepoints.length > 0) {
            const cluster = buildCluster(collectedCodepoints);
            return Type.list([cluster]);
          }

          return Type.list();
        }

        const newCollectedText =
          collectedText + String.fromCodePoint(codepoint);

        const newCollectedCodepoints = [...collectedCodepoints, codepoint];
        const segment = firstSegment(newCollectedText);
        const segmentLength = Array.from(segment).length;
        const consumedLength = newCollectedCodepoints.length;

        // Check if we've reached a definitive boundary
        const tailIsEmptyList = Type.isList(tail) && tail.data.length === 0;

        const tailBinaryTextForEmptyCheck = Type.isBinary(tail)
          ? Bitstring.toText(tail)
          : null;

        const tailIsEmptyBinary =
          Type.isBinary(tail) &&
          (tailBinaryTextForEmptyCheck === null ||
            tailBinaryTextForEmptyCheck === false ||
            tailBinaryTextForEmptyCheck.length === 0);

        const tailFirstBinaryText =
          Type.isList(tail) &&
          tail.data.length > 0 &&
          Type.isBinary(tail.data[0])
            ? Bitstring.toText(tail.data[0])
            : null;

        const tailStartsWithEmptyBinary =
          tailFirstBinaryText !== null &&
          (tailFirstBinaryText === false || tailFirstBinaryText.length === 0);

        // When we've consumed exactly one grapheme and the tail starts with a binary,
        // check if that binary starts with a combining character. If it starts with a
        // non-combining character (base character), we've found a boundary.
        const shouldCheckCombining =
          segmentLength === consumedLength &&
          Type.isList(tail) &&
          tail.data.length > 0 &&
          Type.isBinary(tail.data[0]);

        const tailBinaryTextRaw = shouldCheckCombining
          ? Bitstring.toText(tail.data[0])
          : null;

        const tailBinaryText =
          tailBinaryTextRaw === false ? null : tailBinaryTextRaw;

        const hasValidBinaryText =
          typeof tailBinaryText === "string" && tailBinaryText.length > 0;

        // Test if the next character combines with a base character.
        // If "a" + nextChar segments as just "a", then nextChar doesn't combine.
        const nextCharForCombiningCheck = hasValidBinaryText
          ? Array.from(tailBinaryText)[0]
          : null;

        const tailStartsWithNonCombiningChar =
          nextCharForCombiningCheck !== null &&
          firstSegment("a" + nextCharForCombiningCheck) === "a";

        const boundaryKnown =
          segmentLength < consumedLength ||
          tailIsEmptyList ||
          tailIsEmptyBinary ||
          tailStartsWithEmptyBinary ||
          tailStartsWithNonCombiningChar;

        // Keep collecting codepoints if boundary not yet determined
        if (!boundaryKnown)
          return collectCluster(tail, newCollectedText, newCollectedCodepoints);

        // We've found the boundary. Check if we overshot (consumed too many codepoints).

        const clusterLength = segmentLength;
        const extraCount = consumedLength - clusterLength;

        const adjustedTail =
          extraCount > 0
            ? prependCodepointToRest(
                newCollectedCodepoints[newCollectedCodepoints.length - 1],
                tail,
              )
            : tail;

        const clusterCodepoints = newCollectedCodepoints.slice(
          0,
          clusterLength,
        );

        const cluster = buildCluster(clusterCodepoints);

        if (Type.isBitstring(adjustedTail))
          return Type.improperList([cluster, adjustedTail]);

        if (Type.isList(adjustedTail)) {
          const tailData = adjustedTail.data;

          const singleInvalidBinary =
            tailData.length === 1 &&
            Type.isBinary(tailData[0]) &&
            Bitstring.toText(tailData[0]) === false;

          if (singleInvalidBinary)
            return Type.improperList([cluster, tailData[0]]);

          // Preserve improper list structure if adjustedTail is improper
          if (Type.isImproperList(adjustedTail)) {
            return Type.improperList([cluster, ...tailData]);
          }

          return Type.list([cluster, ...tailData]);
        }

        return Type.list([cluster]);
      };

      return collectCluster(arg, "", []);
    }

    return Erlang_Unicode_Util["cp/1"](arg);
  });

Interpreter.defineElixirFunction("Access", "get", 2, "public", [{params: (context) => [Type.variablePattern("x0_0"), Type.variablePattern("x1_1")], guards: [], body: (context) => {
return Elixir_Access["get/3"](context.vars.x0_0, context.vars.x1_1, Type.atom("nil"));
}}]);

Interpreter.defineElixirFunction("Access", "get", 3, "public", [{params: (context) => [Type.matchPattern(Type.map([[Type.atom("__struct__"), Type.variablePattern("module_0")]]), Type.variablePattern("container_1")), Type.variablePattern("key_2"), Type.variablePattern("default_3")], guards: [], body: (context) => {
globalThis.Hologram.return = Interpreter.try((context) => {
return Interpreter.callNamedFunction(context.vars.module_0, Type.atom("fetch"), Type.list([context.vars.container_1, context.vars.key_2]), context);
}, [{variable: Type.variablePattern("exception_4"), modules: [Type.atom("Elixir.UndefinedFunctionError")], body: (context) => {
Interpreter.matchOperator(Interpreter.case(context.stacktrace, [{match: Type.consPattern(Type.tuple([context.vars.module_0, Type.atom("fetch"), Type.list([context.vars.container_1, context.vars.key_2]), Type.matchPlaceholder()]), Type.matchPlaceholder()), guards: [], body: (context) => {
Interpreter.matchOperator(Type.bitstring([Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.module_0), {type: "binary"}), Type.bitstringSegment(Type.string(" does not implement the Access behaviour\n\nYou can use the \"struct.field\" syntax to access struct fields. You can also use Access.key!/1 to access struct fields dynamically inside get_in/put_in/update_in"), {type: "binary"})]), Type.variablePattern("reason_5"), context);
Interpreter.updateVarsToMatchedValues(context);
return Elixir_Map["merge/2"](context.vars.exception_4, Type.map([[Type.atom("reason"), context.vars.reason_5]]));
}}, {match: Type.matchPlaceholder(), guards: [], body: (context) => {
return context.vars.exception_4;
}}], context), Type.variablePattern("exception_6"), context);
Interpreter.updateVarsToMatchedValues(context);
return Erlang["raise/3"](Type.atom("error"), Elixir_Kernel_Utils["raise/1"](context.vars.exception_6), context.stacktrace);
}}], [], [{match: Type.tuple([Type.atom("ok"), Type.variablePattern("value_7")]), guards: [], body: (context) => {
return context.vars.value_7;
}}, {match: Type.atom("error"), guards: [], body: (context) => {
return context.vars.default_3;
}}], null, context);
Interpreter.updateVarsToMatchedValues(context);
return globalThis.Hologram.return;
}}, {params: (context) => [Type.variablePattern("map_0"), Type.variablePattern("key_1"), Type.variablePattern("default_2")], guards: [(context) => Erlang["is_map/1"](context.vars.map_0)], body: (context) => {
return Interpreter.case(context.vars.map_0, [{match: Type.map([[context.vars.key_1, Type.variablePattern("value_3")]]), guards: [], body: (context) => {
return context.vars.value_3;
}}, {match: Type.matchPlaceholder(), guards: [], body: (context) => {
return context.vars.default_2;
}}], context);
}}, {params: (context) => [Type.variablePattern("list_0"), Type.variablePattern("key_1"), Type.variablePattern("default_2")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_list/1"](context.vars.list_0)) ? Erlang["is_atom/1"](context.vars.key_1) : Type.boolean(false))], body: (context) => {
return Interpreter.case(Erlang_Lists["keyfind/3"](context.vars.key_1, Type.integer(1n), context.vars.list_0), [{match: Type.tuple([Type.matchPlaceholder(), Type.variablePattern("value_3")]), guards: [], body: (context) => {
return context.vars.value_3;
}}, {match: Type.atom("false"), guards: [], body: (context) => {
return context.vars.default_2;
}}], context);
}}, {params: (context) => [Type.variablePattern("list_0"), Type.variablePattern("key_1"), Type.matchPlaceholder()], guards: [(context) => (Interpreter.toBoolean(Erlang["is_list/1"](context.vars.list_0)) ? Erlang["is_integer/1"](context.vars.key_1) : Type.boolean(false))], body: (context) => {
return Erlang["error/1"](Elixir_ArgumentError["exception/1"](Type.bitstring([Type.bitstringSegment(Type.string("the Access module does not support accessing lists by index, got: "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.key_1), {type: "binary"}), Type.bitstringSegment(Type.string("\n\nAccessing a list by index is typically discouraged in Elixir, instead we prefer to use the Enum module to manipulate lists as a whole. If you really must access a list element by index, you can use Enum.at/2 or the functions in the List module"), {type: "binary"})])));
}}, {params: (context) => [Type.variablePattern("list_0"), Type.variablePattern("key_1"), Type.matchPlaceholder()], guards: [(context) => Erlang["is_list/1"](context.vars.list_0)], body: (context) => {
return Erlang["error/1"](Elixir_ArgumentError["exception/1"](Type.bitstring([Type.bitstringSegment(Type.string("the Access module supports only keyword lists (with atom keys), got: "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.key_1), {type: "binary"}), Type.bitstringSegment(Type.string("\n\nIf you want to search lists of tuples, use List.keyfind/3"), {type: "binary"})])));
}}, {params: (context) => [Type.atom("nil"), Type.matchPlaceholder(), Type.variablePattern("default_1")], guards: [], body: (context) => {
return context.vars.default_1;
}}]);

Interpreter.defineElixirFunction("ArgumentError", "__struct__", 0, "public", [{params: (context) => [], guards: [], body: (context) => {
return Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.ArgumentError")], [Type.atom("message"), Type.bitstring("argument error")]]);
}}]);

Interpreter.defineElixirFunction("ArgumentError", "__struct__", 1, "public", [{params: (context) => [Type.variablePattern("kv_0")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.kv_0, Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.ArgumentError")], [Type.atom("message"), Type.bitstring("argument error")]]), Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("val_2")]), Type.variablePattern("map_3")], guards: [], body: (context) => {
return Elixir_Map["merge/2"](context.vars.map_3, Type.map([[context.vars.key_1, context.vars.val_2]]));
}}], context, "-__struct__/1-fun-0-"));
}}]);

Interpreter.defineElixirFunction("ArgumentError", "exception", 1, "public", [{params: (context) => [Type.variablePattern("msg_0")], guards: [(context) => Erlang["is_binary/1"](context.vars.msg_0)], body: (context) => {
return Elixir_ArgumentError["exception/1"](Type.list([Type.tuple([Type.atom("message"), context.vars.msg_0])]));
}}, {params: (context) => [Type.variablePattern("args_0")], guards: [(context) => Erlang["is_list/1"](context.vars.args_0)], body: (context) => {
return Elixir_Kernel["struct!/2"](Type.atom("Elixir.ArgumentError"), context.vars.args_0);
}}]);

Interpreter.defineElixirFunction("ArgumentError", "message", 1, "public", [{params: (context) => [Type.variablePattern("exception_0")], guards: [], body: (context) => {
return Interpreter.dotOperator(context.vars.exception_0, Type.atom("message"));
}}]);

Interpreter.defineElixirFunction("ArithmeticError", "__struct__", 0, "public", [{params: (context) => [], guards: [], body: (context) => {
return Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.ArithmeticError")], [Type.atom("message"), Type.bitstring("bad argument in arithmetic expression")]]);
}}]);

Interpreter.defineElixirFunction("ArithmeticError", "__struct__", 1, "public", [{params: (context) => [Type.variablePattern("kv_0")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.kv_0, Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.ArithmeticError")], [Type.atom("message"), Type.bitstring("bad argument in arithmetic expression")]]), Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("val_2")]), Type.variablePattern("map_3")], guards: [], body: (context) => {
return Elixir_Map["merge/2"](context.vars.map_3, Type.map([[context.vars.key_1, context.vars.val_2]]));
}}], context, "-__struct__/1-fun-0-"));
}}]);

{
const s0 = Interpreter.literalSet(() => [Type.atom("+"), Type.atom("-")]);
const s1 = Interpreter.literalSet(() => [Type.atom("+"), Type.atom("-"), Type.atom("*"), Type.atom("/")]);
const s2 = Interpreter.literalSet(() => [Type.atom("div"), Type.atom("rem")]);
const s3 = Interpreter.literalSet(() => [Type.atom("band"), Type.atom("bor"), Type.atom("bxor"), Type.atom("bsl"), Type.atom("bsr")]);
Interpreter.defineElixirFunction("ArithmeticError", "blame", 2, "public", [{params: (context) => [Type.matchPattern(Type.map([[Type.atom("message"), Type.variablePattern("message_0")]]), Type.variablePattern("exception_1")), Type.matchPattern(Type.consPattern(Type.tuple([Type.atom("erlang"), Type.variablePattern("fun_2"), Type.variablePattern("args_3"), Type.matchPlaceholder()]), Type.matchPlaceholder()), Type.variablePattern("stacktrace_4"))], guards: [], body: (context) => {
Interpreter.matchOperator(Type.bitstring([Type.bitstringSegment(context.vars.message_0, {type: "binary"}), Type.bitstringSegment(Interpreter.case(Type.tuple([context.vars.fun_2, context.vars.args_3]), [{match: Type.tuple([Type.variablePattern("op_5"), Type.list([Type.variablePattern("a_6")])]), guards: [(context) => Type.boolean(s0.has(context.vars.op_5))], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string(": "), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.op_5), {type: "binary"}), Type.bitstringSegment(Type.string("("), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.a_6), {type: "binary"}), Type.bitstringSegment(Type.string(")"), {type: "binary"})]);
}}, {match: Type.tuple([Type.variablePattern("op_7"), Type.list([Type.variablePattern("a_8"), Type.variablePattern("b_9")])]), guards: [(context) => Type.boolean(s1.has(context.vars.op_7))], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string(": "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.a_8), {type: "binary"}), Type.bitstringSegment(Type.string(" "), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.op_7), {type: "binary"}), Type.bitstringSegment(Type.string(" "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.b_9), {type: "binary"})]);
}}, {match: Type.tuple([Type.variablePattern("fun_10"), Type.list([Type.variablePattern("a_11"), Type.variablePattern("b_12")])]), guards: [(context) => Type.boolean(s2.has(context.vars.fun_10))], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string(": "), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.fun_10), {type: "binary"}), Type.bitstringSegment(Type.string("("), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.a_11), {type: "binary"}), Type.bitstringSegment(Type.string(", "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.b_12), {type: "binary"}), Type.bitstringSegment(Type.string(")"), {type: "binary"})]);
}}, {match: Type.tuple([Type.variablePattern("fun_13"), Type.list([Type.variablePattern("a_14"), Type.variablePattern("b_15")])]), guards: [(context) => Type.boolean(s3.has(context.vars.fun_13))], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string(": Bitwise."), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.fun_13), {type: "binary"}), Type.bitstringSegment(Type.string("("), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.a_14), {type: "binary"}), Type.bitstringSegment(Type.string(", "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.b_15), {type: "binary"}), Type.bitstringSegment(Type.string(")"), {type: "binary"})]);
}}, {match: Type.tuple([Type.atom("bnot"), Type.list([Type.variablePattern("a_16")])]), guards: [], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string(": Bitwise.bnot("), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.a_16), {type: "binary"}), Type.bitstringSegment(Type.string(")"), {type: "binary"})]);
}}, {match: Type.matchPlaceholder(), guards: [], body: (context) => {
return Type.bitstring("");
}}], context), {type: "binary"})]), Type.variablePattern("message_17"), context);
Interpreter.updateVarsToMatchedValues(context);
return Type.tuple([Elixir_Map["merge/2"](context.vars.exception_1, Type.map([[Type.atom("message"), context.vars.message_17]])), context.vars.stacktrace_4]);
}}, {params: (context) => [Type.variablePattern("exception_0"), Type.variablePattern("stacktrace_1")], guards: [], body: (context) => {
return Type.tuple([context.vars.exception_0, context.vars.stacktrace_1]);
}}]);
}

Interpreter.defineElixirFunction("ArithmeticError", "message", 1, "public", [{params: (context) => [Type.variablePattern("exception_0")], guards: [], body: (context) => {
return Interpreter.dotOperator(context.vars.exception_0, Type.atom("message"));
}}]);

Interpreter.defineElixirFunction("BadArityError", "__struct__", 0, "public", [{params: (context) => [], guards: [], body: (context) => {
return Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.BadArityError")], [Type.atom("args"), Type.atom("nil")], [Type.atom("function"), Type.atom("nil")]]);
}}]);

Interpreter.defineElixirFunction("BadArityError", "__struct__", 1, "public", [{params: (context) => [Type.variablePattern("kv_0")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.kv_0, Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.BadArityError")], [Type.atom("args"), Type.atom("nil")], [Type.atom("function"), Type.atom("nil")]]), Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("val_2")]), Type.variablePattern("map_3")], guards: [], body: (context) => {
return Elixir_Map["merge/2"](context.vars.map_3, Type.map([[context.vars.key_1, context.vars.val_2]]));
}}], context, "-__struct__/1-fun-0-"));
}}]);

Interpreter.defineElixirFunction("BadArityError", "count", 2, "private", [{params: (context) => [Type.integer(0n), Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.bitstring("no arguments");
}}, {params: (context) => [Type.integer(1n), Type.variablePattern("insp_0")], guards: [], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string("1 argument ("), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.insp_0), {type: "binary"}), Type.bitstringSegment(Type.string(")"), {type: "binary"})]);
}}, {params: (context) => [Type.variablePattern("x_0"), Type.variablePattern("insp_1")], guards: [], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.x_0), {type: "binary"}), Type.bitstringSegment(Type.string(" arguments ("), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.insp_1), {type: "binary"}), Type.bitstringSegment(Type.string(")"), {type: "binary"})]);
}}]);

Interpreter.defineElixirFunction("BadArityError", "message", 1, "public", [{params: (context) => [Type.variablePattern("exception_0")], guards: [], body: (context) => {
Interpreter.matchOperator(Interpreter.dotOperator(context.vars.exception_0, Type.atom("function")), Type.variablePattern("fun_1"), context);
Interpreter.updateVarsToMatchedValues(context);
Interpreter.matchOperator(Interpreter.dotOperator(context.vars.exception_0, Type.atom("args")), Type.variablePattern("args_2"), context);
Interpreter.updateVarsToMatchedValues(context);
Interpreter.matchOperator(Elixir_Enum["map_join/3"](context.vars.args_2, Type.bitstring(", "), Type.functionCapture("Kernel", "inspect", 1, [{params: (context) => [Type.variablePattern("$1")], guards: [], body: (context) => {
return Elixir_Kernel["inspect/1"](context.vars["$1"]);
}}], context)), Type.variablePattern("insp_3"), context);
Interpreter.updateVarsToMatchedValues(context);
Interpreter.matchOperator(Erlang["fun_info/2"](context.vars.fun_1, Type.atom("arity")), Type.tuple([Type.atom("arity"), Type.variablePattern("arity_4")]), context);
Interpreter.updateVarsToMatchedValues(context);
return Type.bitstring([Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.fun_1), {type: "binary"}), Type.bitstringSegment(Type.string(" with arity "), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.arity_4), {type: "binary"}), Type.bitstringSegment(Type.string(" called with "), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](Elixir_BadArityError["count/2"](Erlang["length/1"](context.vars.args_2), context.vars.insp_3)), {type: "binary"})]);
}}]);

Interpreter.defineElixirFunction("BadBooleanError", "__struct__", 0, "public", [{params: (context) => [], guards: [], body: (context) => {
return Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.BadBooleanError")], [Type.atom("operator"), Type.atom("nil")], [Type.atom("term"), Type.atom("nil")]]);
}}]);

Interpreter.defineElixirFunction("BadBooleanError", "__struct__", 1, "public", [{params: (context) => [Type.variablePattern("kv_0")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.kv_0, Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.BadBooleanError")], [Type.atom("operator"), Type.atom("nil")], [Type.atom("term"), Type.atom("nil")]]), Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("val_2")]), Type.variablePattern("map_3")], guards: [], body: (context) => {
return Elixir_Map["merge/2"](context.vars.map_3, Type.map([[context.vars.key_1, context.vars.val_2]]));
}}], context, "-__struct__/1-fun-0-"));
}}]);

Interpreter.defineElixirFunction("BadBooleanError", "message", 1, "public", [{params: (context) => [Type.variablePattern("exception_0")], guards: [], body: (context) => {
return Elixir_Exception["__format_message_with_term__/2"](Type.bitstring([Type.bitstringSegment(Type.string("expected a boolean on left-side of \""), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](Interpreter.dotOperator(context.vars.exception_0, Type.atom("operator"))), {type: "binary"}), Type.bitstringSegment(Type.string("\", got:"), {type: "binary"})]), Interpreter.dotOperator(context.vars.exception_0, Type.atom("term")));
}}]);

Interpreter.defineElixirFunction("BadFunctionError", "__struct__", 0, "public", [{params: (context) => [], guards: [], body: (context) => {
return Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.BadFunctionError")], [Type.atom("term"), Type.atom("nil")]]);
}}]);

Interpreter.defineElixirFunction("BadFunctionError", "__struct__", 1, "public", [{params: (context) => [Type.variablePattern("kv_0")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.kv_0, Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.BadFunctionError")], [Type.atom("term"), Type.atom("nil")]]), Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("val_2")]), Type.variablePattern("map_3")], guards: [], body: (context) => {
return Elixir_Map["merge/2"](context.vars.map_3, Type.map([[context.vars.key_1, context.vars.val_2]]));
}}], context, "-__struct__/1-fun-0-"));
}}]);

Interpreter.defineElixirFunction("BadFunctionError", "message", 1, "public", [{params: (context) => [Type.map([[Type.atom("term"), Type.variablePattern("term_0")]])], guards: [(context) => Erlang["is_function/1"](context.vars.term_0)], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string("function "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.term_0), {type: "binary"}), Type.bitstringSegment(Type.string(" is invalid, likely because it points to an old version of the code"), {type: "binary"})]);
}}, {params: (context) => [Type.variablePattern("exception_0")], guards: [], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string("expected a function, got: "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](Interpreter.dotOperator(context.vars.exception_0, Type.atom("term"))), {type: "binary"})]);
}}]);

Interpreter.defineElixirFunction("BadMapError", "__struct__", 0, "public", [{params: (context) => [], guards: [], body: (context) => {
return Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.BadMapError")], [Type.atom("term"), Type.atom("nil")]]);
}}]);

Interpreter.defineElixirFunction("BadMapError", "__struct__", 1, "public", [{params: (context) => [Type.variablePattern("kv_0")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.kv_0, Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.BadMapError")], [Type.atom("term"), Type.atom("nil")]]), Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("val_2")]), Type.variablePattern("map_3")], guards: [], body: (context) => {
return Elixir_Map["merge/2"](context.vars.map_3, Type.map([[context.vars.key_1, context.vars.val_2]]));
}}], context, "-__struct__/1-fun-0-"));
}}]);

Interpreter.defineElixirFunction("BadMapError", "message", 1, "public", [{params: (context) => [Type.variablePattern("exception_0")], guards: [], body: (context) => {
return Elixir_Exception["__format_message_with_term__/2"](Type.bitstring("expected a map, got:"), Interpreter.dotOperator(context.vars.exception_0, Type.atom("term")));
}}]);

Interpreter.defineElixirFunction("BadStructError", "__struct__", 0, "public", [{params: (context) => [], guards: [], body: (context) => {
return Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.BadStructError")], [Type.atom("struct"), Type.atom("nil")], [Type.atom("term"), Type.atom("nil")]]);
}}]);

Interpreter.defineElixirFunction("BadStructError", "__struct__", 1, "public", [{params: (context) => [Type.variablePattern("kv_0")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.kv_0, Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.BadStructError")], [Type.atom("struct"), Type.atom("nil")], [Type.atom("term"), Type.atom("nil")]]), Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("val_2")]), Type.variablePattern("map_3")], guards: [], body: (context) => {
return Elixir_Map["merge/2"](context.vars.map_3, Type.map([[context.vars.key_1, context.vars.val_2]]));
}}], context, "-__struct__/1-fun-0-"));
}}]);

Interpreter.defineElixirFunction("BadStructError", "message", 1, "public", [{params: (context) => [Type.variablePattern("exception_0")], guards: [], body: (context) => {
return Elixir_Exception["__format_message_with_term__/2"](Type.bitstring([Type.bitstringSegment(Type.string("expected a struct named "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](Interpreter.dotOperator(context.vars.exception_0, Type.atom("struct"))), {type: "binary"}), Type.bitstringSegment(Type.string(", got:"), {type: "binary"})]), Interpreter.dotOperator(context.vars.exception_0, Type.atom("term")));
}}]);

Interpreter.defineElixirFunction("CaseClauseError", "__struct__", 0, "public", [{params: (context) => [], guards: [], body: (context) => {
return Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.CaseClauseError")], [Type.atom("term"), Type.atom("nil")]]);
}}]);

Interpreter.defineElixirFunction("CaseClauseError", "__struct__", 1, "public", [{params: (context) => [Type.variablePattern("kv_0")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.kv_0, Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.CaseClauseError")], [Type.atom("term"), Type.atom("nil")]]), Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("val_2")]), Type.variablePattern("map_3")], guards: [], body: (context) => {
return Elixir_Map["merge/2"](context.vars.map_3, Type.map([[context.vars.key_1, context.vars.val_2]]));
}}], context, "-__struct__/1-fun-0-"));
}}]);

Interpreter.defineElixirFunction("CaseClauseError", "message", 1, "public", [{params: (context) => [Type.variablePattern("exception_0")], guards: [], body: (context) => {
return Elixir_Exception["__format_message_with_term__/2"](Type.bitstring("no case clause matching:"), Interpreter.dotOperator(context.vars.exception_0, Type.atom("term")));
}}]);

Interpreter.defineElixirFunction("Code", "ensure_loaded?", 1, "public", [{params: (context) => [Type.variablePattern("module_0")], guards: [(context) => Erlang["is_atom/1"](context.vars.module_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_loaded/1"](context.vars.module_0), [{match: Type.tuple([Type.atom("module"), context.vars.module_0]), guards: [], body: (context) => {
return Type.atom("true");
}}, {match: Type.matchPlaceholder(), guards: [], body: (context) => {
return Type.atom("false");
}}], context);
}}]);

{
const s0 = Interpreter.literalSet(() => [Type.atom("\u{3C}-"), Type.atom("\\\\")]);
const s1 = Interpreter.literalSet(() => [Type.atom("||"), Type.atom("|||"), Type.atom("or")]);
const s2 = Interpreter.literalSet(() => [Type.atom("&&"), Type.atom("&&&"), Type.atom("and")]);
const s3 = Interpreter.literalSet(() => [Type.atom("=="), Type.atom("!="), Type.atom("=~"), Type.atom("==="), Type.atom("!==")]);
const s4 = Interpreter.literalSet(() => [Type.atom("\u{3C}"), Type.atom("\u{3C}="), Type.atom(">="), Type.atom(">")]);
const s5 = Interpreter.literalSet(() => [Type.atom("|>"), Type.atom("\u{3C}\u{3C}\u{3C}"), Type.atom(">>>"), Type.atom("\u{3C}~"), Type.atom("~>"), Type.atom("\u{3C}\u{3C}~"), Type.atom("~>>"), Type.atom("\u{3C}~>"), Type.atom("\u{3C}|>")]);
const s6 = Interpreter.literalSet(() => [Type.atom("++"), Type.atom("--"), Type.atom(".."), Type.atom("\u{3C}>"), Type.atom("+++"), Type.atom("---")]);
const s7 = Interpreter.literalSet(() => [Type.atom("+"), Type.atom("-")]);
const s8 = Interpreter.literalSet(() => [Type.atom("*"), Type.atom("/")]);
Interpreter.defineElixirFunction("Code.Identifier", "binary_op", 1, "public", [{params: (context) => [Type.variablePattern("op_0")], guards: [], body: (context) => {
return Interpreter.cond([{condition: (context) => Type.boolean(s0.has(context.vars.op_0)), body: (context) => {
return Type.tuple([Type.atom("left"), Type.integer(40n)]);
}}, {condition: (context) => Erlang["=:=/2"](context.vars.op_0, Type.atom("when")), body: (context) => {
return Type.tuple([Type.atom("right"), Type.integer(50n)]);
}}, {condition: (context) => Erlang["=:=/2"](context.vars.op_0, Type.atom("::")), body: (context) => {
return Type.tuple([Type.atom("right"), Type.integer(60n)]);
}}, {condition: (context) => Erlang["=:=/2"](context.vars.op_0, Type.atom("|")), body: (context) => {
return Type.tuple([Type.atom("right"), Type.integer(70n)]);
}}, {condition: (context) => Erlang["=:=/2"](context.vars.op_0, Type.atom("=")), body: (context) => {
return Type.tuple([Type.atom("right"), Type.integer(100n)]);
}}, {condition: (context) => Type.boolean(s1.has(context.vars.op_0)), body: (context) => {
return Type.tuple([Type.atom("left"), Type.integer(120n)]);
}}, {condition: (context) => Type.boolean(s2.has(context.vars.op_0)), body: (context) => {
return Type.tuple([Type.atom("left"), Type.integer(130n)]);
}}, {condition: (context) => Type.boolean(s3.has(context.vars.op_0)), body: (context) => {
return Type.tuple([Type.atom("left"), Type.integer(140n)]);
}}, {condition: (context) => Type.boolean(s4.has(context.vars.op_0)), body: (context) => {
return Type.tuple([Type.atom("left"), Type.integer(150n)]);
}}, {condition: (context) => Type.boolean(s5.has(context.vars.op_0)), body: (context) => {
return Type.tuple([Type.atom("left"), Type.integer(160n)]);
}}, {condition: (context) => Erlang["=:=/2"](context.vars.op_0, Type.atom("in")), body: (context) => {
return Type.tuple([Type.atom("left"), Type.integer(170n)]);
}}, {condition: (context) => Erlang["=:=/2"](context.vars.op_0, Type.atom("^^^")), body: (context) => {
return Type.tuple([Type.atom("left"), Type.integer(180n)]);
}}, {condition: (context) => Type.boolean(s6.has(context.vars.op_0)), body: (context) => {
return Type.tuple([Type.atom("right"), Type.integer(200n)]);
}}, {condition: (context) => Type.boolean(s7.has(context.vars.op_0)), body: (context) => {
return Type.tuple([Type.atom("left"), Type.integer(210n)]);
}}, {condition: (context) => Type.boolean(s8.has(context.vars.op_0)), body: (context) => {
return Type.tuple([Type.atom("left"), Type.integer(220n)]);
}}, {condition: (context) => Erlang["=:=/2"](context.vars.op_0, Type.atom("**")), body: (context) => {
return Type.tuple([Type.atom("left"), Type.integer(230n)]);
}}, {condition: (context) => Erlang["=:=/2"](context.vars.op_0, Type.atom(".")), body: (context) => {
return Type.tuple([Type.atom("left"), Type.integer(310n)]);
}}, {condition: (context) => Type.atom("true"), body: (context) => {
return Type.atom("error");
}}], context);
}}]);
}

Interpreter.defineElixirFunction("Code.Identifier", "decrement", 1, "private", [{params: (context) => [Type.atom("infinity")], guards: [], body: (context) => {
return Type.atom("infinity");
}}, {params: (context) => [Type.variablePattern("counter_0")], guards: [], body: (context) => {
return Erlang["-/2"](context.vars.counter_0, Type.integer(1n));
}}]);

Interpreter.defineElixirFunction("Code.Identifier", "escape", 2, "public", [{params: (context) => [Type.variablePattern("x0_0"), Type.variablePattern("x1_1")], guards: [], body: (context) => {
return Elixir_Code_Identifier["escape/4"](context.vars.x0_0, context.vars.x1_1, Type.atom("infinity"), Type.functionCapture("Code.Identifier", "escape_map", 1, [{params: (context) => [Type.variablePattern("$1")], guards: [], body: (context) => {
return Elixir_Code_Identifier["escape_map/1"](context.vars["$1"]);
}}], context));
}}]);

Interpreter.defineElixirFunction("Code.Identifier", "escape", 4, "public", [{params: (context) => [Type.variablePattern("binary_0"), Type.variablePattern("char_1"), Type.variablePattern("limit_2"), Type.variablePattern("fun_3")], guards: [(context) => ((Interpreter.toBoolean(Erlang["is_binary/1"](context.vars.binary_0)) && ((Interpreter.toBoolean(Erlang["is_integer/1"](context.vars.char_1)) && Interpreter.toBoolean(Erlang[">=/2"](context.vars.char_1, Type.integer(0n))) && Interpreter.toBoolean(Erlang["=</2"](context.vars.char_1, Type.integer(1114111n)))) || Interpreter.toBoolean(Erlang["==/2"](context.vars.char_1, Type.atom("nil")))) && Interpreter.toBoolean(Erlang["==/2"](context.vars.limit_2, Type.atom("infinity")))) ? Type.boolean(true) : (Interpreter.toBoolean(Erlang["is_integer/1"](context.vars.limit_2)) ? Erlang[">=/2"](context.vars.limit_2, Type.integer(0n)) : Type.boolean(false)))], body: (context) => {
return Elixir_Code_Identifier["escape/5"](context.vars.binary_0, context.vars.char_1, context.vars.limit_2, Type.bitstring([]), context.vars.fun_3);
}}]);

{
const s0 = Interpreter.literalSet(() => [Type.atom("false"), Type.atom("nil")]);
Interpreter.defineElixirFunction("Code.Identifier", "escape", 5, "private", [{params: (context) => [Type.matchPattern(Type.bitstringPattern([Type.bitstringSegment(Type.matchPlaceholder(), {type: "integer"}), Type.bitstringSegment(Type.matchPlaceholder(), {type: "binary"})]), Type.variablePattern("binary_0")), Type.matchPlaceholder(), Type.integer(0n), Type.variablePattern("acc_2"), Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.tuple([context.vars.acc_2, context.vars.binary_0]);
}}, {params: (context) => [Type.bitstringPattern([Type.bitstringSegment(Type.variablePattern("char_0"), {type: "integer"}), Type.bitstringSegment(Type.variablePattern("t_1"), {type: "binary"})]), Type.variablePattern("char_0"), Type.variablePattern("count_2"), Type.variablePattern("acc_3"), Type.variablePattern("fun_4")], guards: [], body: (context) => {
return Elixir_Code_Identifier["escape/5"](context.vars.t_1, context.vars.char_0, Elixir_Code_Identifier["decrement/1"](context.vars.count_2), Type.bitstring([Type.bitstringSegment(context.vars.acc_3, {type: "binary"}), Type.bitstringSegment(Type.integer(92n), {type: "integer"}), Type.bitstringSegment(context.vars.char_0, {type: "integer"})]), context.vars.fun_4);
}}, {params: (context) => [Type.bitstringPattern([Type.bitstringSegment(Type.integer(35n), {type: "integer"}), Type.bitstringSegment(Type.integer(123n), {type: "integer"}), Type.bitstringSegment(Type.variablePattern("t_0"), {type: "binary"})]), Type.variablePattern("char_1"), Type.variablePattern("count_2"), Type.variablePattern("acc_3"), Type.variablePattern("fun_4")], guards: [], body: (context) => {
return Elixir_Code_Identifier["escape/5"](context.vars.t_0, context.vars.char_1, Elixir_Code_Identifier["decrement/1"](context.vars.count_2), Type.bitstring([Type.bitstringSegment(context.vars.acc_3, {type: "binary"}), Type.bitstringSegment(Type.integer(92n), {type: "integer"}), Type.bitstringSegment(Type.integer(35n), {type: "integer"}), Type.bitstringSegment(Type.integer(123n), {type: "integer"})]), context.vars.fun_4);
}}, {params: (context) => [Type.bitstringPattern([Type.bitstringSegment(Type.variablePattern("h_0"), {type: "utf8"}), Type.bitstringSegment(Type.variablePattern("t_1"), {type: "binary"})]), Type.variablePattern("char_2"), Type.variablePattern("count_3"), Type.variablePattern("acc_4"), Type.variablePattern("fun_5")], guards: [], body: (context) => {
globalThis.Hologram.return = Interpreter.case(Interpreter.matchOperator(Interpreter.callAnonymousFunction(context.vars.fun_5, [context.vars.h_0]), Type.variablePattern("value_6"), context), [{match: Type.variablePattern("x_7"), guards: [(context) => Type.boolean(s0.has(context.vars.x_7))], body: (context) => {
return Elixir_Code_Identifier["escape/5"](context.vars.t_1, context.vars.char_2, Elixir_Code_Identifier["decrement/1"](context.vars.count_3), Elixir_Code_Identifier["escape_char/2"](context.vars.h_0, context.vars.acc_4), context.vars.fun_5);
}}, {match: Type.matchPlaceholder(), guards: [], body: (context) => {
Interpreter.matchOperator(Erlang["iolist_to_binary/1"](context.vars.value_6), Type.variablePattern("value_8"), context);
Interpreter.updateVarsToMatchedValues(context);
return Elixir_Code_Identifier["escape/5"](context.vars.t_1, context.vars.char_2, Elixir_Code_Identifier["decrement/1"](context.vars.count_3), Type.bitstring([Type.bitstringSegment(context.vars.acc_4, {type: "binary"}), Type.bitstringSegment(context.vars.value_8, {type: "binary"})]), context.vars.fun_5);
}}], context);
Interpreter.updateVarsToMatchedValues(context);
return globalThis.Hologram.return;
}}, {params: (context) => [Type.bitstringPattern([Type.bitstringSegment(Type.variablePattern("a_0"), {size: Type.integer(4n), type: "integer"}), Type.bitstringSegment(Type.variablePattern("b_1"), {size: Type.integer(4n), type: "integer"}), Type.bitstringSegment(Type.variablePattern("t_2"), {type: "binary"})]), Type.variablePattern("char_3"), Type.variablePattern("count_4"), Type.variablePattern("acc_5"), Type.variablePattern("fun_6")], guards: [], body: (context) => {
return Elixir_Code_Identifier["escape/5"](context.vars.t_2, context.vars.char_3, Elixir_Code_Identifier["decrement/1"](context.vars.count_4), Type.bitstring([Type.bitstringSegment(context.vars.acc_5, {type: "binary"}), Type.bitstringSegment(Type.integer(92n), {type: "integer"}), Type.bitstringSegment(Type.integer(120n), {type: "integer"}), Type.bitstringSegment(Elixir_Code_Identifier["to_hex/1"](context.vars.a_0), {type: "integer"}), Type.bitstringSegment(Elixir_Code_Identifier["to_hex/1"](context.vars.b_1), {type: "integer"})]), context.vars.fun_6);
}}, {params: (context) => [Type.bitstringPattern([]), Type.matchPlaceholder(), Type.matchPlaceholder(), Type.variablePattern("acc_2"), Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.tuple([context.vars.acc_2, Type.bitstring([])]);
}}]);
}

{
const s0 = Interpreter.literalSet(() => [Type.integer(1564n), Type.integer(8206n), Type.integer(8207n)]);
const s1 = Interpreter.literalSet(() => [Type.integer(8204n), Type.integer(8205n), Type.integer(847n)]);
const s2 = Interpreter.literalSet(() => [Type.integer(160n), Type.integer(8203n), Type.integer(8288n)]);
const s3 = Interpreter.literalSet(() => [Type.integer(8232n), Type.integer(8233n)]);
Interpreter.defineElixirFunction("Code.Identifier", "escape_char", 2, "private", [{params: (context) => [Type.integer(0n), Type.variablePattern("acc_0")], guards: [], body: (context) => {
return Type.bitstring([Type.bitstringSegment(context.vars.acc_0, {type: "binary"}), Type.bitstringSegment(Type.integer(92n), {type: "integer"}), Type.bitstringSegment(Type.integer(48n), {type: "integer"})]);
}}, {params: (context) => [Type.variablePattern("char_0"), Type.variablePattern("acc_1")], guards: [(context) => Erlang["==/2"](context.vars.char_0, Type.integer(65279n)), (context) => (Interpreter.toBoolean(Erlang["is_integer/1"](context.vars.char_0)) && Interpreter.toBoolean(Erlang[">=/2"](context.vars.char_0, Type.integer(8289n))) ? Erlang["=</2"](context.vars.char_0, Type.integer(8292n)) : Type.boolean(false)), (context) => Type.boolean(s0.has(context.vars.char_0)), (context) => (Interpreter.toBoolean(Erlang["is_integer/1"](context.vars.char_0)) && Interpreter.toBoolean(Erlang[">=/2"](context.vars.char_0, Type.integer(8234n))) ? Erlang["=</2"](context.vars.char_0, Type.integer(8238n)) : Type.boolean(false)), (context) => (Interpreter.toBoolean(Erlang["is_integer/1"](context.vars.char_0)) && Interpreter.toBoolean(Erlang[">=/2"](context.vars.char_0, Type.integer(8294n))) ? Erlang["=</2"](context.vars.char_0, Type.integer(8297n)) : Type.boolean(false)), (context) => (Interpreter.toBoolean(Erlang["is_integer/1"](context.vars.char_0)) && Interpreter.toBoolean(Erlang[">=/2"](context.vars.char_0, Type.integer(65529n))) ? Erlang["=</2"](context.vars.char_0, Type.integer(65532n)) : Type.boolean(false)), (context) => Type.boolean(s1.has(context.vars.char_0)), (context) => Type.boolean(s2.has(context.vars.char_0)), (context) => Type.boolean(s3.has(context.vars.char_0)), (context) => (Interpreter.toBoolean(Erlang["is_integer/1"](context.vars.char_0)) && Interpreter.toBoolean(Erlang[">=/2"](context.vars.char_0, Type.integer(8192n))) ? Erlang["=</2"](context.vars.char_0, Type.integer(8202n)) : Type.boolean(false)), (context) => Erlang["==/2"](context.vars.char_0, Type.integer(8287n))], body: (context) => {
Interpreter.matchOperator(Type.bitstring([Type.bitstringSegment(context.vars.char_0, {size: Type.integer(16n), type: "integer"})]), Type.bitstringPattern([Type.bitstringSegment(Type.variablePattern("a_2"), {size: Type.integer(4n), type: "integer"}), Type.bitstringSegment(Type.variablePattern("b_3"), {size: Type.integer(4n), type: "integer"}), Type.bitstringSegment(Type.variablePattern("c_4"), {size: Type.integer(4n), type: "integer"}), Type.bitstringSegment(Type.variablePattern("d_5"), {size: Type.integer(4n), type: "integer"})]), context);
Interpreter.updateVarsToMatchedValues(context);
return Type.bitstring([Type.bitstringSegment(context.vars.acc_1, {type: "binary"}), Type.bitstringSegment(Type.integer(92n), {type: "integer"}), Type.bitstringSegment(Type.integer(117n), {type: "integer"}), Type.bitstringSegment(Elixir_Code_Identifier["to_hex/1"](context.vars.a_2), {type: "integer"}), Type.bitstringSegment(Elixir_Code_Identifier["to_hex/1"](context.vars.b_3), {type: "integer"}), Type.bitstringSegment(Elixir_Code_Identifier["to_hex/1"](context.vars.c_4), {type: "integer"}), Type.bitstringSegment(Elixir_Code_Identifier["to_hex/1"](context.vars.d_5), {type: "integer"})]);
}}, {params: (context) => [Type.variablePattern("char_0"), Type.variablePattern("acc_1")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_integer/1"](context.vars.char_0)) && Interpreter.toBoolean(Erlang[">=/2"](context.vars.char_0, Type.integer(32n))) ? Erlang["=</2"](context.vars.char_0, Type.integer(126n)) : Type.boolean(false)), (context) => (Interpreter.toBoolean(Erlang["is_integer/1"](context.vars.char_0)) && Interpreter.toBoolean(Erlang[">=/2"](context.vars.char_0, Type.integer(160n))) ? Erlang["=</2"](context.vars.char_0, Type.integer(55295n)) : Type.boolean(false)), (context) => (Interpreter.toBoolean(Erlang["is_integer/1"](context.vars.char_0)) && Interpreter.toBoolean(Erlang[">=/2"](context.vars.char_0, Type.integer(57344n))) ? Erlang["=</2"](context.vars.char_0, Type.integer(65533n)) : Type.boolean(false)), (context) => (Interpreter.toBoolean(Erlang["is_integer/1"](context.vars.char_0)) && Interpreter.toBoolean(Erlang[">=/2"](context.vars.char_0, Type.integer(65536n))) ? Erlang["=</2"](context.vars.char_0, Type.integer(1114111n)) : Type.boolean(false))], body: (context) => {
return Type.bitstring([Type.bitstringSegment(context.vars.acc_1, {type: "binary"}), Type.bitstringSegment(context.vars.char_0, {type: "utf8"})]);
}}, {params: (context) => [Type.variablePattern("char_0"), Type.variablePattern("acc_1")], guards: [(context) => Erlang["</2"](context.vars.char_0, Type.integer(256n))], body: (context) => {
Interpreter.matchOperator(Type.bitstring([Type.bitstringSegment(context.vars.char_0, {size: Type.integer(8n), type: "integer"})]), Type.bitstringPattern([Type.bitstringSegment(Type.variablePattern("a_2"), {size: Type.integer(4n), type: "integer"}), Type.bitstringSegment(Type.variablePattern("b_3"), {size: Type.integer(4n), type: "integer"})]), context);
Interpreter.updateVarsToMatchedValues(context);
return Type.bitstring([Type.bitstringSegment(context.vars.acc_1, {type: "binary"}), Type.bitstringSegment(Type.integer(92n), {type: "integer"}), Type.bitstringSegment(Type.integer(120n), {type: "integer"}), Type.bitstringSegment(Elixir_Code_Identifier["to_hex/1"](context.vars.a_2), {type: "integer"}), Type.bitstringSegment(Elixir_Code_Identifier["to_hex/1"](context.vars.b_3), {type: "integer"})]);
}}, {params: (context) => [Type.variablePattern("char_0"), Type.variablePattern("acc_1")], guards: [(context) => Erlang["</2"](context.vars.char_0, Type.integer(65536n))], body: (context) => {
Interpreter.matchOperator(Type.bitstring([Type.bitstringSegment(context.vars.char_0, {size: Type.integer(16n), type: "integer"})]), Type.bitstringPattern([Type.bitstringSegment(Type.variablePattern("a_2"), {size: Type.integer(4n), type: "integer"}), Type.bitstringSegment(Type.variablePattern("b_3"), {size: Type.integer(4n), type: "integer"}), Type.bitstringSegment(Type.variablePattern("c_4"), {size: Type.integer(4n), type: "integer"}), Type.bitstringSegment(Type.variablePattern("d_5"), {size: Type.integer(4n), type: "integer"})]), context);
Interpreter.updateVarsToMatchedValues(context);
return Type.bitstring([Type.bitstringSegment(context.vars.acc_1, {type: "binary"}), Type.bitstringSegment(Type.integer(92n), {type: "integer"}), Type.bitstringSegment(Type.integer(120n), {type: "integer"}), Type.bitstringSegment(Type.integer(123n), {type: "integer"}), Type.bitstringSegment(Elixir_Code_Identifier["to_hex/1"](context.vars.a_2), {type: "integer"}), Type.bitstringSegment(Elixir_Code_Identifier["to_hex/1"](context.vars.b_3), {type: "integer"}), Type.bitstringSegment(Elixir_Code_Identifier["to_hex/1"](context.vars.c_4), {type: "integer"}), Type.bitstringSegment(Elixir_Code_Identifier["to_hex/1"](context.vars.d_5), {type: "integer"}), Type.bitstringSegment(Type.integer(125n), {type: "integer"})]);
}}, {params: (context) => [Type.variablePattern("char_0"), Type.variablePattern("acc_1")], guards: [(context) => Erlang["</2"](context.vars.char_0, Type.integer(16777216n))], body: (context) => {
Interpreter.matchOperator(Type.bitstring([Type.bitstringSegment(context.vars.char_0, {size: Type.integer(24n), type: "integer"})]), Type.bitstringPattern([Type.bitstringSegment(Type.variablePattern("a_2"), {size: Type.integer(4n), type: "integer"}), Type.bitstringSegment(Type.variablePattern("b_3"), {size: Type.integer(4n), type: "integer"}), Type.bitstringSegment(Type.variablePattern("c_4"), {size: Type.integer(4n), type: "integer"}), Type.bitstringSegment(Type.variablePattern("d_5"), {size: Type.integer(4n), type: "integer"}), Type.bitstringSegment(Type.variablePattern("e_6"), {size: Type.integer(4n), type: "integer"}), Type.bitstringSegment(Type.variablePattern("f_7"), {size: Type.integer(4n), type: "integer"})]), context);
Interpreter.updateVarsToMatchedValues(context);
return Type.bitstring([Type.bitstringSegment(context.vars.acc_1, {type: "binary"}), Type.bitstringSegment(Type.integer(92n), {type: "integer"}), Type.bitstringSegment(Type.integer(120n), {type: "integer"}), Type.bitstringSegment(Type.integer(123n), {type: "integer"}), Type.bitstringSegment(Elixir_Code_Identifier["to_hex/1"](context.vars.a_2), {type: "integer"}), Type.bitstringSegment(Elixir_Code_Identifier["to_hex/1"](context.vars.b_3), {type: "integer"}), Type.bitstringSegment(Elixir_Code_Identifier["to_hex/1"](context.vars.c_4), {type: "integer"}), Type.bitstringSegment(Elixir_Code_Identifier["to_hex/1"](context.vars.d_5), {type: "integer"}), Type.bitstringSegment(Elixir_Code_Identifier["to_hex/1"](context.vars.e_6), {type: "integer"}), Type.bitstringSegment(Elixir_Code_Identifier["to_hex/1"](context.vars.f_7), {type: "integer"}), Type.bitstringSegment(Type.integer(125n), {type: "integer"})]);
}}]);
}

Interpreter.defineElixirFunction("Code.Identifier", "escape_map", 1, "private", [{params: (context) => [Type.integer(7n)], guards: [], body: (context) => {
return Type.bitstring("\\a");
}}, {params: (context) => [Type.integer(8n)], guards: [], body: (context) => {
return Type.bitstring("\\b");
}}, {params: (context) => [Type.integer(127n)], guards: [], body: (context) => {
return Type.bitstring("\\d");
}}, {params: (context) => [Type.integer(27n)], guards: [], body: (context) => {
return Type.bitstring("\\e");
}}, {params: (context) => [Type.integer(12n)], guards: [], body: (context) => {
return Type.bitstring("\\f");
}}, {params: (context) => [Type.integer(10n)], guards: [], body: (context) => {
return Type.bitstring("\\n");
}}, {params: (context) => [Type.integer(13n)], guards: [], body: (context) => {
return Type.bitstring("\\r");
}}, {params: (context) => [Type.integer(9n)], guards: [], body: (context) => {
return Type.bitstring("\\t");
}}, {params: (context) => [Type.integer(11n)], guards: [], body: (context) => {
return Type.bitstring("\\v");
}}, {params: (context) => [Type.integer(92n)], guards: [], body: (context) => {
return Type.bitstring("\\\\");
}}, {params: (context) => [Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.atom("false");
}}]);

Interpreter.defineElixirFunction("Code.Identifier", "extract_anonymous_fun_parent", 1, "public", [{params: (context) => [Type.variablePattern("atom_0")], guards: [(context) => Erlang["is_atom/1"](context.vars.atom_0)], body: (context) => {
globalThis.Hologram.return = Interpreter.with((context) => {
return Type.tuple([Erlang["binary_to_atom/1"](Elixir_Enum["join/2"](Elixir_Enum["reverse/1"](context.vars.reversed_3), Type.bitstring("/"))), context.vars.arity_4]);
}, [{match: Type.bitstringPattern([Type.bitstringSegment(Type.string("-"), {type: "binary"}), Type.bitstringSegment(Type.variablePattern("rest_1"), {type: "binary"})]), guards: [], expression: (context) => Erlang["atom_to_binary/1"](context.vars.atom_0)}, {expression: (context) => Interpreter.matchOperator(Elixir_Enum["reverse/1"](Elixir_String["split/2"](context.vars.rest_1, Type.bitstring("/"))), Type.consPattern(Type.variablePattern("trailing_2"), Type.variablePattern("reversed_3")), context)}, {match: Type.list([Type.variablePattern("arity_4"), Type.matchPlaceholder(), Type.matchPlaceholder(), Type.bitstring("")]), guards: [], expression: (context) => Elixir_String["split/2"](context.vars.trailing_2, Type.bitstring("-"))}], [{match: Type.matchPlaceholder(), guards: [], body: (context) => {
return Type.atom("error");
}}], context);
Interpreter.updateVarsToMatchedValues(context);
return globalThis.Hologram.return;
}}]);

Interpreter.defineElixirFunction("Code.Identifier", "to_hex", 1, "private", [{params: (context) => [Type.variablePattern("c_0")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_integer/1"](context.vars.c_0)) && Interpreter.toBoolean(Erlang[">=/2"](context.vars.c_0, Type.integer(0n))) ? Erlang["=</2"](context.vars.c_0, Type.integer(9n)) : Type.boolean(false))], body: (context) => {
return Erlang["+/2"](Type.integer(48n), context.vars.c_0);
}}, {params: (context) => [Type.variablePattern("c_0")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_integer/1"](context.vars.c_0)) && Interpreter.toBoolean(Erlang[">=/2"](context.vars.c_0, Type.integer(10n))) ? Erlang["=</2"](context.vars.c_0, Type.integer(15n)) : Type.boolean(false))], body: (context) => {
return Erlang["-/2"](Erlang["+/2"](Type.integer(65n), context.vars.c_0), Type.integer(10n));
}}]);

{
const s0 = Interpreter.literalSet(() => [Type.atom("&"), Type.atom("...")]);
const s1 = Interpreter.literalSet(() => [Type.atom("!"), Type.atom("^"), Type.atom("not"), Type.atom("+"), Type.atom("-"), Type.atom("~~~")]);
Interpreter.defineElixirFunction("Code.Identifier", "unary_op", 1, "public", [{params: (context) => [Type.variablePattern("op_0")], guards: [], body: (context) => {
return Interpreter.cond([{condition: (context) => Type.boolean(s0.has(context.vars.op_0)), body: (context) => {
return Type.tuple([Type.atom("non_associative"), Type.integer(90n)]);
}}, {condition: (context) => Type.boolean(s1.has(context.vars.op_0)), body: (context) => {
return Type.tuple([Type.atom("non_associative"), Type.integer(300n)]);
}}, {condition: (context) => Erlang["=:=/2"](context.vars.op_0, Type.atom("@")), body: (context) => {
return Type.tuple([Type.atom("non_associative"), Type.integer(320n)]);
}}, {condition: (context) => Type.atom("true"), body: (context) => {
return Type.atom("error");
}}], context);
}}]);
}

Interpreter.defineElixirFunction("Collectable", "impl_for", 1, "public", [{params: (context) => [Type.map([[Type.atom("__struct__"), Type.variablePattern("struct_0")]])], guards: [], body: (context) => {
return Elixir_Collectable["struct_impl_for/1"](context.vars.struct_0);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_tuple/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Collectable.Tuple")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_atom/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Collectable.Atom")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_list/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Collectable.List")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_map/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Collectable.Map")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_bitstring/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Collectable.BitString")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_integer/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Collectable.Integer")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_float/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Collectable.Float")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_function/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Collectable.Function")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_pid/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Collectable.PID")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_port/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Collectable.Port")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_reference/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Collectable.Reference")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.atom("nil");
}}]);

{
const s0 = Interpreter.literalSet(() => [Type.atom("false"), Type.atom("nil")]);
Interpreter.defineElixirFunction("Collectable", "impl_for!", 1, "public", [{params: (context) => [Type.variablePattern("data_0")], guards: [], body: (context) => {
return Interpreter.case(Elixir_Collectable["impl_for/1"](context.vars.data_0), [{match: Type.variablePattern("x_1"), guards: [(context) => Type.boolean(s0.has(context.vars.x_1))], body: (context) => {
return Erlang["error/1"](Elixir_Protocol_UndefinedError["exception/1"](Type.list([Type.tuple([Type.atom("protocol"), Type.atom("Elixir.Collectable")]), Type.tuple([Type.atom("value"), context.vars.data_0]), Type.tuple([Type.atom("description"), Type.bitstring("")])])));
}}, {match: Type.variablePattern("x_2"), guards: [], body: (context) => {
return context.vars.x_2;
}}], context);
}}]);
}

Interpreter.defineElixirFunction("Collectable", "into", 1, "public", [{params: (context) => [Type.variablePattern("term_0")], guards: [], body: (context) => {
return Interpreter.callNamedFunction(Elixir_Collectable["impl_for!/1"](context.vars.term_0), Type.atom("into"), Type.list([context.vars.term_0]), context);
}}]);

Interpreter.defineElixirFunction("Collectable", "struct_impl_for", 1, "private", [{params: (context) => [Type.variablePattern("struct_0")], guards: [], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Elixir_Protocol["__concat__/2"](Type.atom("Elixir.Collectable"), context.vars.struct_0)), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}]);

Interpreter.defineElixirFunction("Collectable.BitString", "__impl__", 1, "public", [{params: (context) => [Type.atom("for")], guards: [], body: (context) => {
return Type.atom("Elixir.BitString");
}}, {params: (context) => [Type.atom("protocol")], guards: [], body: (context) => {
return Type.atom("Elixir.Collectable");
}}]);

Interpreter.defineElixirFunction("Collectable.BitString", "into", 1, "public", [{params: (context) => [Type.variablePattern("binary_0")], guards: [(context) => Erlang["is_binary/1"](context.vars.binary_0)], body: (context) => {
Interpreter.matchOperator(Type.anonymousFunction(2, [{params: (context) => [Type.variablePattern("acc_1"), Type.tuple([Type.atom("cont"), Type.variablePattern("x_2")])], guards: [(context) => (Interpreter.toBoolean(Erlang["is_binary/1"](context.vars.x_2)) ? Erlang["is_list/1"](context.vars.acc_1) : Type.boolean(false))], body: (context) => {
return Interpreter.consOperator(context.vars.acc_1, context.vars.x_2);
}}, {params: (context) => [Type.variablePattern("acc_3"), Type.tuple([Type.atom("cont"), Type.variablePattern("x_4")])], guards: [(context) => (Interpreter.toBoolean(Erlang["is_bitstring/1"](context.vars.x_4)) ? Erlang["is_bitstring/1"](context.vars.acc_3) : Type.boolean(false))], body: (context) => {
return Type.bitstring([Type.bitstringSegment(context.vars.acc_3, {type: "bitstring"}), Type.bitstringSegment(context.vars.x_4, {type: "bitstring"})]);
}}, {params: (context) => [Type.variablePattern("acc_5"), Type.tuple([Type.atom("cont"), Type.variablePattern("x_6")])], guards: [(context) => Erlang["is_bitstring/1"](context.vars.x_6)], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Erlang["iolist_to_binary/1"](context.vars.acc_5), {type: "bitstring"}), Type.bitstringSegment(context.vars.x_6, {type: "bitstring"})]);
}}, {params: (context) => [Type.variablePattern("acc_7"), Type.atom("done")], guards: [(context) => Erlang["is_bitstring/1"](context.vars.acc_7)], body: (context) => {
return context.vars.acc_7;
}}, {params: (context) => [Type.variablePattern("acc_8"), Type.atom("done")], guards: [], body: (context) => {
return Erlang["iolist_to_binary/1"](context.vars.acc_8);
}}, {params: (context) => [Type.matchPlaceholder(), Type.atom("halt")], guards: [], body: (context) => {
return Type.atom("ok");
}}, {params: (context) => [Type.matchPlaceholder(), Type.tuple([Type.atom("cont"), Type.variablePattern("other_11")])], guards: [], body: (context) => {
return Erlang["error/1"](Elixir_ArgumentError["exception/1"](Type.bitstring([Type.bitstringSegment(Type.string("collecting into a binary requires a bitstring, got: "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.other_11), {type: "binary"})])));
}}], context, "-into/1-fun-0-"), Type.variablePattern("fun_12"), context);
Interpreter.updateVarsToMatchedValues(context);
return Type.tuple([Type.list([context.vars.binary_0]), context.vars.fun_12]);
}}, {params: (context) => [Type.variablePattern("bitstring_0")], guards: [], body: (context) => {
Interpreter.matchOperator(Type.anonymousFunction(2, [{params: (context) => [Type.variablePattern("acc_1"), Type.tuple([Type.atom("cont"), Type.variablePattern("x_2")])], guards: [(context) => Erlang["is_bitstring/1"](context.vars.x_2)], body: (context) => {
return Type.bitstring([Type.bitstringSegment(context.vars.acc_1, {type: "bitstring"}), Type.bitstringSegment(context.vars.x_2, {type: "bitstring"})]);
}}, {params: (context) => [Type.variablePattern("acc_3"), Type.atom("done")], guards: [], body: (context) => {
return context.vars.acc_3;
}}, {params: (context) => [Type.matchPlaceholder(), Type.atom("halt")], guards: [], body: (context) => {
return Type.atom("ok");
}}, {params: (context) => [Type.matchPlaceholder(), Type.tuple([Type.atom("cont"), Type.variablePattern("other_6")])], guards: [], body: (context) => {
return Erlang["error/1"](Elixir_ArgumentError["exception/1"](Type.bitstring([Type.bitstringSegment(Type.string("collecting into a bitstring requires a bitstring, got: "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.other_6), {type: "binary"})])));
}}], context, "-into/1-fun-0-"), Type.variablePattern("fun_7"), context);
Interpreter.updateVarsToMatchedValues(context);
return Type.tuple([context.vars.bitstring_0, context.vars.fun_7]);
}}]);

Interpreter.defineElixirFunction("Collectable.List", "__impl__", 1, "public", [{params: (context) => [Type.atom("for")], guards: [], body: (context) => {
return Type.atom("Elixir.List");
}}, {params: (context) => [Type.atom("protocol")], guards: [], body: (context) => {
return Type.atom("Elixir.Collectable");
}}]);

Interpreter.defineElixirFunction("Collectable.List", "into", 1, "public", [{params: (context) => [Type.variablePattern("list_0")], guards: [], body: (context) => {
Interpreter.case(Erlang["/=/2"](context.vars.list_0, Type.list([])), [{match: Type.atom("false"), guards: [], body: (context) => {
return Type.atom("nil");
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Elixir_IO["warn/1"](Type.bitstring([Type.bitstringSegment(Type.string("the Collectable protocol is deprecated for non-empty lists. The behavior of "), {type: "binary"}), Type.bitstringSegment(Type.string("Enum.into/2 and \"for\" comprehensions with an :into option is incorrect "), {type: "binary"}), Type.bitstringSegment(Type.string("when collecting into non-empty lists. If you're collecting into a non-empty keyword "), {type: "binary"}), Type.bitstringSegment(Type.string("list, consider using Keyword.merge/2 instead. If you're collecting into a non-empty "), {type: "binary"}), Type.bitstringSegment(Type.string("list, consider concatenating the two lists with the ++ operator."), {type: "binary"})]));
}}], context);
Interpreter.matchOperator(Type.anonymousFunction(2, [{params: (context) => [Type.variablePattern("list_acc_1"), Type.tuple([Type.atom("cont"), Type.variablePattern("elem_2")])], guards: [], body: (context) => {
return Interpreter.consOperator(context.vars.elem_2, context.vars.list_acc_1);
}}, {params: (context) => [Type.variablePattern("list_acc_3"), Type.atom("done")], guards: [], body: (context) => {
return Erlang["++/2"](context.vars.list_0, Erlang_Lists["reverse/1"](context.vars.list_acc_3));
}}, {params: (context) => [Type.matchPlaceholder(), Type.atom("halt")], guards: [], body: (context) => {
return Type.atom("ok");
}}], context, "-into/1-fun-0-"), Type.variablePattern("fun_5"), context);
Interpreter.updateVarsToMatchedValues(context);
return Type.tuple([Type.list([]), context.vars.fun_5]);
}}]);

Interpreter.defineElixirFunction("Collectable.Map", "__impl__", 1, "public", [{params: (context) => [Type.atom("for")], guards: [], body: (context) => {
return Type.atom("Elixir.Map");
}}, {params: (context) => [Type.atom("protocol")], guards: [], body: (context) => {
return Type.atom("Elixir.Collectable");
}}]);

Interpreter.defineElixirFunction("Collectable.Map", "into", 1, "public", [{params: (context) => [Type.variablePattern("map_0")], guards: [], body: (context) => {
Interpreter.matchOperator(Type.anonymousFunction(2, [{params: (context) => [Type.variablePattern("map_acc_1"), Type.tuple([Type.atom("cont"), Type.tuple([Type.variablePattern("key_2"), Type.variablePattern("value_3")])])], guards: [], body: (context) => {
return Erlang_Maps["put/3"](context.vars.key_2, context.vars.value_3, context.vars.map_acc_1);
}}, {params: (context) => [Type.variablePattern("map_acc_4"), Type.atom("done")], guards: [], body: (context) => {
return context.vars.map_acc_4;
}}, {params: (context) => [Type.matchPlaceholder(), Type.atom("halt")], guards: [], body: (context) => {
return Type.atom("ok");
}}, {params: (context) => [Type.matchPlaceholder(), Type.tuple([Type.atom("cont"), Type.variablePattern("other_7")])], guards: [], body: (context) => {
return Erlang["error/1"](Elixir_ArgumentError["exception/1"](Type.bitstring([Type.bitstringSegment(Type.string("collecting into a map requires {key, value} tuples, got: "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.other_7), {type: "binary"})])));
}}], context, "-into/1-fun-0-"), Type.variablePattern("fun_8"), context);
Interpreter.updateVarsToMatchedValues(context);
return Type.tuple([context.vars.map_0, context.vars.fun_8]);
}}]);

Interpreter.defineElixirFunction("CondClauseError", "__struct__", 0, "public", [{params: (context) => [], guards: [], body: (context) => {
return Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.CondClauseError")]]);
}}]);

Interpreter.defineElixirFunction("CondClauseError", "__struct__", 1, "public", [{params: (context) => [Type.variablePattern("kv_0")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.kv_0, Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.CondClauseError")]]), Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("val_2")]), Type.variablePattern("map_3")], guards: [], body: (context) => {
return Elixir_Map["merge/2"](context.vars.map_3, Type.map([[context.vars.key_1, context.vars.val_2]]));
}}], context, "-__struct__/1-fun-0-"));
}}]);

Interpreter.defineElixirFunction("CondClauseError", "message", 1, "public", [{params: (context) => [Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.bitstring("no cond clause evaluated to a truthy value");
}}]);

Interpreter.defineElixirFunction("Enum", "entry_to_string", 1, "private", [{params: (context) => [Type.variablePattern("entry_0")], guards: [(context) => Erlang["is_binary/1"](context.vars.entry_0)], body: (context) => {
return context.vars.entry_0;
}}, {params: (context) => [Type.variablePattern("entry_0")], guards: [], body: (context) => {
return Elixir_String_Chars["to_string/1"](context.vars.entry_0);
}}]);

Interpreter.defineElixirFunction("Enum", "into", 2, "public", [{params: (context) => [Type.variablePattern("enumerable_0"), Type.list([])], guards: [], body: (context) => {
return Elixir_Enum["to_list/1"](context.vars.enumerable_0);
}}, {params: (context) => [Type.matchPattern(Type.map([[Type.atom("__struct__"), Type.matchPlaceholder()]]), Type.variablePattern("enumerable_0")), Type.variablePattern("collectable_1")], guards: [], body: (context) => {
return Elixir_Enum["into_protocol/2"](context.vars.enumerable_0, context.vars.collectable_1);
}}, {params: (context) => [Type.variablePattern("enumerable_0"), Type.matchPattern(Type.map([[Type.atom("__struct__"), Type.matchPlaceholder()]]), Type.variablePattern("collectable_1"))], guards: [], body: (context) => {
return Elixir_Enum["into_protocol/2"](context.vars.enumerable_0, context.vars.collectable_1);
}}, {params: (context) => [Type.variablePattern("enumerable_0"), Type.matchPattern(Type.map([]), Type.variablePattern("collectable_1"))], guards: [], body: (context) => {
return Interpreter.case(Erlang["==/2"](Erlang["map_size/1"](context.vars.collectable_1), Type.integer(0n)), [{match: Type.atom("false"), guards: [], body: (context) => {
return Elixir_Enum["into_map/2"](context.vars.enumerable_0, context.vars.collectable_1);
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Elixir_Enum["into_map/1"](context.vars.enumerable_0);
}}], context);
}}, {params: (context) => [Type.variablePattern("enumerable_0"), Type.variablePattern("collectable_1")], guards: [], body: (context) => {
return Elixir_Enum["into_protocol/2"](context.vars.enumerable_0, context.vars.collectable_1);
}}]);

Interpreter.defineElixirFunction("Enum", "into_map", 1, "private", [{params: (context) => [Type.matchPattern(Type.map([]), Type.variablePattern("enumerable_0"))], guards: [], body: (context) => {
return context.vars.enumerable_0;
}}, {params: (context) => [Type.variablePattern("enumerable_0")], guards: [(context) => Erlang["is_list/1"](context.vars.enumerable_0)], body: (context) => {
return Erlang_Maps["from_list/1"](context.vars.enumerable_0);
}}, {params: (context) => [Type.variablePattern("enumerable_0")], guards: [], body: (context) => {
return Erlang_Maps["from_list/1"](Elixir_Enum["to_list/1"](context.vars.enumerable_0));
}}]);

Interpreter.defineElixirFunction("Enum", "into_map", 2, "private", [{params: (context) => [Type.matchPattern(Type.map([]), Type.variablePattern("enumerable_0")), Type.variablePattern("collectable_1")], guards: [], body: (context) => {
return Erlang_Maps["merge/2"](context.vars.collectable_1, context.vars.enumerable_0);
}}, {params: (context) => [Type.variablePattern("enumerable_0"), Type.variablePattern("collectable_1")], guards: [(context) => Erlang["is_list/1"](context.vars.enumerable_0)], body: (context) => {
return Erlang_Maps["merge/2"](context.vars.collectable_1, Erlang_Maps["from_list/1"](context.vars.enumerable_0));
}}, {params: (context) => [Type.variablePattern("enumerable_0"), Type.variablePattern("collectable_1")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.enumerable_0, context.vars.collectable_1, Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_2"), Type.variablePattern("val_3")]), Type.variablePattern("acc_4")], guards: [], body: (context) => {
return Erlang_Maps["put/3"](context.vars.key_2, context.vars.val_3, context.vars.acc_4);
}}], context, "-into_map/2-fun-0-"));
}}]);

Interpreter.defineElixirFunction("Enum", "into_protocol", 2, "private", [{params: (context) => [Type.variablePattern("enumerable_0"), Type.variablePattern("collectable_1")], guards: [], body: (context) => {
Interpreter.matchOperator(Elixir_Collectable["into/1"](context.vars.collectable_1), Type.tuple([Type.variablePattern("initial_2"), Type.variablePattern("fun_3")]), context);
Interpreter.updateVarsToMatchedValues(context);
return Interpreter.try((context) => {
return Elixir_Enum["reduce_into_protocol/3"](context.vars.enumerable_0, context.vars.initial_2, context.vars.fun_3);
}, [], [{kind: Type.variablePattern("kind_4"), value: Type.variablePattern("reason_5"), guards: [], body: (context) => {
Interpreter.callAnonymousFunction(context.vars.fun_3, [context.vars.initial_2, Type.atom("halt")]);
return Erlang["raise/3"](context.vars.kind_4, context.vars.reason_5, context.stacktrace);
}}], [{match: Type.variablePattern("acc_6"), guards: [], body: (context) => {
return Interpreter.callAnonymousFunction(context.vars.fun_3, [context.vars.acc_6, Type.atom("done")]);
}}], null, context);
}}]);

Interpreter.defineElixirFunction("Enum", "join", 2, "public", [{params: (context) => [Type.variablePattern("enumerable_0"), Type.bitstring("")], guards: [], body: (context) => {
return Erlang["iolist_to_binary/1"](Elixir_Enum["map/2"](context.vars.enumerable_0, Type.functionCapture("Enum", "entry_to_string", 1, [{params: (context) => [Type.variablePattern("$1")], guards: [], body: (context) => {
return Elixir_Enum["entry_to_string/1"](context.vars["$1"]);
}}], context)));
}}, {params: (context) => [Type.variablePattern("enumerable_0"), Type.variablePattern("joiner_1")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_list/1"](context.vars.enumerable_0)) ? Erlang["is_binary/1"](context.vars.joiner_1) : Type.boolean(false))], body: (context) => {
return Elixir_Enum["join_list/2"](context.vars.enumerable_0, context.vars.joiner_1);
}}, {params: (context) => [Type.variablePattern("enumerable_0"), Type.variablePattern("joiner_1")], guards: [(context) => Erlang["is_binary/1"](context.vars.joiner_1)], body: (context) => {
Interpreter.matchOperator(Elixir_Enum["reduce/3"](context.vars.enumerable_0, Type.atom("first"), Type.anonymousFunction(2, [{params: (context) => [Type.variablePattern("entry_2"), Type.atom("first")], guards: [], body: (context) => {
return Elixir_Enum["entry_to_string/1"](context.vars.entry_2);
}}, {params: (context) => [Type.variablePattern("entry_3"), Type.variablePattern("acc_4")], guards: [], body: (context) => {
return Interpreter.consOperator(context.vars.acc_4, Interpreter.consOperator(context.vars.joiner_1, Elixir_Enum["entry_to_string/1"](context.vars.entry_3)));
}}], context, "-join/2-fun-0-")), Type.variablePattern("reduced_5"), context);
Interpreter.updateVarsToMatchedValues(context);
return Interpreter.case(Erlang["==/2"](context.vars.reduced_5, Type.atom("first")), [{match: Type.atom("false"), guards: [], body: (context) => {
return Erlang["iolist_to_binary/1"](context.vars.reduced_5);
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Type.bitstring("");
}}], context);
}}]);

Interpreter.defineElixirFunction("Enum", "join_list", 2, "private", [{params: (context) => [Type.list([]), Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.bitstring("");
}}, {params: (context) => [Type.variablePattern("list_0"), Type.variablePattern("joiner_1")], guards: [], body: (context) => {
return Erlang["iolist_to_binary/1"](Erlang_Lists["reverse/1"](Elixir_Enum["join_non_empty_list/3"](context.vars.list_0, context.vars.joiner_1, Type.list([]))));
}}]);

Interpreter.defineElixirFunction("Enum", "join_non_empty_list", 3, "private", [{params: (context) => [Type.list([Type.variablePattern("first_0")]), Type.matchPlaceholder(), Type.variablePattern("acc_2")], guards: [], body: (context) => {
return Interpreter.consOperator(Elixir_Enum["entry_to_string/1"](context.vars.first_0), context.vars.acc_2);
}}, {params: (context) => [Type.consPattern(Type.variablePattern("first_0"), Type.variablePattern("rest_1")), Type.variablePattern("joiner_2"), Type.variablePattern("acc_3")], guards: [], body: (context) => {
return Elixir_Enum["join_non_empty_list/3"](context.vars.rest_1, context.vars.joiner_2, Interpreter.consOperator(context.vars.joiner_2, Interpreter.consOperator(Elixir_Enum["entry_to_string/1"](context.vars.first_0), context.vars.acc_3)));
}}]);

Interpreter.defineElixirFunction("Enum", "map", 2, "public", [{params: (context) => [Type.variablePattern("enumerable_0"), Type.variablePattern("fun_1")], guards: [(context) => Erlang["is_list/1"](context.vars.enumerable_0)], body: (context) => {
return Erlang_Lists["map/2"](context.vars.fun_1, context.vars.enumerable_0);
}}, {params: (context) => [Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Range")], [Type.atom("first"), Type.variablePattern("first_0")], [Type.atom("last"), Type.variablePattern("last_1")], [Type.atom("step"), Type.variablePattern("step_2")]]), Type.variablePattern("fun_3")], guards: [], body: (context) => {
return Elixir_Enum["map_range/4"](context.vars.first_0, context.vars.last_1, context.vars.step_2, context.vars.fun_3);
}}, {params: (context) => [Type.variablePattern("enumerable_0"), Type.variablePattern("fun_1")], guards: [], body: (context) => {
return Erlang_Lists["reverse/1"](Elixir_Enum["reduce/3"](context.vars.enumerable_0, Type.list([]), Type.anonymousFunction(2, [{params: (context) => [Type.variablePattern("entry_2"), Type.variablePattern("acc_3")], guards: [], body: (context) => {
return Interpreter.consOperator(Interpreter.callAnonymousFunction(context.vars.fun_1, [context.vars.entry_2]), context.vars.acc_3);
}}], context, "-map/2-fun-0-")));
}}]);

Interpreter.defineElixirFunction("Enum", "map_intersperse", 3, "public", [{params: (context) => [Type.variablePattern("enumerable_0"), Type.variablePattern("separator_1"), Type.variablePattern("mapper_2")], guards: [(context) => Erlang["is_list/1"](context.vars.enumerable_0)], body: (context) => {
return Elixir_Enum["map_intersperse_list/3"](context.vars.enumerable_0, context.vars.separator_1, context.vars.mapper_2);
}}, {params: (context) => [Type.variablePattern("enumerable_0"), Type.variablePattern("separator_1"), Type.variablePattern("mapper_2")], guards: [], body: (context) => {
Interpreter.matchOperator(Elixir_Enum["reduce/3"](context.vars.enumerable_0, Type.atom("first"), Type.anonymousFunction(2, [{params: (context) => [Type.variablePattern("entry_3"), Type.atom("first")], guards: [], body: (context) => {
return Type.list([Interpreter.callAnonymousFunction(context.vars.mapper_2, [context.vars.entry_3])]);
}}, {params: (context) => [Type.variablePattern("entry_4"), Type.variablePattern("acc_5")], guards: [], body: (context) => {
return Interpreter.consOperator(Interpreter.callAnonymousFunction(context.vars.mapper_2, [context.vars.entry_4]), Interpreter.consOperator(context.vars.separator_1, context.vars.acc_5));
}}], context, "-map_intersperse/3-fun-0-")), Type.variablePattern("reduced_6"), context);
Interpreter.updateVarsToMatchedValues(context);
return Interpreter.case(Erlang["==/2"](context.vars.reduced_6, Type.atom("first")), [{match: Type.atom("false"), guards: [], body: (context) => {
return Erlang_Lists["reverse/1"](context.vars.reduced_6);
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Type.list([]);
}}], context);
}}]);

Interpreter.defineElixirFunction("Enum", "map_intersperse_list", 3, "private", [{params: (context) => [Type.list([]), Type.matchPlaceholder(), Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.list([]);
}}, {params: (context) => [Type.list([Type.variablePattern("last_0")]), Type.matchPlaceholder(), Type.variablePattern("mapper_1")], guards: [], body: (context) => {
return Type.list([Interpreter.callAnonymousFunction(context.vars.mapper_1, [context.vars.last_0])]);
}}, {params: (context) => [Type.consPattern(Type.variablePattern("head_0"), Type.variablePattern("rest_1")), Type.variablePattern("separator_2"), Type.variablePattern("mapper_3")], guards: [], body: (context) => {
return Interpreter.consOperator(Interpreter.callAnonymousFunction(context.vars.mapper_3, [context.vars.head_0]), Interpreter.consOperator(context.vars.separator_2, Elixir_Enum["map_intersperse_list/3"](context.vars.rest_1, context.vars.separator_2, context.vars.mapper_3)));
}}]);

Interpreter.defineElixirFunction("Enum", "map_join", 3, "public", [{params: (context) => [Type.variablePattern("enumerable_0"), Type.variablePattern("joiner_1"), Type.variablePattern("mapper_2")], guards: [(context) => Erlang["is_binary/1"](context.vars.joiner_1)], body: (context) => {
return Erlang["iolist_to_binary/1"](Elixir_Enum["map_intersperse/3"](context.vars.enumerable_0, context.vars.joiner_1, Type.anonymousFunction(1, [{params: (context) => [Type.variablePattern("$797")], guards: [], body: (context) => {
return Elixir_Enum["entry_to_string/1"](Interpreter.callAnonymousFunction(context.vars.mapper_2, [context.vars["$797"]]));
}}], context, "-map_join/3-fun-0-")));
}}]);

Interpreter.defineElixirFunction("Enum", "map_range", 4, "private", [{params: (context) => [Type.variablePattern("first_0"), Type.variablePattern("last_1"), Type.variablePattern("step_2"), Type.variablePattern("fun_3")], guards: [(context) => (Interpreter.toBoolean(Erlang[">/2"](context.vars.step_2, Type.integer(0n))) ? Erlang["=</2"](context.vars.first_0, context.vars.last_1) : Type.boolean(false)), (context) => (Interpreter.toBoolean(Erlang["</2"](context.vars.step_2, Type.integer(0n))) ? Erlang[">=/2"](context.vars.first_0, context.vars.last_1) : Type.boolean(false))], body: (context) => {
return Interpreter.consOperator(Interpreter.callAnonymousFunction(context.vars.fun_3, [context.vars.first_0]), Elixir_Enum["map_range/4"](Erlang["+/2"](context.vars.first_0, context.vars.step_2), context.vars.last_1, context.vars.step_2, context.vars.fun_3));
}}, {params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder(), Type.matchPlaceholder(), Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.list([]);
}}]);

Interpreter.defineElixirFunction("Enum", "member?", 2, "public", [{params: (context) => [Type.variablePattern("enumerable_0"), Type.variablePattern("element_1")], guards: [(context) => Erlang["is_list/1"](context.vars.enumerable_0)], body: (context) => {
return Erlang_Lists["member/2"](context.vars.element_1, context.vars.enumerable_0);
}}, {params: (context) => [Type.variablePattern("enumerable_0"), Type.variablePattern("element_1")], guards: [], body: (context) => {
return Interpreter.case(Elixir_Enumerable["member?/2"](context.vars.enumerable_0, context.vars.element_1), [{match: Type.tuple([Type.atom("ok"), Type.variablePattern("element_2")]), guards: [(context) => Erlang["is_boolean/1"](context.vars.element_2)], body: (context) => {
return context.vars.element_2;
}}, {match: Type.tuple([Type.atom("error"), Type.variablePattern("module_3")]), guards: [], body: (context) => {
return Erlang["element/2"](Type.integer(2n), Interpreter.callNamedFunction(context.vars.module_3, Type.atom("reduce"), Type.list([context.vars.enumerable_0, Type.tuple([Type.atom("cont"), Type.atom("false")]), Type.anonymousFunction(2, [{params: (context) => [Type.variablePattern("v_4"), Type.matchPlaceholder()], guards: [(context) => Erlang["=:=/2"](context.vars.v_4, context.vars.element_1)], body: (context) => {
return Type.tuple([Type.atom("halt"), Type.atom("true")]);
}}, {params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.tuple([Type.atom("cont"), Type.atom("false")]);
}}], context, "-member?/2-fun-0-")]), context));
}}], context);
}}]);

Interpreter.defineElixirFunction("Enum", "reduce", 3, "public", [{params: (context) => [Type.variablePattern("enumerable_0"), Type.variablePattern("acc_1"), Type.variablePattern("fun_2")], guards: [(context) => Erlang["is_list/1"](context.vars.enumerable_0)], body: (context) => {
return Erlang_Lists["foldl/3"](context.vars.fun_2, context.vars.acc_1, context.vars.enumerable_0);
}}, {params: (context) => [Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Range")], [Type.atom("first"), Type.variablePattern("first_0")], [Type.atom("last"), Type.variablePattern("last_1")], [Type.atom("step"), Type.variablePattern("step_2")]]), Type.variablePattern("acc_3"), Type.variablePattern("fun_4")], guards: [], body: (context) => {
return Elixir_Enum["reduce_range/5"](context.vars.first_0, context.vars.last_1, context.vars.step_2, context.vars.acc_3, context.vars.fun_4);
}}, {params: (context) => [Type.matchPattern(Type.map([[Type.atom("__struct__"), Type.matchPlaceholder()]]), Type.variablePattern("enumerable_0")), Type.variablePattern("acc_1"), Type.variablePattern("fun_2")], guards: [], body: (context) => {
return Elixir_Enum["reduce_enumerable/3"](context.vars.enumerable_0, context.vars.acc_1, context.vars.fun_2);
}}, {params: (context) => [Type.matchPattern(Type.map([]), Type.variablePattern("enumerable_0")), Type.variablePattern("acc_1"), Type.variablePattern("fun_2")], guards: [], body: (context) => {
return Erlang_Maps["fold/3"](Type.anonymousFunction(3, [{params: (context) => [Type.variablePattern("k_3"), Type.variablePattern("v_4"), Type.variablePattern("acc_5")], guards: [], body: (context) => {
return Interpreter.callAnonymousFunction(context.vars.fun_2, [Type.tuple([context.vars.k_3, context.vars.v_4]), context.vars.acc_5]);
}}], context, "-reduce/3-fun-0-"), context.vars.acc_1, context.vars.enumerable_0);
}}, {params: (context) => [Type.variablePattern("enumerable_0"), Type.variablePattern("acc_1"), Type.variablePattern("fun_2")], guards: [], body: (context) => {
return Elixir_Enum["reduce_enumerable/3"](context.vars.enumerable_0, context.vars.acc_1, context.vars.fun_2);
}}]);

Interpreter.defineElixirFunction("Enum", "reduce_enumerable", 3, "private", [{params: (context) => [Type.variablePattern("enumerable_0"), Type.variablePattern("acc_1"), Type.variablePattern("fun_2")], guards: [], body: (context) => {
return Erlang["element/2"](Type.integer(2n), Elixir_Enumerable["reduce/3"](context.vars.enumerable_0, Type.tuple([Type.atom("cont"), context.vars.acc_1]), Type.anonymousFunction(2, [{params: (context) => [Type.variablePattern("x_3"), Type.variablePattern("acc_4")], guards: [], body: (context) => {
return Type.tuple([Type.atom("cont"), Interpreter.callAnonymousFunction(context.vars.fun_2, [context.vars.x_3, context.vars.acc_4])]);
}}], context, "-reduce_enumerable/3-fun-0-")));
}}]);

Interpreter.defineElixirFunction("Enum", "reduce_into_protocol", 3, "private", [{params: (context) => [Type.variablePattern("enumerable_0"), Type.variablePattern("initial_1"), Type.variablePattern("fun_2")], guards: [(context) => Erlang["is_list/1"](context.vars.enumerable_0)], body: (context) => {
return Erlang_Lists["foldl/3"](Type.anonymousFunction(2, [{params: (context) => [Type.variablePattern("x_3"), Type.variablePattern("acc_4")], guards: [], body: (context) => {
return Interpreter.callAnonymousFunction(context.vars.fun_2, [context.vars.acc_4, Type.tuple([Type.atom("cont"), context.vars.x_3])]);
}}], context, "-reduce_into_protocol/3-fun-0-"), context.vars.initial_1, context.vars.enumerable_0);
}}, {params: (context) => [Type.variablePattern("enumerable_0"), Type.variablePattern("initial_1"), Type.variablePattern("fun_2")], guards: [], body: (context) => {
return Erlang["element/2"](Type.integer(2n), Elixir_Enumerable["reduce/3"](context.vars.enumerable_0, Type.tuple([Type.atom("cont"), context.vars.initial_1]), Type.anonymousFunction(2, [{params: (context) => [Type.variablePattern("x_3"), Type.variablePattern("acc_4")], guards: [], body: (context) => {
return Type.tuple([Type.atom("cont"), Interpreter.callAnonymousFunction(context.vars.fun_2, [context.vars.acc_4, Type.tuple([Type.atom("cont"), context.vars.x_3])])]);
}}], context, "-reduce_into_protocol/3-fun-0-")));
}}]);

Interpreter.defineElixirFunction("Enum", "reduce_range", 5, "private", [{params: (context) => [Type.variablePattern("first_0"), Type.variablePattern("last_1"), Type.variablePattern("step_2"), Type.variablePattern("acc_3"), Type.variablePattern("fun_4")], guards: [(context) => (Interpreter.toBoolean(Erlang[">/2"](context.vars.step_2, Type.integer(0n))) ? Erlang["=</2"](context.vars.first_0, context.vars.last_1) : Type.boolean(false)), (context) => (Interpreter.toBoolean(Erlang["</2"](context.vars.step_2, Type.integer(0n))) ? Erlang[">=/2"](context.vars.first_0, context.vars.last_1) : Type.boolean(false))], body: (context) => {
return Elixir_Enum["reduce_range/5"](Erlang["+/2"](context.vars.first_0, context.vars.step_2), context.vars.last_1, context.vars.step_2, Interpreter.callAnonymousFunction(context.vars.fun_4, [context.vars.first_0, context.vars.acc_3]), context.vars.fun_4);
}}, {params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder(), Type.matchPlaceholder(), Type.variablePattern("acc_3"), Type.matchPlaceholder()], guards: [], body: (context) => {
return context.vars.acc_3;
}}]);

Interpreter.defineElixirFunction("Enum", "reverse", 1, "public", [{params: (context) => [Type.list([])], guards: [], body: (context) => {
return Type.list([]);
}}, {params: (context) => [Type.matchPattern(Type.list([Type.matchPlaceholder()]), Type.variablePattern("list_0"))], guards: [], body: (context) => {
return context.vars.list_0;
}}, {params: (context) => [Type.list([Type.variablePattern("element1_0"), Type.variablePattern("element2_1")])], guards: [], body: (context) => {
return Type.list([context.vars.element2_1, context.vars.element1_0]);
}}, {params: (context) => [Type.consPattern(Type.variablePattern("element1_0"), Type.consPattern(Type.variablePattern("element2_1"), Type.variablePattern("rest_2")))], guards: [], body: (context) => {
return Erlang_Lists["reverse/2"](context.vars.rest_2, Type.list([context.vars.element2_1, context.vars.element1_0]));
}}, {params: (context) => [Type.variablePattern("enumerable_0")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.enumerable_0, Type.list([]), Type.anonymousFunction(2, [{params: (context) => [Type.variablePattern("$841"), Type.variablePattern("$842")], guards: [], body: (context) => {
return Interpreter.consOperator(context.vars["$841"], context.vars["$842"]);
}}], context, "-reverse/1-fun-0-"));
}}]);

Interpreter.defineElixirFunction("Enum", "to_list", 1, "public", [{params: (context) => [Type.variablePattern("enumerable_0")], guards: [(context) => Erlang["is_list/1"](context.vars.enumerable_0)], body: (context) => {
return context.vars.enumerable_0;
}}, {params: (context) => [Type.matchPattern(Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Range")]]), Type.variablePattern("range_0"))], guards: [], body: (context) => {
return Elixir_Range["to_list/1"](context.vars.range_0);
}}, {params: (context) => [Type.matchPattern(Type.map([[Type.atom("__struct__"), Type.matchPlaceholder()]]), Type.variablePattern("enumerable_0"))], guards: [], body: (context) => {
return Erlang_Lists["reverse/1"](Elixir_Enum["reverse/1"](context.vars.enumerable_0));
}}, {params: (context) => [Type.matchPattern(Type.map([]), Type.variablePattern("enumerable_0"))], guards: [], body: (context) => {
return Erlang_Maps["to_list/1"](context.vars.enumerable_0);
}}, {params: (context) => [Type.variablePattern("enumerable_0")], guards: [], body: (context) => {
return Erlang_Lists["reverse/1"](Elixir_Enum["reverse/1"](context.vars.enumerable_0));
}}]);

Interpreter.defineElixirFunction("Enumerable", "impl_for", 1, "public", [{params: (context) => [Type.map([[Type.atom("__struct__"), Type.variablePattern("struct_0")]])], guards: [], body: (context) => {
return Elixir_Enumerable["struct_impl_for/1"](context.vars.struct_0);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_tuple/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Enumerable.Tuple")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_atom/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Enumerable.Atom")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_list/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Enumerable.List")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_map/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Enumerable.Map")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_bitstring/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Enumerable.BitString")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_integer/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Enumerable.Integer")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_float/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Enumerable.Float")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_function/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Enumerable.Function")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_pid/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Enumerable.PID")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_port/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Enumerable.Port")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_reference/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.Enumerable.Reference")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.atom("nil");
}}]);

{
const s0 = Interpreter.literalSet(() => [Type.atom("false"), Type.atom("nil")]);
Interpreter.defineElixirFunction("Enumerable", "impl_for!", 1, "public", [{params: (context) => [Type.variablePattern("data_0")], guards: [], body: (context) => {
return Interpreter.case(Elixir_Enumerable["impl_for/1"](context.vars.data_0), [{match: Type.variablePattern("x_1"), guards: [(context) => Type.boolean(s0.has(context.vars.x_1))], body: (context) => {
return Erlang["error/1"](Elixir_Protocol_UndefinedError["exception/1"](Type.list([Type.tuple([Type.atom("protocol"), Type.atom("Elixir.Enumerable")]), Type.tuple([Type.atom("value"), context.vars.data_0]), Type.tuple([Type.atom("description"), Type.bitstring("")])])));
}}, {match: Type.variablePattern("x_2"), guards: [], body: (context) => {
return context.vars.x_2;
}}], context);
}}]);
}

Interpreter.defineElixirFunction("Enumerable", "member?", 2, "public", [{params: (context) => [Type.variablePattern("term_0"), Type.variablePattern("arg2_1")], guards: [], body: (context) => {
return Interpreter.callNamedFunction(Elixir_Enumerable["impl_for!/1"](context.vars.term_0), Type.atom("member?"), Type.list([context.vars.term_0, context.vars.arg2_1]), context);
}}]);

Interpreter.defineElixirFunction("Enumerable", "reduce", 3, "public", [{params: (context) => [Type.variablePattern("term_0"), Type.variablePattern("arg2_1"), Type.variablePattern("arg3_2")], guards: [], body: (context) => {
return Interpreter.callNamedFunction(Elixir_Enumerable["impl_for!/1"](context.vars.term_0), Type.atom("reduce"), Type.list([context.vars.term_0, context.vars.arg2_1, context.vars.arg3_2]), context);
}}]);

Interpreter.defineElixirFunction("Enumerable", "struct_impl_for", 1, "private", [{params: (context) => [Type.variablePattern("struct_0")], guards: [], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Elixir_Protocol["__concat__/2"](Type.atom("Elixir.Enumerable"), context.vars.struct_0)), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}]);

Interpreter.defineElixirFunction("Enumerable.Function", "__impl__", 1, "public", [{params: (context) => [Type.atom("for")], guards: [], body: (context) => {
return Type.atom("Elixir.Function");
}}, {params: (context) => [Type.atom("protocol")], guards: [], body: (context) => {
return Type.atom("Elixir.Enumerable");
}}]);

Interpreter.defineElixirFunction("Enumerable.Function", "member?", 2, "public", [{params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.tuple([Type.atom("error"), Type.atom("Elixir.Enumerable.Function")]);
}}]);

Interpreter.defineElixirFunction("Enumerable.Function", "reduce", 3, "public", [{params: (context) => [Type.variablePattern("function_0"), Type.variablePattern("acc_1"), Type.variablePattern("fun_2")], guards: [(context) => Erlang["is_function/2"](context.vars.function_0, Type.integer(2n))], body: (context) => {
return Interpreter.callAnonymousFunction(context.vars.function_0, [context.vars.acc_1, context.vars.fun_2]);
}}, {params: (context) => [Type.variablePattern("function_0"), Type.matchPlaceholder(), Type.matchPlaceholder()], guards: [], body: (context) => {
return Erlang["error/1"](Elixir_Protocol_UndefinedError["exception/1"](Type.list([Type.tuple([Type.atom("protocol"), Type.atom("Elixir.Enumerable")]), Type.tuple([Type.atom("value"), context.vars.function_0]), Type.tuple([Type.atom("description"), Type.bitstring("only anonymous functions of arity 2 are enumerable")])])));
}}]);

Interpreter.defineElixirFunction("Enumerable.List", "__impl__", 1, "public", [{params: (context) => [Type.atom("for")], guards: [], body: (context) => {
return Type.atom("Elixir.List");
}}, {params: (context) => [Type.atom("protocol")], guards: [], body: (context) => {
return Type.atom("Elixir.Enumerable");
}}]);

Interpreter.defineElixirFunction("Enumerable.List", "member?", 2, "public", [{params: (context) => [Type.list([]), Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.tuple([Type.atom("ok"), Type.atom("false")]);
}}, {params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.tuple([Type.atom("error"), Type.atom("Elixir.Enumerable.List")]);
}}]);

Interpreter.defineElixirFunction("Enumerable.List", "reduce", 3, "public", [{params: (context) => [Type.matchPlaceholder(), Type.tuple([Type.atom("halt"), Type.variablePattern("acc_1")]), Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.tuple([Type.atom("halted"), context.vars.acc_1]);
}}, {params: (context) => [Type.variablePattern("list_0"), Type.tuple([Type.atom("suspend"), Type.variablePattern("acc_1")]), Type.variablePattern("fun_2")], guards: [], body: (context) => {
return Type.tuple([Type.atom("suspended"), context.vars.acc_1, Type.anonymousFunction(1, [{params: (context) => [Type.variablePattern("$22")], guards: [], body: (context) => {
return Elixir_Enumerable_List["reduce/3"](context.vars.list_0, context.vars["$22"], context.vars.fun_2);
}}], context, "-reduce/3-fun-0-")]);
}}, {params: (context) => [Type.list([]), Type.tuple([Type.atom("cont"), Type.variablePattern("acc_0")]), Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.tuple([Type.atom("done"), context.vars.acc_0]);
}}, {params: (context) => [Type.consPattern(Type.variablePattern("head_0"), Type.variablePattern("tail_1")), Type.tuple([Type.atom("cont"), Type.variablePattern("acc_2")]), Type.variablePattern("fun_3")], guards: [], body: (context) => {
return Elixir_Enumerable_List["reduce/3"](context.vars.tail_1, Interpreter.callAnonymousFunction(context.vars.fun_3, [context.vars.head_0, context.vars.acc_2]), context.vars.fun_3);
}}]);

Interpreter.defineElixirFunction("Enumerable.Map", "__impl__", 1, "public", [{params: (context) => [Type.atom("for")], guards: [], body: (context) => {
return Type.atom("Elixir.Map");
}}, {params: (context) => [Type.atom("protocol")], guards: [], body: (context) => {
return Type.atom("Elixir.Enumerable");
}}]);

Interpreter.defineElixirFunction("Enumerable.Map", "member?", 2, "public", [{params: (context) => [Type.variablePattern("map_0"), Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("value_2")])], guards: [], body: (context) => {
return Type.tuple([Type.atom("ok"), Interpreter.case(context.vars.map_0, [{match: Type.map([[context.vars.key_1, context.vars.value_2]]), guards: [], body: (context) => {
return Type.atom("true");
}}, {match: Type.matchPlaceholder(), guards: [], body: (context) => {
return Type.atom("false");
}}], context)]);
}}, {params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.tuple([Type.atom("ok"), Type.atom("false")]);
}}]);

Interpreter.defineElixirFunction("Enumerable.Map", "reduce", 3, "public", [{params: (context) => [Type.variablePattern("map_0"), Type.variablePattern("acc_1"), Type.variablePattern("fun_2")], guards: [], body: (context) => {
return Elixir_Enumerable_List["reduce/3"](Erlang_Maps["to_list/1"](context.vars.map_0), context.vars.acc_1, context.vars.fun_2);
}}]);

Interpreter.defineElixirFunction("Enumerable.Range", "__impl__", 1, "public", [{params: (context) => [Type.atom("for")], guards: [], body: (context) => {
return Type.atom("Elixir.Range");
}}, {params: (context) => [Type.atom("protocol")], guards: [], body: (context) => {
return Type.atom("Elixir.Enumerable");
}}]);

Interpreter.defineElixirFunction("Enumerable.Range", "member?", 2, "public", [{params: (context) => [Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Range")], [Type.atom("first"), Type.variablePattern("first_0")], [Type.atom("last"), Type.variablePattern("last_1")], [Type.atom("step"), Type.variablePattern("step_2")]]), Type.variablePattern("value_3")], guards: [(context) => Erlang["is_integer/1"](context.vars.value_3)], body: (context) => {
return Interpreter.case(Erlang[">/2"](context.vars.step_2, Type.integer(0n)), [{match: Type.atom("false"), guards: [], body: (context) => {
return Type.tuple([Type.atom("ok"), Interpreter.case(Interpreter.case(Erlang["=</2"](context.vars.last_1, context.vars.value_3), [{match: Type.atom("false"), guards: [], body: (context) => {
return Type.atom("false");
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Erlang["=</2"](context.vars.value_3, context.vars.first_0);
}}], context), [{match: Type.atom("false"), guards: [], body: (context) => {
return Type.atom("false");
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Erlang["==/2"](Erlang["rem/2"](Erlang["-/2"](context.vars.value_3, context.vars.first_0), context.vars.step_2), Type.integer(0n));
}}], context)]);
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Type.tuple([Type.atom("ok"), Interpreter.case(Interpreter.case(Erlang["=</2"](context.vars.first_0, context.vars.value_3), [{match: Type.atom("false"), guards: [], body: (context) => {
return Type.atom("false");
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Erlang["=</2"](context.vars.value_3, context.vars.last_1);
}}], context), [{match: Type.atom("false"), guards: [], body: (context) => {
return Type.atom("false");
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Erlang["==/2"](Erlang["rem/2"](Erlang["-/2"](context.vars.value_3, context.vars.first_0), context.vars.step_2), Type.integer(0n));
}}], context)]);
}}], context);
}}, {params: (context) => [Type.matchPattern(Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Range")], [Type.atom("first"), Type.variablePattern("first_0")], [Type.atom("last"), Type.variablePattern("last_1")]]), Type.variablePattern("range_2")), Type.variablePattern("value_3")], guards: [(context) => Erlang["is_integer/1"](context.vars.value_3)], body: (context) => {
Interpreter.matchOperator(Interpreter.case(Erlang["=</2"](context.vars.first_0, context.vars.last_1), [{match: Type.atom("false"), guards: [], body: (context) => {
return Type.integer(-1n);
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Type.integer(1n);
}}], context), Type.variablePattern("step_4"), context);
Interpreter.updateVarsToMatchedValues(context);
return Elixir_Enumerable_Range["member?/2"](Erlang_Maps["put/3"](Type.atom("step"), context.vars.step_4, context.vars.range_2), context.vars.value_3);
}}, {params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.tuple([Type.atom("ok"), Type.atom("false")]);
}}]);

Interpreter.defineElixirFunction("Enumerable.Range", "reduce", 3, "public", [{params: (context) => [Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Range")], [Type.atom("first"), Type.variablePattern("first_0")], [Type.atom("last"), Type.variablePattern("last_1")], [Type.atom("step"), Type.variablePattern("step_2")]]), Type.variablePattern("acc_3"), Type.variablePattern("fun_4")], guards: [], body: (context) => {
return Elixir_Enumerable_Range["reduce/5"](context.vars.first_0, context.vars.last_1, context.vars.acc_3, context.vars.fun_4, context.vars.step_2);
}}, {params: (context) => [Type.matchPattern(Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Range")], [Type.atom("first"), Type.variablePattern("first_0")], [Type.atom("last"), Type.variablePattern("last_1")]]), Type.variablePattern("range_2")), Type.variablePattern("acc_3"), Type.variablePattern("fun_4")], guards: [], body: (context) => {
Interpreter.matchOperator(Interpreter.case(Erlang["=</2"](context.vars.first_0, context.vars.last_1), [{match: Type.atom("false"), guards: [], body: (context) => {
return Type.integer(-1n);
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Type.integer(1n);
}}], context), Type.variablePattern("step_5"), context);
Interpreter.updateVarsToMatchedValues(context);
return Elixir_Enumerable_Range["reduce/3"](Erlang_Maps["put/3"](Type.atom("step"), context.vars.step_5, context.vars.range_2), context.vars.acc_3, context.vars.fun_4);
}}]);

Interpreter.defineElixirFunction("Enumerable.Range", "reduce", 5, "private", [{params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder(), Type.tuple([Type.atom("halt"), Type.variablePattern("acc_2")]), Type.matchPlaceholder(), Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.tuple([Type.atom("halted"), context.vars.acc_2]);
}}, {params: (context) => [Type.variablePattern("first_0"), Type.variablePattern("last_1"), Type.tuple([Type.atom("suspend"), Type.variablePattern("acc_2")]), Type.variablePattern("fun_3"), Type.variablePattern("step_4")], guards: [], body: (context) => {
return Type.tuple([Type.atom("suspended"), context.vars.acc_2, Type.anonymousFunction(1, [{params: (context) => [Type.variablePattern("$29")], guards: [], body: (context) => {
return Elixir_Enumerable_Range["reduce/5"](context.vars.first_0, context.vars.last_1, context.vars["$29"], context.vars.fun_3, context.vars.step_4);
}}], context, "-reduce/5-fun-0-")]);
}}, {params: (context) => [Type.variablePattern("first_0"), Type.variablePattern("last_1"), Type.tuple([Type.atom("cont"), Type.variablePattern("acc_2")]), Type.variablePattern("fun_3"), Type.variablePattern("step_4")], guards: [(context) => (Interpreter.toBoolean(Erlang[">/2"](context.vars.step_4, Type.integer(0n))) ? Erlang["=</2"](context.vars.first_0, context.vars.last_1) : Type.boolean(false)), (context) => (Interpreter.toBoolean(Erlang["</2"](context.vars.step_4, Type.integer(0n))) ? Erlang[">=/2"](context.vars.first_0, context.vars.last_1) : Type.boolean(false))], body: (context) => {
return Elixir_Enumerable_Range["reduce/5"](Erlang["+/2"](context.vars.first_0, context.vars.step_4), context.vars.last_1, Interpreter.callAnonymousFunction(context.vars.fun_3, [context.vars.first_0, context.vars.acc_2]), context.vars.fun_3, context.vars.step_4);
}}, {params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder(), Type.tuple([Type.atom("cont"), Type.variablePattern("acc_0")]), Type.matchPlaceholder(), Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.tuple([Type.atom("done"), context.vars.acc_0]);
}}]);

Interpreter.defineElixirFunction("ErlangError", "__struct__", 0, "public", [{params: (context) => [], guards: [], body: (context) => {
return Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.ErlangError")], [Type.atom("original"), Type.atom("nil")], [Type.atom("reason"), Type.atom("nil")]]);
}}]);

Interpreter.defineElixirFunction("ErlangError", "__struct__", 1, "public", [{params: (context) => [Type.variablePattern("kv_0")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.kv_0, Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.ErlangError")], [Type.atom("original"), Type.atom("nil")], [Type.atom("reason"), Type.atom("nil")]]), Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("val_2")]), Type.variablePattern("map_3")], guards: [], body: (context) => {
return Elixir_Map["merge/2"](context.vars.map_3, Type.map([[context.vars.key_1, context.vars.val_2]]));
}}], context, "-__struct__/1-fun-0-"));
}}]);

Interpreter.defineElixirFunction("ErlangError", "arg_error", 1, "private", [{params: (context) => [Type.tuple([Type.variablePattern("n_0"), Type.variablePattern("message_1")])], guards: [], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string("  * "), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](Elixir_ErlangError["nth/1"](context.vars.n_0)), {type: "binary"}), Type.bitstringSegment(Type.string(" argument: "), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.message_1), {type: "binary"}), Type.bitstringSegment(Type.string("\n"), {type: "binary"})]);
}}]);

Interpreter.defineElixirFunction("ErlangError", "error_info", 3, "private", [{params: (context) => [Type.variablePattern("erl_exception_0"), Type.variablePattern("stacktrace_1"), Type.variablePattern("default_reason_2")], guards: [], body: (context) => {
globalThis.Hologram.return = Interpreter.with((context) => {
Interpreter.matchOperator(Elixir_Map["get/3"](context.vars.error_info_8, Type.atom("module"), context.vars.module_3), Type.variablePattern("error_module_9"), context);
Interpreter.updateVarsToMatchedValues(context);
Interpreter.matchOperator(Elixir_Map["get/3"](context.vars.error_info_8, Type.atom("function"), Type.atom("format_error")), Type.variablePattern("error_fun_10"), context);
Interpreter.updateVarsToMatchedValues(context);
Interpreter.matchOperator(Erlang_Maps["put/3"](Type.atom("pretty_printer"), Type.functionCapture("Kernel", "inspect", 1, [{params: (context) => [Type.variablePattern("$1")], guards: [], body: (context) => {
return Elixir_Kernel["inspect/1"](context.vars["$1"]);
}}], context), context.vars.error_info_8), Type.variablePattern("error_info_11"), context);
Interpreter.updateVarsToMatchedValues(context);
Interpreter.matchOperator(Type.tuple([context.vars.module_3, context.vars.fun_4, context.vars.args_or_arity_5, Elixir_Keyword["put/3"](context.vars.opts_6, Type.atom("error_info"), context.vars.error_info_11)]), Type.variablePattern("head_12"), context);
Interpreter.updateVarsToMatchedValues(context);
Interpreter.matchOperator(Interpreter.try((context) => {
return Interpreter.callNamedFunction(context.vars.error_module_9, context.vars.error_fun_10, Type.list([context.vars.erl_exception_0, Interpreter.consOperator(context.vars.head_12, context.vars.tail_7)]), context);
}, [{variable: Type.matchPlaceholder(), modules: [], body: (context) => {
return Type.map([]);
}}], [], [], null, context), Type.variablePattern("extra_13"), context);
Interpreter.updateVarsToMatchedValues(context);
Interpreter.matchOperator(Interpreter.case(Erlang["is_integer/1"](context.vars.args_or_arity_5), [{match: Type.atom("false"), guards: [], body: (context) => {
return Erlang["length/1"](context.vars.args_or_arity_5);
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return context.vars.args_or_arity_5;
}}], context), Type.variablePattern("arity_14"), context);
Interpreter.updateVarsToMatchedValues(context);
Interpreter.matchOperator(Elixir_Map["take/2"](context.vars.extra_13, Elixir_Enum["to_list/1"](Elixir_Range["new/3"](Type.integer(1n), context.vars.arity_14, Type.integer(1n)))), Type.variablePattern("args_errors_15"), context);
Interpreter.updateVarsToMatchedValues(context);
Interpreter.matchOperator(Elixir_Map["get/3"](context.vars.extra_13, Type.atom("reason"), context.vars.default_reason_2), Type.variablePattern("reason_16"), context);
Interpreter.updateVarsToMatchedValues(context);
globalThis.Hologram.return = Interpreter.cond([{condition: (context) => Erlang[">/2"](Erlang["map_size/1"](context.vars.args_errors_15), Type.integer(0n)), body: (context) => {
return Type.tuple([Type.atom("ok"), context.vars.reason_16, Erlang["iolist_to_binary/1"](Interpreter.consOperator(Type.bitstring(":\n\n"), Elixir_Enum["map/2"](context.vars.args_errors_15, Type.functionCapture("ErlangError", "arg_error", 1, [{params: (context) => [Type.variablePattern("$1")], guards: [], body: (context) => {
return Elixir_ErlangError["arg_error/1"](context.vars["$1"]);
}}], context))))]);
}}, {condition: (context) => Interpreter.matchOperator(Elixir_Access["get/2"](context.vars.extra_13, Type.atom("general")), Type.variablePattern("general_17"), context), body: (context) => {
return Type.tuple([Type.atom("ok"), context.vars.reason_16, Type.bitstring([Type.bitstringSegment(Type.string(": "), {type: "binary"}), Type.bitstringSegment(Elixir_IO["chardata_to_string/1"](context.vars.general_17), {type: "binary"})])]);
}}, {condition: (context) => Type.atom("true"), body: (context) => {
return Type.atom("error");
}}], context);
Interpreter.updateVarsToMatchedValues(context);
return globalThis.Hologram.return;
}, [{match: Type.consPattern(Type.tuple([Type.variablePattern("module_3"), Type.variablePattern("fun_4"), Type.variablePattern("args_or_arity_5"), Type.variablePattern("opts_6")]), Type.variablePattern("tail_7")), guards: [], expression: (context) => context.vars.stacktrace_1}, {match: Type.matchPattern(Type.map([]), Type.variablePattern("error_info_8")), guards: [], expression: (context) => Elixir_Access["get/2"](context.vars.opts_6, Type.atom("error_info"))}], [{match: Type.matchPlaceholder(), guards: [], body: (context) => {
return Type.atom("error");
}}], context);
Interpreter.updateVarsToMatchedValues(context);
return globalThis.Hologram.return;
}}]);

Interpreter.defineElixirFunction("ErlangError", "from_stacktrace", 1, "private", [{params: (context) => [Type.consPattern(Type.tuple([Type.variablePattern("module_0"), Type.variablePattern("function_1"), Type.variablePattern("args_2"), Type.matchPlaceholder()]), Type.matchPlaceholder())], guards: [(context) => Erlang["is_list/1"](context.vars.args_2)], body: (context) => {
return Type.tuple([context.vars.module_0, context.vars.function_1, Erlang["length/1"](context.vars.args_2)]);
}}, {params: (context) => [Type.consPattern(Type.tuple([Type.variablePattern("module_0"), Type.variablePattern("function_1"), Type.variablePattern("arity_2"), Type.matchPlaceholder()]), Type.matchPlaceholder())], guards: [], body: (context) => {
return Type.tuple([context.vars.module_0, context.vars.function_1, context.vars.arity_2]);
}}, {params: (context) => [Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.tuple([Type.atom("nil"), Type.atom("nil"), Type.atom("nil")]);
}}]);

Interpreter.defineElixirFunction("ErlangError", "message", 1, "public", [{params: (context) => [Type.map([[Type.atom("__struct__"), Type.atom("Elixir.ErlangError")], [Type.atom("original"), Type.variablePattern("original_0")], [Type.atom("reason"), Type.atom("nil")]])], guards: [], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string("Erlang error: "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.original_0), {type: "binary"})]);
}}, {params: (context) => [Type.map([[Type.atom("__struct__"), Type.atom("Elixir.ErlangError")], [Type.atom("original"), Type.variablePattern("original_0")], [Type.atom("reason"), Type.variablePattern("reason_1")]])], guards: [], body: (context) => {
return Erlang["iolist_to_binary/1"](Type.list([Type.bitstring("Erlang error: "), Elixir_Kernel["inspect/1"](context.vars.original_0), context.vars.reason_1]));
}}]);

Interpreter.defineElixirFunction("ErlangError", "normalize", 2, "public", [{params: (context) => [Type.atom("badarg"), Type.variablePattern("stacktrace_0")], guards: [], body: (context) => {
globalThis.Hologram.return = Interpreter.case(context.vars.stacktrace_0, [{match: Type.consPattern(Type.tuple([Type.atom("erlang"), Type.atom("apply"), Type.list([Type.variablePattern("module_1"), Type.variablePattern("function_2"), Type.variablePattern("args_3")]), Type.matchPlaceholder()]), Type.matchPlaceholder()), guards: [(context) => Erlang["not/1"](Erlang["is_atom/1"](context.vars.module_1))], body: (context) => {
Interpreter.matchOperator(Interpreter.cond([{condition: (context) => Interpreter.case(Interpreter.case(Erlang["is_map/1"](context.vars.module_1), [{match: Type.atom("false"), guards: [], body: (context) => {
return Type.atom("false");
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Erlang["is_atom/1"](context.vars.function_2);
}}], context), [{match: Type.atom("false"), guards: [], body: (context) => {
return Type.atom("false");
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Erlang["is_map_key/2"](context.vars.function_2, context.vars.module_1);
}}], context), body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string("you attempted to apply a function named "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.function_2), {type: "binary"}), Type.bitstringSegment(Type.string(" on a map/struct. "), {type: "binary"}), Type.bitstringSegment(Type.string("If you are using Kernel.apply/3, make sure the module is an atom. "), {type: "binary"}), Type.bitstringSegment(Interpreter.case(Erlang["is_function/1"](Elixir_Map["get/2"](context.vars.module_1, context.vars.function_2)), [{match: Type.atom("false"), guards: [], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string("If you are using the dot syntax, ensure there are no parentheses "), {type: "binary"}), Type.bitstringSegment(Type.string("after the field name, such as map."), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.function_2), {type: "binary"})]);
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string("If you are trying to invoke an anonymous function in a map/struct, "), {type: "binary"}), Type.bitstringSegment(Type.string("add a dot between the function name and the parenthesis: map."), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.function_2), {type: "binary"}), Type.bitstringSegment(Type.string(".()"), {type: "binary"})]);
}}], context), {type: "binary"})]);
}}, {condition: (context) => Interpreter.case(Erlang["is_atom/1"](context.vars.function_2), [{match: Type.atom("false"), guards: [], body: (context) => {
return Type.atom("false");
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Erlang["==/2"](context.vars.args_3, Type.list([]));
}}], context), body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string("you attempted to apply a function named "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.function_2), {type: "binary"}), Type.bitstringSegment(Type.string(" on "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.module_1), {type: "binary"}), Type.bitstringSegment(Type.string(". "), {type: "binary"}), Type.bitstringSegment(Type.string("If you are using Kernel.apply/3, make sure the module is an atom. "), {type: "binary"}), Type.bitstringSegment(Type.string("If you are using the dot syntax, such as module.function(), "), {type: "binary"}), Type.bitstringSegment(Type.string("make sure the left-hand side of the dot is an atom representing a module"), {type: "binary"})]);
}}, {condition: (context) => Type.atom("true"), body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string("you attempted to apply a function on "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.module_1), {type: "binary"}), Type.bitstringSegment(Type.string(". "), {type: "binary"}), Type.bitstringSegment(Type.string("Modules (the first argument of apply) must always be an atom"), {type: "binary"})]);
}}], context), Type.variablePattern("message_4"), context);
Interpreter.updateVarsToMatchedValues(context);
return Elixir_ArgumentError["__struct__/1"](Type.list([Type.tuple([Type.atom("__exception__"), Type.atom("true")]), Type.tuple([Type.atom("message"), context.vars.message_4])]));
}}, {match: Type.matchPlaceholder(), guards: [], body: (context) => {
return Interpreter.case(Elixir_ErlangError["error_info/3"](Type.atom("badarg"), context.vars.stacktrace_0, Type.bitstring("errors were found at the given arguments")), [{match: Type.tuple([Type.atom("ok"), Type.variablePattern("reason_5"), Type.variablePattern("details_6")]), guards: [], body: (context) => {
return Elixir_ArgumentError["__struct__/1"](Type.list([Type.tuple([Type.atom("__exception__"), Type.atom("true")]), Type.tuple([Type.atom("message"), Type.bitstring([Type.bitstringSegment(context.vars.reason_5, {type: "binary"}), Type.bitstringSegment(context.vars.details_6, {type: "binary"})])])]));
}}, {match: Type.atom("error"), guards: [], body: (context) => {
return Elixir_ArgumentError["__struct__/1"](Type.list([Type.tuple([Type.atom("__exception__"), Type.atom("true")]), Type.tuple([Type.atom("message"), Type.bitstring("argument error")])]));
}}], context);
}}], context);
Interpreter.updateVarsToMatchedValues(context);
return globalThis.Hologram.return;
}}, {params: (context) => [Type.atom("badarith"), Type.matchPlaceholder()], guards: [], body: (context) => {
return Elixir_ArithmeticError["__struct__/1"](Type.list([Type.tuple([Type.atom("__exception__"), Type.atom("true")]), Type.tuple([Type.atom("message"), Type.bitstring("bad argument in arithmetic expression")])]));
}}, {params: (context) => [Type.atom("system_limit"), Type.variablePattern("stacktrace_0")], guards: [], body: (context) => {
Interpreter.matchOperator(Type.bitstring("a system limit has been reached due to errors at the given arguments"), Type.variablePattern("default_reason_1"), context);
Interpreter.updateVarsToMatchedValues(context);
return Interpreter.case(Elixir_ErlangError["error_info/3"](Type.atom("system_limit"), context.vars.stacktrace_0, context.vars.default_reason_1), [{match: Type.tuple([Type.atom("ok"), Type.variablePattern("reason_2"), Type.variablePattern("details_3")]), guards: [], body: (context) => {
return Elixir_SystemLimitError["__struct__/1"](Type.list([Type.tuple([Type.atom("__exception__"), Type.atom("true")]), Type.tuple([Type.atom("message"), Type.bitstring([Type.bitstringSegment(context.vars.reason_2, {type: "binary"}), Type.bitstringSegment(context.vars.details_3, {type: "binary"})])])]));
}}, {match: Type.atom("error"), guards: [], body: (context) => {
return Elixir_SystemLimitError["__struct__/1"](Type.list([Type.tuple([Type.atom("__exception__"), Type.atom("true")]), Type.tuple([Type.atom("message"), Type.bitstring("a system limit has been reached")])]));
}}], context);
}}, {params: (context) => [Type.atom("cond_clause"), Type.matchPlaceholder()], guards: [], body: (context) => {
return Elixir_CondClauseError["__struct__/1"](Type.list([Type.tuple([Type.atom("__exception__"), Type.atom("true")])]));
}}, {params: (context) => [Type.tuple([Type.atom("badarity"), Type.tuple([Type.variablePattern("fun_0"), Type.variablePattern("args_1")])]), Type.matchPlaceholder()], guards: [], body: (context) => {
return Elixir_BadArityError["__struct__/1"](Type.list([Type.tuple([Type.atom("__exception__"), Type.atom("true")]), Type.tuple([Type.atom("function"), context.vars.fun_0]), Type.tuple([Type.atom("args"), context.vars.args_1])]));
}}, {params: (context) => [Type.tuple([Type.atom("badfun"), Type.variablePattern("term_0")]), Type.matchPlaceholder()], guards: [], body: (context) => {
return Elixir_BadFunctionError["__struct__/1"](Type.list([Type.tuple([Type.atom("__exception__"), Type.atom("true")]), Type.tuple([Type.atom("term"), context.vars.term_0])]));
}}, {params: (context) => [Type.tuple([Type.atom("badstruct"), Type.variablePattern("struct_0"), Type.variablePattern("term_1")]), Type.matchPlaceholder()], guards: [], body: (context) => {
return Elixir_BadStructError["__struct__/1"](Type.list([Type.tuple([Type.atom("__exception__"), Type.atom("true")]), Type.tuple([Type.atom("struct"), context.vars.struct_0]), Type.tuple([Type.atom("term"), context.vars.term_1])]));
}}, {params: (context) => [Type.tuple([Type.atom("badmatch"), Type.variablePattern("term_0")]), Type.matchPlaceholder()], guards: [], body: (context) => {
return Elixir_MatchError["__struct__/1"](Type.list([Type.tuple([Type.atom("__exception__"), Type.atom("true")]), Type.tuple([Type.atom("term"), context.vars.term_0])]));
}}, {params: (context) => [Type.tuple([Type.atom("badmap"), Type.variablePattern("term_0")]), Type.matchPlaceholder()], guards: [], body: (context) => {
return Elixir_BadMapError["__struct__/1"](Type.list([Type.tuple([Type.atom("__exception__"), Type.atom("true")]), Type.tuple([Type.atom("term"), context.vars.term_0])]));
}}, {params: (context) => [Type.tuple([Type.atom("badbool"), Type.variablePattern("op_0"), Type.variablePattern("term_1")]), Type.matchPlaceholder()], guards: [], body: (context) => {
return Elixir_BadBooleanError["__struct__/1"](Type.list([Type.tuple([Type.atom("__exception__"), Type.atom("true")]), Type.tuple([Type.atom("operator"), context.vars.op_0]), Type.tuple([Type.atom("term"), context.vars.term_1])]));
}}, {params: (context) => [Type.tuple([Type.atom("badkey"), Type.variablePattern("key_0")]), Type.variablePattern("stacktrace_1")], guards: [], body: (context) => {
Interpreter.matchOperator(Interpreter.case(context.vars.stacktrace_1, [{match: Type.consPattern(Type.tuple([Type.atom("Elixir.Map"), Type.atom("get_and_update!"), Type.list([Type.variablePattern("map_2"), Type.matchPlaceholder(), Type.matchPlaceholder()]), Type.matchPlaceholder()]), Type.matchPlaceholder()), guards: [], body: (context) => {
return context.vars.map_2;
}}, {match: Type.consPattern(Type.tuple([Type.atom("Elixir.Map"), Type.atom("update!"), Type.list([Type.variablePattern("map_3"), Type.matchPlaceholder(), Type.matchPlaceholder()]), Type.matchPlaceholder()]), Type.matchPlaceholder()), guards: [], body: (context) => {
return context.vars.map_3;
}}, {match: Type.consPattern(Type.tuple([Type.atom("maps"), Type.atom("update"), Type.list([Type.matchPlaceholder(), Type.matchPlaceholder(), Type.variablePattern("map_4")]), Type.matchPlaceholder()]), Type.matchPlaceholder()), guards: [], body: (context) => {
return context.vars.map_4;
}}, {match: Type.consPattern(Type.tuple([Type.atom("maps"), Type.atom("get"), Type.list([Type.matchPlaceholder(), Type.variablePattern("map_5")]), Type.matchPlaceholder()]), Type.matchPlaceholder()), guards: [], body: (context) => {
return context.vars.map_5;
}}, {match: Type.consPattern(Type.tuple([Type.atom("erlang"), Type.atom("map_get"), Type.list([Type.matchPlaceholder(), Type.variablePattern("map_6")]), Type.matchPlaceholder()]), Type.matchPlaceholder()), guards: [], body: (context) => {
return context.vars.map_6;
}}, {match: Type.matchPlaceholder(), guards: [], body: (context) => {
return Type.atom("nil");
}}], context), Type.variablePattern("term_7"), context);
Interpreter.updateVarsToMatchedValues(context);
return Elixir_KeyError["__struct__/1"](Type.list([Type.tuple([Type.atom("__exception__"), Type.atom("true")]), Type.tuple([Type.atom("message"), Type.atom("nil")]), Type.tuple([Type.atom("key"), context.vars.key_0]), Type.tuple([Type.atom("term"), context.vars.term_7])]));
}}, {params: (context) => [Type.tuple([Type.atom("badkey"), Type.variablePattern("key_0"), Type.variablePattern("map_1")]), Type.matchPlaceholder()], guards: [(context) => Erlang["is_map/1"](context.vars.map_1)], body: (context) => {
return Elixir_KeyError["__struct__/1"](Type.list([Type.tuple([Type.atom("__exception__"), Type.atom("true")]), Type.tuple([Type.atom("message"), Type.atom("nil")]), Type.tuple([Type.atom("key"), context.vars.key_0]), Type.tuple([Type.atom("term"), context.vars.map_1])]));
}}, {params: (context) => [Type.tuple([Type.atom("badkey"), Type.variablePattern("key_0"), Type.variablePattern("term_1")]), Type.matchPlaceholder()], guards: [], body: (context) => {
Interpreter.matchOperator(Type.bitstring([Type.bitstringSegment(Type.string("key "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.key_0), {type: "binary"}), Type.bitstringSegment(Type.string(" not found in: "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/2"](context.vars.term_1, Type.list([Type.tuple([Type.atom("pretty"), Type.atom("true")]), Type.tuple([Type.atom("limit"), Type.atom("infinity")])])), {type: "binary"}), Type.bitstringSegment(Type.string("\n\n"), {type: "binary"}), Type.bitstringSegment(Type.string("If you are using the dot syntax, such as map.field, "), {type: "binary"}), Type.bitstringSegment(Type.string("make sure the left-hand side of the dot is a map"), {type: "binary"})]), Type.variablePattern("message_3"), context);
Interpreter.updateVarsToMatchedValues(context);
return Elixir_KeyError["__struct__/1"](Type.list([Type.tuple([Type.atom("__exception__"), Type.atom("true")]), Type.tuple([Type.atom("key"), context.vars.key_0]), Type.tuple([Type.atom("term"), context.vars.term_1]), Type.tuple([Type.atom("message"), context.vars.message_3])]));
}}, {params: (context) => [Type.tuple([Type.atom("case_clause"), Type.variablePattern("term_0")]), Type.matchPlaceholder()], guards: [], body: (context) => {
return Elixir_CaseClauseError["__struct__/1"](Type.list([Type.tuple([Type.atom("__exception__"), Type.atom("true")]), Type.tuple([Type.atom("term"), context.vars.term_0])]));
}}, {params: (context) => [Type.tuple([Type.atom("else_clause"), Type.variablePattern("term_0")]), Type.matchPlaceholder()], guards: [], body: (context) => {
return Elixir_WithClauseError["__struct__/1"](Type.list([Type.tuple([Type.atom("__exception__"), Type.atom("true")]), Type.tuple([Type.atom("term"), context.vars.term_0])]));
}}, {params: (context) => [Type.tuple([Type.atom("try_clause"), Type.variablePattern("term_0")]), Type.matchPlaceholder()], guards: [], body: (context) => {
return Elixir_TryClauseError["__struct__/1"](Type.list([Type.tuple([Type.atom("__exception__"), Type.atom("true")]), Type.tuple([Type.atom("term"), context.vars.term_0])]));
}}, {params: (context) => [Type.atom("undef"), Type.variablePattern("stacktrace_0")], guards: [], body: (context) => {
Interpreter.matchOperator(Elixir_ErlangError["from_stacktrace/1"](context.vars.stacktrace_0), Type.tuple([Type.variablePattern("mod_1"), Type.variablePattern("fun_2"), Type.variablePattern("arity_3")]), context);
Interpreter.updateVarsToMatchedValues(context);
return Elixir_UndefinedFunctionError["__struct__/1"](Type.list([Type.tuple([Type.atom("__exception__"), Type.atom("true")]), Type.tuple([Type.atom("message"), Type.atom("nil")]), Type.tuple([Type.atom("reason"), Type.atom("nil")]), Type.tuple([Type.atom("module"), context.vars.mod_1]), Type.tuple([Type.atom("function"), context.vars.fun_2]), Type.tuple([Type.atom("arity"), context.vars.arity_3])]));
}}, {params: (context) => [Type.atom("function_clause"), Type.variablePattern("stacktrace_0")], guards: [], body: (context) => {
Interpreter.matchOperator(Elixir_ErlangError["from_stacktrace/1"](context.vars.stacktrace_0), Type.tuple([Type.variablePattern("mod_1"), Type.variablePattern("fun_2"), Type.variablePattern("arity_3")]), context);
Interpreter.updateVarsToMatchedValues(context);
return Elixir_FunctionClauseError["__struct__/1"](Type.list([Type.tuple([Type.atom("__exception__"), Type.atom("true")]), Type.tuple([Type.atom("args"), Type.atom("nil")]), Type.tuple([Type.atom("clauses"), Type.atom("nil")]), Type.tuple([Type.atom("kind"), Type.atom("nil")]), Type.tuple([Type.atom("module"), context.vars.mod_1]), Type.tuple([Type.atom("function"), context.vars.fun_2]), Type.tuple([Type.atom("arity"), context.vars.arity_3])]));
}}, {params: (context) => [Type.tuple([Type.atom("badarg"), Type.variablePattern("payload_0")]), Type.matchPlaceholder()], guards: [], body: (context) => {
return Elixir_ArgumentError["__struct__/1"](Type.list([Type.tuple([Type.atom("__exception__"), Type.atom("true")]), Type.tuple([Type.atom("message"), Type.bitstring([Type.bitstringSegment(Type.string("argument error: "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.payload_0), {type: "binary"})])])]));
}}, {params: (context) => [Type.variablePattern("other_0"), Type.variablePattern("stacktrace_1")], guards: [], body: (context) => {
return Interpreter.case(Elixir_ErlangError["error_info/3"](context.vars.other_0, context.vars.stacktrace_1, Type.bitstring("")), [{match: Type.tuple([Type.atom("ok"), Type.matchPlaceholder(), Type.variablePattern("details_3")]), guards: [], body: (context) => {
return Elixir_ErlangError["__struct__/1"](Type.list([Type.tuple([Type.atom("__exception__"), Type.atom("true")]), Type.tuple([Type.atom("original"), context.vars.other_0]), Type.tuple([Type.atom("reason"), context.vars.details_3])]));
}}, {match: Type.atom("error"), guards: [], body: (context) => {
return Elixir_ErlangError["__struct__/1"](Type.list([Type.tuple([Type.atom("__exception__"), Type.atom("true")]), Type.tuple([Type.atom("reason"), Type.atom("nil")]), Type.tuple([Type.atom("original"), context.vars.other_0])]));
}}], context);
}}]);

Interpreter.defineElixirFunction("ErlangError", "nth", 1, "private", [{params: (context) => [Type.integer(1n)], guards: [], body: (context) => {
return Type.bitstring("1st");
}}, {params: (context) => [Type.integer(2n)], guards: [], body: (context) => {
return Type.bitstring("2nd");
}}, {params: (context) => [Type.integer(3n)], guards: [], body: (context) => {
return Type.bitstring("3rd");
}}, {params: (context) => [Type.variablePattern("n_0")], guards: [], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.n_0), {type: "binary"}), Type.bitstringSegment(Type.string("th"), {type: "binary"})]);
}}]);

Interpreter.defineElixirFunction("Exception", "__format_message_with_term__", 2, "public", [{params: (context) => [Type.variablePattern("message_0"), Type.variablePattern("term_1")], guards: [], body: (context) => {
Interpreter.matchOperator(Elixir_Enum["map_intersperse/3"](Elixir_String["split/2"](Elixir_Kernel["inspect/2"](context.vars.term_1, Type.list([Type.tuple([Type.atom("pretty"), Type.atom("true")])])), Type.bitstring("\n")), Type.bitstring("\n"), Type.anonymousFunction(1, [{params: (context) => [Type.bitstring("")], guards: [], body: (context) => {
return Type.bitstring("");
}}, {params: (context) => [Type.variablePattern("line_2")], guards: [], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string("    "), {type: "binary"}), Type.bitstringSegment(context.vars.line_2, {type: "binary"})]);
}}], context, "-__format_message_with_term__/2-fun-0-")), Type.variablePattern("inspected_3"), context);
Interpreter.updateVarsToMatchedValues(context);
return Erlang["iolist_to_binary/1"](Type.list([context.vars.message_0, Type.bitstring("\n\n"), context.vars.inspected_3, Type.bitstring("\n")]));
}}]);

{
const s0 = Interpreter.literalSet(() => [Type.atom("false"), Type.atom("nil")]);
Interpreter.defineElixirFunction("Exception", "blame", 3, "public", [{params: (context) => [Type.atom("error"), Type.variablePattern("error_0"), Type.variablePattern("stacktrace_1")], guards: [], body: (context) => {
Interpreter.matchOperator(Interpreter.matchOperator(Elixir_Exception["normalize/3"](Type.atom("error"), context.vars.error_0, context.vars.stacktrace_1), Type.variablePattern("struct_2"), context), Type.map([[Type.atom("__struct__"), Type.variablePattern("module_3")]]), context);
Interpreter.updateVarsToMatchedValues(context);
return Interpreter.case(Interpreter.case(Elixir_Code["ensure_loaded?/1"](context.vars.module_3), [{match: Type.atom("false"), guards: [], body: (context) => {
return Type.atom("false");
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Erlang["function_exported/3"](context.vars.module_3, Type.atom("blame"), Type.integer(2n));
}}, {match: Type.variablePattern("other_4"), guards: [], body: (context) => {
return Erlang["error/1"](Type.tuple([Type.atom("badbool"), Type.atom("and"), context.vars.other_4]));
}}], context), [{match: Type.variablePattern("x_5"), guards: [(context) => Type.boolean(s0.has(context.vars.x_5))], body: (context) => {
return Type.tuple([context.vars.struct_2, context.vars.stacktrace_1]);
}}, {match: Type.matchPlaceholder(), guards: [], body: (context) => {
return Interpreter.callNamedFunction(context.vars.module_3, Type.atom("blame"), Type.list([context.vars.struct_2, context.vars.stacktrace_1]), context);
}}], context);
}}, {params: (context) => [Type.matchPlaceholder(), Type.variablePattern("reason_1"), Type.variablePattern("stacktrace_2")], guards: [], body: (context) => {
return Type.tuple([context.vars.reason_1, context.vars.stacktrace_2]);
}}]);
}

Interpreter.defineElixirFunction("Exception", "format_arity", 1, "private", [{params: (context) => [Type.variablePattern("arity_0")], guards: [(context) => Erlang["is_list/1"](context.vars.arity_0)], body: (context) => {
Interpreter.matchOperator(Interpreter.comprehension([{type: "generator", match: Type.variablePattern("x_1"), guards: [], body: (context) => context.vars.arity_0}], Type.list([]), false, (context) => {
return Elixir_Kernel["inspect/1"](context.vars.x_1);
}, context), Type.variablePattern("inspected_2"), context);
Interpreter.updateVarsToMatchedValues(context);
return Type.bitstring([Type.bitstringSegment(Type.string("("), {type: "binary"}), Type.bitstringSegment(Elixir_Enum["join/2"](context.vars.inspected_2, Type.bitstring(", ")), {type: "binary"}), Type.bitstringSegment(Type.string(")"), {type: "binary"})]);
}}, {params: (context) => [Type.variablePattern("arity_0")], guards: [(context) => Erlang["is_integer/1"](context.vars.arity_0)], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string("/"), {type: "binary"}), Type.bitstringSegment(Erlang["integer_to_binary/1"](context.vars.arity_0), {type: "binary"})]);
}}]);

Interpreter.defineElixirFunction("Exception", "format_mfa", 3, "public", [{params: (context) => [Type.variablePattern("module_0"), Type.variablePattern("fun_1"), Type.variablePattern("arity_2")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_atom/1"](context.vars.module_0)) ? Erlang["is_atom/1"](context.vars.fun_1) : Type.boolean(false))], body: (context) => {
return Interpreter.case(Elixir_Code_Identifier["extract_anonymous_fun_parent/1"](context.vars.fun_1), [{match: Type.tuple([Type.variablePattern("outer_name_3"), Type.variablePattern("outer_arity_4")]), guards: [], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string("anonymous fn"), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](Elixir_Exception["format_arity/1"](context.vars.arity_2)), {type: "binary"}), Type.bitstringSegment(Type.string(" in "), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](Elixir_Macro["inspect_atom/2"](Type.atom("literal"), context.vars.module_0)), {type: "binary"}), Type.bitstringSegment(Type.string("."), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](Elixir_Macro["inspect_atom/2"](Type.atom("remote_call"), context.vars.outer_name_3)), {type: "binary"}), Type.bitstringSegment(Type.string("/"), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.outer_arity_4), {type: "binary"})]);
}}, {match: Type.atom("error"), guards: [], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Elixir_String_Chars["to_string/1"](Elixir_Macro["inspect_atom/2"](Type.atom("literal"), context.vars.module_0)), {type: "binary"}), Type.bitstringSegment(Type.string("."), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](Elixir_Macro["inspect_atom/2"](Type.atom("remote_call"), context.vars.fun_1)), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](Elixir_Exception["format_arity/1"](context.vars.arity_2)), {type: "binary"})]);
}}], context);
}}]);

Interpreter.defineElixirFunction("Exception", "message", 1, "public", [{params: (context) => [Type.matchPattern(Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.variablePattern("module_0")]]), Type.variablePattern("exception_1"))], guards: [], body: (context) => {
return Interpreter.try((context) => {
return Interpreter.callNamedFunction(context.vars.module_0, Type.atom("message"), Type.list([context.vars.exception_1]), context);
}, [{variable: Type.variablePattern("caught_exception_2"), modules: [], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string("got "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](Interpreter.dotOperator(context.vars.caught_exception_2, Type.atom("__struct__"))), {type: "binary"}), Type.bitstringSegment(Type.string(" with message "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](Elixir_Exception["message/1"](context.vars.caught_exception_2)), {type: "binary"}), Type.bitstringSegment(Type.string(" while retrieving Exception.message/1 "), {type: "binary"}), Type.bitstringSegment(Type.string("for "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.exception_1), {type: "binary"}), Type.bitstringSegment(Type.string(". Stacktrace:\n"), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](Elixir_Exception["format_stacktrace/1"](context.stacktrace)), {type: "binary"})]);
}}], [], [{match: Type.variablePattern("result_3"), guards: [(context) => Erlang["is_binary/1"](context.vars.result_3)], body: (context) => {
return context.vars.result_3;
}}, {match: Type.variablePattern("result_4"), guards: [], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string("got "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.result_4), {type: "binary"}), Type.bitstringSegment(Type.string(" "), {type: "binary"}), Type.bitstringSegment(Type.string("while retrieving Exception.message/1 for "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.exception_1), {type: "binary"}), Type.bitstringSegment(Type.string(" "), {type: "binary"}), Type.bitstringSegment(Type.string("(expected a string)"), {type: "binary"})]);
}}], null, context);
}}]);

Interpreter.defineElixirFunction("Exception", "normalize", 3, "public", [{params: (context) => [Type.atom("error"), Type.matchPattern(Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.matchPlaceholder()]]), Type.variablePattern("payload_0")), Type.matchPlaceholder()], guards: [], body: (context) => {
return context.vars.payload_0;
}}, {params: (context) => [Type.atom("error"), Type.variablePattern("payload_0"), Type.variablePattern("stacktrace_1")], guards: [], body: (context) => {
return Elixir_ErlangError["normalize/2"](context.vars.payload_0, context.vars.stacktrace_1);
}}, {params: (context) => [Type.matchPlaceholder(), Type.variablePattern("payload_1"), Type.matchPlaceholder()], guards: [], body: (context) => {
return context.vars.payload_1;
}}]);

Interpreter.defineElixirFunction("FunctionClauseError", "__struct__", 0, "public", [{params: (context) => [], guards: [], body: (context) => {
return Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.FunctionClauseError")], [Type.atom("args"), Type.atom("nil")], [Type.atom("arity"), Type.atom("nil")], [Type.atom("clauses"), Type.atom("nil")], [Type.atom("function"), Type.atom("nil")], [Type.atom("kind"), Type.atom("nil")], [Type.atom("module"), Type.atom("nil")]]);
}}]);

Interpreter.defineElixirFunction("FunctionClauseError", "__struct__", 1, "public", [{params: (context) => [Type.variablePattern("kv_0")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.kv_0, Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.FunctionClauseError")], [Type.atom("args"), Type.atom("nil")], [Type.atom("arity"), Type.atom("nil")], [Type.atom("clauses"), Type.atom("nil")], [Type.atom("function"), Type.atom("nil")], [Type.atom("kind"), Type.atom("nil")], [Type.atom("module"), Type.atom("nil")]]), Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("val_2")]), Type.variablePattern("map_3")], guards: [], body: (context) => {
return Elixir_Map["merge/2"](context.vars.map_3, Type.map([[context.vars.key_1, context.vars.val_2]]));
}}], context, "-__struct__/1-fun-0-"));
}}]);

Interpreter.defineElixirFunction("Hologram.Component", "__struct__", 0, "public", [{params: (context) => [], guards: [], body: (context) => {
return Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Hologram.Component")], [Type.atom("emitted_context"), Type.map([])], [Type.atom("next_action"), Type.atom("nil")], [Type.atom("next_command"), Type.atom("nil")], [Type.atom("next_page"), Type.atom("nil")], [Type.atom("props"), Type.map([])], [Type.atom("state"), Type.map([])]]);
}}]);

Interpreter.defineElixirFunction("Hologram.Component", "__struct__", 1, "public", [{params: (context) => [Type.variablePattern("kv_0")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.kv_0, Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Hologram.Component")], [Type.atom("emitted_context"), Type.map([])], [Type.atom("next_action"), Type.atom("nil")], [Type.atom("next_command"), Type.atom("nil")], [Type.atom("next_page"), Type.atom("nil")], [Type.atom("props"), Type.map([])], [Type.atom("state"), Type.map([])]]), Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("val_2")]), Type.variablePattern("map_3")], guards: [], body: (context) => {
return Elixir_Map["merge/2"](context.vars.map_3, Type.map([[context.vars.key_1, context.vars.val_2]]));
}}], context, "-__struct__/1-fun-0-"));
}}]);

Interpreter.defineElixirFunction("Hologram.Component.Action", "__struct__", 0, "public", [{params: (context) => [], guards: [], body: (context) => {
return Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Hologram.Component.Action")], [Type.atom("delay"), Type.integer(0n)], [Type.atom("name"), Type.atom("nil")], [Type.atom("params"), Type.map([])], [Type.atom("target"), Type.atom("nil")]]);
}}]);

Interpreter.defineElixirFunction("Hologram.Component.Action", "__struct__", 1, "public", [{params: (context) => [Type.variablePattern("kv_0")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.kv_0, Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Hologram.Component.Action")], [Type.atom("delay"), Type.integer(0n)], [Type.atom("name"), Type.atom("nil")], [Type.atom("params"), Type.map([])], [Type.atom("target"), Type.atom("nil")]]), Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("val_2")]), Type.variablePattern("map_3")], guards: [], body: (context) => {
return Elixir_Map["merge/2"](context.vars.map_3, Type.map([[context.vars.key_1, context.vars.val_2]]));
}}], context, "-__struct__/1-fun-0-"));
}}]);

Interpreter.defineElixirFunction("Hologram.Component.Command", "__struct__", 0, "public", [{params: (context) => [], guards: [], body: (context) => {
return Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Hologram.Component.Command")], [Type.atom("name"), Type.atom("nil")], [Type.atom("params"), Type.map([])], [Type.atom("target"), Type.atom("nil")]]);
}}]);

Interpreter.defineElixirFunction("Hologram.Component.Command", "__struct__", 1, "public", [{params: (context) => [Type.variablePattern("kv_0")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.kv_0, Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Hologram.Component.Command")], [Type.atom("name"), Type.atom("nil")], [Type.atom("params"), Type.map([])], [Type.atom("target"), Type.atom("nil")]]), Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("val_2")]), Type.variablePattern("map_3")], guards: [], body: (context) => {
return Elixir_Map["merge/2"](context.vars.map_3, Type.map([[context.vars.key_1, context.vars.val_2]]));
}}], context, "-__struct__/1-fun-0-"));
}}]);

Interpreter.defineElixirFunction("Hologram.PropError", "message", 1, "public", [{params: (context) => [Type.variablePattern("exception_0")], guards: [], body: (context) => {
return Interpreter.dotOperator(context.vars.exception_0, Type.atom("message"));
}}]);

Interpreter.defineElixirFunction("Hologram.Reflection", "module_name", 1, "public", [{params: (context) => [Type.variablePattern("module_0")], guards: [], body: (context) => {
return Elixir_Enum["join/2"](Elixir_Module["split/1"](context.vars.module_0), Type.bitstring("."));
}}]);

Interpreter.defineElixirFunction("Hologram.Router.Helpers", "encode_param_value", 1, "private", [{params: (context) => [Type.variablePattern("value_0")], guards: [], body: (context) => {
return Elixir_URI["encode/2"](Elixir_String_Chars["to_string/1"](context.vars.value_0), Type.functionCapture("URI", "char_unreserved?", 1, [{params: (context) => [Type.variablePattern("$1")], guards: [], body: (context) => {
return Elixir_URI["char_unreserved?/1"](context.vars["$1"]);
}}], context));
}}]);

Interpreter.defineElixirFunction("Hologram.Router.Helpers", "ensure_no_extra_params!", 2, "private", [{params: (context) => [Type.list([]), Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.atom("ok");
}}, {params: (context) => [Type.variablePattern("remaining_params_0"), Type.variablePattern("page_module_1")], guards: [], body: (context) => {
Interpreter.matchOperator(Erlang["hd/1"](context.vars.remaining_params_0), Type.tuple([Type.variablePattern("key_2"), Type.matchPlaceholder()]), context);
Interpreter.updateVarsToMatchedValues(context);
return Erlang["error/1"](Elixir_ArgumentError["exception/1"](Type.bitstring([Type.bitstringSegment(Type.string("page \""), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](Elixir_Hologram_Reflection["module_name/1"](context.vars.page_module_1)), {type: "binary"}), Type.bitstringSegment(Type.string("\" doesn't expect \""), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.key_2), {type: "binary"}), Type.bitstringSegment(Type.string("\" param"), {type: "binary"})])));
}}]);

Interpreter.defineElixirFunction("Hologram.Router.Helpers", "ensure_param_exists!", 3, "private", [{params: (context) => [Type.variablePattern("params_0"), Type.variablePattern("key_1"), Type.variablePattern("page_module_2")], guards: [], body: (context) => {
return Interpreter.case(Erlang["not/1"](Elixir_Keyword["has_key?/2"](context.vars.params_0, context.vars.key_1)), [{match: Type.atom("false"), guards: [], body: (context) => {
return Type.atom("nil");
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Erlang["error/1"](Elixir_ArgumentError["exception/1"](Type.bitstring([Type.bitstringSegment(Type.string("page \""), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](Elixir_Hologram_Reflection["module_name/1"](context.vars.page_module_2)), {type: "binary"}), Type.bitstringSegment(Type.string("\" expects \""), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.key_1), {type: "binary"}), Type.bitstringSegment(Type.string("\" param"), {type: "binary"})])));
}}], context);
}}]);

Interpreter.defineElixirFunction("Hologram.Router.Helpers", "page_path", 1, "public", [{params: (context) => [Type.tuple([Type.variablePattern("module_0"), Type.variablePattern("params_1")])], guards: [], body: (context) => {
return Elixir_Hologram_Router_Helpers["page_path/2"](context.vars.module_0, context.vars.params_1);
}}, {params: (context) => [Type.variablePattern("module_0")], guards: [], body: (context) => {
return Elixir_Hologram_Router_Helpers["page_path/2"](context.vars.module_0, Type.list([]));
}}]);

Interpreter.defineElixirFunction("Hologram.Router.Helpers", "page_path", 2, "public", [{params: (context) => [Type.variablePattern("page_module_0"), Type.variablePattern("params_1")], guards: [(context) => Erlang["is_map/1"](context.vars.params_1)], body: (context) => {
return Elixir_Hologram_Router_Helpers["page_path/2"](context.vars.page_module_0, Erlang_Maps["to_list/1"](context.vars.params_1));
}}, {params: (context) => [Type.variablePattern("page_module_0"), Type.variablePattern("params_1")], guards: [], body: (context) => {
Interpreter.matchOperator(Interpreter.callNamedFunction(context.vars.page_module_0, Type.atom("__params__"), Type.list([]), context), Type.variablePattern("required_params_2"), context);
Interpreter.updateVarsToMatchedValues(context);
Interpreter.matchOperator(Type.tuple([context.vars.params_1, Interpreter.callNamedFunction(context.vars.page_module_0, Type.atom("__route__"), Type.list([]), context)]), Type.variablePattern("initial_acc_3"), context);
Interpreter.updateVarsToMatchedValues(context);
Interpreter.matchOperator(Elixir_Enum["reduce/3"](context.vars.required_params_2, context.vars.initial_acc_3, Type.anonymousFunction(2, [{params: (context) => [Type.variablePattern("$29"), Type.variablePattern("$30")], guards: [], body: (context) => {
return Elixir_Hologram_Router_Helpers["process_param/4"](context.vars["$29"], context.vars["$30"], context.vars.page_module_0, context.vars.params_1);
}}], context, "-page_path/2-fun-0-")), Type.tuple([Type.variablePattern("remaining_params_6"), Type.variablePattern("path_7")]), context);
Interpreter.updateVarsToMatchedValues(context);
Elixir_Hologram_Router_Helpers["ensure_no_extra_params!/2"](context.vars.remaining_params_6, context.vars.page_module_0);
return context.vars.path_7;
}}]);

{
const s0 = Interpreter.literalSet(() => [Type.atom("false"), Type.atom("nil")]);
Interpreter.defineElixirFunction("Hologram.Router.Helpers", "process_optional_param", 5, "private", [{params: (context) => [Type.variablePattern("key_0"), Type.variablePattern("params_acc_1"), Type.variablePattern("path_acc_2"), Type.variablePattern("optional_marker_3"), Type.variablePattern("original_params_4")], guards: [], body: (context) => {
Interpreter.matchOperator(Elixir_Keyword["drop/2"](context.vars.params_acc_1, Type.list([context.vars.key_0])), Type.variablePattern("new_params_5"), context);
Interpreter.updateVarsToMatchedValues(context);
Interpreter.matchOperator(Interpreter.case(Elixir_Keyword["has_key?/2"](context.vars.params_acc_1, context.vars.key_0), [{match: Type.variablePattern("x_6"), guards: [(context) => Type.boolean(s0.has(context.vars.x_6))], body: (context) => {
return Elixir_String["replace/3"](context.vars.path_acc_2, Type.bitstring([Type.bitstringSegment(Type.string("/"), {type: "binary"}), Type.bitstringSegment(context.vars.optional_marker_3, {type: "binary"})]), Type.bitstring(""));
}}, {match: Type.matchPlaceholder(), guards: [], body: (context) => {
Interpreter.matchOperator(Elixir_Hologram_Router_Helpers["encode_param_value/1"](Elixir_Access["get/2"](context.vars.original_params_4, context.vars.key_0)), Type.variablePattern("encoded_value_7"), context);
Interpreter.updateVarsToMatchedValues(context);
return Elixir_String["replace/3"](context.vars.path_acc_2, context.vars.optional_marker_3, context.vars.encoded_value_7);
}}], context), Type.variablePattern("new_path_8"), context);
Interpreter.updateVarsToMatchedValues(context);
return Type.tuple([context.vars.new_params_5, context.vars.new_path_8]);
}}]);
}

{
const s0 = Interpreter.literalSet(() => [Type.atom("false"), Type.atom("nil")]);
Interpreter.defineElixirFunction("Hologram.Router.Helpers", "process_param", 4, "private", [{params: (context) => [Type.tuple([Type.variablePattern("key_0"), Type.matchPlaceholder(), Type.matchPlaceholder()]), Type.tuple([Type.variablePattern("params_acc_3"), Type.variablePattern("path_acc_4")]), Type.variablePattern("page_module_5"), Type.variablePattern("original_params_6")], guards: [], body: (context) => {
Interpreter.matchOperator(Type.bitstring([Type.bitstringSegment(Type.string(":"), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.key_0), {type: "binary"}), Type.bitstringSegment(Type.string("?"), {type: "binary"})]), Type.variablePattern("optional_marker_7"), context);
Interpreter.updateVarsToMatchedValues(context);
globalThis.Hologram.return = Interpreter.case(Elixir_String["contains?/2"](context.vars.path_acc_4, context.vars.optional_marker_7), [{match: Type.variablePattern("x_8"), guards: [(context) => Type.boolean(s0.has(context.vars.x_8))], body: (context) => {
Elixir_Hologram_Router_Helpers["ensure_param_exists!/3"](context.vars.params_acc_3, context.vars.key_0, context.vars.page_module_5);
Interpreter.matchOperator(Elixir_Hologram_Router_Helpers["encode_param_value/1"](Elixir_Access["get/2"](context.vars.original_params_6, context.vars.key_0)), Type.variablePattern("encoded_value_9"), context);
Interpreter.updateVarsToMatchedValues(context);
Interpreter.matchOperator(Elixir_String["replace/3"](context.vars.path_acc_4, Type.bitstring([Type.bitstringSegment(Type.string(":"), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.key_0), {type: "binary"})]), context.vars.encoded_value_9), Type.variablePattern("new_path_10"), context);
Interpreter.updateVarsToMatchedValues(context);
Interpreter.matchOperator(Elixir_Keyword["drop/2"](context.vars.params_acc_3, Type.list([context.vars.key_0])), Type.variablePattern("new_params_11"), context);
Interpreter.updateVarsToMatchedValues(context);
return Type.tuple([context.vars.new_params_11, context.vars.new_path_10]);
}}, {match: Type.matchPlaceholder(), guards: [], body: (context) => {
return Elixir_Hologram_Router_Helpers["process_optional_param/5"](context.vars.key_0, context.vars.params_acc_3, context.vars.path_acc_4, context.vars.optional_marker_7, context.vars.original_params_6);
}}], context);
Interpreter.updateVarsToMatchedValues(context);
return globalThis.Hologram.return;
}}]);
}

Interpreter.defineElixirFunction("IO", "chardata_to_string", 1, "public", [{params: (context) => [Type.variablePattern("string_0")], guards: [(context) => Erlang["is_binary/1"](context.vars.string_0)], body: (context) => {
return context.vars.string_0;
}}, {params: (context) => [Type.variablePattern("list_0")], guards: [(context) => Erlang["is_list/1"](context.vars.list_0)], body: (context) => {
return Elixir_List["to_string/1"](context.vars.list_0);
}}]);

Interpreter.defineElixirFunction("Kernel", "=~", 2, "public", [{params: (context) => [Type.variablePattern("left_0"), Type.bitstring("")], guards: [(context) => Erlang["is_binary/1"](context.vars.left_0)], body: (context) => {
return Type.atom("true");
}}, {params: (context) => [Type.variablePattern("left_0"), Type.variablePattern("right_1")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_binary/1"](context.vars.left_0)) ? Erlang["is_binary/1"](context.vars.right_1) : Type.boolean(false))], body: (context) => {
return Erlang["/=/2"](Erlang_Binary["match/2"](context.vars.left_0, context.vars.right_1), Type.atom("nomatch"));
}}, {params: (context) => [Type.variablePattern("left_0"), Type.variablePattern("right_1")], guards: [(context) => Erlang["is_binary/1"](context.vars.left_0)], body: (context) => {
return Elixir_Regex["match?/2"](context.vars.right_1, context.vars.left_0);
}}]);

Interpreter.defineElixirFunction("Kernel", "struct", 3, "private", [{params: (context) => [Type.variablePattern("struct_0"), Type.list([]), Type.matchPlaceholder()], guards: [(context) => Erlang["is_atom/1"](context.vars.struct_0)], body: (context) => {
return Elixir_Kernel["validate_struct!/3"](Interpreter.callNamedFunction(context.vars.struct_0, Type.atom("__struct__"), Type.list([]), context), context.vars.struct_0, Type.integer(0n));
}}, {params: (context) => [Type.variablePattern("struct_0"), Type.variablePattern("fields_1"), Type.variablePattern("fun_2")], guards: [(context) => Erlang["is_atom/1"](context.vars.struct_0)], body: (context) => {
return Elixir_Kernel["struct/3"](Elixir_Kernel["validate_struct!/3"](Interpreter.callNamedFunction(context.vars.struct_0, Type.atom("__struct__"), Type.list([]), context), context.vars.struct_0, Type.integer(0n)), context.vars.fields_1, context.vars.fun_2);
}}, {params: (context) => [Type.matchPattern(Type.map([[Type.atom("__struct__"), Type.matchPlaceholder()]]), Type.variablePattern("struct_0")), Type.list([]), Type.matchPlaceholder()], guards: [], body: (context) => {
return context.vars.struct_0;
}}, {params: (context) => [Type.matchPattern(Type.map([[Type.atom("__struct__"), Type.matchPlaceholder()]]), Type.variablePattern("struct_0")), Type.variablePattern("fields_1"), Type.variablePattern("fun_2")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.fields_1, context.vars.struct_0, context.vars.fun_2);
}}]);

Interpreter.defineElixirFunction("Kernel", "struct!", 2, "public", [{params: (context) => [Type.variablePattern("struct_0"), Type.variablePattern("fields_1")], guards: [(context) => Erlang["is_atom/1"](context.vars.struct_0)], body: (context) => {
return Elixir_Kernel["validate_struct!/3"](Interpreter.callNamedFunction(context.vars.struct_0, Type.atom("__struct__"), Type.list([context.vars.fields_1]), context), context.vars.struct_0, Type.integer(1n));
}}, {params: (context) => [Type.variablePattern("struct_0"), Type.variablePattern("fields_1")], guards: [(context) => Erlang["is_map/1"](context.vars.struct_0)], body: (context) => {
return Elixir_Kernel["struct/3"](context.vars.struct_0, context.vars.fields_1, Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.atom("__struct__"), Type.matchPlaceholder()]), Type.variablePattern("acc_2")], guards: [], body: (context) => {
return context.vars.acc_2;
}}, {params: (context) => [Type.tuple([Type.variablePattern("key_3"), Type.variablePattern("val_4")]), Type.variablePattern("acc_5")], guards: [], body: (context) => {
return Erlang_Maps["update/3"](context.vars.key_3, context.vars.val_4, context.vars.acc_5);
}}], context, "-struct!/2-fun-0-"));
}}]);

Interpreter.defineElixirFunction("Kernel", "validate_struct!", 3, "private", [{params: (context) => [Type.matchPattern(Type.map([[Type.atom("__struct__"), Type.variablePattern("module_0")]]), Type.variablePattern("struct_1")), Type.variablePattern("module_0"), Type.matchPlaceholder()], guards: [], body: (context) => {
return context.vars.struct_1;
}}, {params: (context) => [Type.map([[Type.atom("__struct__"), Type.variablePattern("struct_name_0")]]), Type.variablePattern("module_1"), Type.variablePattern("arity_2")], guards: [(context) => Erlang["is_atom/1"](context.vars.struct_name_0)], body: (context) => {
Interpreter.matchOperator(Type.bitstring([Type.bitstringSegment(Type.string("expected struct name returned by "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.module_1), {type: "binary"}), Type.bitstringSegment(Type.string(".__struct__/"), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.arity_2), {type: "binary"}), Type.bitstringSegment(Type.string(" to be "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.module_1), {type: "binary"}), Type.bitstringSegment(Type.string(", got: "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.struct_name_0), {type: "binary"})]), Type.variablePattern("error_message_3"), context);
Interpreter.updateVarsToMatchedValues(context);
return Erlang["error/1"](Elixir_ArgumentError["exception/1"](context.vars.error_message_3));
}}, {params: (context) => [Type.variablePattern("expr_0"), Type.variablePattern("module_1"), Type.variablePattern("arity_2")], guards: [], body: (context) => {
Interpreter.matchOperator(Type.bitstring([Type.bitstringSegment(Type.string("expected "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.module_1), {type: "binary"}), Type.bitstringSegment(Type.string(".__struct__/"), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.arity_2), {type: "binary"}), Type.bitstringSegment(Type.string(" to return a map with a :__struct__ "), {type: "binary"}), Type.bitstringSegment(Type.string("key that holds the name of the struct (atom), got: "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.expr_0), {type: "binary"})]), Type.variablePattern("error_message_3"), context);
Interpreter.updateVarsToMatchedValues(context);
return Erlang["error/1"](Elixir_ArgumentError["exception/1"](context.vars.error_message_3));
}}]);

Interpreter.defineElixirFunction("Kernel.Utils", "raise", 1, "public", [{params: (context) => [Type.variablePattern("msg_0")], guards: [(context) => Erlang["is_binary/1"](context.vars.msg_0)], body: (context) => {
return Elixir_RuntimeError["exception/1"](context.vars.msg_0);
}}, {params: (context) => [Type.variablePattern("module_0")], guards: [(context) => Erlang["is_atom/1"](context.vars.module_0)], body: (context) => {
return Interpreter.callNamedFunction(context.vars.module_0, Type.atom("exception"), Type.list([Type.list([])]), context);
}}, {params: (context) => [Type.matchPattern(Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.matchPlaceholder()]]), Type.variablePattern("exception_0"))], guards: [], body: (context) => {
return context.vars.exception_0;
}}, {params: (context) => [Type.variablePattern("other_0")], guards: [], body: (context) => {
return Elixir_ArgumentError["exception/1"](Type.bitstring([Type.bitstringSegment(Type.string("raise/1 and reraise/2 expect a module name, string or exception "), {type: "binary"}), Type.bitstringSegment(Type.string("as the first argument, got: "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.other_0), {type: "binary"})]));
}}]);

Interpreter.defineElixirFunction("KeyError", "__struct__", 0, "public", [{params: (context) => [], guards: [], body: (context) => {
return Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.KeyError")], [Type.atom("key"), Type.atom("nil")], [Type.atom("message"), Type.atom("nil")], [Type.atom("term"), Type.atom("nil")]]);
}}]);

Interpreter.defineElixirFunction("KeyError", "__struct__", 1, "public", [{params: (context) => [Type.variablePattern("kv_0")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.kv_0, Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.KeyError")], [Type.atom("key"), Type.atom("nil")], [Type.atom("message"), Type.atom("nil")], [Type.atom("term"), Type.atom("nil")]]), Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("val_2")]), Type.variablePattern("map_3")], guards: [], body: (context) => {
return Elixir_Map["merge/2"](context.vars.map_3, Type.map([[context.vars.key_1, context.vars.val_2]]));
}}], context, "-__struct__/1-fun-0-"));
}}]);

Interpreter.defineElixirFunction("KeyError", "exception", 1, "public", [{params: (context) => [Type.variablePattern("msg_0")], guards: [(context) => Erlang["is_binary/1"](context.vars.msg_0)], body: (context) => {
return Elixir_KeyError["exception/1"](Type.list([Type.tuple([Type.atom("message"), context.vars.msg_0])]));
}}, {params: (context) => [Type.variablePattern("args_0")], guards: [(context) => Erlang["is_list/1"](context.vars.args_0)], body: (context) => {
return Elixir_Kernel["struct!/2"](Type.atom("Elixir.KeyError"), context.vars.args_0);
}}]);

Interpreter.defineElixirFunction("KeyError", "message", 1, "public", [{params: (context) => [Type.matchPattern(Type.variablePattern("exception_0"), Type.map([[Type.atom("message"), Type.atom("nil")]]))], guards: [], body: (context) => {
return Elixir_KeyError["message/2"](Interpreter.dotOperator(context.vars.exception_0, Type.atom("key")), Interpreter.dotOperator(context.vars.exception_0, Type.atom("term")));
}}, {params: (context) => [Type.map([[Type.atom("message"), Type.variablePattern("message_0")]])], guards: [], body: (context) => {
return context.vars.message_0;
}}]);

Interpreter.defineElixirFunction("KeyError", "message", 2, "private", [{params: (context) => [Type.variablePattern("key_0"), Type.variablePattern("term_1")], guards: [], body: (context) => {
Interpreter.matchOperator(Type.bitstring([Type.bitstringSegment(Type.string("key "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.key_0), {type: "binary"}), Type.bitstringSegment(Type.string(" not found"), {type: "binary"})]), Type.variablePattern("message_2"), context);
Interpreter.updateVarsToMatchedValues(context);
return Interpreter.cond([{condition: (context) => Erlang["==/2"](context.vars.term_1, Type.atom("nil")), body: (context) => {
return context.vars.message_2;
}}, {condition: (context) => Interpreter.case(Erlang["is_atom/1"](context.vars.term_1), [{match: Type.atom("false"), guards: [], body: (context) => {
return Type.atom("false");
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Erlang["is_atom/1"](context.vars.key_0);
}}], context), body: (context) => {
return Type.bitstring([Type.bitstringSegment(context.vars.message_2, {type: "binary"}), Type.bitstringSegment(Type.string(" in: "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.term_1), {type: "binary"}), Type.bitstringSegment(Type.string(" (if instead you want to invoke "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.term_1), {type: "binary"}), Type.bitstringSegment(Type.string("."), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.key_0), {type: "binary"}), Type.bitstringSegment(Type.string("(), "), {type: "binary"}), Type.bitstringSegment(Type.string("make sure to add parentheses after the function name)"), {type: "binary"})]);
}}, {condition: (context) => Type.atom("true"), body: (context) => {
return Elixir_Exception["__format_message_with_term__/2"](Type.bitstring([Type.bitstringSegment(context.vars.message_2, {type: "binary"}), Type.bitstringSegment(Type.string(" in:"), {type: "binary"})]), context.vars.term_1);
}}], context);
}}]);

Interpreter.defineElixirFunction("Keyword", "delete", 2, "public", [{params: (context) => [Type.variablePattern("keywords_0"), Type.variablePattern("key_1")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_list/1"](context.vars.keywords_0)) ? Erlang["is_atom/1"](context.vars.key_1) : Type.boolean(false))], body: (context) => {
return Interpreter.case(Erlang_Lists["keymember/3"](context.vars.key_1, Type.integer(1n), context.vars.keywords_0), [{match: Type.atom("true"), guards: [], body: (context) => {
return Elixir_Keyword["delete_key/2"](context.vars.keywords_0, context.vars.key_1);
}}, {match: Type.matchPlaceholder(), guards: [], body: (context) => {
return context.vars.keywords_0;
}}], context);
}}]);

Interpreter.defineElixirFunction("Keyword", "delete_key", 2, "private", [{params: (context) => [Type.consPattern(Type.tuple([Type.variablePattern("key_0"), Type.matchPlaceholder()]), Type.variablePattern("tail_1")), Type.variablePattern("key_0")], guards: [], body: (context) => {
return Elixir_Keyword["delete_key/2"](context.vars.tail_1, context.vars.key_0);
}}, {params: (context) => [Type.consPattern(Type.matchPattern(Type.tuple([Type.matchPlaceholder(), Type.matchPlaceholder()]), Type.variablePattern("pair_0")), Type.variablePattern("tail_1")), Type.variablePattern("key_2")], guards: [], body: (context) => {
return Interpreter.consOperator(context.vars.pair_0, Elixir_Keyword["delete_key/2"](context.vars.tail_1, context.vars.key_2));
}}, {params: (context) => [Type.list([]), Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.list([]);
}}]);

Interpreter.defineElixirFunction("Keyword", "drop", 2, "public", [{params: (context) => [Type.variablePattern("keywords_0"), Type.variablePattern("keys_1")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_list/1"](context.vars.keywords_0)) ? Erlang["is_list/1"](context.vars.keys_1) : Type.boolean(false))], body: (context) => {
return Erlang_Lists["filter/2"](Type.anonymousFunction(1, [{params: (context) => [Type.tuple([Type.variablePattern("k_2"), Type.matchPlaceholder()])], guards: [], body: (context) => {
return Erlang["not/1"](Elixir_Enum["member?/2"](context.vars.keys_1, context.vars.k_2));
}}], context, "-drop/2-fun-0-"), context.vars.keywords_0);
}}]);

Interpreter.defineElixirFunction("Keyword", "fetch!", 2, "public", [{params: (context) => [Type.variablePattern("keywords_0"), Type.variablePattern("key_1")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_list/1"](context.vars.keywords_0)) ? Erlang["is_atom/1"](context.vars.key_1) : Type.boolean(false))], body: (context) => {
return Interpreter.case(Erlang_Lists["keyfind/3"](context.vars.key_1, Type.integer(1n), context.vars.keywords_0), [{match: Type.tuple([context.vars.key_1, Type.variablePattern("value_2")]), guards: [], body: (context) => {
return context.vars.value_2;
}}, {match: Type.atom("false"), guards: [], body: (context) => {
return Erlang["error/1"](Elixir_KeyError["exception/1"](Type.list([Type.tuple([Type.atom("key"), context.vars.key_1]), Type.tuple([Type.atom("term"), context.vars.keywords_0])])));
}}], context);
}}]);

Interpreter.defineElixirFunction("Keyword", "get", 3, "public", [{params: (context) => [Type.variablePattern("keywords_0"), Type.variablePattern("key_1"), Type.variablePattern("default_2")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_list/1"](context.vars.keywords_0)) ? Erlang["is_atom/1"](context.vars.key_1) : Type.boolean(false))], body: (context) => {
return Interpreter.case(Erlang_Lists["keyfind/3"](context.vars.key_1, Type.integer(1n), context.vars.keywords_0), [{match: Type.tuple([context.vars.key_1, Type.variablePattern("value_3")]), guards: [], body: (context) => {
return context.vars.value_3;
}}, {match: Type.atom("false"), guards: [], body: (context) => {
return context.vars.default_2;
}}], context);
}}]);

Interpreter.defineElixirFunction("Keyword", "has_key?", 2, "public", [{params: (context) => [Type.variablePattern("keywords_0"), Type.variablePattern("key_1")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_list/1"](context.vars.keywords_0)) ? Erlang["is_atom/1"](context.vars.key_1) : Type.boolean(false))], body: (context) => {
return Erlang_Lists["keymember/3"](context.vars.key_1, Type.integer(1n), context.vars.keywords_0);
}}]);

Interpreter.defineElixirFunction("Keyword", "put", 3, "public", [{params: (context) => [Type.variablePattern("keywords_0"), Type.variablePattern("key_1"), Type.variablePattern("value_2")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_list/1"](context.vars.keywords_0)) ? Erlang["is_atom/1"](context.vars.key_1) : Type.boolean(false))], body: (context) => {
return Interpreter.consOperator(Type.tuple([context.vars.key_1, context.vars.value_2]), Elixir_Keyword["delete/2"](context.vars.keywords_0, context.vars.key_1));
}}]);

Interpreter.defineElixirFunction("List", "delete", 2, "public", [{params: (context) => [Type.consPattern(Type.variablePattern("element_0"), Type.variablePattern("list_1")), Type.variablePattern("element_0")], guards: [], body: (context) => {
return context.vars.list_1;
}}, {params: (context) => [Type.consPattern(Type.variablePattern("other_0"), Type.variablePattern("list_1")), Type.variablePattern("element_2")], guards: [], body: (context) => {
return Interpreter.consOperator(context.vars.other_0, Elixir_List["delete/2"](context.vars.list_1, context.vars.element_2));
}}, {params: (context) => [Type.list([]), Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.list([]);
}}]);

Interpreter.defineElixirFunction("List", "to_string", 1, "public", [{params: (context) => [Type.variablePattern("list_0")], guards: [(context) => Erlang["is_list/1"](context.vars.list_0)], body: (context) => {
return Interpreter.try((context) => {
return Erlang_Unicode["characters_to_binary/1"](context.vars.list_0);
}, [{variable: Type.matchPlaceholder(), modules: [Type.atom("Elixir.ArgumentError")], body: (context) => {
return Erlang["error/1"](Elixir_ArgumentError["exception/1"](Type.bitstring([Type.bitstringSegment(Type.string("cannot convert the given list to a string.\n\nTo be converted to a string, a list must either be empty or only\ncontain the following elements:\n\n  * strings\n  * integers representing Unicode code points\n  * a list containing one of these three elements\n\nPlease check the given list or call inspect/1 to get the list representation, got:\n\n"), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.list_0), {type: "binary"}), Type.bitstringSegment(Type.string("\n"), {type: "binary"})])));
}}], [], [{match: Type.variablePattern("result_1"), guards: [(context) => Erlang["is_binary/1"](context.vars.result_1)], body: (context) => {
return context.vars.result_1;
}}, {match: Type.tuple([Type.atom("error"), Type.variablePattern("encoded_2"), Type.variablePattern("rest_3")]), guards: [], body: (context) => {
return Erlang["error/1"](Elixir_UnicodeConversionError["exception/1"](Type.list([Type.tuple([Type.atom("encoded"), context.vars.encoded_2]), Type.tuple([Type.atom("rest"), context.vars.rest_3]), Type.tuple([Type.atom("kind"), Type.atom("invalid")])])));
}}, {match: Type.tuple([Type.atom("incomplete"), Type.variablePattern("encoded_4"), Type.variablePattern("rest_5")]), guards: [], body: (context) => {
return Erlang["error/1"](Elixir_UnicodeConversionError["exception/1"](Type.list([Type.tuple([Type.atom("encoded"), context.vars.encoded_4]), Type.tuple([Type.atom("rest"), context.vars.rest_5]), Type.tuple([Type.atom("kind"), Type.atom("incomplete")])])));
}}], null, context);
}}]);

{
const s0 = Interpreter.literalSet(() => [Type.atom("unquoted_operator"), Type.atom("not_callable")]);
Interpreter.defineElixirFunction("Macro", "classify_atom", 1, "public", [{params: (context) => [Type.variablePattern("atom_0")], guards: [], body: (context) => {
return Interpreter.case(Elixir_Macro["inner_classify/1"](context.vars.atom_0), [{match: Type.atom("alias"), guards: [], body: (context) => {
return Type.atom("alias");
}}, {match: Type.atom("identifier"), guards: [], body: (context) => {
return Type.atom("identifier");
}}, {match: Type.variablePattern("type_1"), guards: [(context) => Type.boolean(s0.has(context.vars.type_1))], body: (context) => {
return Type.atom("unquoted");
}}, {match: Type.matchPlaceholder(), guards: [], body: (context) => {
return Type.atom("quoted");
}}], context);
}}]);
}

{
const s0 = Interpreter.literalSet(() => [Type.atom("%"), Type.atom("%{}"), Type.atom("{}"), Type.atom("\u{3C}\u{3C}>>"), Type.atom("..."), Type.atom(".."), Type.atom("."), Type.atom("..//"), Type.atom("->")]);
const s1 = Interpreter.literalSet(() => [Type.atom("::"), Type.atom("^^^"), Type.atom("~~~"), Type.atom("\u{3C}|>")]);
const s2 = Interpreter.literalSet(() => [Type.atom("false"), Type.atom("nil")]);
Interpreter.defineElixirFunction("Macro", "inner_classify", 1, "private", [{params: (context) => [Type.variablePattern("atom_0")], guards: [(context) => Erlang["is_atom/1"](context.vars.atom_0)], body: (context) => {
globalThis.Hologram.return = Interpreter.cond([{condition: (context) => Type.boolean(s0.has(context.vars.atom_0)), body: (context) => {
return Type.atom("not_callable");
}}, {condition: (context) => Type.boolean(s1.has(context.vars.atom_0)), body: (context) => {
return Type.atom("quoted_operator");
}}, {condition: (context) => Interpreter.case(Elixir_Macro["operator?/2"](context.vars.atom_0, Type.integer(1n)), [{match: Type.atom("false"), guards: [], body: (context) => {
return Elixir_Macro["operator?/2"](context.vars.atom_0, Type.integer(2n));
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Type.atom("true");
}}, {match: Type.variablePattern("other_1"), guards: [], body: (context) => {
return Erlang["error/1"](Type.tuple([Type.atom("badbool"), Type.atom("or"), context.vars.other_1]));
}}], context), body: (context) => {
return Type.atom("unquoted_operator");
}}, {condition: (context) => Type.atom("true"), body: (context) => {
Interpreter.matchOperator(Erlang["atom_to_list/1"](context.vars.atom_0), Type.variablePattern("charlist_2"), context);
Interpreter.updateVarsToMatchedValues(context);
return Interpreter.case(Elixir_Macro["valid_alias?/1"](context.vars.charlist_2), [{match: Type.variablePattern("x_3"), guards: [(context) => Type.boolean(s2.has(context.vars.x_3))], body: (context) => {
return Interpreter.case(Interpreter.callNamedFunction(Erlang_Elixir_Config["identifier_tokenizer/0"](), Type.atom("tokenize"), Type.list([context.vars.charlist_2]), context), [{match: Type.tuple([Type.variablePattern("kind_4"), Type.matchPlaceholder(), Type.list([]), Type.matchPlaceholder(), Type.matchPlaceholder(), Type.variablePattern("special_6")]), guards: [], body: (context) => {
return Interpreter.cond([{condition: (context) => Interpreter.case(Erlang["/=/2"](context.vars.kind_4, Type.atom("identifier")), [{match: Type.atom("false"), guards: [], body: (context) => {
return Erlang_Lists["member/2"](Type.atom("at"), context.vars.special_6);
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Type.atom("true");
}}], context), body: (context) => {
return Type.atom("not_callable");
}}, {condition: (context) => Erlang_Lists["member/2"](Type.atom("nfkc"), context.vars.special_6), body: (context) => {
return Type.atom("other");
}}, {condition: (context) => Type.atom("true"), body: (context) => {
return Type.atom("identifier");
}}], context);
}}, {match: Type.matchPlaceholder(), guards: [], body: (context) => {
return Type.atom("other");
}}], context);
}}, {match: Type.matchPlaceholder(), guards: [], body: (context) => {
return Type.atom("alias");
}}], context);
}}], context);
Interpreter.updateVarsToMatchedValues(context);
return globalThis.Hologram.return;
}}]);
}

Interpreter.defineElixirFunction("Macro", "inspect_atom", 2, "public", [{params: (context) => [Type.variablePattern("x0_0"), Type.variablePattern("x1_1")], guards: [], body: (context) => {
return Elixir_Macro["inspect_atom/3"](context.vars.x0_0, context.vars.x1_1, Type.list([]));
}}]);

{
const s0 = Interpreter.literalSet(() => [Type.bitstring("Elixir"), Type.bitstring("Elixir.Elixir")]);
const s1 = Interpreter.literalSet(() => [Type.atom("identifier"), Type.atom("unquoted_operator"), Type.atom("quoted_operator")]);
const s2 = Interpreter.literalSet(() => [Type.atom("not_callable"), Type.atom("alias")]);
Interpreter.defineElixirFunction("Macro", "inspect_atom", 3, "public", [{params: (context) => [Type.atom("literal"), Type.variablePattern("atom_0"), Type.matchPlaceholder()], guards: [(context) => (Interpreter.toBoolean(Erlang["==/2"](context.vars.atom_0, Type.atom("nil"))) ? Type.boolean(true) : Erlang["is_boolean/1"](context.vars.atom_0))], body: (context) => {
return Erlang["atom_to_binary/1"](context.vars.atom_0);
}}, {params: (context) => [Type.atom("literal"), Type.variablePattern("atom_0"), Type.variablePattern("opts_1")], guards: [(context) => Erlang["is_atom/1"](context.vars.atom_0)], body: (context) => {
Interpreter.matchOperator(Erlang["atom_to_binary/1"](context.vars.atom_0), Type.variablePattern("binary_2"), context);
Interpreter.updateVarsToMatchedValues(context);
globalThis.Hologram.return = Interpreter.case(Elixir_Macro["classify_atom/1"](context.vars.atom_0), [{match: Type.atom("alias"), guards: [], body: (context) => {
return Interpreter.case(context.vars.binary_2, [{match: Type.variablePattern("binary_3"), guards: [(context) => Type.boolean(s0.has(context.vars.binary_3))], body: (context) => {
return context.vars.binary_3;
}}, {match: Type.bitstringPattern([Type.bitstringSegment(Type.string("Elixir.Elixir."), {type: "binary"}), Type.bitstringSegment(Type.matchPlaceholder(), {type: "binary"})]), guards: [], body: (context) => {
return context.vars.binary_2;
}}, {match: Type.bitstringPattern([Type.bitstringSegment(Type.string("Elixir."), {type: "binary"}), Type.bitstringSegment(Type.variablePattern("rest_5"), {type: "binary"})]), guards: [], body: (context) => {
return context.vars.rest_5;
}}], context);
}}, {match: Type.atom("quoted"), guards: [], body: (context) => {
Interpreter.matchOperator(Elixir_Macro["inspect_atom_escape/3"](context.vars.opts_1, context.vars.binary_2, Type.integer(34n)), Type.variablePattern("escaped_6"), context);
Interpreter.updateVarsToMatchedValues(context);
return Erlang["iolist_to_binary/1"](Type.list([Type.integer(58n), Type.integer(34n), context.vars.escaped_6, Type.integer(34n)]));
}}, {match: Type.matchPlaceholder(), guards: [], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string(":"), {type: "binary"}), Type.bitstringSegment(context.vars.binary_2, {type: "binary"})]);
}}], context);
Interpreter.updateVarsToMatchedValues(context);
return globalThis.Hologram.return;
}}, {params: (context) => [Type.atom("key"), Type.variablePattern("atom_0"), Type.variablePattern("opts_1")], guards: [(context) => Erlang["is_atom/1"](context.vars.atom_0)], body: (context) => {
Interpreter.matchOperator(Erlang["atom_to_binary/1"](context.vars.atom_0), Type.variablePattern("binary_2"), context);
Interpreter.updateVarsToMatchedValues(context);
globalThis.Hologram.return = Interpreter.case(Elixir_Macro["classify_atom/1"](context.vars.atom_0), [{match: Type.atom("alias"), guards: [], body: (context) => {
return Erlang["iolist_to_binary/1"](Type.list([Type.integer(34n), context.vars.binary_2, Type.integer(34n), Type.integer(58n)]));
}}, {match: Type.atom("quoted"), guards: [], body: (context) => {
Interpreter.matchOperator(Elixir_Macro["inspect_atom_escape/3"](context.vars.opts_1, context.vars.binary_2, Type.integer(34n)), Type.variablePattern("escaped_3"), context);
Interpreter.updateVarsToMatchedValues(context);
return Erlang["iolist_to_binary/1"](Type.list([Type.integer(34n), context.vars.escaped_3, Type.integer(34n), Type.integer(58n)]));
}}, {match: Type.matchPlaceholder(), guards: [], body: (context) => {
return Erlang["iolist_to_binary/1"](Type.list([context.vars.binary_2, Type.integer(58n)]));
}}], context);
Interpreter.updateVarsToMatchedValues(context);
return globalThis.Hologram.return;
}}, {params: (context) => [Type.atom("remote_call"), Type.variablePattern("atom_0"), Type.variablePattern("opts_1")], guards: [(context) => Erlang["is_atom/1"](context.vars.atom_0)], body: (context) => {
Interpreter.matchOperator(Erlang["atom_to_binary/1"](context.vars.atom_0), Type.variablePattern("binary_2"), context);
Interpreter.updateVarsToMatchedValues(context);
globalThis.Hologram.return = Interpreter.case(Elixir_Macro["inner_classify/1"](context.vars.atom_0), [{match: Type.variablePattern("type_3"), guards: [(context) => Type.boolean(s1.has(context.vars.type_3))], body: (context) => {
return context.vars.binary_2;
}}, {match: Type.variablePattern("type_4"), guards: [], body: (context) => {
Interpreter.matchOperator(Interpreter.case(Type.boolean(s2.has(context.vars.type_4)), [{match: Type.atom("false"), guards: [], body: (context) => {
return Elixir_Macro["inspect_atom_escape/3"](context.vars.opts_1, context.vars.binary_2, Type.integer(34n));
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return context.vars.binary_2;
}}], context), Type.variablePattern("escaped_5"), context);
Interpreter.updateVarsToMatchedValues(context);
return Erlang["iolist_to_binary/1"](Type.list([Type.integer(34n), context.vars.escaped_5, Type.integer(34n)]));
}}], context);
Interpreter.updateVarsToMatchedValues(context);
return globalThis.Hologram.return;
}}]);
}

{
const s0 = Interpreter.literalSet(() => [Type.atom("false"), Type.atom("nil")]);
Interpreter.defineElixirFunction("Macro", "inspect_atom_escape", 3, "private", [{params: (context) => [Type.variablePattern("opts_0"), Type.variablePattern("string_1"), Type.variablePattern("char_2")], guards: [], body: (context) => {
globalThis.Hologram.return = Interpreter.case(Interpreter.matchOperator(Elixir_Access["get/2"](context.vars.opts_0, Type.atom("escape")), Type.variablePattern("escape_3"), context), [{match: Type.variablePattern("x_4"), guards: [(context) => Type.boolean(s0.has(context.vars.x_4))], body: (context) => {
Interpreter.matchOperator(Elixir_Code_Identifier["escape/2"](context.vars.string_1, context.vars.char_2), Type.tuple([Type.variablePattern("escaped_5"), Type.matchPlaceholder()]), context);
Interpreter.updateVarsToMatchedValues(context);
return context.vars.escaped_5;
}}, {match: Type.matchPlaceholder(), guards: [], body: (context) => {
return Interpreter.callAnonymousFunction(context.vars.escape_3, [context.vars.string_1, context.vars.char_2]);
}}], context);
Interpreter.updateVarsToMatchedValues(context);
return globalThis.Hologram.return;
}}]);
}

Interpreter.defineElixirFunction("Macro", "operator?", 2, "public", [{params: (context) => [Type.atom("..//"), Type.integer(3n)], guards: [], body: (context) => {
return Type.atom("true");
}}, {params: (context) => [Type.variablePattern("name_0"), Type.integer(2n)], guards: [(context) => Erlang["is_atom/1"](context.vars.name_0)], body: (context) => {
return Interpreter.case(Erlang["/=/2"](Elixir_Code_Identifier["binary_op/1"](context.vars.name_0), Type.atom("error")), [{match: Type.atom("false"), guards: [], body: (context) => {
return Type.atom("false");
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Erlang["/=/2"](context.vars.name_0, Type.atom("//"));
}}], context);
}}, {params: (context) => [Type.variablePattern("name_0"), Type.integer(1n)], guards: [(context) => Erlang["is_atom/1"](context.vars.name_0)], body: (context) => {
return Erlang["/=/2"](Elixir_Code_Identifier["unary_op/1"](context.vars.name_0), Type.atom("error"));
}}, {params: (context) => [Type.atom(".."), Type.integer(0n)], guards: [], body: (context) => {
return Type.atom("true");
}}, {params: (context) => [Type.atom("..."), Type.integer(0n)], guards: [], body: (context) => {
return Type.atom("true");
}}, {params: (context) => [Type.variablePattern("name_0"), Type.variablePattern("arity_1")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_atom/1"](context.vars.name_0)) ? Erlang["is_integer/1"](context.vars.arity_1) : Type.boolean(false))], body: (context) => {
return Type.atom("false");
}}]);

Interpreter.defineElixirFunction("Macro", "trim_leading_while_valid_identifier", 1, "private", [{params: (context) => [Type.consPattern(Type.variablePattern("char_0"), Type.variablePattern("rest_1"))], guards: [(context) => (Interpreter.toBoolean(Erlang[">=/2"](context.vars.char_0, Type.integer(97n))) ? Erlang["=</2"](context.vars.char_0, Type.integer(122n)) : Type.boolean(false)), (context) => (Interpreter.toBoolean(Erlang[">=/2"](context.vars.char_0, Type.integer(65n))) ? Erlang["=</2"](context.vars.char_0, Type.integer(90n)) : Type.boolean(false)), (context) => (Interpreter.toBoolean(Erlang[">=/2"](context.vars.char_0, Type.integer(48n))) ? Erlang["=</2"](context.vars.char_0, Type.integer(57n)) : Type.boolean(false)), (context) => Erlang["==/2"](context.vars.char_0, Type.integer(95n))], body: (context) => {
return Elixir_Macro["trim_leading_while_valid_identifier/1"](context.vars.rest_1);
}}, {params: (context) => [Type.variablePattern("other_0")], guards: [], body: (context) => {
return context.vars.other_0;
}}]);

Interpreter.defineElixirFunction("Macro", "valid_alias?", 1, "private", [{params: (context) => [Type.consPattern(Type.integer(69n), Type.consPattern(Type.integer(108n), Type.consPattern(Type.integer(105n), Type.consPattern(Type.integer(120n), Type.consPattern(Type.integer(105n), Type.consPattern(Type.integer(114n), Type.variablePattern("rest_0")))))))], guards: [], body: (context) => {
return Elixir_Macro["valid_alias_piece?/1"](context.vars.rest_0);
}}, {params: (context) => [Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.atom("false");
}}]);

Interpreter.defineElixirFunction("Macro", "valid_alias_piece?", 1, "private", [{params: (context) => [Type.consPattern(Type.integer(46n), Type.consPattern(Type.variablePattern("char_0"), Type.variablePattern("rest_1")))], guards: [(context) => (Interpreter.toBoolean(Erlang[">=/2"](context.vars.char_0, Type.integer(65n))) ? Erlang["=</2"](context.vars.char_0, Type.integer(90n)) : Type.boolean(false))], body: (context) => {
return Elixir_Macro["valid_alias_piece?/1"](Elixir_Macro["trim_leading_while_valid_identifier/1"](context.vars.rest_1));
}}, {params: (context) => [Type.list([])], guards: [], body: (context) => {
return Type.atom("true");
}}, {params: (context) => [Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.atom("false");
}}]);

Interpreter.defineElixirFunction("Map", "get", 2, "public", [{params: (context) => [Type.variablePattern("x0_0"), Type.variablePattern("x1_1")], guards: [], body: (context) => {
return Elixir_Map["get/3"](context.vars.x0_0, context.vars.x1_1, Type.atom("nil"));
}}]);

Interpreter.defineElixirFunction("Map", "get", 3, "public", [{params: (context) => [Type.variablePattern("map_0"), Type.variablePattern("key_1"), Type.variablePattern("default_2")], guards: [], body: (context) => {
return Interpreter.case(context.vars.map_0, [{match: Type.map([[context.vars.key_1, Type.variablePattern("value_3")]]), guards: [], body: (context) => {
return context.vars.value_3;
}}, {match: Type.map([]), guards: [], body: (context) => {
return context.vars.default_2;
}}, {match: Type.variablePattern("other_4"), guards: [], body: (context) => {
return Erlang["error/2"](Type.tuple([Type.atom("badmap"), context.vars.other_4]), Type.list([context.vars.map_0, context.vars.key_1, context.vars.default_2]));
}}], context);
}}]);

Interpreter.defineElixirFunction("Map", "merge", 2, "public", [{params: (context) => [Type.variablePattern("map1_0"), Type.variablePattern("map2_1")], guards: [], body: (context) => {
return Erlang_Maps["merge/2"](context.vars.map1_0, context.vars.map2_1);
}}]);

Interpreter.defineElixirFunction("Map", "take", 2, "public", [{params: (context) => [Type.variablePattern("map_0"), Type.variablePattern("keys_1")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_map/1"](context.vars.map_0)) ? Erlang["is_list/1"](context.vars.keys_1) : Type.boolean(false))], body: (context) => {
globalThis.Hologram.return = Elixir_Map["take/3"](context.vars.keys_1, context.vars.map_0, Interpreter.matchOperator(Type.list([]), Type.matchPlaceholder(), context));
Interpreter.updateVarsToMatchedValues(context);
return globalThis.Hologram.return;
}}, {params: (context) => [Type.variablePattern("map_0"), Type.variablePattern("keys_1")], guards: [(context) => Erlang["is_map/1"](context.vars.map_0)], body: (context) => {
Elixir_IO["warn/1"](Type.bitstring([Type.bitstringSegment(Type.string("Map.take/2 with an Enumerable of keys that is not a list is deprecated. "), {type: "binary"}), Type.bitstringSegment(Type.string(" Use a list of keys instead."), {type: "binary"})]));
return Elixir_Map["take/2"](context.vars.map_0, Elixir_Enum["to_list/1"](context.vars.keys_1));
}}, {params: (context) => [Type.variablePattern("non_map_0"), Type.matchPlaceholder()], guards: [], body: (context) => {
return Erlang["error/1"](Type.tuple([Type.atom("badmap"), context.vars.non_map_0]));
}}]);

Interpreter.defineElixirFunction("Map", "take", 3, "private", [{params: (context) => [Type.list([]), Type.matchPlaceholder(), Type.variablePattern("acc_1")], guards: [], body: (context) => {
return Erlang_Maps["from_list/1"](context.vars.acc_1);
}}, {params: (context) => [Type.consPattern(Type.variablePattern("key_0"), Type.variablePattern("rest_1")), Type.variablePattern("map_2"), Type.variablePattern("acc_3")], guards: [], body: (context) => {
Interpreter.matchOperator(Interpreter.case(context.vars.map_2, [{match: Type.map([[context.vars.key_0, Type.variablePattern("value_4")]]), guards: [], body: (context) => {
return Interpreter.consOperator(Type.tuple([context.vars.key_0, context.vars.value_4]), context.vars.acc_3);
}}, {match: Type.map([]), guards: [], body: (context) => {
return context.vars.acc_3;
}}], context), Type.variablePattern("acc_5"), context);
Interpreter.updateVarsToMatchedValues(context);
return Elixir_Map["take/3"](context.vars.rest_1, context.vars.map_2, context.vars.acc_5);
}}]);

Interpreter.defineElixirFunction("MatchError", "__struct__", 0, "public", [{params: (context) => [], guards: [], body: (context) => {
return Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.MatchError")], [Type.atom("term"), Type.atom("nil")]]);
}}]);

Interpreter.defineElixirFunction("MatchError", "__struct__", 1, "public", [{params: (context) => [Type.variablePattern("kv_0")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.kv_0, Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.MatchError")], [Type.atom("term"), Type.atom("nil")]]), Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("val_2")]), Type.variablePattern("map_3")], guards: [], body: (context) => {
return Elixir_Map["merge/2"](context.vars.map_3, Type.map([[context.vars.key_1, context.vars.val_2]]));
}}], context, "-__struct__/1-fun-0-"));
}}]);

Interpreter.defineElixirFunction("MatchError", "message", 1, "public", [{params: (context) => [Type.variablePattern("exception_0")], guards: [], body: (context) => {
return Elixir_Exception["__format_message_with_term__/2"](Type.bitstring("no match of right hand side value:"), Interpreter.dotOperator(context.vars.exception_0, Type.atom("term")));
}}]);

Interpreter.defineElixirFunction("Module", "split", 1, "public", [{params: (context) => [Type.variablePattern("module_0")], guards: [(context) => Erlang["is_atom/1"](context.vars.module_0)], body: (context) => {
globalThis.Hologram.return = Elixir_Module["split/2"](Erlang["atom_to_binary/1"](context.vars.module_0), Interpreter.matchOperator(context.vars.module_0, Type.matchPlaceholder(), context));
Interpreter.updateVarsToMatchedValues(context);
return globalThis.Hologram.return;
}}, {params: (context) => [Type.variablePattern("module_0")], guards: [(context) => Erlang["is_binary/1"](context.vars.module_0)], body: (context) => {
globalThis.Hologram.return = Elixir_Module["split/2"](context.vars.module_0, Interpreter.matchOperator(context.vars.module_0, Type.matchPlaceholder(), context));
Interpreter.updateVarsToMatchedValues(context);
return globalThis.Hologram.return;
}}]);

Interpreter.defineElixirFunction("Module", "split", 2, "private", [{params: (context) => [Type.bitstringPattern([Type.bitstringSegment(Type.string("Elixir."), {type: "binary"}), Type.bitstringSegment(Type.variablePattern("name_0"), {type: "binary"})]), Type.matchPlaceholder()], guards: [], body: (context) => {
return Elixir_String["split/2"](context.vars.name_0, Type.bitstring("."));
}}, {params: (context) => [Type.matchPlaceholder(), Type.variablePattern("original_1")], guards: [], body: (context) => {
return Erlang["error/1"](Elixir_ArgumentError["exception/1"](Type.bitstring([Type.bitstringSegment(Type.string("expected an Elixir module, got: "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.original_1), {type: "binary"})])));
}}]);

Interpreter.defineElixirFunction("Protocol", "__concat__", 2, "public", [{params: (context) => [Type.variablePattern("left_0"), Type.variablePattern("right_1")], guards: [], body: (context) => {
return Erlang["binary_to_atom/1"](Type.bitstring([Type.bitstringSegment(Elixir_Protocol["ensure_prefix/1"](Erlang["atom_to_binary/1"](context.vars.left_0)), {type: "binary"}), Type.bitstringSegment(Type.string("."), {type: "binary"}), Type.bitstringSegment(Elixir_Protocol["remove_prefix/1"](Erlang["atom_to_binary/1"](context.vars.right_1)), {type: "binary"})]));
}}]);

Interpreter.defineElixirFunction("Protocol", "ensure_prefix", 1, "private", [{params: (context) => [Type.matchPattern(Type.bitstringPattern([Type.bitstringSegment(Type.string("Elixir."), {type: "binary"}), Type.bitstringSegment(Type.matchPlaceholder(), {type: "binary"})]), Type.variablePattern("left_0"))], guards: [], body: (context) => {
return context.vars.left_0;
}}, {params: (context) => [Type.variablePattern("left_0")], guards: [], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string("Elixir."), {type: "binary"}), Type.bitstringSegment(context.vars.left_0, {type: "binary"})]);
}}]);

Interpreter.defineElixirFunction("Protocol", "remove_prefix", 1, "private", [{params: (context) => [Type.bitstringPattern([Type.bitstringSegment(Type.string("Elixir."), {type: "binary"}), Type.bitstringSegment(Type.variablePattern("right_0"), {type: "binary"})])], guards: [], body: (context) => {
return context.vars.right_0;
}}, {params: (context) => [Type.variablePattern("right_0")], guards: [], body: (context) => {
return context.vars.right_0;
}}]);

Interpreter.defineElixirFunction("Protocol.UndefinedError", "__struct__", 0, "public", [{params: (context) => [], guards: [], body: (context) => {
return Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.Protocol.UndefinedError")], [Type.atom("description"), Type.bitstring("")], [Type.atom("protocol"), Type.atom("nil")], [Type.atom("value"), Type.atom("nil")]]);
}}]);

Interpreter.defineElixirFunction("Protocol.UndefinedError", "__struct__", 1, "public", [{params: (context) => [Type.variablePattern("kv_0")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.kv_0, Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.Protocol.UndefinedError")], [Type.atom("description"), Type.bitstring("")], [Type.atom("protocol"), Type.atom("nil")], [Type.atom("value"), Type.atom("nil")]]), Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("val_2")]), Type.variablePattern("map_3")], guards: [], body: (context) => {
return Elixir_Map["merge/2"](context.vars.map_3, Type.map([[context.vars.key_1, context.vars.val_2]]));
}}], context, "-__struct__/1-fun-0-"));
}}]);

Interpreter.defineElixirFunction("Protocol.UndefinedError", "exception", 1, "public", [{params: (context) => [Type.variablePattern("args_0")], guards: [(context) => Erlang["is_list/1"](context.vars.args_0)], body: (context) => {
return Elixir_Kernel["struct!/2"](Type.atom("Elixir.Protocol.UndefinedError"), context.vars.args_0);
}}]);

Interpreter.defineElixirFunction("Protocol.UndefinedError", "maybe_available", 1, "private", [{params: (context) => [Type.variablePattern("protocol_0")], guards: [], body: (context) => {
return Interpreter.case(Interpreter.callNamedFunction(context.vars.protocol_0, Type.atom("__protocol__"), Type.list([Type.atom("impls")]), context), [{match: Type.tuple([Type.atom("consolidated"), Type.list([])]), guards: [], body: (context) => {
return Type.bitstring(". There are no implementations for this protocol.");
}}, {match: Type.tuple([Type.atom("consolidated"), Type.variablePattern("types_1")]), guards: [], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string(". This protocol is implemented for: "), {type: "binary"}), Type.bitstringSegment(Elixir_Enum["map_join/3"](context.vars.types_1, Type.bitstring(", "), Type.functionCapture("Kernel", "inspect", 1, [{params: (context) => [Type.variablePattern("$1")], guards: [], body: (context) => {
return Elixir_Kernel["inspect/1"](context.vars["$1"]);
}}], context)), {type: "binary"})]);
}}, {match: Type.atom("not_consolidated"), guards: [], body: (context) => {
return Type.bitstring("");
}}], context);
}}]);

Interpreter.defineElixirFunction("Protocol.UndefinedError", "maybe_description", 1, "private", [{params: (context) => [Type.bitstring("")], guards: [], body: (context) => {
return Type.bitstring("");
}}, {params: (context) => [Type.variablePattern("description_0")], guards: [], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string(", "), {type: "binary"}), Type.bitstringSegment(context.vars.description_0, {type: "binary"})]);
}}]);

Interpreter.defineElixirFunction("Protocol.UndefinedError", "message", 1, "public", [{params: (context) => [Type.map([[Type.atom("description"), Type.variablePattern("description_2")], [Type.atom("protocol"), Type.variablePattern("protocol_0")], [Type.atom("value"), Type.variablePattern("value_1")]])], guards: [], body: (context) => {
Interpreter.matchOperator(Elixir_String["replace/3"](Elixir_Kernel["inspect/2"](context.vars.value_1, Type.list([Type.tuple([Type.atom("pretty"), Type.atom("true")])])), Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Regex")], [Type.atom("opts"), Type.list([Type.atom("multiline")])], [Type.atom("re_pattern"), ((context) => {
Interpreter.matchOperator(Erlang_Re["compile/2"](Type.bitstring("^(?=.+)"), Type.list([Type.atom("multiline")])), Type.tuple([Type.atom("ok"), Type.variablePattern("pattern_3")]), context);
Interpreter.updateVarsToMatchedValues(context);
return context.vars.pattern_3;
})(context)], [Type.atom("source"), Type.bitstring("^(?=.+)")]]), Type.bitstring("    ")), Type.variablePattern("inspected_4"), context);
Interpreter.updateVarsToMatchedValues(context);
return Type.bitstring([Type.bitstringSegment(Type.string("protocol "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.protocol_0), {type: "binary"}), Type.bitstringSegment(Type.string(" not implemented for "), {type: "binary"}), Type.bitstringSegment(Elixir_Protocol_UndefinedError["value_type/1"](context.vars.value_1), {type: "binary"}), Type.bitstringSegment(Elixir_Protocol_UndefinedError["maybe_description/1"](context.vars.description_2), {type: "binary"}), Type.bitstringSegment(Elixir_Protocol_UndefinedError["maybe_available/1"](context.vars.protocol_0), {type: "binary"}), Type.bitstringSegment(Type.string("\n\nGot value:\n\n"), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.inspected_4), {type: "binary"}), Type.bitstringSegment(Type.string("\n"), {type: "binary"})]);
}}]);

Interpreter.defineElixirFunction("Protocol.UndefinedError", "value_type", 1, "private", [{params: (context) => [Type.map([[Type.atom("__struct__"), Type.variablePattern("struct_0")]])], guards: [], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.struct_0), {type: "binary"}), Type.bitstringSegment(Type.string(" (a struct)"), {type: "binary"})]);
}}, {params: (context) => [Type.variablePattern("value_0")], guards: [(context) => Erlang["is_atom/1"](context.vars.value_0)], body: (context) => {
return Type.bitstring("Atom");
}}, {params: (context) => [Type.variablePattern("value_0")], guards: [(context) => Erlang["is_bitstring/1"](context.vars.value_0)], body: (context) => {
return Type.bitstring("BitString");
}}, {params: (context) => [Type.variablePattern("value_0")], guards: [(context) => Erlang["is_float/1"](context.vars.value_0)], body: (context) => {
return Type.bitstring("Float");
}}, {params: (context) => [Type.variablePattern("value_0")], guards: [(context) => Erlang["is_function/1"](context.vars.value_0)], body: (context) => {
return Type.bitstring("Function");
}}, {params: (context) => [Type.variablePattern("value_0")], guards: [(context) => Erlang["is_integer/1"](context.vars.value_0)], body: (context) => {
return Type.bitstring("Integer");
}}, {params: (context) => [Type.variablePattern("value_0")], guards: [(context) => Erlang["is_list/1"](context.vars.value_0)], body: (context) => {
return Type.bitstring("List");
}}, {params: (context) => [Type.variablePattern("value_0")], guards: [(context) => Erlang["is_map/1"](context.vars.value_0)], body: (context) => {
return Type.bitstring("Map");
}}, {params: (context) => [Type.variablePattern("value_0")], guards: [(context) => Erlang["is_pid/1"](context.vars.value_0)], body: (context) => {
return Type.bitstring("PID");
}}, {params: (context) => [Type.variablePattern("value_0")], guards: [(context) => Erlang["is_port/1"](context.vars.value_0)], body: (context) => {
return Type.bitstring("Port");
}}, {params: (context) => [Type.variablePattern("value_0")], guards: [(context) => Erlang["is_reference/1"](context.vars.value_0)], body: (context) => {
return Type.bitstring("Reference");
}}, {params: (context) => [Type.variablePattern("value_0")], guards: [(context) => Erlang["is_tuple/1"](context.vars.value_0)], body: (context) => {
return Type.bitstring("Tuple");
}}]);

Interpreter.defineElixirFunction("Range", "__struct__", 0, "public", [{params: (context) => [], guards: [], body: (context) => {
return Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Range")], [Type.atom("first"), Type.atom("nil")], [Type.atom("last"), Type.atom("nil")], [Type.atom("step"), Type.atom("nil")]]);
}}]);

Interpreter.defineElixirFunction("Range", "__struct__", 1, "public", [{params: (context) => [Type.variablePattern("kv_0")], guards: [], body: (context) => {
Interpreter.matchOperator(Elixir_Enum["reduce/3"](context.vars.kv_0, Type.tuple([Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Range")], [Type.atom("first"), Type.atom("nil")], [Type.atom("last"), Type.atom("nil")], [Type.atom("step"), Type.atom("nil")]]), Type.list([Type.atom("first"), Type.atom("last"), Type.atom("step")])]), Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("val_2")]), Type.tuple([Type.variablePattern("map_3"), Type.variablePattern("keys_4")])], guards: [], body: (context) => {
return Type.tuple([Elixir_Map["merge/2"](context.vars.map_3, Type.map([[context.vars.key_1, context.vars.val_2]])), Elixir_List["delete/2"](context.vars.keys_4, context.vars.key_1)]);
}}], context, "-__struct__/1-fun-0-")), Type.tuple([Type.variablePattern("map_5"), Type.variablePattern("keys_6")]), context);
Interpreter.updateVarsToMatchedValues(context);
return Interpreter.case(context.vars.keys_6, [{match: Type.list([]), guards: [], body: (context) => {
return context.vars.map_5;
}}, {match: Type.matchPlaceholder(), guards: [], body: (context) => {
return Erlang["error/1"](Elixir_ArgumentError["exception/1"](Type.bitstring([Type.bitstringSegment(Type.string("the following keys must also be given when building "), {type: "binary"}), Type.bitstringSegment(Type.string("struct "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](Type.atom("Elixir.Range")), {type: "binary"}), Type.bitstringSegment(Type.string(": "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.keys_6), {type: "binary"})])));
}}], context);
}}]);

Interpreter.defineElixirFunction("Range", "new", 3, "public", [{params: (context) => [Type.variablePattern("first_0"), Type.variablePattern("last_1"), Type.variablePattern("step_2")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_integer/1"](context.vars.first_0)) && Interpreter.toBoolean(Erlang["is_integer/1"](context.vars.last_1)) && Interpreter.toBoolean(Erlang["is_integer/1"](context.vars.step_2)) ? Erlang["/=/2"](context.vars.step_2, Type.integer(0n)) : Type.boolean(false))], body: (context) => {
return Elixir_Range["__struct__/1"](Type.list([Type.tuple([Type.atom("first"), context.vars.first_0]), Type.tuple([Type.atom("last"), context.vars.last_1]), Type.tuple([Type.atom("step"), context.vars.step_2])]));
}}, {params: (context) => [Type.variablePattern("first_0"), Type.variablePattern("last_1"), Type.variablePattern("step_2")], guards: [], body: (context) => {
return Erlang["error/1"](Elixir_ArgumentError["exception/1"](Type.bitstring([Type.bitstringSegment(Type.string("ranges (first..last//step) expect both sides to be integers and the step to be a "), {type: "binary"}), Type.bitstringSegment(Type.string("non-zero integer, got: "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.first_0), {type: "binary"}), Type.bitstringSegment(Type.string(".."), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.last_1), {type: "binary"}), Type.bitstringSegment(Type.string("//"), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.step_2), {type: "binary"})])));
}}]);

Interpreter.defineElixirFunction("Range", "to_list", 1, "public", [{params: (context) => [Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Range")], [Type.atom("first"), Type.variablePattern("first_0")], [Type.atom("last"), Type.variablePattern("last_1")], [Type.atom("step"), Type.variablePattern("step_2")]])], guards: [(context) => (Interpreter.toBoolean(Erlang[">/2"](context.vars.step_2, Type.integer(0n))) ? Erlang["=</2"](context.vars.first_0, context.vars.last_1) : Type.boolean(false)), (context) => (Interpreter.toBoolean(Erlang["</2"](context.vars.step_2, Type.integer(0n))) ? Erlang[">=/2"](context.vars.first_0, context.vars.last_1) : Type.boolean(false))], body: (context) => {
return Erlang_Lists["seq/3"](context.vars.first_0, context.vars.last_1, context.vars.step_2);
}}, {params: (context) => [Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Range")], [Type.atom("first"), Type.matchPlaceholder()], [Type.atom("last"), Type.matchPlaceholder()], [Type.atom("step"), Type.matchPlaceholder()]])], guards: [], body: (context) => {
return Type.list([]);
}}, {params: (context) => [Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Range")], [Type.atom("first"), Type.variablePattern("first_0")], [Type.atom("last"), Type.variablePattern("last_1")]])], guards: [], body: (context) => {
Interpreter.matchOperator(Interpreter.case(Erlang["=</2"](context.vars.first_0, context.vars.last_1), [{match: Type.atom("false"), guards: [], body: (context) => {
return Type.integer(-1n);
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Type.integer(1n);
}}], context), Type.variablePattern("step_2"), context);
Interpreter.updateVarsToMatchedValues(context);
return Erlang_Lists["seq/3"](context.vars.first_0, context.vars.last_1, context.vars.step_2);
}}]);

Interpreter.defineElixirFunction("Regex", "__struct__", 0, "public", [{params: (context) => [], guards: [], body: (context) => {
return Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Regex")], [Type.atom("opts"), Type.list([])], [Type.atom("re_pattern"), Type.atom("nil")], [Type.atom("source"), Type.bitstring("")]]);
}}]);

Interpreter.defineElixirFunction("Regex", "__struct__", 1, "public", [{params: (context) => [Type.variablePattern("kv_0")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.kv_0, Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Regex")], [Type.atom("opts"), Type.list([])], [Type.atom("re_pattern"), Type.atom("nil")], [Type.atom("source"), Type.bitstring("")]]), Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("val_2")]), Type.variablePattern("map_3")], guards: [], body: (context) => {
return Elixir_Map["merge/2"](context.vars.map_3, Type.map([[context.vars.key_1, context.vars.val_2]]));
}}], context, "-__struct__/1-fun-0-"));
}}]);

{
const s0 = Interpreter.literalSet(() => [Type.atom("false"), Type.atom("nil")]);
Interpreter.defineElixirFunction("Regex", "do_split", 6, "private", [{params: (context) => [Type.matchPlaceholder(), Type.variablePattern("string_0"), Type.variablePattern("offset_1"), Type.matchPlaceholder(), Type.atom("true"), Type.matchPlaceholder()], guards: [(context) => Erlang["=</2"](Erlang["byte_size/1"](context.vars.string_0), context.vars.offset_1)], body: (context) => {
return Type.list([]);
}}, {params: (context) => [Type.matchPlaceholder(), Type.variablePattern("string_0"), Type.variablePattern("offset_1"), Type.integer(1n), Type.matchPlaceholder(), Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.list([Erlang["binary_part/3"](context.vars.string_0, context.vars.offset_1, Erlang["-/2"](Erlang["byte_size/1"](context.vars.string_0), context.vars.offset_1))]);
}}, {params: (context) => [Type.list([]), Type.variablePattern("string_0"), Type.variablePattern("offset_1"), Type.matchPlaceholder(), Type.matchPlaceholder(), Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.list([Erlang["binary_part/3"](context.vars.string_0, context.vars.offset_1, Erlang["-/2"](Erlang["byte_size/1"](context.vars.string_0), context.vars.offset_1))]);
}}, {params: (context) => [Type.consPattern(Type.consPattern(Type.tuple([Type.variablePattern("pos_0"), Type.matchPlaceholder()]), Type.variablePattern("h_1")), Type.variablePattern("t_2")), Type.variablePattern("string_3"), Type.variablePattern("offset_4"), Type.variablePattern("counter_5"), Type.variablePattern("trim_6"), Type.variablePattern("with_captures_7")], guards: [(context) => Erlang["</2"](Erlang["-/2"](context.vars.pos_0, context.vars.offset_4), Type.integer(0n))], body: (context) => {
return Elixir_Regex["do_split/6"](Interpreter.consOperator(context.vars.h_1, context.vars.t_2), context.vars.string_3, context.vars.offset_4, context.vars.counter_5, context.vars.trim_6, context.vars.with_captures_7);
}}, {params: (context) => [Type.consPattern(Type.list([]), Type.variablePattern("t_0")), Type.variablePattern("string_1"), Type.variablePattern("offset_2"), Type.variablePattern("counter_3"), Type.variablePattern("trim_4"), Type.variablePattern("with_captures_5")], guards: [], body: (context) => {
return Elixir_Regex["do_split/6"](context.vars.t_0, context.vars.string_1, context.vars.offset_2, context.vars.counter_3, context.vars.trim_4, context.vars.with_captures_5);
}}, {params: (context) => [Type.consPattern(Type.consPattern(Type.tuple([Type.variablePattern("pos_0"), Type.variablePattern("length_1")]), Type.variablePattern("h_2")), Type.variablePattern("t_3")), Type.variablePattern("string_4"), Type.variablePattern("offset_5"), Type.variablePattern("counter_6"), Type.variablePattern("trim_7"), Type.atom("true")], guards: [], body: (context) => {
Interpreter.matchOperator(Erlang["+/2"](context.vars.pos_0, context.vars.length_1), Type.variablePattern("new_offset_8"), context);
Interpreter.updateVarsToMatchedValues(context);
Interpreter.matchOperator(Erlang["-/2"](context.vars.pos_0, context.vars.offset_5), Type.variablePattern("keep_9"), context);
Interpreter.updateVarsToMatchedValues(context);
Interpreter.matchOperator(context.vars.string_4, Type.bitstringPattern([Type.bitstringSegment(Type.matchPlaceholder(), {size: context.vars.offset_5, type: "binary"}), Type.bitstringSegment(Type.variablePattern("part_10"), {size: context.vars.keep_9, type: "binary"}), Type.bitstringSegment(Type.variablePattern("match_11"), {size: context.vars.length_1, type: "binary"}), Type.bitstringSegment(Type.matchPlaceholder(), {type: "binary"})]), context);
Interpreter.updateVarsToMatchedValues(context);
return Interpreter.cond([{condition: (context) => Interpreter.case(Erlang["==/2"](context.vars.keep_9, Type.integer(0n)), [{match: Type.atom("false"), guards: [], body: (context) => {
return Type.atom("false");
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Interpreter.case(Erlang["/=/2"](context.vars.offset_5, Type.integer(0n)), [{match: Type.atom("false"), guards: [], body: (context) => {
return Type.atom("false");
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Erlang["==/2"](context.vars.length_1, Type.integer(0n));
}}], context);
}}], context), body: (context) => {
return Elixir_Regex["do_split/6"](Interpreter.consOperator(context.vars.h_2, context.vars.t_3), context.vars.string_4, context.vars.new_offset_8, Erlang["-/2"](context.vars.counter_6, Type.integer(1n)), context.vars.trim_7, Type.atom("true"));
}}, {condition: (context) => Interpreter.case(Erlang["==/2"](context.vars.keep_9, Type.integer(0n)), [{match: Type.atom("false"), guards: [], body: (context) => {
return Type.atom("false");
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return context.vars.trim_7;
}}], context), body: (context) => {
return Interpreter.consOperator(context.vars.match_11, Elixir_Regex["do_split/6"](Interpreter.consOperator(context.vars.h_2, context.vars.t_3), context.vars.string_4, context.vars.new_offset_8, Erlang["-/2"](context.vars.counter_6, Type.integer(1n)), context.vars.trim_7, Type.atom("true")));
}}, {condition: (context) => Type.atom("true"), body: (context) => {
return Interpreter.consOperator(context.vars.part_10, Interpreter.consOperator(context.vars.match_11, Elixir_Regex["do_split/6"](Interpreter.consOperator(context.vars.h_2, context.vars.t_3), context.vars.string_4, context.vars.new_offset_8, Erlang["-/2"](context.vars.counter_6, Type.integer(1n)), context.vars.trim_7, Type.atom("true"))));
}}], context);
}}, {params: (context) => [Type.consPattern(Type.consPattern(Type.tuple([Type.variablePattern("pos_0"), Type.variablePattern("length_1")]), Type.variablePattern("h_2")), Type.variablePattern("t_3")), Type.variablePattern("string_4"), Type.variablePattern("offset_5"), Type.variablePattern("counter_6"), Type.variablePattern("trim_7"), Type.atom("false")], guards: [], body: (context) => {
Interpreter.matchOperator(Erlang["+/2"](context.vars.pos_0, context.vars.length_1), Type.variablePattern("new_offset_8"), context);
Interpreter.updateVarsToMatchedValues(context);
Interpreter.matchOperator(Erlang["-/2"](context.vars.pos_0, context.vars.offset_5), Type.variablePattern("keep_9"), context);
Interpreter.updateVarsToMatchedValues(context);
globalThis.Hologram.return = Interpreter.case(Interpreter.case(Erlang["==/2"](context.vars.keep_9, Type.integer(0n)), [{match: Type.atom("false"), guards: [], body: (context) => {
return Type.atom("false");
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Interpreter.case(context.vars.trim_7, [{match: Type.atom("false"), guards: [], body: (context) => {
return Interpreter.case(Erlang["/=/2"](context.vars.offset_5, Type.integer(0n)), [{match: Type.atom("false"), guards: [], body: (context) => {
return Type.atom("false");
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Erlang["==/2"](context.vars.length_1, Type.integer(0n));
}}], context);
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Type.atom("true");
}}, {match: Type.variablePattern("other_10"), guards: [], body: (context) => {
return Erlang["error/1"](Type.tuple([Type.atom("badbool"), Type.atom("or"), context.vars.other_10]));
}}], context);
}}], context), [{match: Type.variablePattern("x_11"), guards: [(context) => Type.boolean(s0.has(context.vars.x_11))], body: (context) => {
Interpreter.matchOperator(context.vars.string_4, Type.bitstringPattern([Type.bitstringSegment(Type.matchPlaceholder(), {size: context.vars.offset_5, type: "binary"}), Type.bitstringSegment(Type.variablePattern("part_12"), {size: context.vars.keep_9, type: "binary"}), Type.bitstringSegment(Type.matchPlaceholder(), {type: "binary"})]), context);
Interpreter.updateVarsToMatchedValues(context);
return Interpreter.consOperator(context.vars.part_12, Elixir_Regex["do_split/6"](Interpreter.consOperator(context.vars.h_2, context.vars.t_3), context.vars.string_4, context.vars.new_offset_8, Erlang["-/2"](context.vars.counter_6, Type.integer(1n)), context.vars.trim_7, Type.atom("false")));
}}, {match: Type.matchPlaceholder(), guards: [], body: (context) => {
return Elixir_Regex["do_split/6"](Interpreter.consOperator(context.vars.h_2, context.vars.t_3), context.vars.string_4, context.vars.new_offset_8, context.vars.counter_6, context.vars.trim_7, Type.atom("false"));
}}], context);
Interpreter.updateVarsToMatchedValues(context);
return globalThis.Hologram.return;
}}]);
}

Interpreter.defineElixirFunction("Regex", "match?", 2, "public", [{params: (context) => [Type.matchPattern(Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Regex")]]), Type.variablePattern("regex_0")), Type.variablePattern("string_1")], guards: [(context) => Erlang["is_binary/1"](context.vars.string_1)], body: (context) => {
return Erlang["==/2"](Elixir_Regex["safe_run/3"](context.vars.regex_0, context.vars.string_1, Type.list([Type.tuple([Type.atom("capture"), Type.atom("none")])])), Type.atom("match"));
}}]);

Interpreter.defineElixirFunction("Regex", "maybe_import_pattern", 1, "private", [{params: (context) => [Type.matchPattern(Type.tuple([Type.atom("re_exported_pattern"), Type.matchPlaceholder(), Type.matchPlaceholder(), Type.matchPlaceholder(), Type.matchPlaceholder()]), Type.variablePattern("exported_0"))], guards: [], body: (context) => {
return Erlang_Re["import/1"](context.vars.exported_0);
}}, {params: (context) => [Type.variablePattern("pattern_0")], guards: [], body: (context) => {
return context.vars.pattern_0;
}}]);

Interpreter.defineElixirFunction("Regex", "parts_to_index", 1, "private", [{params: (context) => [Type.atom("infinity")], guards: [], body: (context) => {
return Type.integer(0n);
}}, {params: (context) => [Type.variablePattern("n_0")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_integer/1"](context.vars.n_0)) ? Erlang[">/2"](context.vars.n_0, Type.integer(0n)) : Type.boolean(false))], body: (context) => {
return context.vars.n_0;
}}]);

Interpreter.defineElixirFunction("Regex", "safe_run", 3, "private", [{params: (context) => [Type.matchPattern(Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Regex")], [Type.atom("re_pattern"), Type.variablePattern("re_pattern_0")]]), Type.variablePattern("regex_1")), Type.variablePattern("string_2"), Type.variablePattern("options_3")], guards: [], body: (context) => {
globalThis.Hologram.return = Interpreter.with((context) => {
Interpreter.matchOperator(context.vars.regex_1, Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Regex")], [Type.atom("opts"), Type.variablePattern("compile_opts_5")], [Type.atom("source"), Type.variablePattern("source_4")]]), context);
Interpreter.updateVarsToMatchedValues(context);
return Erlang_Re["run/3"](context.vars.string_2, context.vars.source_4, Erlang["++/2"](context.vars.compile_opts_5, context.vars.options_3));
}, [{match: Type.atom("true"), guards: [], expression: (context) => Erlang[">=/2"](Erlang["system_info/1"](Type.atom("otp_release")), Type.list([Type.integer(50n), Type.integer(56n)]))}, {match: Type.tuple([Type.atom("re_pattern"), Type.matchPlaceholder(), Type.matchPlaceholder(), Type.matchPlaceholder(), Type.bitstringPattern([Type.bitstringSegment(Type.matchPlaceholder(), {type: "bitstring"})])]), guards: [], expression: (context) => context.vars.re_pattern_0}], [{match: Type.matchPlaceholder(), guards: [], body: (context) => {
return Erlang_Re["run/3"](context.vars.string_2, Elixir_Regex["maybe_import_pattern/1"](context.vars.re_pattern_0), context.vars.options_3);
}}], context);
Interpreter.updateVarsToMatchedValues(context);
return globalThis.Hologram.return;
}}]);

{
const s0 = Interpreter.literalSet(() => [Type.atom("false"), Type.atom("nil")]);
Interpreter.defineElixirFunction("Regex", "split", 3, "public", [{params: (context) => [Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Regex")]]), Type.bitstring(""), Type.variablePattern("opts_0")], guards: [], body: (context) => {
return Interpreter.case(Elixir_Keyword["get/3"](context.vars.opts_0, Type.atom("trim"), Type.atom("false")), [{match: Type.variablePattern("x_1"), guards: [(context) => Type.boolean(s0.has(context.vars.x_1))], body: (context) => {
return Type.list([Type.bitstring("")]);
}}, {match: Type.matchPlaceholder(), guards: [], body: (context) => {
return Type.list([]);
}}], context);
}}, {params: (context) => [Type.matchPattern(Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Regex")]]), Type.variablePattern("regex_0")), Type.variablePattern("string_1"), Type.variablePattern("opts_2")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_binary/1"](context.vars.string_1)) ? Erlang["is_list/1"](context.vars.opts_2) : Type.boolean(false))], body: (context) => {
Interpreter.matchOperator(Elixir_Keyword["get/3"](context.vars.opts_2, Type.atom("on"), Type.atom("first")), Type.variablePattern("on_3"), context);
Interpreter.updateVarsToMatchedValues(context);
globalThis.Hologram.return = Interpreter.case(Elixir_Regex["safe_run/3"](context.vars.regex_0, context.vars.string_1, Type.list([Type.atom("global"), Type.tuple([Type.atom("capture"), context.vars.on_3])])), [{match: Type.tuple([Type.atom("match"), Type.variablePattern("matches_4")]), guards: [], body: (context) => {
Interpreter.matchOperator(Elixir_Regex["parts_to_index/1"](Elixir_Keyword["get/3"](context.vars.opts_2, Type.atom("parts"), Type.atom("infinity"))), Type.variablePattern("index_5"), context);
Interpreter.updateVarsToMatchedValues(context);
Interpreter.matchOperator(Elixir_Keyword["get/3"](context.vars.opts_2, Type.atom("trim"), Type.atom("false")), Type.variablePattern("trim_6"), context);
Interpreter.updateVarsToMatchedValues(context);
Interpreter.matchOperator(Elixir_Keyword["get/3"](context.vars.opts_2, Type.atom("include_captures"), Type.atom("false")), Type.variablePattern("include_captures_7"), context);
Interpreter.updateVarsToMatchedValues(context);
return Elixir_Regex["do_split/6"](context.vars.matches_4, context.vars.string_1, Type.integer(0n), context.vars.index_5, context.vars.trim_6, context.vars.include_captures_7);
}}, {match: Type.atom("match"), guards: [], body: (context) => {
return Type.list([context.vars.string_1]);
}}, {match: Type.atom("nomatch"), guards: [], body: (context) => {
return Type.list([context.vars.string_1]);
}}], context);
Interpreter.updateVarsToMatchedValues(context);
return globalThis.Hologram.return;
}}]);
}

Interpreter.defineElixirFunction("RuntimeError", "__struct__", 0, "public", [{params: (context) => [], guards: [], body: (context) => {
return Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.RuntimeError")], [Type.atom("message"), Type.bitstring("runtime error")]]);
}}]);

Interpreter.defineElixirFunction("RuntimeError", "__struct__", 1, "public", [{params: (context) => [Type.variablePattern("kv_0")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.kv_0, Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.RuntimeError")], [Type.atom("message"), Type.bitstring("runtime error")]]), Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("val_2")]), Type.variablePattern("map_3")], guards: [], body: (context) => {
return Elixir_Map["merge/2"](context.vars.map_3, Type.map([[context.vars.key_1, context.vars.val_2]]));
}}], context, "-__struct__/1-fun-0-"));
}}]);

Interpreter.defineElixirFunction("RuntimeError", "exception", 1, "public", [{params: (context) => [Type.variablePattern("msg_0")], guards: [(context) => Erlang["is_binary/1"](context.vars.msg_0)], body: (context) => {
return Elixir_RuntimeError["exception/1"](Type.list([Type.tuple([Type.atom("message"), context.vars.msg_0])]));
}}, {params: (context) => [Type.variablePattern("args_0")], guards: [(context) => Erlang["is_list/1"](context.vars.args_0)], body: (context) => {
return Elixir_Kernel["struct!/2"](Type.atom("Elixir.RuntimeError"), context.vars.args_0);
}}]);

Interpreter.defineElixirFunction("RuntimeError", "message", 1, "public", [{params: (context) => [Type.variablePattern("exception_0")], guards: [], body: (context) => {
return Interpreter.dotOperator(context.vars.exception_0, Type.atom("message"));
}}]);

Interpreter.defineElixirFunction("String", "do_splitter", 3, "private", [{params: (context) => [Type.atom("nomatch"), Type.matchPlaceholder(), Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.atom("nil");
}}, {params: (context) => [Type.bitstring(""), Type.matchPlaceholder(), Type.atom("false")], guards: [], body: (context) => {
return Type.tuple([Type.bitstring(""), Type.atom("nomatch")]);
}}, {params: (context) => [Type.bitstring(""), Type.matchPlaceholder(), Type.atom("true")], guards: [], body: (context) => {
return Type.atom("nil");
}}, {params: (context) => [Type.variablePattern("bin_0"), Type.variablePattern("pattern_1"), Type.variablePattern("trim_2")], guards: [], body: (context) => {
return Interpreter.case(Erlang_Binary["split/2"](context.vars.bin_0, context.vars.pattern_1), [{match: Type.list([Type.bitstring(""), Type.variablePattern("second_3")]), guards: [(context) => context.vars.trim_2], body: (context) => {
return Elixir_String["do_splitter/3"](context.vars.second_3, context.vars.pattern_1, context.vars.trim_2);
}}, {match: Type.list([Type.variablePattern("first_4"), Type.variablePattern("second_5")]), guards: [], body: (context) => {
return Type.tuple([context.vars.first_4, context.vars.second_5]);
}}, {match: Type.list([Type.variablePattern("first_6")]), guards: [], body: (context) => {
return Type.tuple([context.vars.first_6, Type.atom("nomatch")]);
}}], context);
}}]);

Interpreter.defineElixirFunction("String", "grapheme_to_binary", 1, "private", [{params: (context) => [Type.variablePattern("cp_0")], guards: [(context) => Erlang["is_integer/1"](context.vars.cp_0)], body: (context) => {
return Type.bitstring([Type.bitstringSegment(context.vars.cp_0, {type: "utf8"})]);
}}, {params: (context) => [Type.variablePattern("gc_0")], guards: [(context) => Erlang["is_list/1"](context.vars.gc_0)], body: (context) => {
return Interpreter.comprehension([{type: "generator", match: Type.variablePattern("cp_1"), guards: [], body: (context) => context.vars.gc_0}], Type.bitstring(""), false, (context) => {
return Type.bitstring([Type.bitstringSegment(context.vars.cp_1, {type: "utf8"})]);
}, context);
}}]);

Interpreter.defineElixirFunction("String", "maybe_compile_pattern", 1, "private", [{params: (context) => [Type.variablePattern("pattern_0")], guards: [(context) => Erlang["is_tuple/1"](context.vars.pattern_0)], body: (context) => {
return context.vars.pattern_0;
}}, {params: (context) => [Type.variablePattern("pattern_0")], guards: [], body: (context) => {
return Erlang_Binary["compile_pattern/1"](context.vars.pattern_0);
}}]);

Interpreter.defineElixirFunction("String", "parts_to_index", 1, "private", [{params: (context) => [Type.atom("infinity")], guards: [], body: (context) => {
return Type.integer(0n);
}}, {params: (context) => [Type.variablePattern("n_0")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_integer/1"](context.vars.n_0)) ? Erlang[">/2"](context.vars.n_0, Type.integer(0n)) : Type.boolean(false))], body: (context) => {
return context.vars.n_0;
}}]);

Interpreter.defineElixirFunction("String", "split", 2, "public", [{params: (context) => [Type.variablePattern("x0_0"), Type.variablePattern("x1_1")], guards: [], body: (context) => {
return Elixir_String["split/3"](context.vars.x0_0, context.vars.x1_1, Type.list([]));
}}]);

{
const s0 = Interpreter.literalSet(() => [Type.atom("false"), Type.atom("nil")]);
Interpreter.defineElixirFunction("String", "split", 3, "public", [{params: (context) => [Type.variablePattern("string_0"), Type.matchPattern(Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Regex")]]), Type.variablePattern("pattern_1")), Type.variablePattern("options_2")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_binary/1"](context.vars.string_0)) ? Erlang["is_list/1"](context.vars.options_2) : Type.boolean(false))], body: (context) => {
return Elixir_Regex["split/3"](context.vars.pattern_1, context.vars.string_0, context.vars.options_2);
}}, {params: (context) => [Type.variablePattern("string_0"), Type.bitstring(""), Type.variablePattern("options_1")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_binary/1"](context.vars.string_0)) ? Erlang["is_list/1"](context.vars.options_1) : Type.boolean(false))], body: (context) => {
Interpreter.matchOperator(Elixir_Keyword["get/3"](context.vars.options_1, Type.atom("parts"), Type.atom("infinity")), Type.variablePattern("parts_2"), context);
Interpreter.updateVarsToMatchedValues(context);
Interpreter.matchOperator(Elixir_String["parts_to_index/1"](context.vars.parts_2), Type.variablePattern("index_3"), context);
Interpreter.updateVarsToMatchedValues(context);
Interpreter.matchOperator(Elixir_Keyword["get/3"](context.vars.options_1, Type.atom("trim"), Type.atom("false")), Type.variablePattern("trim_4"), context);
Interpreter.updateVarsToMatchedValues(context);
return Interpreter.case(Interpreter.case(Erlang["==/2"](context.vars.trim_4, Type.atom("false")), [{match: Type.atom("false"), guards: [], body: (context) => {
return Type.atom("false");
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Erlang["/=/2"](context.vars.index_3, Type.integer(1n));
}}], context), [{match: Type.atom("false"), guards: [], body: (context) => {
return Elixir_String["split_empty/3"](context.vars.string_0, context.vars.trim_4, context.vars.index_3);
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Interpreter.consOperator(Type.bitstring(""), Elixir_String["split_empty/3"](context.vars.string_0, context.vars.trim_4, Erlang["-/2"](context.vars.index_3, Type.integer(1n))));
}}], context);
}}, {params: (context) => [Type.variablePattern("string_0"), Type.list([]), Type.variablePattern("options_1")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_binary/1"](context.vars.string_0)) ? Erlang["is_list/1"](context.vars.options_1) : Type.boolean(false))], body: (context) => {
return Interpreter.case(Interpreter.case(Erlang["==/2"](context.vars.string_0, Type.bitstring("")), [{match: Type.atom("false"), guards: [], body: (context) => {
return Type.atom("false");
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Elixir_Keyword["get/3"](context.vars.options_1, Type.atom("trim"), Type.atom("false"));
}}], context), [{match: Type.variablePattern("x_2"), guards: [(context) => Type.boolean(s0.has(context.vars.x_2))], body: (context) => {
return Type.list([context.vars.string_0]);
}}, {match: Type.matchPlaceholder(), guards: [], body: (context) => {
return Type.list([]);
}}], context);
}}, {params: (context) => [Type.variablePattern("string_0"), Type.variablePattern("pattern_1"), Type.variablePattern("options_2")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_binary/1"](context.vars.string_0)) ? Erlang["is_list/1"](context.vars.options_2) : Type.boolean(false))], body: (context) => {
Interpreter.matchOperator(Elixir_Keyword["get/3"](context.vars.options_2, Type.atom("parts"), Type.atom("infinity")), Type.variablePattern("parts_3"), context);
Interpreter.updateVarsToMatchedValues(context);
Interpreter.matchOperator(Elixir_Keyword["get/3"](context.vars.options_2, Type.atom("trim"), Type.atom("false")), Type.variablePattern("trim_4"), context);
Interpreter.updateVarsToMatchedValues(context);
globalThis.Hologram.return = Interpreter.case(Type.tuple([context.vars.parts_3, context.vars.trim_4]), [{match: Type.tuple([Type.atom("infinity"), Type.atom("false")]), guards: [], body: (context) => {
return Erlang_Binary["split/3"](context.vars.string_0, context.vars.pattern_1, Type.list([Type.atom("global")]));
}}, {match: Type.tuple([Type.atom("infinity"), Type.atom("true")]), guards: [], body: (context) => {
return Erlang_Binary["split/3"](context.vars.string_0, context.vars.pattern_1, Type.list([Type.atom("global"), Type.atom("trim_all")]));
}}, {match: Type.tuple([Type.integer(2n), Type.atom("false")]), guards: [], body: (context) => {
return Erlang_Binary["split/2"](context.vars.string_0, context.vars.pattern_1);
}}, {match: Type.matchPlaceholder(), guards: [], body: (context) => {
Interpreter.matchOperator(Elixir_String["maybe_compile_pattern/1"](context.vars.pattern_1), Type.variablePattern("pattern_5"), context);
Interpreter.updateVarsToMatchedValues(context);
return Elixir_String["split_each/4"](context.vars.string_0, context.vars.pattern_5, context.vars.trim_4, Elixir_String["parts_to_index/1"](context.vars.parts_3));
}}], context);
Interpreter.updateVarsToMatchedValues(context);
return globalThis.Hologram.return;
}}]);
}

Interpreter.defineElixirFunction("String", "split_each", 4, "private", [{params: (context) => [Type.bitstring(""), Type.matchPlaceholder(), Type.atom("true"), Type.integer(1n)], guards: [], body: (context) => {
return Type.list([]);
}}, {params: (context) => [Type.variablePattern("string_0"), Type.matchPlaceholder(), Type.matchPlaceholder(), Type.integer(1n)], guards: [(context) => Erlang["is_binary/1"](context.vars.string_0)], body: (context) => {
return Type.list([context.vars.string_0]);
}}, {params: (context) => [Type.variablePattern("string_0"), Type.variablePattern("pattern_1"), Type.variablePattern("trim_2"), Type.variablePattern("count_3")], guards: [], body: (context) => {
return Interpreter.case(Elixir_String["do_splitter/3"](context.vars.string_0, context.vars.pattern_1, context.vars.trim_2), [{match: Type.tuple([Type.variablePattern("h_4"), Type.variablePattern("t_5")]), guards: [], body: (context) => {
return Interpreter.consOperator(context.vars.h_4, Elixir_String["split_each/4"](context.vars.t_5, context.vars.pattern_1, context.vars.trim_2, Erlang["-/2"](context.vars.count_3, Type.integer(1n))));
}}, {match: Type.atom("nil"), guards: [], body: (context) => {
return Type.list([]);
}}], context);
}}]);

Interpreter.defineElixirFunction("String", "split_empty", 3, "private", [{params: (context) => [Type.bitstring(""), Type.atom("true"), Type.integer(1n)], guards: [], body: (context) => {
return Type.list([]);
}}, {params: (context) => [Type.variablePattern("string_0"), Type.matchPlaceholder(), Type.integer(1n)], guards: [], body: (context) => {
return Type.list([Erlang["iolist_to_binary/1"](context.vars.string_0)]);
}}, {params: (context) => [Type.variablePattern("string_0"), Type.variablePattern("trim_1"), Type.variablePattern("count_2")], guards: [], body: (context) => {
return Interpreter.case(Erlang_Unicode_Util["gc/1"](context.vars.string_0), [{match: Type.list([Type.variablePattern("gc_3")]), guards: [], body: (context) => {
return Interpreter.consOperator(Elixir_String["grapheme_to_binary/1"](context.vars.gc_3), Elixir_String["split_empty/3"](Type.bitstring(""), context.vars.trim_1, Type.integer(1n)));
}}, {match: Type.consPattern(Type.variablePattern("gc_4"), Type.variablePattern("rest_5")), guards: [], body: (context) => {
return Interpreter.consOperator(Elixir_String["grapheme_to_binary/1"](context.vars.gc_4), Elixir_String["split_empty/3"](context.vars.rest_5, context.vars.trim_1, Erlang["-/2"](context.vars.count_2, Type.integer(1n))));
}}, {match: Type.list([]), guards: [], body: (context) => {
return Elixir_String["split_empty/3"](Type.bitstring(""), context.vars.trim_1, Type.integer(1n));
}}, {match: Type.tuple([Type.atom("error"), Type.bitstringPattern([Type.bitstringSegment(Type.variablePattern("byte_6"), {type: "integer"}), Type.bitstringSegment(Type.variablePattern("rest_7"), {type: "bitstring"})])]), guards: [], body: (context) => {
return Interpreter.consOperator(Type.bitstring([Type.bitstringSegment(context.vars.byte_6, {type: "integer"})]), Elixir_String["split_empty/3"](context.vars.rest_7, context.vars.trim_1, Erlang["-/2"](context.vars.count_2, Type.integer(1n))));
}}], context);
}}]);

Interpreter.defineElixirFunction("String.Chars", "impl_for", 1, "public", [{params: (context) => [Type.map([[Type.atom("__struct__"), Type.variablePattern("struct_0")]])], guards: [], body: (context) => {
return Elixir_String_Chars["struct_impl_for/1"](context.vars.struct_0);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_tuple/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.String.Chars.Tuple")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_atom/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.String.Chars.Atom")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_list/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.String.Chars.List")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_map/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.String.Chars.Map")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_bitstring/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.String.Chars.BitString")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_integer/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.String.Chars.Integer")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_float/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.String.Chars.Float")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_function/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.String.Chars.Function")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_pid/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.String.Chars.PID")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_port/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.String.Chars.Port")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.variablePattern("data_0")], guards: [(context) => Erlang["is_reference/1"](context.vars.data_0)], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Type.atom("Elixir.String.Chars.Reference")), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}, {params: (context) => [Type.matchPlaceholder()], guards: [], body: (context) => {
return Type.atom("nil");
}}]);

{
const s0 = Interpreter.literalSet(() => [Type.atom("false"), Type.atom("nil")]);
Interpreter.defineElixirFunction("String.Chars", "impl_for!", 1, "public", [{params: (context) => [Type.variablePattern("data_0")], guards: [], body: (context) => {
return Interpreter.case(Elixir_String_Chars["impl_for/1"](context.vars.data_0), [{match: Type.variablePattern("x_1"), guards: [(context) => Type.boolean(s0.has(context.vars.x_1))], body: (context) => {
return Erlang["error/1"](Elixir_Protocol_UndefinedError["exception/1"](Type.list([Type.tuple([Type.atom("protocol"), Type.atom("Elixir.String.Chars")]), Type.tuple([Type.atom("value"), context.vars.data_0]), Type.tuple([Type.atom("description"), Type.bitstring("")])])));
}}, {match: Type.variablePattern("x_2"), guards: [], body: (context) => {
return context.vars.x_2;
}}], context);
}}]);
}

Interpreter.defineElixirFunction("String.Chars", "struct_impl_for", 1, "private", [{params: (context) => [Type.variablePattern("struct_0")], guards: [], body: (context) => {
return Interpreter.case(Elixir_Code["ensure_compiled/1"](Elixir_Protocol["__concat__/2"](Type.atom("Elixir.String.Chars"), context.vars.struct_0)), [{match: Type.tuple([Type.atom("module"), Type.variablePattern("module_1")]), guards: [], body: (context) => {
return context.vars.module_1;
}}, {match: Type.tuple([Type.atom("error"), Type.matchPlaceholder()]), guards: [], body: (context) => {
return Type.atom("nil");
}}], context);
}}]);

Interpreter.defineElixirFunction("String.Chars", "to_string", 1, "public", [{params: (context) => [Type.variablePattern("term_0")], guards: [], body: (context) => {
return Interpreter.callNamedFunction(Elixir_String_Chars["impl_for!/1"](context.vars.term_0), Type.atom("to_string"), Type.list([context.vars.term_0]), context);
}}]);

Interpreter.defineElixirFunction("String.Chars.Atom", "__impl__", 1, "public", [{params: (context) => [Type.atom("for")], guards: [], body: (context) => {
return Type.atom("Elixir.Atom");
}}, {params: (context) => [Type.atom("protocol")], guards: [], body: (context) => {
return Type.atom("Elixir.String.Chars");
}}]);

Interpreter.defineElixirFunction("String.Chars.Atom", "to_string", 1, "public", [{params: (context) => [Type.atom("nil")], guards: [], body: (context) => {
return Type.bitstring("");
}}, {params: (context) => [Type.variablePattern("atom_0")], guards: [], body: (context) => {
return Erlang["atom_to_binary/1"](context.vars.atom_0);
}}]);

Interpreter.defineElixirFunction("String.Chars.BitString", "__impl__", 1, "public", [{params: (context) => [Type.atom("for")], guards: [], body: (context) => {
return Type.atom("Elixir.BitString");
}}, {params: (context) => [Type.atom("protocol")], guards: [], body: (context) => {
return Type.atom("Elixir.String.Chars");
}}]);

Interpreter.defineElixirFunction("String.Chars.BitString", "to_string", 1, "public", [{params: (context) => [Type.variablePattern("term_0")], guards: [(context) => Erlang["is_binary/1"](context.vars.term_0)], body: (context) => {
return context.vars.term_0;
}}, {params: (context) => [Type.variablePattern("term_0")], guards: [], body: (context) => {
return Erlang["error/1"](Elixir_Protocol_UndefinedError["exception/1"](Type.list([Type.tuple([Type.atom("protocol"), Type.atom("Elixir.String.Chars")]), Type.tuple([Type.atom("value"), context.vars.term_0]), Type.tuple([Type.atom("description"), Type.bitstring("cannot convert a bitstring to a string")])])));
}}]);

Interpreter.defineElixirFunction("String.Chars.Float", "__impl__", 1, "public", [{params: (context) => [Type.atom("for")], guards: [], body: (context) => {
return Type.atom("Elixir.Float");
}}, {params: (context) => [Type.atom("protocol")], guards: [], body: (context) => {
return Type.atom("Elixir.String.Chars");
}}]);

Interpreter.defineElixirFunction("String.Chars.Float", "to_string", 1, "public", [{params: (context) => [Type.variablePattern("term_0")], guards: [], body: (context) => {
return Erlang["float_to_binary/2"](context.vars.term_0, Type.list([Type.atom("short")]));
}}]);

Interpreter.defineElixirFunction("String.Chars.Integer", "__impl__", 1, "public", [{params: (context) => [Type.atom("for")], guards: [], body: (context) => {
return Type.atom("Elixir.Integer");
}}, {params: (context) => [Type.atom("protocol")], guards: [], body: (context) => {
return Type.atom("Elixir.String.Chars");
}}]);

Interpreter.defineElixirFunction("String.Chars.Integer", "to_string", 1, "public", [{params: (context) => [Type.variablePattern("term_0")], guards: [], body: (context) => {
return Erlang["integer_to_binary/1"](context.vars.term_0);
}}]);

Interpreter.defineElixirFunction("String.Chars.List", "__impl__", 1, "public", [{params: (context) => [Type.atom("for")], guards: [], body: (context) => {
return Type.atom("Elixir.List");
}}, {params: (context) => [Type.atom("protocol")], guards: [], body: (context) => {
return Type.atom("Elixir.String.Chars");
}}]);

Interpreter.defineElixirFunction("String.Chars.List", "to_string", 1, "public", [{params: (context) => [Type.variablePattern("charlist_0")], guards: [], body: (context) => {
return Elixir_List["to_string/1"](context.vars.charlist_0);
}}]);

Interpreter.defineElixirFunction("SystemLimitError", "__struct__", 0, "public", [{params: (context) => [], guards: [], body: (context) => {
return Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.SystemLimitError")], [Type.atom("message"), Type.bitstring("a system limit has been reached")]]);
}}]);

Interpreter.defineElixirFunction("SystemLimitError", "__struct__", 1, "public", [{params: (context) => [Type.variablePattern("kv_0")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.kv_0, Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.SystemLimitError")], [Type.atom("message"), Type.bitstring("a system limit has been reached")]]), Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("val_2")]), Type.variablePattern("map_3")], guards: [], body: (context) => {
return Elixir_Map["merge/2"](context.vars.map_3, Type.map([[context.vars.key_1, context.vars.val_2]]));
}}], context, "-__struct__/1-fun-0-"));
}}]);

Interpreter.defineElixirFunction("SystemLimitError", "message", 1, "public", [{params: (context) => [Type.variablePattern("exception_0")], guards: [], body: (context) => {
return Interpreter.dotOperator(context.vars.exception_0, Type.atom("message"));
}}]);

Interpreter.defineElixirFunction("TryClauseError", "__struct__", 0, "public", [{params: (context) => [], guards: [], body: (context) => {
return Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.TryClauseError")], [Type.atom("term"), Type.atom("nil")]]);
}}]);

Interpreter.defineElixirFunction("TryClauseError", "__struct__", 1, "public", [{params: (context) => [Type.variablePattern("kv_0")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.kv_0, Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.TryClauseError")], [Type.atom("term"), Type.atom("nil")]]), Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("val_2")]), Type.variablePattern("map_3")], guards: [], body: (context) => {
return Elixir_Map["merge/2"](context.vars.map_3, Type.map([[context.vars.key_1, context.vars.val_2]]));
}}], context, "-__struct__/1-fun-0-"));
}}]);

Interpreter.defineElixirFunction("TryClauseError", "message", 1, "public", [{params: (context) => [Type.variablePattern("exception_0")], guards: [], body: (context) => {
return Elixir_Exception["__format_message_with_term__/2"](Type.bitstring("no try clause matching:"), Interpreter.dotOperator(context.vars.exception_0, Type.atom("term")));
}}]);

Interpreter.defineElixirFunction("URI", "char_unreserved?", 1, "public", [{params: (context) => [Type.variablePattern("character_0")], guards: [], body: (context) => {
return Interpreter.case(Interpreter.case(Interpreter.case((Interpreter.toBoolean(Erlang["is_integer/1"](context.vars.character_0)) && Interpreter.toBoolean(Erlang[">=/2"](context.vars.character_0, Type.integer(48n))) ? Erlang["=</2"](context.vars.character_0, Type.integer(57n)) : Type.boolean(false)), [{match: Type.atom("false"), guards: [], body: (context) => {
return (Interpreter.toBoolean(Erlang["is_integer/1"](context.vars.character_0)) && Interpreter.toBoolean(Erlang[">=/2"](context.vars.character_0, Type.integer(97n))) ? Erlang["=</2"](context.vars.character_0, Type.integer(122n)) : Type.boolean(false));
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Type.atom("true");
}}], context), [{match: Type.atom("false"), guards: [], body: (context) => {
return (Interpreter.toBoolean(Erlang["is_integer/1"](context.vars.character_0)) && Interpreter.toBoolean(Erlang[">=/2"](context.vars.character_0, Type.integer(65n))) ? Erlang["=</2"](context.vars.character_0, Type.integer(90n)) : Type.boolean(false));
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Type.atom("true");
}}], context), [{match: Type.atom("false"), guards: [], body: (context) => {
return Erlang_Lists["member/2"](context.vars.character_0, Type.list([Type.integer(126n), Type.integer(95n), Type.integer(45n), Type.integer(46n)]));
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Type.atom("true");
}}], context);
}}]);

Interpreter.defineElixirFunction("UndefinedFunctionError", "__struct__", 0, "public", [{params: (context) => [], guards: [], body: (context) => {
return Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.UndefinedFunctionError")], [Type.atom("arity"), Type.atom("nil")], [Type.atom("function"), Type.atom("nil")], [Type.atom("message"), Type.atom("nil")], [Type.atom("module"), Type.atom("nil")], [Type.atom("reason"), Type.atom("nil")]]);
}}]);

Interpreter.defineElixirFunction("UndefinedFunctionError", "__struct__", 1, "public", [{params: (context) => [Type.variablePattern("kv_0")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.kv_0, Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.UndefinedFunctionError")], [Type.atom("arity"), Type.atom("nil")], [Type.atom("function"), Type.atom("nil")], [Type.atom("message"), Type.atom("nil")], [Type.atom("module"), Type.atom("nil")], [Type.atom("reason"), Type.atom("nil")]]), Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("val_2")]), Type.variablePattern("map_3")], guards: [], body: (context) => {
return Elixir_Map["merge/2"](context.vars.map_3, Type.map([[context.vars.key_1, context.vars.val_2]]));
}}], context, "-__struct__/1-fun-0-"));
}}]);

Interpreter.defineElixirFunction("UndefinedFunctionError", "hint_for_missing_alias", 1, "private", [{params: (context) => [Type.variablePattern("module_0")], guards: [], body: (context) => {
return Interpreter.with((context) => {
return Type.bitstring(". Make sure the module name is correct and has been specified in full (or that an alias has been defined)");
}, [{match: Type.bitstringPattern([Type.bitstringSegment(Type.string("Elixir."), {type: "binary"}), Type.bitstringSegment(Type.variablePattern("rest_1"), {type: "binary"})]), guards: [], expression: (context) => Erlang["atom_to_binary/1"](context.vars.module_0)}, {match: Type.atom("false"), guards: [], expression: (context) => Elixir_Kernel["=~/2"](context.vars.rest_1, Type.bitstring("."))}], [{match: Type.matchPlaceholder(), guards: [], body: (context) => {
return Type.bitstring("");
}}], context);
}}]);

Interpreter.defineElixirFunction("UndefinedFunctionError", "message", 1, "public", [{params: (context) => [Type.matchPattern(Type.map([[Type.atom("message"), Type.atom("nil")]]), Type.variablePattern("exception_0"))], guards: [], body: (context) => {
Interpreter.matchOperator(context.vars.exception_0, Type.map([[Type.atom("arity"), Type.variablePattern("arity_4")], [Type.atom("function"), Type.variablePattern("function_3")], [Type.atom("module"), Type.variablePattern("module_2")], [Type.atom("reason"), Type.variablePattern("reason_1")]]), context);
Interpreter.updateVarsToMatchedValues(context);
Interpreter.matchOperator(Elixir_UndefinedFunctionError["message/4"](context.vars.reason_1, context.vars.module_2, context.vars.function_3, context.vars.arity_4), Type.tuple([Type.variablePattern("message_5"), Type.variablePattern("hint_type_6")]), context);
Interpreter.updateVarsToMatchedValues(context);
return Type.bitstring([Type.bitstringSegment(context.vars.message_5, {type: "binary"}), Type.bitstringSegment(Interpreter.case(Erlang["==/2"](context.vars.hint_type_6, Type.atom("suggest_module")), [{match: Type.atom("false"), guards: [], body: (context) => {
return Type.bitstring("");
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Elixir_UndefinedFunctionError["hint_for_missing_alias/1"](context.vars.module_2);
}}], context), {type: "binary"})]);
}}, {params: (context) => [Type.map([[Type.atom("message"), Type.variablePattern("message_0")]])], guards: [], body: (context) => {
return context.vars.message_0;
}}]);

Interpreter.defineElixirFunction("UndefinedFunctionError", "message", 4, "private", [{params: (context) => [Type.atom("nil"), Type.variablePattern("module_0"), Type.variablePattern("function_1"), Type.variablePattern("arity_2")], guards: [], body: (context) => {
globalThis.Hologram.return = Interpreter.cond([{condition: (context) => Interpreter.case(Erlang["==/2"](context.vars.function_1, Type.atom("nil")), [{match: Type.atom("false"), guards: [], body: (context) => {
return Erlang["==/2"](context.vars.arity_2, Type.atom("nil"));
}}, {match: Type.atom("true"), guards: [], body: (context) => {
return Type.atom("true");
}}], context), body: (context) => {
return Type.tuple([Type.bitstring("undefined function"), Type.atom("suggest_module")]);
}}, {condition: (context) => Erlang["==/2"](context.vars.module_0, Type.atom("nil")), body: (context) => {
Interpreter.matchOperator(Elixir_Exception["format_mfa/3"](context.vars.module_0, context.vars.function_1, context.vars.arity_2), Type.variablePattern("formatted_fun_3"), context);
Interpreter.updateVarsToMatchedValues(context);
return Type.tuple([Type.bitstring([Type.bitstringSegment(Type.string("function "), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.formatted_fun_3), {type: "binary"}), Type.bitstringSegment(Type.string(" is undefined"), {type: "binary"})]), Type.atom("suggest_module")]);
}}, {condition: (context) => Erlang["function_exported/3"](context.vars.module_0, Type.atom("module_info"), Type.integer(0n)), body: (context) => {
return Elixir_UndefinedFunctionError["message/4"](Type.atom("function not exported"), context.vars.module_0, context.vars.function_1, context.vars.arity_2);
}}, {condition: (context) => Type.atom("true"), body: (context) => {
return Elixir_UndefinedFunctionError["message/4"](Type.atom("module could not be loaded"), context.vars.module_0, context.vars.function_1, context.vars.arity_2);
}}], context);
Interpreter.updateVarsToMatchedValues(context);
return globalThis.Hologram.return;
}}, {params: (context) => [Type.atom("module could not be loaded"), Type.variablePattern("module_0"), Type.variablePattern("function_1"), Type.variablePattern("arity_2")], guards: [], body: (context) => {
Interpreter.matchOperator(Elixir_Exception["format_mfa/3"](context.vars.module_0, context.vars.function_1, context.vars.arity_2), Type.variablePattern("formatted_fun_3"), context);
Interpreter.updateVarsToMatchedValues(context);
return Type.tuple([Type.bitstring([Type.bitstringSegment(Type.string("function "), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.formatted_fun_3), {type: "binary"}), Type.bitstringSegment(Type.string(" is undefined (module "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.module_0), {type: "binary"}), Type.bitstringSegment(Type.string(" is not available)"), {type: "binary"})]), Type.atom("suggest_module")]);
}}, {params: (context) => [Type.atom("function not exported"), Type.variablePattern("module_0"), Type.variablePattern("function_1"), Type.variablePattern("arity_2")], guards: [], body: (context) => {
Interpreter.matchOperator(Elixir_Exception["format_mfa/3"](context.vars.module_0, context.vars.function_1, context.vars.arity_2), Type.variablePattern("formatted_fun_3"), context);
Interpreter.updateVarsToMatchedValues(context);
return Type.tuple([Type.bitstring([Type.bitstringSegment(Type.string("function "), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.formatted_fun_3), {type: "binary"}), Type.bitstringSegment(Type.string(" is undefined or private"), {type: "binary"})]), Type.atom("suggest_function")]);
}}, {params: (context) => [Type.atom("undefined local"), Type.atom("nil"), Type.variablePattern("function_0"), Type.variablePattern("arity_1")], guards: [], body: (context) => {
return Type.tuple([Type.bitstring([Type.bitstringSegment(Type.string("function "), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.function_0), {type: "binary"}), Type.bitstringSegment(Type.string("/"), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.arity_1), {type: "binary"}), Type.bitstringSegment(Type.string(" is undefined (there is no such import)"), {type: "binary"})]), Type.atom("no_hint")]);
}}, {params: (context) => [Type.variablePattern("reason_0"), Type.variablePattern("module_1"), Type.variablePattern("function_2"), Type.variablePattern("arity_3")], guards: [], body: (context) => {
Interpreter.matchOperator(Elixir_Exception["format_mfa/3"](context.vars.module_1, context.vars.function_2, context.vars.arity_3), Type.variablePattern("formatted_fun_4"), context);
Interpreter.updateVarsToMatchedValues(context);
return Type.tuple([Type.bitstring([Type.bitstringSegment(Type.string("function "), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.formatted_fun_4), {type: "binary"}), Type.bitstringSegment(Type.string(" is undefined ("), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.reason_0), {type: "binary"}), Type.bitstringSegment(Type.string(")"), {type: "binary"})]), Type.atom("suggest_module")]);
}}]);

Interpreter.defineElixirFunction("UnicodeConversionError", "__struct__", 0, "public", [{params: (context) => [], guards: [], body: (context) => {
return Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.UnicodeConversionError")], [Type.atom("encoded"), Type.atom("nil")], [Type.atom("message"), Type.atom("nil")]]);
}}]);

Interpreter.defineElixirFunction("UnicodeConversionError", "__struct__", 1, "public", [{params: (context) => [Type.variablePattern("kv_0")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.kv_0, Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.UnicodeConversionError")], [Type.atom("encoded"), Type.atom("nil")], [Type.atom("message"), Type.atom("nil")]]), Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("val_2")]), Type.variablePattern("map_3")], guards: [], body: (context) => {
return Elixir_Map["merge/2"](context.vars.map_3, Type.map([[context.vars.key_1, context.vars.val_2]]));
}}], context, "-__struct__/1-fun-0-"));
}}]);

Interpreter.defineElixirFunction("UnicodeConversionError", "detail", 1, "private", [{params: (context) => [Type.variablePattern("rest_0")], guards: [(context) => Erlang["is_binary/1"](context.vars.rest_0)], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string("encoding starting at "), {type: "binary"}), Type.bitstringSegment(Elixir_Kernel["inspect/1"](context.vars.rest_0), {type: "binary"})]);
}}, {params: (context) => [Type.consPattern(Type.variablePattern("h_0"), Type.matchPlaceholder())], guards: [(context) => Erlang["is_integer/1"](context.vars.h_0)], body: (context) => {
return Type.bitstring([Type.bitstringSegment(Type.string("code point "), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](context.vars.h_0), {type: "binary"})]);
}}, {params: (context) => [Type.consPattern(Type.variablePattern("h_0"), Type.matchPlaceholder())], guards: [], body: (context) => {
return Elixir_UnicodeConversionError["detail/1"](context.vars.h_0);
}}]);

Interpreter.defineElixirFunction("UnicodeConversionError", "exception", 1, "public", [{params: (context) => [Type.variablePattern("opts_0")], guards: [], body: (context) => {
return Elixir_UnicodeConversionError["__struct__/1"](Type.list([Type.tuple([Type.atom("__exception__"), Type.atom("true")]), Type.tuple([Type.atom("encoded"), Elixir_Keyword["fetch!/2"](context.vars.opts_0, Type.atom("encoded"))]), Type.tuple([Type.atom("message"), Type.bitstring([Type.bitstringSegment(Elixir_String_Chars["to_string/1"](Elixir_Keyword["fetch!/2"](context.vars.opts_0, Type.atom("kind"))), {type: "binary"}), Type.bitstringSegment(Type.string(" "), {type: "binary"}), Type.bitstringSegment(Elixir_String_Chars["to_string/1"](Elixir_UnicodeConversionError["detail/1"](Elixir_Keyword["fetch!/2"](context.vars.opts_0, Type.atom("rest")))), {type: "binary"})])])]));
}}]);

Interpreter.defineElixirFunction("UnicodeConversionError", "message", 1, "public", [{params: (context) => [Type.variablePattern("exception_0")], guards: [], body: (context) => {
return Interpreter.dotOperator(context.vars.exception_0, Type.atom("message"));
}}]);

Interpreter.defineElixirFunction("WithClauseError", "__struct__", 0, "public", [{params: (context) => [], guards: [], body: (context) => {
return Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.WithClauseError")], [Type.atom("term"), Type.atom("nil")]]);
}}]);

Interpreter.defineElixirFunction("WithClauseError", "__struct__", 1, "public", [{params: (context) => [Type.variablePattern("kv_0")], guards: [], body: (context) => {
return Elixir_Enum["reduce/3"](context.vars.kv_0, Type.map([[Type.atom("__exception__"), Type.atom("true")], [Type.atom("__struct__"), Type.atom("Elixir.WithClauseError")], [Type.atom("term"), Type.atom("nil")]]), Type.anonymousFunction(2, [{params: (context) => [Type.tuple([Type.variablePattern("key_1"), Type.variablePattern("val_2")]), Type.variablePattern("map_3")], guards: [], body: (context) => {
return Elixir_Map["merge/2"](context.vars.map_3, Type.map([[context.vars.key_1, context.vars.val_2]]));
}}], context, "-__struct__/1-fun-0-"));
}}]);

Interpreter.defineElixirFunction("WithClauseError", "message", 1, "public", [{params: (context) => [Type.variablePattern("exception_0")], guards: [], body: (context) => {
return Elixir_Exception["__format_message_with_term__/2"](Type.bitstring("no with clause matching:"), Interpreter.dotOperator(context.vars.exception_0, Type.atom("term")));
}}]);

Interpreter.defineFunctionClauseHeads("Application", "get_env", 2, "public", [{params: (context) => [Type.variablePattern("x0_0"), Type.variablePattern("x1_1")], guards: []}]);
Interpreter.defineFunctionClauseHeads("Application", "get_env", 3, "public", [{params: (context) => [Type.variablePattern("app_0"), Type.variablePattern("key_1"), Type.variablePattern("default_2")], guards: [(context) => Erlang["is_atom/1"](context.vars.app_0)]}]);
Interpreter.defineFunctionClauseHeads("Code", "ensure_compiled", 1, "public", [{params: (context) => [Type.variablePattern("module_0")], guards: [(context) => Erlang["is_atom/1"](context.vars.module_0)]}]);
Interpreter.defineFunctionClauseHeads("Code", "ensure_compiled", 2, "private", [{params: (context) => [Type.variablePattern("module_0"), Type.variablePattern("mode_1")], guards: []}]);
Interpreter.defineFunctionClauseHeads("Code", "ensure_loaded", 1, "public", [{params: (context) => [Type.variablePattern("module_0")], guards: [(context) => Erlang["is_atom/1"](context.vars.module_0)]}]);
Interpreter.defineFunctionClauseHeads("Exception", "format_stacktrace", 0, "public", [{params: (context) => [], guards: []}]);
Interpreter.defineFunctionClauseHeads("Exception", "format_stacktrace", 1, "public", [{params: (context) => [Type.variablePattern("trace_0")], guards: []}]);
Interpreter.defineFunctionClauseHeads("FunctionClauseError", "message", 1, "public", [{params: (context) => [Type.variablePattern("exception_0")], guards: []}]);
Interpreter.defineFunctionClauseHeads("Hologram.JS", "call", 4, "public", [{params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder(), Type.matchPlaceholder(), Type.matchPlaceholder()], guards: []}]);
Interpreter.defineFunctionClauseHeads("Hologram.JS", "delete", 3, "public", [{params: (context) => [Type.variablePattern("receiver_0"), Type.matchPlaceholder(), Type.matchPlaceholder()], guards: []}]);
Interpreter.defineFunctionClauseHeads("Hologram.JS", "dispatch_event", 5, "public", [{params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder(), Type.matchPlaceholder(), Type.matchPlaceholder(), Type.matchPlaceholder()], guards: []}]);
Interpreter.defineFunctionClauseHeads("Hologram.JS", "eval", 1, "public", [{params: (context) => [Type.matchPlaceholder()], guards: []}]);
Interpreter.defineFunctionClauseHeads("Hologram.JS", "exec", 1, "public", [{params: (context) => [Type.matchPlaceholder()], guards: []}]);
Interpreter.defineFunctionClauseHeads("Hologram.JS", "get", 3, "public", [{params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder(), Type.matchPlaceholder()], guards: []}]);
Interpreter.defineFunctionClauseHeads("Hologram.JS", "instanceof", 3, "public", [{params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder(), Type.matchPlaceholder()], guards: []}]);
Interpreter.defineFunctionClauseHeads("Hologram.JS", "new", 3, "public", [{params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder(), Type.matchPlaceholder()], guards: []}]);
Interpreter.defineFunctionClauseHeads("Hologram.JS", "set", 4, "public", [{params: (context) => [Type.variablePattern("receiver_0"), Type.matchPlaceholder(), Type.matchPlaceholder(), Type.matchPlaceholder()], guards: []}]);
Interpreter.defineFunctionClauseHeads("Hologram.JS", "typeof", 2, "public", [{params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder()], guards: []}]);
Interpreter.defineFunctionClauseHeads("Hologram.Router.Helpers", "asset_path", 1, "public", [{params: (context) => [Type.variablePattern("static_path_0")], guards: []}]);
Interpreter.defineFunctionClauseHeads("Hologram.Template.Marker", "item_key", 1, "public", [{params: (context) => [Type.map([[Type.atom("id"), Type.variablePattern("id_0")]])], guards: []}, {params: (context) => [Type.matchPlaceholder()], guards: []}]);
Interpreter.defineFunctionClauseHeads("Hologram.Template.Marker", "key_from_value", 1, "public", [{params: (context) => [Type.variablePattern("value_0")], guards: [(context) => Erlang["is_binary/1"](context.vars.value_0)]}, {params: (context) => [Type.variablePattern("value_0")], guards: [(context) => Erlang["is_integer/1"](context.vars.value_0)]}, {params: (context) => [Type.variablePattern("value_0")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_atom/1"](context.vars.value_0)) && Interpreter.toBoolean(Erlang["not/1"](Erlang["==/2"](context.vars.value_0, Type.atom("nil")))) ? Erlang["not/1"](Erlang["is_boolean/1"](context.vars.value_0)) : Type.boolean(false))]}, {params: (context) => [Type.matchPlaceholder()], guards: []}]);
Interpreter.defineFunctionClauseHeads("Hologram.Template.Marker", "memoized_item", 5, "public", [{params: (context) => [Type.matchPlaceholder(), Type.matchPlaceholder(), Type.matchPlaceholder(), Type.matchPlaceholder(), Type.variablePattern("item_fun_4")], guards: []}]);
Interpreter.defineFunctionClauseHeads("IO", "inspect", 1, "public", [{params: (context) => [Type.variablePattern("x0_0")], guards: []}]);
Interpreter.defineFunctionClauseHeads("IO", "inspect", 2, "public", [{params: (context) => [Type.variablePattern("item_0"), Type.variablePattern("opts_1")], guards: []}]);
Interpreter.defineFunctionClauseHeads("IO", "inspect", 3, "public", [{params: (context) => [Type.variablePattern("device_0"), Type.variablePattern("item_1"), Type.variablePattern("opts_2")], guards: [(context) => ((Interpreter.toBoolean(Erlang["is_atom/1"](context.vars.device_0)) || Interpreter.toBoolean(Erlang["is_pid/1"](context.vars.device_0))) ? Erlang["is_list/1"](context.vars.opts_2) : Type.boolean(false))]}]);
Interpreter.defineFunctionClauseHeads("IO", "warn", 1, "public", [{params: (context) => [Type.variablePattern("message_0")], guards: []}]);
Interpreter.defineFunctionClauseHeads("IO", "warn", 2, "public", [{params: (context) => [Type.variablePattern("message_0"), Type.matchPattern(Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Macro.Env")], [Type.atom("file"), Type.variablePattern("file_2")], [Type.atom("line"), Type.variablePattern("line_1")]]), Type.variablePattern("env_3"))], guards: []}, {params: (context) => [Type.variablePattern("message_0"), Type.matchPattern(Type.consPattern(Type.tuple([Type.matchPlaceholder(), Type.matchPlaceholder()]), Type.matchPlaceholder()), Type.variablePattern("keyword_1"))], guards: []}, {params: (context) => [Type.variablePattern("message_0"), Type.list([])], guards: []}, {params: (context) => [Type.variablePattern("message_0"), Type.matchPattern(Type.consPattern(Type.tuple([Type.matchPlaceholder(), Type.matchPlaceholder(), Type.matchPlaceholder(), Type.matchPlaceholder()]), Type.matchPlaceholder()), Type.variablePattern("stacktrace_1"))], guards: []}]);
Interpreter.defineFunctionClauseHeads("IO", "warn_once", 3, "public", [{params: (context) => [Type.variablePattern("key_0"), Type.variablePattern("message_1"), Type.variablePattern("stacktrace_drop_levels_2")], guards: []}]);
Interpreter.defineFunctionClauseHeads("Kernel", "inspect", 1, "public", [{params: (context) => [Type.variablePattern("x0_0")], guards: []}]);
Interpreter.defineFunctionClauseHeads("Kernel", "inspect", 2, "public", [{params: (context) => [Type.variablePattern("term_0"), Type.variablePattern("opts_1")], guards: [(context) => Erlang["is_list/1"](context.vars.opts_1)]}]);
Interpreter.defineFunctionClauseHeads("String", "contains?", 2, "public", [{params: (context) => [Type.variablePattern("string_0"), Type.variablePattern("contents_1")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_binary/1"](context.vars.string_0)) ? Erlang["is_list/1"](context.vars.contents_1) : Type.boolean(false))]}, {params: (context) => [Type.variablePattern("string_0"), Type.variablePattern("contents_1")], guards: [(context) => Erlang["is_binary/1"](context.vars.string_0)]}]);
Interpreter.defineFunctionClauseHeads("String", "downcase", 1, "public", [{params: (context) => [Type.variablePattern("x0_0")], guards: []}]);
{
const s0 = Interpreter.literalSet(() => [Type.atom("greek"), Type.atom("turkic")]);
Interpreter.defineFunctionClauseHeads("String", "downcase", 2, "public", [{params: (context) => [Type.bitstring(""), Type.matchPlaceholder()], guards: []}, {params: (context) => [Type.variablePattern("string_0"), Type.atom("default")], guards: [(context) => Erlang["is_binary/1"](context.vars.string_0)]}, {params: (context) => [Type.variablePattern("string_0"), Type.atom("ascii")], guards: [(context) => Erlang["is_binary/1"](context.vars.string_0)]}, {params: (context) => [Type.variablePattern("string_0"), Type.variablePattern("mode_1")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_binary/1"](context.vars.string_0)) ? Type.boolean(s0.has(context.vars.mode_1)) : Type.boolean(false))]}]);
}
Interpreter.defineFunctionClauseHeads("String", "replace", 3, "public", [{params: (context) => [Type.variablePattern("x0_0"), Type.variablePattern("x1_1"), Type.variablePattern("x2_2")], guards: []}]);
Interpreter.defineFunctionClauseHeads("String", "replace", 4, "public", [{params: (context) => [Type.variablePattern("subject_0"), Type.variablePattern("pattern_1"), Type.variablePattern("replacement_2"), Type.variablePattern("options_3")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_binary/1"](context.vars.subject_0)) && (Interpreter.toBoolean(Erlang["is_binary/1"](context.vars.replacement_2)) || Interpreter.toBoolean(Erlang["is_function/2"](context.vars.replacement_2, Type.integer(1n)))) ? Erlang["is_list/1"](context.vars.options_3) : Type.boolean(false))]}]);
Interpreter.defineFunctionClauseHeads("String", "trim", 1, "public", [{params: (context) => [Type.variablePattern("string_0")], guards: [(context) => Erlang["is_binary/1"](context.vars.string_0)]}]);
Interpreter.defineFunctionClauseHeads("String", "trim", 2, "public", [{params: (context) => [Type.variablePattern("string_0"), Type.variablePattern("to_trim_1")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_binary/1"](context.vars.string_0)) ? Erlang["is_binary/1"](context.vars.to_trim_1) : Type.boolean(false))]}]);
Interpreter.defineFunctionClauseHeads("String", "upcase", 1, "public", [{params: (context) => [Type.variablePattern("x0_0")], guards: []}]);
{
const s0 = Interpreter.literalSet(() => [Type.atom("greek"), Type.atom("turkic")]);
Interpreter.defineFunctionClauseHeads("String", "upcase", 2, "public", [{params: (context) => [Type.bitstring(""), Type.matchPlaceholder()], guards: []}, {params: (context) => [Type.variablePattern("string_0"), Type.atom("default")], guards: [(context) => Erlang["is_binary/1"](context.vars.string_0)]}, {params: (context) => [Type.variablePattern("string_0"), Type.atom("ascii")], guards: [(context) => Erlang["is_binary/1"](context.vars.string_0)]}, {params: (context) => [Type.variablePattern("string_0"), Type.variablePattern("mode_1")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_binary/1"](context.vars.string_0)) ? Type.boolean(s0.has(context.vars.mode_1)) : Type.boolean(false))]}]);
}
Interpreter.defineFunctionClauseHeads("String.Tokenizer", "tokenize", 1, "public", [{params: (context) => [Type.consPattern(Type.variablePattern("head_0"), Type.variablePattern("tail_1"))], guards: []}, {params: (context) => [Type.list([])], guards: []}]);
Interpreter.defineFunctionClauseHeads("Task", "await", 1, "public", [{params: (context) => [Type.variablePattern("x0_0")], guards: []}]);
Interpreter.defineFunctionClauseHeads("Task", "await", 2, "public", [{params: (context) => [Type.matchPattern(Type.map([[Type.atom("__struct__"), Type.atom("Elixir.Task")], [Type.atom("owner"), Type.variablePattern("owner_1")], [Type.atom("ref"), Type.variablePattern("ref_0")]]), Type.variablePattern("task_2")), Type.variablePattern("timeout_3")], guards: [(context) => (Interpreter.toBoolean(Erlang["==/2"](context.vars.timeout_3, Type.atom("infinity"))) ? Type.boolean(true) : (Interpreter.toBoolean(Erlang["is_integer/1"](context.vars.timeout_3)) ? Erlang[">=/2"](context.vars.timeout_3, Type.integer(0n)) : Type.boolean(false)))]}]);
Interpreter.defineFunctionClauseHeads("URI", "encode", 1, "public", [{params: (context) => [Type.variablePattern("x0_0")], guards: []}]);
Interpreter.defineFunctionClauseHeads("URI", "encode", 2, "public", [{params: (context) => [Type.variablePattern("string_0"), Type.variablePattern("predicate_1")], guards: [(context) => (Interpreter.toBoolean(Erlang["is_binary/1"](context.vars.string_0)) ? Erlang["is_function/2"](context.vars.predicate_1, Type.integer(1n)) : Type.boolean(false))]}]);

Hologram.run();

console.debug("Hologram: runtime script executed in", PerformanceTimer.diff(startTime));